// Dra. Malu / CaptaZap v50 — cabeçalho operacional em uma linha, sem ruído visual.
// src/modules/captazap/CaptaZap.jsx

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as ZapiCallSDK from "@z-api/call";
const initZapiCall = ZapiCallSDK.init;
import {
  Search,
  Clock3,
  RefreshCw,
  Settings,
  MoreHorizontal,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  ArrowRightLeft,
  CheckCircle2,
  Plus,
  Inbox,
  Headphones,
  TrendingUp,
  Activity,
  Star,
  MessageSquare,
  Phone,
  PhoneOff,
  Building2,
  UserRound,
  UserPlus,
  Users,
  BellRing,
  BellOff,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

import {
  FaWhatsapp,
  FaInstagram,
  FaFacebookF,
  FaTelegramPlane,
} from "react-icons/fa";

import { useAuth } from "../../context/AuthContext";
import { useEmpresa } from "../../context/EmpresaContext";

import QueueList from "./components/QueueList";
import SupervisaoEquipeCaptaZap from "./components/SupervisaoEquipeCaptaZap";
import BomDiaProducaoModal from "./components/BomDiaProducaoModal";
import ChatPanel from "./components/ChatPanel";
import MetaHomologacaoViewer from "./MetaHomologacaoViewer";
import LeadDetails from "./components/LeadDetails";
import AgendamentosModal from "../../Components/AgendamentosModal";
import TransferenciaLeadModal from "../../Components/TransferenciaLeadModal";
import TarefaModal from "../../Components/TarefaModal";
import InteracoesModal from "../../Components/InteracoesModal";

const LOGO_CAPTAZAP_HORIZONTAL = "/logo-captazap-horizontal-cortada.png";
const LOGO_CAPTAZAP_ICON = "/logo-captazap-icon-cortada.png";
const MAX_CONTATOS_RENDER = 220;
const MAX_CONTATOS_RENDER_COM_BUSCA = 360;
const HORA_MS = 60 * 60 * 1000;
const DIA_MS = 24 * HORA_MS;
const CAPTAZAP_TOAST_NOVA_MENSAGEM_MS = 6500;
const CAPTAZAP_TOAST_MAX_VISIVEIS = 3;
const CAPTAZAP_TOAST_DEDUPE_MS = 60000;

// Performance de produção: o SSE continua sendo a fonte principal de atualização.
// O polling abaixo é apenas uma rede de segurança para redes que perdem eventos.
const CAPTAZAP_POLLING_FALLBACK_MS = 60000;
const CAPTAZAP_POLLING_MENSAGEM_ABERTA_MS = 120000;
const CAPTAZAP_LISTA_PAGE_SIZE = 50;
const CAPTAZAP_MENSAGENS_PAGE_SIZE = 60;
const CAPTAZAP_ABAS_OPERACIONAIS_PAGINADAS = new Set([
  "minhas",
  "fila",
  "fechados",
]);
const CAPTAZAP_AVATARES_POR_CICLO = 6;
const CAPTAZAP_AVATARES_CONCORRENCIA = 2;
const CAPTAZAP_AVATARES_MAX_AUTO_POR_EMPRESA = 18;
const CAPTAZAP_CRM_BACKGROUND_CONCURRENCY = 2;

// Speed: a mesma carteira CRM serve para o painel e para a busca. Evita baixar
// milhares de leads novamente a cada pesquisa de nome/telefone.
const captaZapLeadsCache = new Map();
const CAPTAZAP_LEADS_CACHE_TTL_MS = 2 * 60 * 1000;

async function carregarLeadsCRMCacheCaptaZap(empresaId, { forcar = false } = {}) {
  const chave = String(empresaId || "");
  if (!chave) return [];
  const atual = captaZapLeadsCache.get(chave);
  if (!forcar && atual?.dados && Date.now() - atual.em < CAPTAZAP_LEADS_CACHE_TTL_MS) {
    return atual.dados;
  }
  if (atual?.promessa) return atual.promessa;
  const promessa = requestJson(`/leads?empresa_id=${encodeURIComponent(chave)}&mostrarInativas=true`)
    .then((resposta) => {
      const dados = pickArray(resposta, ["leads", "data", "items"]);
      captaZapLeadsCache.set(chave, { dados, em: Date.now(), promessa: null });
      return dados;
    })
    .catch((erro) => {
      if (captaZapLeadsCache.get(chave)?.promessa === promessa) {
        captaZapLeadsCache.delete(chave);
      }
      throw erro;
    });
  captaZapLeadsCache.set(chave, { dados: atual?.dados || null, em: atual?.em || 0, promessa });
  return promessa;
}

async function allSettledComConcorrenciaCaptaZap(tarefas = [], limite = 3) {
  const lista = Array.isArray(tarefas) ? tarefas : [];
  const resultados = new Array(lista.length);
  const concorrencia = Math.max(1, Math.min(Number(limite || 3), lista.length || 1));
  let proximoIndice = 0;

  async function worker() {
    while (true) {
      const indice = proximoIndice++;
      if (indice >= lista.length) return;

      try {
        resultados[indice] = {
          status: "fulfilled",
          value: await lista[indice](),
        };
      } catch (reason) {
        resultados[indice] = { status: "rejected", reason };
      }
    }
  }

  await Promise.all(Array.from({ length: concorrencia }, () => worker()));
  return resultados;
}

// Alertas nativos do Chrome. Esta implementação fica no CaptaZap.jsx,
// isolada do ChatPanel para não interferir no fluxo de Lead Fria.
const CAPTAZAP_CHROME_NOTIFICACOES_STORAGE_PREFIX = "captazap:alertas-chrome";
const CAPTAZAP_CHROME_NOTIFICACAO_MAX_TEXTO = 160;
const CAPTAZAP_CHROME_NOTIFICACAO_DURACAO_MS = 12000;
const CAPTAZAP_CABECALHO_RECOLHIDO_STORAGE_KEY = "captazap:cabecalho-recolhido";
const CAPTAZAP_CABECALHO_RECOLHIVEL_VERSION =
  "v30.1-cabecalho-recolhivel-browser-fetch-2026-07-13";
const CAPTAZAP_DRA_MALU_OPERACIONAL_VERSION =
  "v67.5-assumir-reabrir-sempre-visivel-2026-08-27";
const CAPTAZAP_NOME_CONTATO_VERSION =
  "v67.6-nome-contato-persistente-lid-sse-2026-08-27";
const CAPTAZAP_REALTIME_VERSION =
  "v68.1-realtime-fila-media-ready-2026-08-27";
const CAPTAZAP_REABERTURA_VERSION =
  "v68.2-reabertura-idempotente-um-clique-2026-08-28";
const CAPTAZAP_DELIVERY_REALTIME_VERSION =
  "v68.3.1-delivery-status-realtime-race-safe-2026-08-28";
const CAPTAZAP_CALLS_PERFORMANCE_VERSION =
  "v7.1-calls-performance-2026-10-16";
const CAPTAZAP_CALLS_FIX_VERSION =
  "v7.2-nome-recusa-confirmada-2026-10-16";
const CAPTAZAP_CALLS_REPLAY_FIX_VERSION =
  "v7.3-no-stale-call-replay-2026-10-16";
const CAPTAZAP_CALLS_SESSION_BASELINE_VERSION =
  "v7.4-ignore-existing-sdk-calls-2026-10-16";
const CAPTAZAP_CALLS_STARTUP_QUARANTINE_VERSION =
  "v7.5-startup-quarantine-2026-10-16";
const CAPTAZAP_CALLS_OPEN_CHAT_VERSION =
  "v7.7-open-chat-on-answer-2026-10-16";
const CAPTAZAP_CALLS_TIMEZONE_VERSION =
  "v7.9-call-timezone-utc-to-local-2026-10-16";
console.info(`[CaptaZap] reabertura: ${CAPTAZAP_REABERTURA_VERSION}`);
console.info(`[CaptaZap] delivery realtime: ${CAPTAZAP_DELIVERY_REALTIME_VERSION}`);
console.info(`[CaptaZap] calls: ${CAPTAZAP_CALLS_PERFORMANCE_VERSION}`);
console.info(`[CaptaZap] calls fix: ${CAPTAZAP_CALLS_FIX_VERSION}`);
console.info(`[CaptaZap] calls replay: ${CAPTAZAP_CALLS_REPLAY_FIX_VERSION}`);
console.info(`[CaptaZap] calls baseline: ${CAPTAZAP_CALLS_SESSION_BASELINE_VERSION}`);
console.info(`[CaptaZap] calls quarantine: ${CAPTAZAP_CALLS_STARTUP_QUARANTINE_VERSION}`);
console.info(`[CaptaZap] calls open chat: ${CAPTAZAP_CALLS_OPEN_CHAT_VERSION}`);

// Sinalização visual da recuperação feita pela Dra. Malu IA.
// O backend grava uma mensagem interna no CaptaZap; aqui normalizamos para
// o front exibir curto na lista e destacado dentro da conversa.
const CAPTAZAP_DRA_MALU_RECUPERADO_BADGE = "🟡 Recuperado Dra. Malu";
const CAPTAZAP_DRA_MALU_RECUPERADO_TEXTO_CHAT =
  "🟡 Chamado recuperado pela Dra. Malu. Favor dar continuidade no atendimento.";
const CAPTAZAP_SOMENTE_CONTATO_BADGE = "Somente contato";
const CAPTAZAP_SOMENTE_CONTATO_TAG = "🟠 Somente contato";

const API_BASE_RAW = String(
  import.meta.env?.VITE_API_BASE_URL ||
    import.meta.env?.VITE_API_URL ||
    import.meta.env?.VITE_BACKEND_URL ||
    "",
).trim();

const API_PUBLIC_BASE = getApiPublicBase(API_BASE_RAW);
const API_BASE = API_PUBLIC_BASE;

const CHANNELS = [
  {
    key: "todos",
    label: "Todos",
    icon: MessageSquare,
    activeClass:
      "border-blue-800 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white shadow-lg shadow-blue-900/20",
    inactiveClass:
      "border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800",
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    icon: FaWhatsapp,
    activeClass:
      "border-[#25D366] bg-[#25D366] text-white shadow-lg shadow-emerald-700/25",
    inactiveClass:
      "border-emerald-200 bg-emerald-50 text-[#128C7E] hover:border-[#25D366] hover:bg-emerald-100",
  },
  {
    key: "instagram",
    label: "Instagram",
    icon: FaInstagram,
    activeClass:
      "border-pink-500 bg-gradient-to-br from-[#833AB4] via-[#E1306C] to-[#FCAF45] text-white shadow-lg shadow-pink-700/25",
    inactiveClass:
      "border-pink-200 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 text-[#C13584] hover:border-pink-400 hover:from-purple-100 hover:via-pink-100 hover:to-orange-100",
  },
  {
    key: "facebook",
    label: "Facebook",
    icon: FaFacebookF,
    activeClass:
      "border-[#1877F2] bg-[#1877F2] text-white shadow-lg shadow-blue-700/25",
    inactiveClass:
      "border-blue-200 bg-blue-50 text-[#1877F2] hover:border-[#1877F2] hover:bg-blue-100",
  },
  {
    key: "telegram",
    label: "Telegram",
    icon: FaTelegramPlane,
    activeClass:
      "border-[#229ED9] bg-[#229ED9] text-white shadow-lg shadow-sky-700/25",
    inactiveClass:
      "border-sky-200 bg-sky-50 text-[#229ED9] hover:border-[#229ED9] hover:bg-sky-100",
  },
  {
    key: "favoritos",
    label: "Favoritos",
    icon: Star,
    activeClass:
      "border-amber-500 bg-gradient-to-br from-amber-400 via-orange-400 to-orange-500 text-white shadow-lg shadow-orange-700/20",
    inactiveClass:
      "border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100",
  },
];

function getNomeConexaoWhatsAppCaptaZap(conexao = {}, index = 0) {
  const candidatos = [
    conexao?.nome,
    conexao?.nome_exibicao,
    conexao?.nome_aba,
    conexao?.nome_conexao,
    conexao?.nomeConexao,
    conexao?.apelido,
    conexao?.descricao,
    conexao?.titulo,
    conexao?.label,
    conexao?.nome_instancia,
    conexao?.nomeInstancia,
  ];

  const nome = candidatos
    .map((value) => String(value || "").trim())
    .find(Boolean);

  return nome || `Conexão ${index + 1}`;
}


function numeroConexaoExibicaoCaptaZap(conexao = {}) {
  const bruto = String(conexao?.telefone || conexao?.numero_whatsapp || "").trim();
  const numero = bruto.replace(/\D/g, "");
  if (numero.length === 13 && numero.startsWith("55"))
    return `(${numero.slice(2, 4)}) ${numero.slice(4, 9)}-${numero.slice(9)}`;
  if (numero.length === 12 && numero.startsWith("55"))
    return `(${numero.slice(2, 4)}) ${numero.slice(4, 8)}-${numero.slice(8)}`;
  if (numero.length === 11)
    return `(${numero.slice(0, 2)}) ${numero.slice(2, 7)}-${numero.slice(7)}`;
  if (numero.length === 10)
    return `(${numero.slice(0, 2)}) ${numero.slice(2, 6)}-${numero.slice(6)}`;
  return "";
}

function getTelefoneConversaFixadaCaptaZap(conversa = {}) {
  const bruto =
    conversa?.telefone ||
    conversa?.numero ||
    conversa?.phone ||
    conversa?.whatsapp ||
    conversa?.telefone_normalizado ||
    conversa?.lead?.telefone1 ||
    conversa?.lead?.telefone ||
    conversa?.lead?.celular ||
    conversa?.lead?.whatsapp ||
    "";

  return (
    normalizarTelefoneWhatsappBrasil(bruto) ||
    normalizarTelefone(bruto) ||
    String(bruto || "").replace(/\D/g, "")
  );
}

function getChaveConversaFixadaCaptaZap(conversa = {}) {
  const instancia = String(
    getConexaoInstanceIdConversaCaptaZap(conversa) ||
      conversa?.zapi_instance_id ||
      conversa?.zapiInstanceId ||
      "",
  ).trim();
  const telefone = getTelefoneConversaFixadaCaptaZap(conversa);

  return instancia && telefone ? `${instancia}::${telefone}` : "";
}

function isConversaFixadaCaptaZap(conversa = {}, fixadas = []) {
  const chave = getChaveConversaFixadaCaptaZap(conversa);
  if (!chave) return false;

  return (Array.isArray(fixadas) ? fixadas : []).some(
    (item) => getChaveConversaFixadaCaptaZap(item) === chave,
  );
}

function getFixacaoConversaCaptaZap(conversa = {}, fixadas = []) {
  const chave = getChaveConversaFixadaCaptaZap(conversa);
  if (!chave) return null;

  return (Array.isArray(fixadas) ? fixadas : []).find(
    (item) => getChaveConversaFixadaCaptaZap(item) === chave,
  ) || null;
}

function isFixacaoDoUsuarioCaptaZap(fixacao = null, usuarioId = null, usuarioNome = "") {
  if (!fixacao) return false;

  const fixacaoUsuarioId = String(fixacao?.usuario_id || fixacao?.usuarioId || "").trim();
  const usuarioIdAtual = String(usuarioId || "").trim();
  if (fixacaoUsuarioId && usuarioIdAtual) return fixacaoUsuarioId === usuarioIdAtual;

  const nomeFixacao = normalizarNomeComparacao(
    fixacao?.usuario_nome || fixacao?.usuarioNome || "",
  );
  const nomeAtual = normalizarNomeComparacao(usuarioNome || "");
  return Boolean(nomeFixacao && nomeAtual && nomeFixacao === nomeAtual);
}

export default function CaptaZap({ abrirSupervisaoInicial = false } = {}) {
  const { usuario } = useAuth();
  const { empresaAtiva } = useEmpresa();
  const location = useLocation();
  const navigate = useNavigate();

  const isDev = Boolean(import.meta.env?.DEV);

  const [canalSelecionado, setCanalSelecionado] = useState("todos");
  const [conexaoSelecionada, setConexaoSelecionada] = useState("");
  const [conexoesWhatsApp, setConexoesWhatsApp] = useState([]);
  const [conversasFixadas, setConversasFixadas] = useState([]);
  const [conversasAcompanhamentoFixadas, setConversasAcompanhamentoFixadas] =
    useState([]);
  const [fixandoConversaKey, setFixandoConversaKey] = useState("");
  const [carregandoConexoesWhatsApp, setCarregandoConexoesWhatsApp] =
    useState(false);
  const conexaoSelecionadaRef = useRef("");
  const [conversaSelecionada, setConversaSelecionada] = useState(null);
  const conversaSelecionadaRef = useRef(null);
  const refreshConversasRealtimeTimerRef = useRef(null);
  const carregamentoCRMEmAndamentoRef = useRef(false);
  const recarregamentoConversasEmAndamentoRef = useRef(false);
  const recarregamentoConversasPendenteRef = useRef(false);
  const recarregamentosMensagensEmAndamentoRef = useRef(new Set());
  const recarregamentosMensagensPendentesRef = useRef(new Set());
  const sincronizacaoRetomadaTimerRef = useRef(null);
  const ultimoPollingMensagensAbertasRef = useRef(0);
  const buscaContatosCRMCacheRef = useRef(new Map());
  const buscaContatosCRMTimerRef = useRef(null);
  const buscaOperacaoCacheRef = useRef(new Map());
  const statusChamadoReconciliadoCacheRef = useRef(new Map());
  // Status da Z-API pode chegar antes da resposta HTTP do /whatsapp/enviar.
  // Guardamos por poucos minutos para reconciliar a mensagem persistida sem F5
  // e sem fazer GET extra do histórico a cada check de entrega/leitura.
  const statusMensagensPendentesRef = useRef(new Map());

  // Mensageria V2: a lateral carrega somente a página visível da aba atual.
  // O total continua conhecido no backend, mas o navegador não recebe centenas
  // de chamados de uma vez.
  const abaQueueListRef = useRef("minhas");
  const paginasConversasV2Ref = useRef({
    minhas: { carregada: false, hasMore: true, cursor: null },
    fila: { carregada: false, hasMore: true, cursor: null },
    fechados: { carregada: false, hasMore: true, cursor: null },
  });
  const carregandoPaginaConversasV2Ref = useRef(new Set());
  const contatosAvulsosCarregadosRef = useRef(false);
  const carregandoContadoresV2Ref = useRef(false);
  const ultimoCarregamentoContadoresV2Ref = useRef(0);
  const [paginacaoConversasV2, setPaginacaoConversasV2] = useState({
    minhas: { carregada: false, hasMore: true, cursor: null, loading: false },
    fila: { carregada: false, hasMore: true, cursor: null, loading: false },
    fechados: { carregada: false, hasMore: true, cursor: null, loading: false },
  });
  const [totaisOperacaoV2, setTotaisOperacaoV2] = useState({
    minhas: null,
    fila: null,
    fechados: null,
  });
  const [pendenciasRespostaPorConexao, setPendenciasRespostaPorConexao] =
    useState({ total: 0, porInstancia: {} });
  const [busca, setBusca] = useState("");
  const [cabecalhoRecolhido, setCabecalhoRecolhido] = useState(() => {
    if (typeof window === "undefined") return false;

    try {
      return (
        window.localStorage.getItem(
          CAPTAZAP_CABECALHO_RECOLHIDO_STORAGE_KEY,
        ) === "1"
      );
    } catch {
      return false;
    }
  });

  const [leadsCRM, setLeadsCRM] = useState([]);
  const [resultadosBuscaOperacao, setResultadosBuscaOperacao] = useState([]);
  const [buscandoBuscaOperacao, setBuscandoBuscaOperacao] = useState(false);
  const [leadsCRMBuscaRemota, setLeadsCRMBuscaRemota] = useState([]);
  const [buscandoContatosCRM, setBuscandoContatosCRM] = useState(false);
  const [tarefas, setTarefas] = useState([]);
  const [interacoes, setInteracoes] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [recebimentosFinanceiros, setRecebimentosFinanceiros] = useState([]);
  const [followupsChamados, setFollowupsChamados] = useState([]);
  const [followupsChamadosEmpresa, setFollowupsChamadosEmpresa] = useState([]);
  const [draMaluAtivos, setDraMaluAtivos] = useState([]);
  const [followupRegrasAtivas, setFollowupRegrasAtivas] = useState([]);
  const [fontes, setFontes] = useState([]);
  const [campanhas, setCampanhas] = useState([]);
  const [procedimentos, setProcedimentos] = useState([]);
  const [regioes, setRegioes] = useState([]);
  const [dentistas, setDentistas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [solicitacoesTransferencia, setSolicitacoesTransferencia] = useState(
    [],
  );
  const [loadingCRM, setLoadingCRM] = useState(false);
  const [usuarioFotoRefreshKey, setUsuarioFotoRefreshKey] = useState(0);
  const [modalAgendamentoAberto, setModalAgendamentoAberto] = useState(false);
  const [leadAgendamentoSelecionada, setLeadAgendamentoSelecionada] =
    useState(null);
  const [modalTransferenciaAberto, setModalTransferenciaAberto] =
    useState(false);
  const [leadTransferenciaSelecionada, setLeadTransferenciaSelecionada] =
    useState(null);
  const [modalTarefaAberto, setModalTarefaAberto] = useState(false);
  const [leadTarefaSelecionada, setLeadTarefaSelecionada] = useState(null);
  const [tarefasDaLeadSelecionada, setTarefasDaLeadSelecionada] = useState([]);
  const [modalInteracoesAberto, setModalInteracoesAberto] = useState(false);
  const [leadInteracaoSelecionada, setLeadInteracaoSelecionada] =
    useState(null);
  const [interacoesDaLeadSelecionada, setInteracoesDaLeadSelecionada] =
    useState([]);

  const [conversasCriadasLocal, setConversasCriadasLocal] = useState([]);
  const [conversasWhatsApp, setConversasWhatsApp] = useState([]);
  const [contatosAvulsos, setContatosAvulsos] = useState([]);
  const [notificacoesNovaMensagem, setNotificacoesNovaMensagem] = useState([]);
  const [notificacoesChromeAtivas, setNotificacoesChromeAtivas] =
    useState(false);
  const [permissaoNotificacoesChrome, setPermissaoNotificacoesChrome] =
    useState(() => getPermissaoNotificacoesChromeCaptaZap());
  const abrirNotificacaoChromeRef = useRef(null);
  const notificacoesNovaMensagemTimersRef = useRef(new Map());
  const notificacoesNovaMensagemDedupeRef = useRef(new Map());
  const notificacoesNovaMensagemSeqRef = useRef(0);
  const [salvandoContextoLead, setSalvandoContextoLead] = useState(false);

  const [modalConverterLeadAberto, setModalConverterLeadAberto] =
    useState(false);
  const [conversaConverterLead, setConversaConverterLead] = useState(null);
  const [salvandoConversaoLead, setSalvandoConversaoLead] = useState(false);

  const [modalFecharChamadoAberto, setModalFecharChamadoAberto] =
    useState(false);
  const [conversaFechamentoChamado, setConversaFechamentoChamado] =
    useState(null);
  const [bloqueioFechamentoChamado, setBloqueioFechamentoChamado] =
    useState(null);
  const [
    modalTransferirAtendimentoAberto,
    setModalTransferirAtendimentoAberto,
  ] = useState(false);
  const [conversaTransferirAtendimento, setConversaTransferirAtendimento] =
    useState(null);
  const [transferindoAtendimento, setTransferindoAtendimento] = useState(false);
  const transferenciaAtendimentoLockRef = useRef(false);
  const assumirAtendimentoLockRef = useRef(false);
  const [assumindoAtendimento, setAssumindoAtendimento] = useState(false);
  const fechamentoChamadoLockRef = useRef(false);
  const [fechandoChamado, setFechandoChamado] = useState(false);
  const [painelDetalhesRecolhido, setPainelDetalhesRecolhido] = useState(false);
  const [supervisaoEquipeAberta, setSupervisaoEquipeAberta] = useState(false);
  const [modalBomDiaProducaoAberto, setModalBomDiaProducaoAberto] = useState(false);

  // captazapMeuDiaDeepLinkAplicado
  // Permite abrir diretamente pelo WhatsApp:
  // /captazap?meu_dia=1
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const params = new URLSearchParams(window.location.search);

      if (params.get("meu_dia") !== "1") return;

      setModalBomDiaProducaoAberto(true);

      // Remove o parâmetro depois de abrir o modal
      // para não reabrir em atualizações internas.
      params.delete("meu_dia");

      const query = params.toString();
      const novaUrl =
        window.location.pathname +
        (query ? `?${query}` : "") +
        window.location.hash;

      window.history.replaceState(
        window.history.state,
        "",
        novaUrl,
      );
    } catch (error) {
      console.warn(
        "CaptaZap | falha ao abrir Meu Dia via link:",
        error,
      );
    }
  }, []);

  const [acaoSupervisaoPendente, setAcaoSupervisaoPendente] = useState(null);
  const [modalNovoContatoAberto, setModalNovoContatoAberto] = useState(false);
  const [novoContatoForm, setNovoContatoForm] = useState(() => ({
    nome: "",
    telefone: "",
    mensagem: "",
    dra_malu_classificacao: "padrao",
  }));
  const [salvandoNovoContato, setSalvandoNovoContato] = useState(false);
  const [erroNovoContato, setErroNovoContato] = useState("");
  const [modalNomeContatoAberto, setModalNomeContatoAberto] = useState(false);
  const [conversaNomeContato, setConversaNomeContato] = useState(null);
  const [nomeContatoForm, setNomeContatoForm] = useState("");
  const [classificacaoContatoForm, setClassificacaoContatoForm] =
    useState("padrao");
  const [salvandoNomeContato, setSalvandoNomeContato] = useState(false);
  const [erroNomeContato, setErroNomeContato] = useState("");

  const empresaAtivaEfetiva = useMemo(() => {
    const empresaAtivaLS = getEmpresaAtivaLocalStorage();
    const usuarioLS = getUsuarioLocalStorage();

    // Mesmo padrão usado nas telas que já funcionam: prioriza a empresa ativa
    // do contexto e, se ele ainda não hidratou, usa empresaAtiva do localStorage.
    // Não usar localStorage.empresa_id aqui, porque essa chave pode ficar antiga.
    if (empresaAtiva?.id) return empresaAtiva;
    if (empresaAtivaLS?.id) return empresaAtivaLS;

    const usuarioBase = usuario?.id ? usuario : usuarioLS;

    if (
      Array.isArray(usuarioBase?.empresas) &&
      usuarioBase.empresas.length > 0
    ) {
      return (
        usuarioBase.empresas.find(
          (empresa) => String(empresa.id) === String(usuarioBase?.empresa_id),
        ) || usuarioBase.empresas[0]
      );
    }

    if (usuarioBase?.empresa_id) {
      return { id: usuarioBase.empresa_id };
    }

    return null;
  }, [empresaAtiva, usuario]);

  const empresaId = empresaAtivaEfetiva?.id;
  const [nomeFantasiaCarregado, setNomeFantasiaCarregado] = useState("");
  const [telefonesCadastradosPorInstancia, setTelefonesCadastradosPorInstancia] = useState({});
  const empresaNome = nomeFantasiaCarregado || getEmpresaNome(empresaAtivaEfetiva);
  const captadorLogado = useMemo(
    () => getUsuarioHeader(usuario),
    [usuario, usuarioFotoRefreshKey],
  );

  const usuarioIdLogado = useMemo(() => getUsuarioId(usuario), [usuario]);
  const usuarioNomeLogado = useMemo(
    () => getNomeUsuarioSistema(usuario),
    [usuario],
  );
  const usuarioPodeVerEquipeCaptaZap = useMemo(
    () => usuarioTemPermissaoVisaoEquipeCaptaZap(usuario),
    [usuario],
  );
  const usuarioPodeFixarAcompanhamentoCaptaZap = useMemo(
    () => usuarioTemPermissaoFixarAcompanhamentoCaptaZap(usuario),
    [usuario],
  );

  const leadsCRMComBuscaRemota = useMemo(() => {
    return normalizarLeadsVinculadasCaptazap([
      ...(Array.isArray(leadsCRM) ? leadsCRM : []),
      ...(Array.isArray(leadsCRMBuscaRemota) ? leadsCRMBuscaRemota : []),
    ]).filter((lead) => pertenceEmpresa(lead, empresaId));
  }, [empresaId, leadsCRM, leadsCRMBuscaRemota]);

  const conversasWhatsAppRef = useRef([]);
  const conversasCriadasLocalRef = useRef([]);
  const avataresConsultadosAutoRef = useRef(new Set());
  const avatarAutoTimerRef = useRef(null);
  const usuarioIdLogadoRef = useRef(null);
  const usuarioNomeLogadoRef = useRef("");

  // Z-API Calls: um cliente persistente por conexão WhatsApp.
  // Isso permite receber chamadas mesmo quando outra conversa está aberta.
  const zapiCallClientsRef = useRef(new Map());
  const chamadaSaidaPendenteRef = useRef(false);
  const chamadaSaidaHandleRef = useRef(null);

  // Call IDs que já existiam no SDK quando a conexão foi aberta.
  // Esses IDs são "estoque antigo" do provedor e não podem gerar card ao entrar/recarregar.
  const zapiCallIdsIgnoradosRef = useRef(new Map());

  // Janela de estabilização por instância.
  // Alguns servidores da Z-API reemitem chamadas antigas imediatamente após
  // abrir o WebSocket. Durante essa janela, esses eventos não entram na UI.
  const zapiCallProntoEmRef = useRef(new Map());
  const zapiCallTelefonesIgnoradosRef = useRef(new Map());

  const [chamadasRecebidas, setChamadasRecebidas] = useState([]);

  // Performance: a lista de conexões do CaptaZap pode ganhar uma nova referência
  // durante refresh/realtime mesmo sem mudar de fato. O SDK de chamadas só deve
  // reinicializar quando o conjunto real de instâncias mudar.
  const conexoesChamadasKey = useMemo(
    () =>
      (Array.isArray(conexoesWhatsApp) ? conexoesWhatsApp : [])
        .map((item) =>
          [
            String(item?.zapi_instance_id || "").trim(),
            String(item?.id || "").trim(),
            String(item?.nome || "").trim(),
          ].join(":"),
        )
        .filter(Boolean)
        .sort()
        .join("|"),
    [conexoesWhatsApp],
  );

  const registrarEventoLigacaoCaptaZap = useCallback(
    async (payload = {}) => {
      if (!empresaId || !payload?.call_id || !payload?.zapi_instance_id) return null;

      try {
        return await requestJson("/whatsapp/calls/event", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            empresa_id: empresaId,
            usuario_id: usuarioIdLogado || null,
            usuario_nome: usuarioNomeLogado || "",
            ...payload,
          }),
        });
      } catch (error) {
        console.warn(
          "CaptaZap Calls | não foi possível registrar evento:",
          error?.message || error,
        );
        return null;
      }
    },
    [empresaId, usuarioIdLogado, usuarioNomeLogado],
  );

  // SPEED CALL: botão de saída utiliza chamada one-shot, isolada dos clientes
  // persistentes que recebem ligações. Não altera o atendimento de entrada.
  const iniciarChamadaSaidaCaptaZap = useCallback(async (conversaAlvo) => {
    if (!empresaId || !conversaAlvo) return;
    if (chamadaSaidaPendenteRef.current || chamadaSaidaHandleRef.current) return;

    const instancia = String(
      getConexaoInstanceIdConversaCaptaZap(conversaAlvo) || ""
    ).trim();
    const telefoneBruto = String(
      conversaAlvo?.telefone || conversaAlvo?.numero || conversaAlvo?.phone || ""
    ).trim();
    const numero = normalizarTelefoneWhatsappBrasil(telefoneBruto);

    if (!instancia) {
      window.alert("Não foi possível identificar a conexão WhatsApp desta conversa.");
      return;
    }
    if (!/^\d{12,15}$/.test(numero)) {
      window.alert("Número de WhatsApp inválido para iniciar a ligação.");
      return;
    }

    chamadaSaidaPendenteRef.current = true;
    try {
      const callOneShot = ZapiCallSDK.ZAPICall?.call || ZapiCallSDK.call;
      if (typeof callOneShot !== "function") {
        throw new Error("A versão instalada do @z-api/call não oferece chamadas one-shot. Atualize o pacote para 1.1.0.");
      }

      // O token fica no backend. Nunca passa token permanente ao navegador.
      const getToken = async () => {
        const params = new URLSearchParams({
          empresa_id: String(empresaId),
          zapi_instance_id: instancia,
        });
        const data = await requestJson(
          `/whatsapp/calls/call-token?${params.toString()}`
        );
        const token = String(data?.token || "").trim();
        if (!token) throw new Error("A Z-API não retornou o token para ligação.");
        return token;
      };

      const handle = callOneShot({
        instanceId: instancia,
        getToken,
        number: numero,
        isVideo: false,
        position: "bottom-right",
      });
      chamadaSaidaHandleRef.current = handle || null;
      if (handle?.done && typeof handle.done.finally === "function") {
        Promise.resolve(handle.done)
          .catch((error) => console.warn("CaptaZap Calls | saída:", error?.message || error))
          .finally(() => { if (chamadaSaidaHandleRef.current === handle) chamadaSaidaHandleRef.current = null; });
      } else {
        // SDK sem handle de ciclo de vida não pode manter o botão travado.
        chamadaSaidaHandleRef.current = null;
      }
    } catch (error) {
      chamadaSaidaHandleRef.current = null;
      window.alert(error?.message || "Não foi possível iniciar a ligação de WhatsApp.");
    } finally {
      chamadaSaidaPendenteRef.current = false;
    }
  }, [empresaId]);

  const removerChamadaLocalPorChave = useCallback((chave = "") => {
    if (!chave) return;
    setChamadasRecebidas((atuais) =>
      (Array.isArray(atuais) ? atuais : []).filter(
        (item) => item?.chave !== chave,
      ),
    );
  }, []);


  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        CAPTAZAP_CABECALHO_RECOLHIDO_STORAGE_KEY,
        cabecalhoRecolhido ? "1" : "0",
      );
    } catch {
      // Navegadores com armazenamento bloqueado continuam funcionando.
    }
  }, [cabecalhoRecolhido]);

  useEffect(() => {
    conversasWhatsAppRef.current = Array.isArray(conversasWhatsApp)
      ? conversasWhatsApp
      : [];
  }, [conversasWhatsApp]);

  useEffect(() => {
    conversasCriadasLocalRef.current = Array.isArray(conversasCriadasLocal)
      ? conversasCriadasLocal
      : [];
  }, [conversasCriadasLocal]);

  useEffect(() => {
    usuarioIdLogadoRef.current = usuarioIdLogado || null;
    usuarioNomeLogadoRef.current = usuarioNomeLogado || "";
  }, [usuarioIdLogado, usuarioNomeLogado]);

  useEffect(() => {
    const atualizarPreferencia = () => {
      const permissao = getPermissaoNotificacoesChromeCaptaZap();
      const ativoSalvo = lerPreferenciaNotificacoesChromeCaptaZap({
        empresaId,
        usuarioId: usuarioIdLogado,
      });

      setPermissaoNotificacoesChrome(permissao);
      setNotificacoesChromeAtivas(
        Boolean(ativoSalvo && permissao === "granted"),
      );
    };

    atualizarPreferencia();

    if (typeof window === "undefined") return undefined;

    window.addEventListener("focus", atualizarPreferencia);
    document.addEventListener("visibilitychange", atualizarPreferencia);

    return () => {
      window.removeEventListener("focus", atualizarPreferencia);
      document.removeEventListener("visibilitychange", atualizarPreferencia);
    };
  }, [empresaId, usuarioIdLogado]);

  useEffect(() => {
    if (!abrirSupervisaoInicial || !usuarioPodeVerEquipeCaptaZap) return;

    setSupervisaoEquipeAberta(true);
  }, [abrirSupervisaoInicial, usuarioPodeVerEquipeCaptaZap]);

  useEffect(() => {
    conversaSelecionadaRef.current = conversaSelecionada;
  }, [conversaSelecionada]);

  // Fonte da verdade do cabeçalho: ao abrir conversa vinda de Contatos, Busca,
  // Supervisão ou qualquer objeto sem status confiável, reconcilia o chamado
  // atual diretamente pela lista operacional V2 (captazap_chamados).
  useEffect(() => {
    if (!empresaId || !conversaSelecionada) return undefined;

    const telefoneBruto =
      conversaSelecionada.telefone ||
      conversaSelecionada.numero ||
      conversaSelecionada.whatsapp ||
      conversaSelecionada.phone ||
      "";
    const telefone =
      normalizarWhatsappLidCaptaZap(telefoneBruto) ||
      normalizarTelefone(telefoneBruto).slice(-11);
    const identidadeTelefone =
      normalizarIdentidadeWhatsappComparacaoCaptaZap(telefoneBruto);
    const chamadoId = String(
      conversaSelecionada.chamado_id ||
        conversaSelecionada.id_chamado ||
        conversaSelecionada._captazap_chamado_operacional_id ||
        "",
    );

    if (!telefone) return undefined;

    const statusBruto = String(
      conversaSelecionada.status_chamado ||
        conversaSelecionada.chamado_status ||
        conversaSelecionada.chamada_status ||
        "",
    )
      .trim()
      .toLowerCase();
    const statusReconhecido = ["aberto", "fechado", "transferido"].includes(
      statusBruto,
    );
    const origemPodeEstarDesatualizada = Boolean(
      conversaSelecionada._captazap_origem_contatos ||
        conversaSelecionada._contato_crm_consulta ||
        conversaSelecionada._captazap_busca_operacional ||
        conversaSelecionada._captazap_visualizacao_supervisao ||
        conversaSelecionada._somente_visualizacao_supervisao ||
        conversaSelecionada._captazap_contato_com_chamado_existente ||
        conversaSelecionada._forcar_historico_por_telefone,
    );
    const statusVisual = getStatusChamadoVisual(conversaSelecionada);
    const inconsistenteComFechamento = Boolean(
      statusBruto === "aberto" && statusVisual === "fechado",
    );

    if (
      statusReconhecido &&
      !origemPodeEstarDesatualizada &&
      !inconsistenteComFechamento &&
      conversaSelecionada._captazap_status_chamado_reconciliado
    ) {
      return undefined;
    }

    const instanciaReconciliacao =
      getConexaoInstanceIdConversaCaptaZap(conversaSelecionada) ||
      String(conexaoSelecionadaRef.current || "").trim();
    const cacheKey = `${empresaId}:${instanciaReconciliacao || "sem-conexao"}:${chamadoId || telefone}`;
    const agora = Date.now();
    const ultimaConsulta = Number(
      statusChamadoReconciliadoCacheRef.current.get(cacheKey) || 0,
    );
    if (agora - ultimaConsulta < 15000) return undefined;
    statusChamadoReconciliadoCacheRef.current.set(cacheKey, agora);

    let cancelado = false;

    (async () => {
      const resultados = await buscarConversasOperacaoCaptaZap({
        empresaId,
        termo: telefone,
        aba: "operacao",
        usuarioId: usuarioIdLogado,
        zapiInstanceId:
          getConexaoInstanceIdConversaCaptaZap(conversaSelecionada) ||
          String(conexaoSelecionadaRef.current || "").trim(),
      });

      if (cancelado || !Array.isArray(resultados) || !resultados.length) return;

      const chamadoAtual =
        (chamadoId
          ? resultados.find((item) =>
              [
                item?.chamado_id,
                item?.id_chamado,
                item?.id,
              ].some((id) => id && String(id) === chamadoId),
            )
          : null) ||
        resultados.find((item) => {
          const telItem = normalizarIdentidadeWhatsappComparacaoCaptaZap(
            item?.telefone || item?.numero || item?.whatsapp || "",
          );
          return Boolean(
            telItem && identidadeTelefone && telItem === identidadeTelefone,
          );
        }) ||
        null;

      if (!chamadoAtual) return;

      const aplicar = (item) => {
        if (!item || !isMesmaConversaCaptazap(item, chamadoAtual)) return item;
        return aplicarEstadoChamadoAutoritativoCaptaZap(item, chamadoAtual);
      };

      setConversaSelecionada((atual) => {
        if (!atual || !isMesmaConversaCaptazap(atual, chamadoAtual)) {
          return atual;
        }
        return aplicarEstadoChamadoAutoritativoCaptaZap(atual, chamadoAtual);
      });
      setConversasWhatsApp((atuais) => (atuais || []).map(aplicar));
      setConversasCriadasLocal((atuais) => (atuais || []).map(aplicar));
    })().catch((error) => {
      console.warn(
        "CaptaZap | não foi possível reconciliar o status atual do chamado:",
        error,
      );
    });

    return () => {
      cancelado = true;
    };
  }, [
    empresaId,
    usuarioIdLogado,
    conversaSelecionada?.id,
    conversaSelecionada?.conversa_id,
    conversaSelecionada?.chamado_id,
    conversaSelecionada?.id_chamado,
    conversaSelecionada?._captazap_chamado_operacional_id,
    conversaSelecionada?.telefone,
    conversaSelecionada?.numero,
    conversaSelecionada?.whatsapp,
    conversaSelecionada?.phone,
  ]);

  useEffect(() => {
    const atualizarUsuarioLocal = () => {
      setUsuarioFotoRefreshKey((current) => current + 1);
    };

    window.addEventListener("storage", atualizarUsuarioLocal);
    window.addEventListener("focus", atualizarUsuarioLocal);

    return () => {
      window.removeEventListener("storage", atualizarUsuarioLocal);
      window.removeEventListener("focus", atualizarUsuarioLocal);
    };
  }, []);


  const atualizarEstadoPaginacaoConversasV2 = useCallback(
    (aba, patch = {}) => {
      if (!CAPTAZAP_ABAS_OPERACIONAIS_PAGINADAS.has(aba)) return;

      const atualRef =
        paginasConversasV2Ref.current?.[aba] || {
          carregada: false,
          hasMore: true,
          cursor: null,
        };
      const proximoRef = { ...atualRef, ...patch };
      paginasConversasV2Ref.current = {
        ...paginasConversasV2Ref.current,
        [aba]: proximoRef,
      };

      setPaginacaoConversasV2((atual) => ({
        ...atual,
        [aba]: {
          ...(atual?.[aba] || {}),
          ...proximoRef,
          loading: Boolean(patch.loading),
        },
      }));
    },
    [],
  );

  const carregarConexoesWhatsApp = useCallback(async () => {
    if (!empresaId) {
      setConexoesWhatsApp([]);
      return [];
    }

    setCarregandoConexoesWhatsApp(true);

    try {
      const data = await requestJson(
        `/whatsapp/captazap/conexoes?empresa_id=${encodeURIComponent(empresaId)}`,
      );

      const lista = pickArray(
        { status: "fulfilled", value: data },
        ["conexoes", "data", "items"],
      )
        .filter((item) => String(item?.zapi_instance_id || "").trim())
        .map((item, index) => ({
          ...item,
          zapi_instance_id: String(item?.zapi_instance_id || "").trim(),
          nome: getNomeConexaoWhatsAppCaptaZap(item, index),
        }));

      setConexoesWhatsApp(lista);

      const atual = String(conexaoSelecionadaRef.current || "").trim();
      const atualAindaExiste = lista.some(
        (item) => String(item?.zapi_instance_id || "").trim() === atual,
      );
      const primeiraInstancia = String(
        lista[0]?.zapi_instance_id || "",
      ).trim();
      const instanciaAtiva = atualAindaExiste ? atual : primeiraInstancia;

      conexaoSelecionadaRef.current = instanciaAtiva;
      setConexaoSelecionada(instanciaAtiva);

      return lista;
    } catch (error) {
      console.warn("CaptaZap | não foi possível listar conexões WhatsApp:", error);
      setConexoesWhatsApp([]);
      return [];
    } finally {
      setCarregandoConexoesWhatsApp(false);
    }
  }, [empresaId]);


  // Metadados cadastrais independentes da lista operacional e da SDK de chamadas.
  // Sem timer artificial, sem ping da Z-API e sem alterar conexoesWhatsApp.
  useEffect(() => {
    if (!empresaId) {
      setNomeFantasiaCarregado("");
      setTelefonesCadastradosPorInstancia({});
      return undefined;
    }
    let cancelado = false;
    setNomeFantasiaCarregado("");
    setTelefonesCadastradosPorInstancia({});

    requestJson(`/configuracoes/zapi/lista/${encodeURIComponent(empresaId)}`)
      .then((data) => {
        if (cancelado) return;
        const configs = Array.isArray(data) ? data :
          Array.isArray(data?.conexoes) ? data.conexoes :
          Array.isArray(data?.data) ? data.data : [];
        const mapa = {};
        for (const c of configs) {
          const instancia = String(c?.zapi_instance_id || "").trim();
          const telefone = String(c?.telefone || "").replace(/\D/g, "");
          if (instancia && telefone.length >= 10 && telefone.length <= 15) {
            mapa[instancia] = telefone;
          }
        }
        setTelefonesCadastradosPorInstancia(mapa);
        if (!Object.keys(mapa).length) {
          console.warn("CaptaZap: rota de cadastro não retornou telefones válidos para as instâncias.");
        }
      })
      .catch((err) => console.warn("CaptaZap: falha ao buscar números cadastrados:", err?.message || err));

    requestJson("/empresas")
      .then((data) => {
        if (cancelado) return;
        const empresas = Array.isArray(data) ? data :
          Array.isArray(data?.empresas) ? data.empresas :
          Array.isArray(data?.data) ? data.data : [];
        const encontrada = empresas.find((e) => String(e?.id) === String(empresaId));
        const nome = String(encontrada?.nome_fantasia || encontrada?.razao_social || encontrada?.nome || "").trim();
        if (nome) setNomeFantasiaCarregado(nome);
      })
      .catch((err) => console.warn("CaptaZap: falha ao buscar nome da empresa:", err?.message || err));

    return () => { cancelado = true; };
  }, [empresaId]);

  const getTimestampChamadaMs = useCallback((call = {}) => {
    const raw = Number(call?.timestamp || 0);
    if (!Number.isFinite(raw) || raw <= 0) return 0;

    // SDK documenta timestamp numérico; toleramos epoch em segundos ou ms.
    return raw < 1_000_000_000_000 ? raw * 1000 : raw;
  }, []);

  const chamadaRecebidaEhRecente = useCallback(
    (call = {}, toleranciaMs = 45_000) => {
      const ts = getTimestampChamadaMs(call);

      // Se o SDK não fornecer timestamp, não bloqueamos uma chamada real.
      if (!ts) return true;

      const idade = Date.now() - ts;

      // Aceita pequena diferença de relógio entre cliente e servidor.
      return idade >= -15_000 && idade <= toleranciaMs;
    },
    [getTimestampChamadaMs],
  );

  useEffect(() => {
    if (!empresaId || !Array.isArray(conexoesWhatsApp) || !conexoesWhatsApp.length) {
      zapiCallClientsRef.current.forEach((client) => {
        try {
          client?.destroy?.();
        } catch (_) {}
      });
      zapiCallClientsRef.current.clear();
      zapiCallIdsIgnoradosRef.current.clear();
      zapiCallProntoEmRef.current.clear();
      zapiCallTelefonesIgnoradosRef.current.clear();
      setChamadasRecebidas([]);
      return undefined;
    }

    let cancelado = false;
    const novosClientes = new Map();

    const normalizarTelefoneChamada = (telefoneBruto = "") => {
      const whatsappNormalizado = normalizarTelefoneWhatsappBrasil(telefoneBruto);
      if (ehWhatsappLidCaptaZap(whatsappNormalizado)) return whatsappNormalizado;

      const digits = normalizarTelefone(whatsappNormalizado || telefoneBruto);
      if (!digits) return "";

      // Para comparação no CRM/CaptaZap usamos DDD + número (11 dígitos).
      // Ex.: SDK pode entregar 556196368800 (formato antigo, 8 dígitos).
      // normalizarTelefoneWhatsappBrasil converte para 5561996368800.
      if (digits.startsWith("55") && digits.length >= 13) {
        return digits.slice(2, 13);
      }

      return normalizarTelefoneLeadBrasil(digits) || digits.slice(-11);
    };

    const localizarConversaPorTelefone = (telefoneBruto = "", instanceId = "") => {
      const telefone = normalizarTelefoneChamadaCaptaZap(telefoneBruto);
      if (!telefone || ehWhatsappLidCaptaZap(telefone)) return null;

      const todas = [
        ...(conversasWhatsAppRef.current || []),
        ...(conversasCriadasLocalRef.current || []),
      ];

      return (
        todas.find((item) => {
          const instanciaItem = String(
            getConexaoInstanceIdConversaCaptaZap(item) || "",
          ).trim();

          if (
            instanceId &&
            instanciaItem &&
            String(instanceId) !== String(instanciaItem)
          ) {
            return false;
          }

          const telefoneItem = normalizarTelefoneChamadaCaptaZap(
            item?.telefone ||
              item?.numero ||
              item?.phone ||
              item?.whatsapp ||
              item?.telefone_normalizado ||
              "",
          );

          return Boolean(telefoneItem && telefoneItem === telefone);
        }) || null
      );
    };

    const upsertChamada = (instanceId, call = {}, patch = {}) => {
      const callId = String(call?.callId || patch?.callId || "").trim();
      if (!callId) return;

      const telefoneBruto = String(
        call?.from || call?.phone || call?.number || patch?.telefone || "",
      ).trim();
      const telefone =
        normalizarTelefoneChamadaCaptaZap(telefoneBruto) || telefoneBruto;
      const conversa = localizarConversaPorTelefone(telefone, instanceId);
      const conexao = conexoesWhatsApp.find(
        (item) =>
          String(item?.zapi_instance_id || "").trim() === String(instanceId),
      );

      const nome = getNomeSeguroChamadaCaptaZap(conversa, telefone);

      const foto = conversa ? getAvatarConversa(conversa) : "";
      const captadorNome = String(
        conversa?.captador_nome ||
          conversa?.captadorNome ||
          conversa?.lead?.captador_nome ||
          conversa?.lead?.captadorNome ||
          "",
      ).trim();
      const responsavelNome = String(
        conversa?.responsavel_nome ||
          conversa?.responsavelNome ||
          "",
      ).trim();
      const responsavelId =
        conversa?.responsavel_id ||
        conversa?.responsavelId ||
        null;
      const emFila = Boolean(
        !responsavelId ||
          conversa?.em_fila ||
          conversa?.fila_atendimento ||
          conversa?.aguardando_atendimento ||
          conversa?._fila_global ||
          conversa?._fila_equipe,
      );

      const chave = `${instanceId}::${callId}`;

      setChamadasRecebidas((atuais) => {
        const lista = Array.isArray(atuais) ? atuais : [];
        const existente = lista.find((item) => item.chave === chave);

        const proxima = {
          ...(existente || {}),
          chave,
          callId,
          instanceId,
          telefone: telefone || existente?.telefone || "",
          nome: nome || existente?.nome || "",
          foto: foto || existente?.foto || "",
          captadorNome: captadorNome || existente?.captadorNome || "",
          responsavelNome:
            responsavelNome || existente?.responsavelNome || "",
          responsavelId:
            responsavelId || existente?.responsavelId || null,
          emFila:
            typeof emFila === "boolean"
              ? emFila
              : Boolean(existente?.emFila),
          conexaoNome:
            String(conexao?.nome || "").trim() ||
            existente?.conexaoNome ||
            "WhatsApp",
          isVideo: Boolean(call?.isVideo ?? existente?.isVideo ?? false),
          estado:
            patch?.estado ||
            call?.state ||
            existente?.estado ||
            "ringing",
          motivo: patch?.motivo ?? existente?.motivo ?? null,
          recebidaEm:
            existente?.recebidaEm ||
            call?.timestamp ||
            Date.now(),
        };

        if (existente) {
          return lista.map((item) => (item.chave === chave ? proxima : item));
        }

        return [proxima, ...lista].slice(0, 4);
      });
    };

    const enriquecerChamadaRecebida = async (
      instanceId,
      callId,
      telefoneBruto = "",
    ) => {
      const telefone = normalizarTelefoneChamadaCaptaZap(telefoneBruto);
      if (!telefone || !callId) return;

      try {
        // Não dependemos de a conversa estar entre as primeiras páginas carregadas.
        // Buscamos o chamado/contato diretamente no backend pelo telefone.
        const resultados = await buscarConversasOperacaoCaptaZap({
          empresaId,
          termo: telefone,
          aba: "operacao",
          usuarioId: usuarioIdLogadoRef.current,
          zapiInstanceId: instanceId,
        });

        let conversa =
          (Array.isArray(resultados) ? resultados : []).find((item) => {
            const telefoneItem = normalizarTelefoneChamadaCaptaZap(
              item?.telefone ||
                item?.numero ||
                item?.phone ||
                item?.whatsapp ||
                item?.telefone_normalizado ||
                "",
            );
            return Boolean(telefoneItem && telefoneItem === telefone);
          }) || localizarConversaPorTelefone(telefone, instanceId);

        // Nome/captador comercial: consulta a Lead pelo telefone.
        // O objeto operacional pode carregar `nome` vindo do preview da última
        // mensagem/mídia; por isso não o usamos como fonte autoritativa.
        let leadCRM = null;
        try {
          const leadsEncontradas = await buscarLeadsCRMRemotasCaptaZap({
            empresaId,
            termo: telefone,
          });

          leadCRM =
            (Array.isArray(leadsEncontradas) ? leadsEncontradas : []).find(
              (lead) => {
                const telefonesLead = [
                  lead?.telefone1,
                  lead?.telefone,
                  lead?.celular,
                  lead?.whatsapp,
                  lead?.numero,
                ]
                  .map((valor) => normalizarTelefoneChamadaCaptaZap(valor))
                  .filter(Boolean);
                return telefonesLead.includes(telefone);
              },
            ) || null;
        } catch {
          leadCRM = null;
        }

        if (leadCRM) {
          conversa = normalizarConversaCaptazap({
            ...(conversa || {}),
            lead: leadCRM,
            lead_id:
              conversa?.lead_id ||
              conversa?.leadId ||
              getLeadId(leadCRM) ||
              null,
            leadId:
              conversa?.leadId ||
              conversa?.lead_id ||
              getLeadId(leadCRM) ||
              null,
          });
        }

        let contatoCadastrado = null;
        try {
          const contatosResp = await requestJson(
            `/whatsapp/captazap/contatos?empresa_id=${encodeURIComponent(
              empresaId,
            )}&telefone=${encodeURIComponent(telefone)}&limit=300`,
          );

          const contatosLista = pickArray(
            { status: "fulfilled", value: contatosResp },
            ["contatos", "data"],
          );

          contatoCadastrado =
            (Array.isArray(contatosLista) ? contatosLista : []).find((contato) => {
              const telefoneContato = normalizarTelefoneChamadaCaptaZap(
                contato?.telefone_normalizado ||
                  contato?.telefone ||
                  contato?.numero ||
                  "",
              );
              return Boolean(telefoneContato && telefoneContato === telefone);
            }) || null;
        } catch {
          contatoCadastrado = null;
        }

        let foto = conversa ? getAvatarConversa(conversa) : "";

        if (!foto) {
          foto = getImagemUrl(
            contatoCadastrado?.profileUrl ||
              contatoCadastrado?.profile_url ||
              contatoCadastrado?.avatar_url ||
              contatoCadastrado?.avatar ||
              contatoCadastrado?.foto ||
              contatoCadastrado?.foto_url ||
              "",
          );
        }

        if (!foto) {
          foto =
            (await buscarAvatarContatoCaptaZap({
              empresaId,
              telefone,
            }).catch(() => "")) || "";
        }

        const leadsVinculadas = normalizarLeadsVinculadasCaptazap([
          ...(Array.isArray(conversa?.leads_vinculadas)
            ? conversa.leads_vinculadas
            : []),
          ...(Array.isArray(conversa?.leadsVinculadas)
            ? conversa.leadsVinculadas
            : []),
          conversa?.lead,
          leadCRM,
        ]);

        const leadDoTelefone =
          leadsVinculadas.find((lead) => {
            const telefones = [
              lead?.telefone1,
              lead?.telefone,
              lead?.celular,
              lead?.whatsapp,
              lead?.numero,
            ]
              .map((valor) => normalizarTelefoneChamadaCaptaZap(valor))
              .filter(Boolean);
            return telefones.includes(telefone);
          }) ||
          leadCRM ||
          conversa?.lead ||
          null;

        const candidatosNome = [
          leadCRM?.nome_lead,
          leadCRM?.nome,
          leadCRM?.name,
          leadDoTelefone?.nome_lead,
          leadDoTelefone?.nome,
          contatoCadastrado?.nome,
          contatoCadastrado?.nome_whatsapp,
          conversa?.contato_nome,
          conversa?.contatoNome,
          conversa?.nome_whatsapp,
          conversa?.nomeWhatsapp,
          conversa?.lead?.nome_lead,
          conversa?.lead?.nome,
          conversa?.nome,
        ];

        const nome =
          candidatosNome
            .map((valor) => String(valor || "").trim())
            .find((valor) => nomeValidoParaChamadaCaptaZap(valor, telefone)) ||
          formatTelefoneChamadaCaptaZap(telefone) ||
          telefone;

        const captadorNome = String(
          leadCRM?.usuario_criador_nome ||
            leadCRM?.captador_nome ||
            leadCRM?.usuario_nome ||
            conversa?.captador_nome ||
            conversa?.captadorNome ||
            "",
        ).trim();

        const responsavelNome = String(
          conversa?.responsavel_nome ||
            conversa?.responsavelNome ||
            "",
        ).trim();

        const responsavelId =
          conversa?.responsavel_id ||
          conversa?.responsavelId ||
          null;

        const emFila = Boolean(
          !responsavelId ||
            conversa?.em_fila ||
            conversa?.fila_atendimento ||
            conversa?.aguardando_atendimento ||
            conversa?._fila_global ||
            conversa?._fila_equipe,
        );

        const chave = `${instanceId}::${callId}`;

        setChamadasRecebidas((atuais) =>
          (Array.isArray(atuais) ? atuais : []).map((item) =>
            item.chave === chave
              ? {
                  ...item,
                  telefone,
                  nome: nome || item.nome || "",
                  foto: foto || item.foto || "",
                  captadorNome:
                    captadorNome || item.captadorNome || "",
                  responsavelNome:
                    responsavelNome || item.responsavelNome || "",
                  responsavelId:
                    responsavelId || item.responsavelId || null,
                  emFila,
                  conversaAlvo:
                    conversa ||
                    item.conversaAlvo ||
                    normalizarConversaCaptazap({
                      id: `call-${instanceId}-${telefone}`,
                      conversa_id: `call-${instanceId}-${telefone}`,
                      empresa_id: empresaId,
                      zapi_instance_id: instanceId,
                      zapiInstanceId: instanceId,
                      conexao_instance_id: instanceId,
                      telefone,
                      numero: telefone,
                      nome: nome || telefone,
                      lead: leadCRM || null,
                      lead_id: getLeadId(leadCRM) || null,
                      leadId: getLeadId(leadCRM) || null,
                      status: "aguardando_atendimento",
                      status_chamado: "aberto",
                      chamado_status: "aberto",
                      chamada_status: "aberto",
                      em_fila: true,
                      fila_atendimento: true,
                      aguardando_atendimento: true,
                      _fila_global: true,
                      _fila_equipe: true,
                      conversa_ativa: true,
                      sem_conversa: false,
                    }),
                  chamadoId:
                    conversa?.chamado_id ||
                    conversa?.id_chamado ||
                    item.chamadoId ||
                    null,
                  leadId:
                    conversa?.lead_contexto_id ||
                    conversa?.leadContextoId ||
                    conversa?.lead_id ||
                    conversa?.leadId ||
                    getLeadId(leadCRM) ||
                    item.leadId ||
                    null,
                  identificacaoCarregada: true,
                }
              : item,
          ),
        );
      } catch (error) {
        console.warn(
          "[CaptaZap Calls] falha ao identificar chamada:",
          error?.message || error,
        );
      }
    };

    const removerChamada = (instanceId, callId, delay = 1200) => {
      const chave = `${instanceId}::${callId}`;
      window.setTimeout(() => {
        if (cancelado) return;
        setChamadasRecebidas((atuais) =>
          (Array.isArray(atuais) ? atuais : []).filter(
            (item) => item.chave !== chave,
          ),
        );
      }, delay);
    };

    for (const conexao of conexoesWhatsApp) {
      const instanceId = String(conexao?.zapi_instance_id || "").trim();
      if (!instanceId) continue;

      try {
        // Quarentena de inicialização:
        // por 8 segundos, eventos call:incoming são considerados replay do
        // bootstrap do SDK. Isso resolve chamadas "fantasma" que reaparecem
        // mesmo após logout/login.
        zapiCallProntoEmRef.current.set(instanceId, Date.now() + 8000);

        const client = initZapiCall({
          instanceId,
          autoWidget: false,
          getToken: async () => {
            const params = new URLSearchParams({
              empresa_id: String(empresaId),
              zapi_instance_id: instanceId,
            });

            const data = await requestJson(
              `/whatsapp/calls/call-token?${params.toString()}`,
            );

            const token = String(data?.token || "").trim();
            if (!token) {
              throw new Error("Token de chamada não retornado pela API.");
            }
            return token;
          },
        });

        client.on("connected", () => {
          console.info("[CaptaZap Calls] conectado:", instanceId);

          // Só consideramos a instância apta a receber chamadas novas alguns
          // segundos depois do WebSocket realmente conectar.
          zapiCallProntoEmRef.current.set(instanceId, Date.now() + 5000);

          try {
            const chamadasExistentes =
              typeof client.getCalls === "function" ? client.getCalls() || [] : [];

            const ignorados =
              zapiCallIdsIgnoradosRef.current.get(instanceId) || new Set();

            for (const chamadaExistente of chamadasExistentes) {
              const callIdExistente = String(
                chamadaExistente?.callId || "",
              ).trim();

              if (callIdExistente) {
                ignorados.add(callIdExistente);
              }
            }

            zapiCallIdsIgnoradosRef.current.set(instanceId, ignorados);

            if (ignorados.size) {
              console.warn(
                "[CaptaZap Calls] chamadas preexistentes ignoradas na inicialização:",
                {
                  instanceId,
                  callIds: [...ignorados],
                },
              );
            }
          } catch (error) {
            console.warn(
              "[CaptaZap Calls] não foi possível ler chamadas preexistentes:",
              error?.message || error,
            );
          }

          // Telefone é usado apenas como proteção transitória contra replay.
          // Depois de 30s liberamos para que uma nova chamada real do mesmo
          // paciente seja aceita normalmente.
          window.setTimeout(() => {
            zapiCallTelefonesIgnoradosRef.current.delete(instanceId);
          }, 30000);
        });

        client.on("call:incoming", (call) => {
          if (cancelado) return;

          const callIdIncoming = String(call?.callId || "").trim();
          const telefoneIncoming =
            normalizarTelefoneChamadaCaptaZap(
              call?.from || call?.phone || call?.number || "",
            ) || "";

          const ignorados =
            zapiCallIdsIgnoradosRef.current.get(instanceId) || new Set();
          const telefonesIgnorados =
            zapiCallTelefonesIgnoradosRef.current.get(instanceId) || new Set();

          const prontoEm = Number(
            zapiCallProntoEmRef.current.get(instanceId) || 0,
          );

          // Proteção mais forte contra replay:
          // durante a inicialização do SDK não exibimos chamadas. Guardamos
          // callId + telefone para que o mesmo replay não escape alguns
          // segundos depois com outro evento.
          if (prontoEm && Date.now() < prontoEm) {
            if (callIdIncoming) ignorados.add(callIdIncoming);
            if (telefoneIncoming) telefonesIgnorados.add(telefoneIncoming);

            zapiCallIdsIgnoradosRef.current.set(instanceId, ignorados);
            zapiCallTelefonesIgnoradosRef.current.set(
              instanceId,
              telefonesIgnorados,
            );

            console.warn(
              "[CaptaZap Calls] replay ignorado durante inicialização:",
              {
                instanceId,
                callId: callIdIncoming,
                telefone: telefoneIncoming,
                prontoEm: new Date(prontoEm).toISOString(),
              },
            );
            return;
          }

          // Se o SDK já conhecia esta chamada no instante em que conectamos,
          // ela não é uma nova ligação desta sessão.
          if (callIdIncoming && ignorados.has(callIdIncoming)) {
            console.warn("[CaptaZap Calls] replay ignorado por callId:", {
              instanceId,
              callId: callIdIncoming,
              from: call?.from,
              state: call?.state,
            });
            return;
          }

          if (telefoneIncoming && telefonesIgnorados.has(telefoneIncoming)) {
            console.warn(
              "[CaptaZap Calls] replay ignorado por telefone da inicialização:",
              {
                instanceId,
                callId: callIdIncoming,
                telefone: telefoneIncoming,
              },
            );
            return;
          }

          const timestampMs = getTimestampChamadaMs(call);
          const idadeMs = timestampMs ? Date.now() - timestampMs : null;

          console.info("[CaptaZap Calls] chamada recebida:", {
            instanceId,
            callId: call?.callId,
            from: call?.from,
            isVideo: call?.isVideo,
            timestamp: call?.timestamp,
            idadeSegundos:
              idadeMs === null ? null : Math.floor(idadeMs / 1000),
          });

          // Proteção contra replay na reconexão/página recarregada.
          // Uma chamada recebida antiga não pode reaparecer como nova.
          if (!chamadaRecebidaEhRecente(call)) {
            console.warn("[CaptaZap Calls] chamada antiga ignorada:", {
              instanceId,
              callId: call?.callId,
              from: call?.from,
              timestamp: call?.timestamp,
              idadeSegundos:
                idadeMs === null ? null : Math.floor(idadeMs / 1000),
            });

            if (callIdIncoming) {
              ignorados.add(callIdIncoming);
              zapiCallIdsIgnoradosRef.current.set(instanceId, ignorados);
            }

            return;
          }

          upsertChamada(instanceId, call, { estado: "ringing" });

          registrarEventoLigacaoCaptaZap({
            zapi_instance_id: instanceId,
            call_id: call?.callId,
            telefone: telefoneIncoming,
            direcao: "recebida",
            status: "ringing",
            iniciado_em: new Date().toISOString(),
          }).catch(() => null);

          enriquecerChamadaRecebida(
            instanceId,
            call?.callId,
            call?.from || call?.phone || call?.number || "",
          ).catch(() => null);
        });

        client.on("call:state", (callId, state) => {
          if (cancelado) return;
          const estadoAtual = String(state || "").toLowerCase() || "ringing";

          setChamadasRecebidas((atuais) =>
            (Array.isArray(atuais) ? atuais : []).map((item) => {
              if (item.chave !== `${instanceId}::${callId}`) return item;
              const agora = Date.now();
              return {
                ...item,
                estado: estadoAtual,
                atendidaEm:
                  ["accepted", "preaccepted", "active"].includes(estadoAtual)
                    ? item.atendidaEm || agora
                    : item.atendidaEm,
              };
            }),
          );

          registrarEventoLigacaoCaptaZap({
            zapi_instance_id: instanceId,
            call_id: callId,
            telefone: null,
            direcao: "recebida",
            status: estadoAtual,
            atendido_em: ["accepted", "preaccepted", "active"].includes(estadoAtual)
              ? new Date().toISOString()
              : null,
          }).catch(() => null);

          if (estadoAtual === "ended") {
            removerChamada(instanceId, callId);
          }
        });

        client.on("call:terminated", (callId, reason) => {
          if (cancelado) return;

          const ignorados = zapiCallIdsIgnoradosRef.current.get(instanceId);
          if (ignorados?.has(String(callId || "").trim())) {
            ignorados.delete(String(callId || "").trim());
          }

          setChamadasRecebidas((atuais) => {
            const lista = Array.isArray(atuais) ? atuais : [];
            const atual = lista.find(
              (item) => item.chave === `${instanceId}::${callId}`,
            );

            const duracaoSegundos = atual?.atendidaEm
              ? Math.max(0, Math.floor((Date.now() - atual.atendidaEm) / 1000))
              : 0;
            const statusFinal = atual?.recusaSolicitadaEm
              ? "rejected"
              : atual?.atendidaEm
                ? "ended"
                : "missed";

            registrarEventoLigacaoCaptaZap({
              zapi_instance_id: instanceId,
              call_id: callId,
              telefone: atual?.telefone || null,
              direcao: "recebida",
              status: statusFinal,
              finalizado_em: new Date().toISOString(),
              duracao_segundos: duracaoSegundos,
              motivo: reason || null,
              chamado_id: atual?.chamadoId || null,
              lead_id: atual?.leadId || null,
              captador_nome: atual?.captadorNome || "",
              responsavel_id: atual?.responsavelId || null,
              responsavel_nome: atual?.responsavelNome || "",
            }).catch(() => null);

            return lista.map((item) =>
              item.chave === `${instanceId}::${callId}`
                ? {
                    ...item,
                    estado: statusFinal,
                    motivo: reason || null,
                    duracaoSegundos,
                  }
                : item,
            );
          });

          removerChamada(instanceId, callId, 6000);
        });

        client.on("call:claimed", (callId) => {
          if (cancelado) return;
          upsertChamada(
            instanceId,
            { callId },
            { callId, estado: "claimed" },
          );
          removerChamada(instanceId, callId, 700);
        });

        client.on("call:dismissed", (callId) => {
          if (cancelado) return;

          const ignorados = zapiCallIdsIgnoradosRef.current.get(instanceId);
          if (ignorados?.has(String(callId || "").trim())) {
            ignorados.delete(String(callId || "").trim());
          }

          setChamadasRecebidas((atuais) => {
            const lista = Array.isArray(atuais) ? atuais : [];
            const atual = lista.find(
              (item) => item.chave === `${instanceId}::${callId}`,
            );

            registrarEventoLigacaoCaptaZap({
              zapi_instance_id: instanceId,
              call_id: callId,
              telefone: atual?.telefone || null,
              direcao: "recebida",
              status: "rejected",
              finalizado_em: new Date().toISOString(),
              chamado_id: atual?.chamadoId || null,
              lead_id: atual?.leadId || null,
              captador_nome: atual?.captadorNome || "",
              responsavel_id: atual?.responsavelId || null,
              responsavel_nome: atual?.responsavelNome || "",
            }).catch(() => null);

            return lista.map((item) =>
              item.chave === `${instanceId}::${callId}`
                ? { ...item, estado: "rejected" }
                : item,
            );
          });

          removerChamada(instanceId, callId, 1200);
        });

        client.on("error", (error) => {
          console.error(
            "[CaptaZap Calls] erro na conexão",
            instanceId,
            error?.message || error,
          );
        });

        novosClientes.set(instanceId, client);
      } catch (error) {
        console.error(
          "[CaptaZap Calls] não foi possível iniciar a conexão",
          instanceId,
          error,
        );
      }
    }

    zapiCallClientsRef.current.forEach((client) => {
      try {
        client?.destroy?.();
      } catch (_) {}
    });

    zapiCallClientsRef.current = novosClientes;

    return () => {
      cancelado = true;
      novosClientes.forEach((client) => {
        try {
          client?.destroy?.();
        } catch (_) {}
      });

      if (zapiCallClientsRef.current === novosClientes) {
        zapiCallClientsRef.current = new Map();
      }
    };
  }, [
    empresaId,
    conexoesChamadasKey,
    registrarEventoLigacaoCaptaZap,
    getTimestampChamadaMs,
    chamadaRecebidaEhRecente,
  ]);

  const handleAtenderChamadaRecebida = useCallback(
    async (chamada) => {
      const instanceId = String(chamada?.instanceId || "").trim();
      const callId = String(chamada?.callId || "").trim();
      if (!instanceId || !callId || !empresaId) return;

      const client = zapiCallClientsRef.current.get(instanceId);
      if (!client) {
        window.alert("A conexão de chamadas desta instância não está disponível.");
        return;
      }

      try {
        // Claim atômico no backend: somente um computador ganha a chamada.
        const claim = await requestJson("/whatsapp/calls/claim", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            empresa_id: empresaId,
            zapi_instance_id: instanceId,
            call_id: callId,
            usuario_id: usuarioIdLogado || null,
            usuario_nome: usuarioNomeLogado || "",
          }),
        });

        if (!claim?.claimed) {
          removerChamadaLocalPorChave(chamada.chave);
          window.alert(
            claim?.atendido_por_nome
              ? `Esta ligação já foi atendida por ${claim.atendido_por_nome}.`
              : "Esta ligação já foi atendida em outro computador.",
          );
          return;
        }

        // Se estava na Fila, quem atende passa a ser o responsável operacional.
        if (chamada?.emFila && chamada?.conversaAlvo) {
          try {
            const dataAssumir = await assumirConversaCaptaZap({
              empresaId,
              conversa: chamada.conversaAlvo,
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
              acao: "assumir",
            });

            const chamado = dataAssumir?.chamada || {};
            const responsavelNome =
              chamado?.responsavel_nome || usuarioNomeLogado || "";
            const responsavelId =
              chamado?.responsavel_id || usuarioIdLogado || null;

            setChamadasRecebidas((atuais) =>
              (Array.isArray(atuais) ? atuais : []).map((item) =>
                item.chave === chamada.chave
                  ? {
                      ...item,
                      emFila: false,
                      responsavelId,
                      responsavelNome,
                    }
                  : item,
              ),
            );
          } catch (errorAssumir) {
            await requestJson("/whatsapp/calls/release", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                empresa_id: empresaId,
                zapi_instance_id: instanceId,
                call_id: callId,
                usuario_id: usuarioIdLogado || null,
              }),
            }).catch(() => null);

            throw new Error(
              errorAssumir?.message ||
                "Não foi possível assumir o chamado antes de atender.",
            );
          }
        }

        client.accept(callId);
        const agora = Date.now();

        // Ao atender, abre automaticamente o chamado/conversa no ChatPanel.
        // Isso deixa o operador já no contexto do paciente durante a ligação.
        if (chamada?.conversaAlvo) {
          try {
            handleSelecionarConversa(chamada.conversaAlvo);
          } catch (errorAbrirConversa) {
            console.warn(
              "CaptaZap Calls | não foi possível abrir a conversa após atender:",
              errorAbrirConversa,
            );
          }
        }

        setChamadasRecebidas((atuais) =>
          (Array.isArray(atuais) ? atuais : []).map((item) =>
            item.chave === chamada.chave
              ? {
                  ...item,
                  estado: "accepted",
                  atendidaEm: item.atendidaEm || agora,
                  responsavelId:
                    item.responsavelId || usuarioIdLogado || null,
                  responsavelNome:
                    item.responsavelNome || usuarioNomeLogado || "",
                }
              : item,
          ),
        );

        await registrarEventoLigacaoCaptaZap({
          zapi_instance_id: instanceId,
          call_id: callId,
          telefone: chamada?.telefone || null,
          direcao: "recebida",
          status: "active",
          atendido_em: new Date(agora).toISOString(),
          chamado_id: chamada?.chamadoId || null,
          lead_id: chamada?.leadId || null,
          captador_nome: chamada?.captadorNome || "",
          responsavel_id: chamada?.responsavelId || usuarioIdLogado || null,
          responsavel_nome:
            chamada?.responsavelNome || usuarioNomeLogado || "",
        });
      } catch (error) {
        console.error("CaptaZap Calls | erro ao atender:", error);
        window.alert(
          error?.message || "Não foi possível atender a chamada no CaptaZap.",
        );
      }
    },
    [
      empresaId,
      registrarEventoLigacaoCaptaZap,
      removerChamadaLocalPorChave,
      usuarioIdLogado,
      usuarioNomeLogado,
    ],
  );

  const handleRecusarChamadaRecebida = useCallback(
    async (chamada) => {
      const instanceId = String(chamada?.instanceId || "").trim();
      const callIdOriginal = String(chamada?.callId || "").trim();
      if (!instanceId || !callIdOriginal) return;

      const client = zapiCallClientsRef.current.get(instanceId);

      if (!client) {
        window.alert("A conexão de chamadas desta instância não está disponível.");
        return;
      }

      if (typeof client.isConnected === "function" && !client.isConnected()) {
        window.alert(
          "A conexão de chamadas está reconectando. Aguarde alguns segundos e tente novamente.",
        );
        return;
      }

      try {
        const chamadasSdk =
          typeof client.getCalls === "function" ? client.getCalls() || [] : [];

        const telefoneAlvo = normalizarTelefoneChamadaCaptaZap(chamada?.telefone || "");

        const chamadaSdk =
          chamadasSdk.find(
            (item) => String(item?.callId || "") === callIdOriginal,
          ) ||
          chamadasSdk.find((item) => {
            const telefoneSdk = normalizarTelefoneChamadaCaptaZap(
              item?.from || item?.phone || item?.number || "",
            );
            return Boolean(
              telefoneAlvo && telefoneSdk && telefoneSdk === telefoneAlvo,
            );
          }) ||
          null;

        const callIdRejeitar = String(
          chamadaSdk?.callId || callIdOriginal,
        ).trim();

        console.info("[CaptaZap Calls] solicitando recusa:", {
          instanceId,
          callIdOriginal,
          callIdRejeitar,
          chamadasAtivas: chamadasSdk.map((item) => ({
            callId: item?.callId,
            from: item?.from,
            state: item?.state,
          })),
        });

        // A SDK documenta reject(callId) como rejeitar/encerrar.
        client.reject(callIdRejeitar);

        // Estado visual intermediário. NÃO removemos o card aqui.
        setChamadasRecebidas((atuais) =>
          (Array.isArray(atuais) ? atuais : []).map((item) =>
            item.chave === chamada.chave
              ? {
                  ...item,
                  estado: "rejecting",
                  recusaSolicitadaEm: Date.now(),
                }
              : item,
          ),
        );

        // Confirmação defensiva: se a Z-API não emitir dismissed/terminated e a
        // chamada continuar no SDK, mostramos o erro e restauramos o card.
        window.setTimeout(() => {
          try {
            const aindaAtivas =
              typeof client.getCalls === "function" ? client.getCalls() || [] : [];

            const aindaExiste = aindaAtivas.some(
              (item) => String(item?.callId || "") === callIdRejeitar,
            );

            if (aindaExiste) {
              console.error(
                "[CaptaZap Calls] recusa não confirmada pela Z-API:",
                aindaAtivas,
              );

              setChamadasRecebidas((atuais) =>
                (Array.isArray(atuais) ? atuais : []).map((item) =>
                  item.chave === chamada.chave
                    ? {
                        ...item,
                        estado: "ringing",
                        recusaSolicitadaEm: null,
                      }
                    : item,
                ),
              );

              window.alert(
                "A Z-API não confirmou a recusa da chamada. A ligação continua ativa no WhatsApp.",
              );
            }
          } catch {
            // O evento da Z-API continua sendo a fonte principal da confirmação.
          }
        }, 2200);
      } catch (error) {
        console.error("CaptaZap Calls | erro ao recusar/encerrar:", error);

        setChamadasRecebidas((atuais) =>
          (Array.isArray(atuais) ? atuais : []).map((item) =>
            item.chave === chamada.chave
              ? { ...item, estado: "ringing", recusaSolicitadaEm: null }
              : item,
          ),
        );

        window.alert(
          error?.message || "Não foi possível recusar a chamada no WhatsApp.",
        );
      }
    },
    [],
  );

  // Relógio visual das chamadas em andamento.
  const [relogioLigacoesAgora, setRelogioLigacoesAgora] = useState(Date.now());

  useEffect(() => {
    const existeAtiva = (Array.isArray(chamadasRecebidas)
      ? chamadasRecebidas
      : []
    ).some((item) =>
      ["accepted", "preaccepted", "active"].includes(
        String(item?.estado || "").toLowerCase(),
      ),
    );

    if (!existeAtiva) return undefined;

    const timer = window.setInterval(() => {
      setRelogioLigacoesAgora(Date.now());
    }, 1000);

    return () => window.clearInterval(timer);
  }, [chamadasRecebidas]);

  // Sincronização entre operadores somente enquanto existe uma chamada local.
  // Não fazemos polling permanente: isso evita dezenas de requests/minuto por PC
  // quando a equipe está apenas usando mensagens.
  useEffect(() => {
    if (!empresaId || !chamadasRecebidas.length) return undefined;

    let cancelado = false;
    let consultando = false;

    const sincronizar = async () => {
      if (cancelado || consultando) return;
      consultando = true;

      try {
        const data = await requestJson(
          `/whatsapp/calls/active?empresa_id=${encodeURIComponent(empresaId)}`,
        );
        const ativas = Array.isArray(data?.calls) ? data.calls : [];
        const mapa = new Map(
          ativas.map((item) => [
            `${String(item?.zapi_instance_id || "")}::${String(item?.call_id || "")}`,
            item,
          ]),
        );

        setChamadasRecebidas((atuais) =>
          (Array.isArray(atuais) ? atuais : []).filter((local) => {
            const remota = mapa.get(local.chave);

            if (!remota) {
              // Dá tempo para o evento inicial chegar ao backend.
              return Date.now() - Number(local?.recebidaEm || Date.now()) < 7000;
            }

            const status = String(remota?.status || "").toLowerCase();
            if (
              ["ended", "missed", "rejected", "dismissed", "failed"].includes(
                status,
              )
            ) {
              return false;
            }

            const atendidoPor = Number(remota?.atendido_por_id || 0) || null;
            if (
              atendidoPor &&
              usuarioIdLogado &&
              String(atendidoPor) !== String(usuarioIdLogado)
            ) {
              return false;
            }

            return true;
          }),
        );
      } catch {
        // A chamada local continua funcionando mesmo se a sincronização falhar.
      } finally {
        consultando = false;
      }
    };

    sincronizar();

    // 3s somente durante uma chamada. Antes era 1,5s permanentemente.
    const timer = window.setInterval(sincronizar, 3000);

    return () => {
      cancelado = true;
      window.clearInterval(timer);
    };
  }, [empresaId, usuarioIdLogado, chamadasRecebidas.length]);

  const chamadasTocandoKey = useMemo(
    () =>
      (Array.isArray(chamadasRecebidas) ? chamadasRecebidas : [])
        .filter(
          (item) =>
            String(item?.estado || "ringing").toLowerCase() === "ringing",
        )
        .map((item) => item.chave)
        .sort()
        .join("|"),
    [chamadasRecebidas],
  );

  useEffect(() => {
    if (!chamadasTocandoKey) return undefined;

    tocarSomLigacaoRecebidaCaptaZap();

    const timer = window.setInterval(() => {
      tocarSomLigacaoRecebidaCaptaZap();
    }, 4200);

    return () => {
      window.clearInterval(timer);
    };
  }, [chamadasTocandoKey]);


  const carregarConversasFixadas = useCallback(async () => {
    if (!empresaId) {
      setConversasFixadas([]);
      setConversasAcompanhamentoFixadas([]);
      return [];
    }

    try {
      const params = new URLSearchParams({
        empresa_id: String(empresaId),
      });
      if (usuarioIdLogado) params.set("usuario_id", String(usuarioIdLogado));

      const data = await requestJson(
        `/whatsapp/captazap/conversas-fixadas?${params.toString()}`,
      );
      const lista = Array.isArray(data?.fixadas)
        ? data.fixadas
        : Array.isArray(data?.data)
          ? data.data
          : [];
      const minhasFixadas = Array.isArray(data?.minhas_fixadas)
        ? data.minhas_fixadas
        : Array.isArray(data?.minhasFixadas)
          ? data.minhasFixadas
          : [];

      setConversasFixadas(
        lista.filter((item) => getChaveConversaFixadaCaptaZap(item)),
      );
      setConversasAcompanhamentoFixadas(
        minhasFixadas
          .map(normalizarConversaCaptazap)
          .filter((item) => getChaveConversaFixadaCaptaZap(item)),
      );
      return lista;
    } catch (error) {
      console.warn("CaptaZap | não foi possível carregar acompanhamentos fixados:", error);
      setConversasFixadas([]);
      setConversasAcompanhamentoFixadas([]);
      return [];
    }
  }, [empresaId, usuarioIdLogado]);

  useEffect(() => {
    carregarConversasFixadas().catch(() => null);
  }, [carregarConversasFixadas]);

  const handleToggleFixarConversa = useCallback(
    async (conversaAlvo = null) => {
      const conversa =
        conversaAlvo || conversaSelecionadaRef.current || conversaSelecionada;
      if (!empresaId || !conversa || !usuarioIdLogado) return false;

      if (!usuarioPodeFixarAcompanhamentoCaptaZap) {
        window.alert(
          "Você não possui permissão para fixar conversas para acompanhamento.",
        );
        return false;
      }

      const telefone = getTelefoneConversaFixadaCaptaZap(conversa);
      const zapiInstanceId = getConexaoInstanceIdConversaCaptaZap(conversa);
      const chave = getChaveConversaFixadaCaptaZap(conversa);

      if (!telefone || !zapiInstanceId || !chave) {
        window.alert(
          "Esta conversa não possui telefone/instância WhatsApp suficientes para fixar.",
        );
        return false;
      }

      const fixacaoAtual = getFixacaoConversaCaptaZap(conversa, conversasFixadas);
      const fixadaPorMim = isFixacaoDoUsuarioCaptaZap(
        fixacaoAtual,
        usuarioIdLogado,
        usuarioNomeLogado,
      );

      if (fixacaoAtual && !fixadaPorMim) {
        window.alert(
          `Esta conversa já está em acompanhamento por ${
            fixacaoAtual?.usuario_nome || fixacaoAtual?.usuarioNome || "outro usuário"
          }.`,
        );
        return false;
      }

      const action = fixadaPorMim ? "unpin" : "pin";

      try {
        setFixandoConversaKey(chave);
        await requestJson("/whatsapp/captazap/conversas/fixar", {
          method: "POST",
          body: JSON.stringify({
            empresa_id: empresaId,
            telefone,
            zapi_instance_id: zapiInstanceId,
            action,
            usuario_id: usuarioIdLogado,
            usuario_nome: usuarioNomeLogado || null,
          }),
        });

        // O servidor é a fonte da verdade: recarrega marcador + conversa completa
        // para que Minhas reflita imediatamente o acompanhamento.
        await carregarConversasFixadas();
        return true;
      } catch (error) {
        console.error("CaptaZap | falha ao fixar/desafixar acompanhamento:", error);
        window.alert(
          error?.message ||
            `Não foi possível ${action === "pin" ? "fixar" : "desafixar"} o acompanhamento.`,
        );
        return false;
      } finally {
        setFixandoConversaKey("");
      }
    },
    [
      empresaId,
      conversaSelecionada,
      conversasFixadas,
      usuarioIdLogado,
      usuarioNomeLogado,
      carregarConversasFixadas,
      usuarioPodeFixarAcompanhamentoCaptaZap,
    ],
  );

  const conversaSelecionadaFixacao = useMemo(
    () => getFixacaoConversaCaptaZap(conversaSelecionada, conversasFixadas),
    [conversaSelecionada, conversasFixadas],
  );

  const conversaSelecionadaFixada = Boolean(conversaSelecionadaFixacao);
  const conversaSelecionadaFixadaPorMim = useMemo(
    () =>
      isFixacaoDoUsuarioCaptaZap(
        conversaSelecionadaFixacao,
        usuarioIdLogado,
        usuarioNomeLogado,
      ),
    [
      conversaSelecionadaFixacao,
      usuarioIdLogado,
      usuarioNomeLogado,
    ],
  );
  const conversaSelecionadaFixadaPorOutro = Boolean(
    conversaSelecionadaFixacao && !conversaSelecionadaFixadaPorMim,
  );
  const conversaSelecionadaFixadaPorNome = String(
    conversaSelecionadaFixacao?.usuario_nome ||
      conversaSelecionadaFixacao?.usuarioNome ||
      "",
  ).trim();

  const fixandoConversaSelecionada = useMemo(() => {
    const chave = getChaveConversaFixadaCaptaZap(conversaSelecionada);
    return Boolean(chave && chave === fixandoConversaKey);
  }, [conversaSelecionada, fixandoConversaKey]);

  const carregarConversasOperacionaisV2 = useCallback(
    async ({
      aba = "minhas",
      reset = false,
      silencioso = false,
      buscaRemota = "",
    } = {}) => {
      if (
        !empresaId ||
        !CAPTAZAP_ABAS_OPERACIONAIS_PAGINADAS.has(aba)
      ) {
        return [];
      }

      if (["minhas", "fila"].includes(aba) && !usuarioIdLogado) {
        return [];
      }

      const chaveCarga = `${empresaId}:${usuarioIdLogado || 0}:${aba}`;
      if (carregandoPaginaConversasV2Ref.current.has(chaveCarga)) {
        return [];
      }

      const estadoAtual =
        paginasConversasV2Ref.current?.[aba] || {
          carregada: false,
          hasMore: true,
          cursor: null,
        };

      if (!reset && estadoAtual.carregada && !estadoAtual.hasMore) {
        return [];
      }

      carregandoPaginaConversasV2Ref.current.add(chaveCarga);
      atualizarEstadoPaginacaoConversasV2(aba, { loading: true });

      const inicio =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();

      try {
        const params = new URLSearchParams({
          empresa_id: String(empresaId),
          aba,
          limit: String(CAPTAZAP_LISTA_PAGE_SIZE),
        });

        if (usuarioIdLogado) {
          params.set("usuario_id", String(usuarioIdLogado));
        }

        const conexaoAtual = String(conexaoSelecionadaRef.current || "").trim();
        if (conexaoAtual) {
          params.set("zapi_instance_id", conexaoAtual);
        }

        if (reset) params.set("incluir_total", "1");
        if (buscaRemota) params.set("busca", String(buscaRemota));

        const cursor = reset ? null : estadoAtual.cursor;
        if (cursor?.cursor_em && cursor?.cursor_id) {
          params.set("cursor_em", String(cursor.cursor_em));
          params.set("cursor_id", String(cursor.cursor_id));
        }

        const data = await requestJson(
          `/whatsapp/captazap/conversas-v2?${params.toString()}`,
        );

        const novasConversas = pickArray(
          { status: "fulfilled", value: data },
          ["conversas", "data", "items"],
        ).map(normalizarConversaCaptazap);

        const paginacao = data?.paginacao || {};
        const nextCursor =
          paginacao.next_cursor || paginacao.nextCursor || null;
        const hasMore = Boolean(
          paginacao.has_more ?? paginacao.hasMore ?? false,
        );

        if (!buscaRemota) {
          setConversasWhatsApp((atuais) => {
            const listaAtual = Array.isArray(atuais) ? atuais : [];
            const base = reset
              ? listaAtual.filter(
                  (item) =>
                    String(item?.aba_captazap || "") !== String(aba),
                )
              : listaAtual;

            return ordenarConversasPorUltimaAtividadeCaptaZap(
              dedupeConversas([...base, ...novasConversas]),
            );
          });

          atualizarEstadoPaginacaoConversasV2(aba, {
            carregada: true,
            hasMore,
            cursor: nextCursor,
            loading: false,
          });

          if (reset && Number.isFinite(Number(data?.total))) {
            setTotaisOperacaoV2((atuais) => ({
              ...atuais,
              [aba]: Number(data.total || 0),
            }));
          }
        }

        if (!silencioso && isDev) {
          const fim =
            typeof performance !== "undefined" && typeof performance.now === "function"
              ? performance.now()
              : Date.now();
          console.info("[CaptaZapPerf] conversas-v2", {
            aba,
            recebidas: novasConversas.length,
            total: data?.total ?? null,
            backend_ms: data?.perf_ms ?? data?.perfMs ?? null,
            browser_ms: Math.round(fim - inicio),
            has_more: hasMore,
          });
        }

        return novasConversas;
      } catch (error) {
        atualizarEstadoPaginacaoConversasV2(aba, {
          loading: false,
        });
        console.error(`❌ CaptaZap V2 | erro ao carregar aba ${aba}:`, error);
        return [];
      } finally {
        carregandoPaginaConversasV2Ref.current.delete(chaveCarga);
      }
    },
    [
      empresaId,
      isDev,
      usuarioIdLogado,
      atualizarEstadoPaginacaoConversasV2,
    ],
  );


  const carregarContadoresOperacaoV2 = useCallback(
    async ({ force = false } = {}) => {
      if (!empresaId || !usuarioIdLogado) return null;
      if (carregandoContadoresV2Ref.current) return null;

      const agora = Date.now();
      if (
        !force &&
        agora - ultimoCarregamentoContadoresV2Ref.current < 10000
      ) {
        return null;
      }

      carregandoContadoresV2Ref.current = true;

      try {
        const params = new URLSearchParams({
          empresa_id: String(empresaId),
          usuario_id: String(usuarioIdLogado),
        });
        const conexaoAtual = String(conexaoSelecionadaRef.current || "").trim();
        if (conexaoAtual) {
          params.set("zapi_instance_id", conexaoAtual);
        }

        const data = await requestJson(
          `/whatsapp/captazap/contadores-v2?${params.toString()}`,
        );

        setTotaisOperacaoV2({
          minhas: Number(data?.minhas || 0),
          fila: Number(data?.fila || 0),
          fechados: Number(data?.fechados || 0),
        });

        const pendenciasPorInstancia = {};
        const pendenciasRecebidas = Array.isArray(
          data?.aguardando_resposta_por_conexao,
        )
          ? data.aguardando_resposta_por_conexao
          : [];

        for (const item of pendenciasRecebidas) {
          const instanceId = String(item?.zapi_instance_id || "").trim();
          if (!instanceId) continue;
          pendenciasPorInstancia[instanceId] = Math.max(
            0,
            Number(item?.total || 0),
          );
        }

        setPendenciasRespostaPorConexao({
          total: Math.max(0, Number(data?.aguardando_resposta_total || 0)),
          porInstancia: pendenciasPorInstancia,
        });

        ultimoCarregamentoContadoresV2Ref.current = Date.now();

        if (isDev) {
          console.info("[CaptaZapPerf] contadores-v2", {
            backend_ms: data?.perf_ms ?? null,
            minhas: data?.minhas ?? 0,
            fila: data?.fila ?? 0,
            fechados: data?.fechados ?? 0,
          });
        }

        return data;
      } catch (error) {
        console.warn("CaptaZap | contadores V2 indisponíveis:", error);
        return null;
      } finally {
        carregandoContadoresV2Ref.current = false;
      }
    },
    [empresaId, usuarioIdLogado, isDev],
  );

  const carregarContatosAvulsosSobDemanda = useCallback(async () => {
    if (!empresaId || contatosAvulsosCarregadosRef.current) return;

    contatosAvulsosCarregadosRef.current = true;

    try {
      const data = await requestJson(
        `/whatsapp/captazap/contatos?empresa_id=${empresaId}&limit=300`,
      );

      setContatosAvulsos(
        pickArray(
          { status: "fulfilled", value: data },
          ["contatos", "data"],
        ).map((contato) =>
          contatoAvulsoToConversaCaptaZap({
            contato,
            empresaId,
            empresaNome,
          }),
        ),
      );
    } catch (error) {
      contatosAvulsosCarregadosRef.current = false;
      console.warn("CaptaZap | contatos avulsos sob demanda falharam:", error);
    }
  }, [empresaId, empresaNome]);


  const handleChangeAbaQueueList = useCallback(
    (novaAba) => {
      const aba = String(novaAba || "minhas").toLowerCase();
      abaQueueListRef.current = aba;

      if (CAPTAZAP_ABAS_OPERACIONAIS_PAGINADAS.has(aba)) {
        const estado = paginasConversasV2Ref.current?.[aba];
        if (!estado?.carregada) {
          carregarConversasOperacionaisV2({
            aba,
            reset: true,
          }).catch(() => null);
        }
        return;
      }

      if (aba === "contatos") {
        carregarContatosAvulsosSobDemanda().catch(() => null);
      }
    },
    [
      carregarConversasOperacionaisV2,
      carregarContatosAvulsosSobDemanda,
    ],
  );

  const handleCarregarMaisQueueList = useCallback(
    (abaSolicitada) => {
      const aba = String(abaSolicitada || abaQueueListRef.current || "minhas")
        .trim()
        .toLowerCase();

      if (!CAPTAZAP_ABAS_OPERACIONAIS_PAGINADAS.has(aba)) return;

      carregarConversasOperacionaisV2({
        aba,
        reset: false,
        silencioso: true,
      }).catch(() => null);
    },
    [carregarConversasOperacionaisV2],
  );

  const carregarDraMaluAtivosCaptaZap = useCallback(async () => {
    if (!empresaId) {
      setDraMaluAtivos([]);
      return [];
    }

    try {
      const data = await requestJson(
        `/whatsapp/captazap/dra-malu-ativos?empresa_id=${encodeURIComponent(empresaId)}`,
      );
      const lista = pickArray(
        { status: "fulfilled", value: data },
        ["atendimentos", "data", "items"],
      );

      const normalizados = (lista || []).map((item) =>
        normalizarFollowupChamadoCaptaZap(item, {
          empresaId,
          usuarioId: usuarioIdLogado,
          usuarioNome: usuarioNomeLogado,
        }),
      );

      setDraMaluAtivos(normalizados);

      // Foto é parte da identidade operacional. A lista virtual da Dra. Malu
      // não pode depender de a conversa estar carregada em "Minhas" do usuário
      // atual. Reaproveita o mesmo endpoint/cache de avatar do CaptaZap em
      // background e só complementa os registros que ainda vieram sem foto.
      const semAvatar = normalizados
        .filter((item) => !getAvatarConversa(item))
        .slice(0, 40);

      if (semAvatar.length) {
        allSettledComConcorrenciaCaptaZap(
          semAvatar.map((item) => async () => {
            const telefone =
              item?.telefone || item?.numero || item?.telefone_norm || "";
            if (!telefone) return null;
            const profileUrl = await buscarAvatarContatoCaptaZap({
              empresaId,
              telefone,
            });
            if (!profileUrl) return null;
            return {
              atendimentoId:
                item?.atendimento_id || item?.dra_malu_atendimento_id || item?.id,
              chamadoId: item?.chamado_id || item?.protocolo_id || null,
              telefone: normalizarTelefone(telefone).slice(-11),
              profileUrl,
            };
          }),
          3,
        )
          .then((resultados) => {
            const encontrados = (resultados || [])
              .filter((resultado) => resultado?.status === "fulfilled")
              .map((resultado) => resultado.value)
              .filter(Boolean);

            if (!encontrados.length) return;

            setDraMaluAtivos((atuais) =>
              (Array.isArray(atuais) ? atuais : []).map((atual) => {
                const telefoneAtual = normalizarTelefone(
                  atual?.telefone || atual?.numero || atual?.telefone_norm || "",
                ).slice(-11);
                const encontrado = encontrados.find((foto) => {
                  const mesmoAtendimento =
                    foto.atendimentoId &&
                    String(foto.atendimentoId) ===
                      String(
                        atual?.atendimento_id ||
                          atual?.dra_malu_atendimento_id ||
                          atual?.id ||
                          "",
                      );
                  const mesmoChamado =
                    foto.chamadoId &&
                    String(foto.chamadoId) ===
                      String(atual?.chamado_id || atual?.protocolo_id || "");
                  return Boolean(
                    mesmoAtendimento ||
                      mesmoChamado ||
                      (foto.telefone && telefoneAtual === foto.telefone),
                  );
                });

                if (!encontrado?.profileUrl) return atual;
                return {
                  ...atual,
                  foto: atual?.foto || encontrado.profileUrl,
                  avatar: atual?.avatar || encontrado.profileUrl,
                  avatar_url: atual?.avatar_url || encontrado.profileUrl,
                  profileUrl: atual?.profileUrl || encontrado.profileUrl,
                };
              }),
            );
          })
          .catch(() => null);
      }

      return normalizados;
    } catch (error) {
      console.warn(
        "⚠️ CaptaZap | não foi possível carregar atendimentos ativos da Dra. Malu:",
        error?.message || error,
      );
      return [];
    }
  }, [empresaId, usuarioIdLogado, usuarioNomeLogado]);

  const carregarDadosCRM = useCallback(async () => {
    if (!empresaId) {
      setLeadsCRM([]);
      setLeadsCRMBuscaRemota([]);
      buscaContatosCRMCacheRef.current?.clear?.();
      setTarefas([]);
      setInteracoes([]);
      setAgendamentos([]);
      setRecebimentosFinanceiros([]);
      setFollowupsChamados([]);
      setFollowupsChamadosEmpresa([]);
      setDraMaluAtivos([]);
      setFollowupRegrasAtivas([]);
      setFontes([]);
      setCampanhas([]);
      setProcedimentos([]);
      setRegioes([]);
      setDentistas([]);
      setUsuarios([]);
      setSolicitacoesTransferencia([]);
      setConversasWhatsApp([]);
      setContatosAvulsos([]);
      return;
    }

    // Evita duas cargas completas em paralelo quando há clique repetido,
    // re-render ou eventos muito próximos. Mantém o mesmo resultado visual.
    if (carregamentoCRMEmAndamentoRef.current) return;

    carregamentoCRMEmAndamentoRef.current = true;
    setLoadingCRM(true);

    // A pasta Follow-up da Dra. Malu é independente do follow-up legado.
    // Atualiza em paralelo, sem bloquear a carga do CRM.
    carregarDraMaluAtivosCaptaZap().catch(() => null);

    try {
      // O CRM é complementar ao mensageiro. Limitamos a concorrência para que
      // uma carga pesada de leads/tarefas/agenda não dispute conexões com a
      // abertura do chat nem com o envio/recebimento de mensagens.
      const results = await allSettledComConcorrenciaCaptaZap(
        [
          () => carregarLeadsCRMCacheCaptaZap(empresaId, { forcar: true }),
          () => requestJson(`/todas-tarefas?empresa_id=${empresaId}`),
          () => requestJson(`/todas-interacoes?empresa_id=${empresaId}`),
          () => requestJson(`/agendamentos/empresa/${empresaId}`),
          () => requestJson(`/receb_leads/financeiro/${empresaId}`),
          () => requestJson("/fonteslead"),
          () => requestJson("/procedimentos"),
          () => requestJson("/regioes"),
          () => requestJson(`/campanhas?empresa_id=${empresaId}`),
          () => requestJson(`/dentistas/${empresaId}`),
          () => requestJson(`/usuarios?empresa_id=${empresaId}`),
          () =>
            requestJson(
              `/leads/transferencias/solicitacoes?empresa_id=${empresaId}&status=pendente`,
            ),
          () =>
            buscarFollowupsChamadosCaptaZap({
              empresaId,
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
            }),
          () => buscarFollowupsChamadosEmpresaCaptaZap({ empresaId }),
          () => buscarRegrasFollowupCaptaZap({ empresaId }),
        ],
        CAPTAZAP_CRM_BACKGROUND_CONCURRENCY,
      );

      const [
        leadsRes,
        tarefasRes,
        interacoesRes,
        agendamentosRes,
        recebimentosRes,
        fontesRes,
        procedimentosRes,
        regioesRes,
        campanhasRes,
        dentistasRes,
        usuariosRes,
        transferenciasRes,
        followupsChamadosRes,
        followupsChamadosEmpresaRes,
        followupRegrasRes,
      ] = results;

      setLeadsCRM(
        pickArray(leadsRes, ["leads", "data"]).filter((lead) =>
          pertenceEmpresa(lead, empresaId),
        ),
      );

      setTarefas(pickArray(tarefasRes, ["tarefas", "data"]));
      setInteracoes(pickArray(interacoesRes, ["interacoes", "data"]));
      setAgendamentos(pickArray(agendamentosRes, ["agendamentos", "data"]));
      setRecebimentosFinanceiros(
        pickArray(recebimentosRes, ["recebimentos", "financeiro", "data"]),
      );

      setFollowupsChamados(
        pickArray(followupsChamadosRes, ["followups", "data"])
          .filter((item) =>
            followupPertenceAoResponsavelLogadoCaptaZap(item, {
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
            }),
          )
          .map((item) =>
            normalizarFollowupChamadoCaptaZap(item, {
              empresaId,
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
            }),
          ),
      );

      setFollowupsChamadosEmpresa(
        pickArray(followupsChamadosEmpresaRes, ["followups", "data"]).map(
          (item) =>
            normalizarFollowupChamadoCaptaZap(item, {
              empresaId,
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
            }),
        ),
      );

      setFollowupRegrasAtivas(
        pickArray(followupRegrasRes, ["regras", "data"])
          .map((item) => ({
            ...item,
            id: item.id == null ? null : Number(item.id),
            followup_ordem: Number(item.followup_ordem || 0),
            followup_canal: String(
              item.followup_canal || item.canal || "whatsapp",
            ).toLowerCase(),
            titulo: item.titulo || `Tentativa ${item.followup_ordem || ""}`,
            texto: item.texto || "",
          }))
          .filter((item) => item.followup_ordem > 0)
          .sort((a, b) => a.followup_ordem - b.followup_ordem),
      );

      setFontes(pickArray(fontesRes, ["fontes", "data"]));
      setProcedimentos(pickArray(procedimentosRes, ["procedimentos", "data"]));
      setRegioes(pickArray(regioesRes, ["regioes", "data"]));
      setCampanhas(pickArray(campanhasRes, ["campanhas", "data"]));
      setDentistas(pickArray(dentistasRes, ["dentistas", "data"]));
      setUsuarios(pickArray(usuariosRes, ["usuarios", "data"]));
      setSolicitacoesTransferencia(
        pickArray(transferenciasRes, [
          "solicitacoes",
          "transferencias",
          "data",
        ]),
      );

    } catch (error) {
      console.error("❌ CaptaZap carregarDadosCRM:", error);
    } finally {
      carregamentoCRMEmAndamentoRef.current = false;
      setLoadingCRM(false);
    }
  }, [
    empresaId,
    empresaNome,
    usuarioIdLogado,
    usuarioNomeLogado,
    carregarDraMaluAtivosCaptaZap,
  ]);

  useEffect(() => {
    setConexaoSelecionada("");
    conexaoSelecionadaRef.current = "";
    setConexoesWhatsApp([]);
    setConversaSelecionada(null);
    setLeadsCRMBuscaRemota([]);
    setResultadosBuscaOperacao([]);
    setBuscandoBuscaOperacao(false);
    buscaContatosCRMCacheRef.current?.clear?.();
    buscaOperacaoCacheRef.current?.clear?.();
    setConversasCriadasLocal([]);
    setConversasWhatsApp([]);
    setContatosAvulsos([]);
    setFollowupsChamados([]);
    setFollowupsChamadosEmpresa([]);
    setDraMaluAtivos([]);
    setFollowupRegrasAtivas([]);
    setSalvandoContextoLead(false);
    carregamentoCRMEmAndamentoRef.current = false;
    recarregamentoConversasEmAndamentoRef.current = false;
    recarregamentoConversasPendenteRef.current = false;
    recarregamentosMensagensEmAndamentoRef.current.clear();
    recarregamentosMensagensPendentesRef.current.clear();
    if (sincronizacaoRetomadaTimerRef.current) {
      window.clearTimeout(sincronizacaoRetomadaTimerRef.current);
      sincronizacaoRetomadaTimerRef.current = null;
    }
    ultimoPollingMensagensAbertasRef.current = 0;
    abaQueueListRef.current = "minhas";
    contatosAvulsosCarregadosRef.current = false;
    avataresConsultadosAutoRef.current.clear();
    if (avatarAutoTimerRef.current) {
      window.clearTimeout(avatarAutoTimerRef.current);
      avatarAutoTimerRef.current = null;
    }
    carregandoPaginaConversasV2Ref.current.clear();
    paginasConversasV2Ref.current = {
      minhas: { carregada: false, hasMore: true, cursor: null },
      fila: { carregada: false, hasMore: true, cursor: null },
      fechados: { carregada: false, hasMore: true, cursor: null },
    };
    setPaginacaoConversasV2({
      minhas: { carregada: false, hasMore: true, cursor: null, loading: false },
      fila: { carregada: false, hasMore: true, cursor: null, loading: false },
      fechados: { carregada: false, hasMore: true, cursor: null, loading: false },
    });
    setTotaisOperacaoV2({ minhas: null, fila: null, fechados: null });
  }, [empresaId]);

  useEffect(() => {
    if (!empresaId) return undefined;

    let cancelado = false;

    (async () => {
      const conexoes = await carregarConexoesWhatsApp().catch(() => []);
      if (cancelado || !Array.isArray(conexoes) || !conexoes.length) return;

      // MULTICONEXÃO: não existe mais visão operacional "Todas".
      // Só carregamos Minhas/Fila/Fechados depois de existir uma conexão ativa,
      // impedindo que o primeiro request misture chamados de números diferentes.
      await carregarConversasOperacionaisV2({
        aba: "minhas",
        reset: true,
      }).catch(() => null);
      carregarContadoresOperacaoV2({ force: true }).catch(() => null);
    })();

    // CRM, agenda e cadastros entram em segundo plano depois que a mensageria
    // já começou a renderizar. Eles não podem disputar o primeiro carregamento.
    const timer = window.setTimeout(() => {
      if (!cancelado) carregarDadosCRM();
    }, 5000);

    return () => {
      cancelado = true;
      window.clearTimeout(timer);
    };
  }, [
    empresaId,
    carregarConexoesWhatsApp,
    carregarConversasOperacionaisV2,
    carregarContadoresOperacaoV2,
    carregarDadosCRM,
  ]);

  useEffect(() => {
    if (!empresaId) return undefined;

    let cancelado = false;
    const atualizar = () => {
      if (!cancelado) carregarDraMaluAtivosCaptaZap().catch(() => null);
    };

    atualizar();
    const timer = window.setInterval(atualizar, 20000);

    return () => {
      cancelado = true;
      window.clearInterval(timer);
    };
  }, [empresaId, carregarDraMaluAtivosCaptaZap]);

  useEffect(() => {
    const onFollowupAtualizar = (event) => {
      const detail = event?.detail || {};
      const proximaEtapa =
        detail.proxima_etapa ||
        detail.proximaEtapa ||
        detail.next_followup ||
        detail.proximo_followup ||
        null;

      const proximaNormalizada = proximaEtapa
        ? normalizarFollowupChamadoCaptaZap(
            {
              ...proximaEtapa,
              empresa_id:
                proximaEtapa.empresa_id || detail.empresa_id || empresaId,
              telefone:
                proximaEtapa.telefone ||
                detail.telefone ||
                detail.numero ||
                detail.phone ||
                "",
            },
            {
              empresaId: detail.empresa_id || empresaId,
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
            },
          )
        : null;

      const atualizarListaFollowups = (atuais = [], somenteUsuario = false) => {
        const semExecutado = removerFollowupPorEventoCaptaZap(
          atuais || [],
          detail,
        );

        if (!proximaNormalizada) return semExecutado;

        if (
          somenteUsuario &&
          !followupPertenceAoResponsavelLogadoCaptaZap(proximaNormalizada, {
            usuarioId: usuarioIdLogado,
            usuarioNome: usuarioNomeLogado,
          })
        ) {
          return semExecutado;
        }

        return upsertFollowupListaCaptaZap(semExecutado, proximaNormalizada);
      };

      setFollowupsChamados((atuais) =>
        atualizarListaFollowups(atuais || [], true),
      );

      setFollowupsChamadosEmpresa((atuais) =>
        atualizarListaFollowups(atuais || [], false),
      );

      const limparConversa = (conversa) => {
        const limpa = limparResumoFollowupPorEventoCaptaZap(conversa, detail);

        if (!proximaNormalizada) return limpa;

        return aplicarResumoFollowupNaConversaCaptaZap(
          limpa,
          [proximaNormalizada],
          followupRegrasAtivas,
        );
      };

      setConversasWhatsApp((atuais) => (atuais || []).map(limparConversa));
      setConversasCriadasLocal((atuais) => (atuais || []).map(limparConversa));
      setConversaSelecionada((atual) => limparConversa(atual));

      // Não recarrega o CRM inteiro aqui. O estado local remove a etapa antiga
      // e, quando o backend devolver proxima_etapa, já injeta a próxima no
      // índice global para aparecer como contexto em Contatos/Busca.
    };

    window.addEventListener("captazap:followup_executado", onFollowupAtualizar);
    window.addEventListener("captazap:followup_atualizar", onFollowupAtualizar);
    window.addEventListener("captazap:followup_cancelado", onFollowupAtualizar);

    return () => {
      window.removeEventListener(
        "captazap:followup_executado",
        onFollowupAtualizar,
      );
      window.removeEventListener(
        "captazap:followup_atualizar",
        onFollowupAtualizar,
      );
      window.removeEventListener(
        "captazap:followup_cancelado",
        onFollowupAtualizar,
      );
    };
  }, [empresaId, followupRegrasAtivas, usuarioIdLogado, usuarioNomeLogado]);

  useEffect(() => {
    const liberar = () => liberarSomCaptaZap();

    window.addEventListener("click", liberar, { once: true });
    window.addEventListener("keydown", liberar, { once: true });
    window.addEventListener("touchstart", liberar, { once: true });

    return () => {
      window.removeEventListener("click", liberar);
      window.removeEventListener("keydown", liberar);
      window.removeEventListener("touchstart", liberar);
    };
  }, []);

  const handleAlternarNotificacoesChrome = useCallback(async () => {
    if (!suportaNotificacoesChromeCaptaZap()) {
      window.alert(
        "Este navegador não oferece notificações nativas. Abra o CaptaZap pelo Google Chrome em uma conexão segura (https).",
      );
      return;
    }

    let permissao = getPermissaoNotificacoesChromeCaptaZap();

    if (notificacoesChromeAtivas && permissao === "granted") {
      salvarPreferenciaNotificacoesChromeCaptaZap({
        empresaId,
        usuarioId: usuarioIdLogado,
        ativo: false,
      });
      setNotificacoesChromeAtivas(false);
      return;
    }

    if (permissao === "default") {
      try {
        permissao = await window.Notification.requestPermission();
      } catch (error) {
        console.warn(
          "CaptaZap | Chrome não permitiu solicitar notificações:",
          error,
        );
        permissao = getPermissaoNotificacoesChromeCaptaZap();
      }
    }

    setPermissaoNotificacoesChrome(permissao);

    if (permissao !== "granted") {
      salvarPreferenciaNotificacoesChromeCaptaZap({
        empresaId,
        usuarioId: usuarioIdLogado,
        ativo: false,
      });
      setNotificacoesChromeAtivas(false);

      window.alert(
        permissao === "denied"
          ? "Os alertas estão bloqueados pelo Chrome. Clique no cadeado ao lado do endereço do CaptaZap, abra Configurações do site e permita Notificações."
          : "A permissão de notificações não foi concedida.",
      );
      return;
    }

    salvarPreferenciaNotificacoesChromeCaptaZap({
      empresaId,
      usuarioId: usuarioIdLogado,
      ativo: true,
    });
    setNotificacoesChromeAtivas(true);

    try {
      const teste = new window.Notification("CaptaZap · Alertas ativados", {
        body: "Teste concluído. Você receberá um aviso quando um paciente enviar uma nova mensagem.",
        icon: LOGO_CAPTAZAP_ICON,
        tag: `captazap-teste-${empresaId || "empresa"}`,
        renotify: true,
      });

      teste.onclick = () => {
        try {
          window.focus();
        } catch {
          // O Chrome pode bloquear focus em alguns ambientes.
        }
        teste.close();
      };

      window.setTimeout(() => teste.close(), 8000);
    } catch (error) {
      console.warn(
        "CaptaZap | não foi possível mostrar a notificação de teste:",
        error,
      );
    }
  }, [empresaId, notificacoesChromeAtivas, usuarioIdLogado]);

  const removerNotificacaoNovaMensagem = useCallback((id) => {
    if (!id) return;

    const timer = notificacoesNovaMensagemTimersRef.current.get(id);

    if (timer) {
      window.clearTimeout(timer);
      notificacoesNovaMensagemTimersRef.current.delete(id);
    }

    setNotificacoesNovaMensagem((atuais) =>
      (atuais || []).filter((item) => item.id !== id),
    );
  }, []);

  const mostrarNotificacaoNovaMensagem = useCallback(
    (payload = {}, { conversa = null } = {}) => {
      if (typeof window === "undefined") return;

      const chave = getChaveNotificacaoMensagemCaptaZap(payload);
      const agora = Date.now();
      const dedupe = notificacoesNovaMensagemDedupeRef.current;

      for (const [key, value] of dedupe.entries()) {
        if (agora - value > CAPTAZAP_TOAST_DEDUPE_MS) {
          dedupe.delete(key);
        }
      }

      if (chave && dedupe.has(chave)) return;
      if (chave) dedupe.set(chave, agora);

      const telefone =
        payload.telefone ||
        payload.phone ||
        payload.numero ||
        conversa?.telefone ||
        conversa?.numero ||
        "";
      const leadId =
        payload.lead_id ||
        payload.leadId ||
        conversa?.lead_id ||
        conversa?.leadId ||
        null;
      const chamadoId =
        payload.chamado_id ||
        payload.id_chamado ||
        payload.protocolo_id ||
        conversa?.chamado_id ||
        conversa?.id_chamado ||
        conversa?.protocolo_id ||
        null;
      const zapiInstanceId =
        getConexaoInstanceIdConversaCaptaZap(payload) ||
        getConexaoInstanceIdConversaCaptaZap(conversa) ||
        "";
      const nome =
        payload.nome ||
        payload.nome_lead ||
        payload.contato_nome ||
        conversa?.nome ||
        conversa?.lead?.nome_lead ||
        conversa?.lead?.nome ||
        formatTelefoneCurtoCaptaZap(telefone) ||
        "Novo contato";
      const texto = limitarTextoNotificacaoCaptaZap(
        payload.texto ||
          payload.mensagem ||
          payload.message ||
          payload.legenda ||
          getPreviewMensagemRealtime(payload),
      );
      const responsavelNome =
        payload.responsavel_nome ||
        payload.captador_nome ||
        conversa?.responsavel_nome ||
        conversa?.captador_nome ||
        "";
      const emFila = !getResponsavelIdSomCaptaZap({ ...conversa, ...payload });

      const id = `toast-msg-${agora}-${notificacoesNovaMensagemSeqRef.current++}`;
      const notificacao = {
        id,
        chave,
        nome,
        texto,
        telefone,
        lead_id: leadId,
        leadId,
        chamado_id: chamadoId,
        chamadoId,
        zapi_instance_id: zapiInstanceId || null,
        zapiInstanceId: zapiInstanceId || null,
        responsavel_nome: responsavelNome,
        emFila,
        criado_em:
          payload.datahora ||
          payload.created_at ||
          payload.timestamp ||
          new Date().toISOString(),
      };

      tocarSomToastNovaMensagemCaptaZap();

      setNotificacoesNovaMensagem((atuais) =>
        [notificacao, ...(atuais || [])].slice(0, CAPTAZAP_TOAST_MAX_VISIVEIS),
      );

      const timer = window.setTimeout(() => {
        notificacoesNovaMensagemTimersRef.current.delete(id);
        setNotificacoesNovaMensagem((atuais) =>
          (atuais || []).filter((item) => item.id !== id),
        );
      }, CAPTAZAP_TOAST_NOVA_MENSAGEM_MS);

      notificacoesNovaMensagemTimersRef.current.set(id, timer);
    },
    [],
  );

  const mostrarNotificacaoChrome = useCallback(
    (payload = {}, { conversa = null } = {}) => {
      if (
        !notificacoesChromeAtivas ||
        permissaoNotificacoesChrome !== "granted" ||
        !suportaNotificacoesChromeCaptaZap()
      ) {
        return;
      }

      const telefone =
        payload.telefone ||
        payload.phone ||
        payload.numero ||
        conversa?.telefone ||
        conversa?.numero ||
        "";
      const leadId =
        payload.lead_id ||
        payload.leadId ||
        conversa?.lead_id ||
        conversa?.leadId ||
        null;
      const chamadoId =
        payload.chamado_id ||
        payload.id_chamado ||
        payload.protocolo_id ||
        conversa?.chamado_id ||
        conversa?.id_chamado ||
        conversa?.protocolo_id ||
        null;
      const zapiInstanceId =
        getConexaoInstanceIdConversaCaptaZap(payload) ||
        getConexaoInstanceIdConversaCaptaZap(conversa) ||
        "";
      const nome =
        payload.nome ||
        payload.nome_lead ||
        payload.contato_nome ||
        conversa?.nome ||
        conversa?.lead?.nome_lead ||
        conversa?.lead?.nome ||
        formatTelefoneCurtoCaptaZap(telefone) ||
        "Novo contato";
      const texto = limitarTextoNotificacaoCaptaZap(
        payload.texto ||
          payload.mensagem ||
          payload.message ||
          payload.legenda ||
          getPreviewMensagemRealtime(payload),
        CAPTAZAP_CHROME_NOTIFICACAO_MAX_TEXTO,
      );
      const responsavelNome =
        payload.responsavel_nome ||
        payload.captador_nome ||
        conversa?.responsavel_nome ||
        conversa?.captador_nome ||
        "";
      const emFila = !getResponsavelIdSomCaptaZap({ ...conversa, ...payload });
      const chave =
        getChaveNotificacaoMensagemCaptaZap(payload) ||
        `${normalizarTelefone(telefone)}:${texto.slice(0, 80)}:${Date.now()}`;

      const notificacao = {
        id: `chrome-msg-${Date.now()}`,
        chave,
        nome,
        texto,
        telefone,
        lead_id: leadId,
        leadId,
        chamado_id: chamadoId,
        chamadoId,
        zapi_instance_id: zapiInstanceId || null,
        zapiInstanceId: zapiInstanceId || null,
        responsavel_nome: responsavelNome,
        emFila,
        criado_em:
          payload.datahora ||
          payload.created_at ||
          payload.timestamp ||
          new Date().toISOString(),
      };

      const contexto = emFila
        ? "Nova mensagem na fila"
        : responsavelNome
          ? `Responsável: ${responsavelNome}`
          : "Nova mensagem recebida";
      const corpo = [contexto, texto || "O paciente enviou uma mensagem."]
        .filter(Boolean)
        .join("\n");

      try {
        const alerta = new window.Notification(`CaptaZap · ${nome}`, {
          body: corpo,
          icon: LOGO_CAPTAZAP_ICON,
          tag: `captazap-${empresaId || "empresa"}-${chave}`,
          renotify: true,
        });

        alerta.onclick = () => {
          try {
            window.focus();
          } catch {
            // O Chrome pode bloquear focus em alguns ambientes.
          }

          abrirNotificacaoChromeRef.current?.(notificacao);
          alerta.close();
        };

        window.setTimeout(
          () => alerta.close(),
          CAPTAZAP_CHROME_NOTIFICACAO_DURACAO_MS,
        );
      } catch (error) {
        console.warn(
          "CaptaZap | erro ao exibir notificação nativa do Chrome:",
          error,
        );
      }
    },
    [empresaId, notificacoesChromeAtivas, permissaoNotificacoesChrome],
  );

  useEffect(() => {
    return () => {
      notificacoesNovaMensagemTimersRef.current.forEach((timer) => {
        window.clearTimeout(timer);
      });
      notificacoesNovaMensagemTimersRef.current.clear();
      notificacoesNovaMensagemDedupeRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!empresaId || !conversasWhatsApp.length) return;

    let cancelado = false;

    async function carregarAvataresContatos() {
      const jaConsultados = avataresConsultadosAutoRef.current;
      if (jaConsultados.size >= CAPTAZAP_AVATARES_MAX_AUTO_POR_EMPRESA) return;

      const candidatos = conversasWhatsApp
        .filter((conversa) => !getAvatarConversa(conversa))
        .map((conversa) => {
          const instagram = isInstagramConversaCaptaZap(conversa);
          const externalUserId = instagram
            ? getInstagramExternalUserIdConversaCaptaZap(conversa)
            : "";
          const telefone = conversa.numero || conversa.telefone || "";
          const chaveIdentidade = instagram
            ? `instagram:${externalUserId}`
            : `whatsapp:${normalizarTelefone(telefone) || telefone}`;

          return {
            id: conversa.id || conversa.conversa_id,
            telefone,
            instagram,
            externalUserId,
            chaveIdentidade,
          };
        })
        .filter((item) => {
          if (!item.id) return false;
          if (item.instagram && !item.externalUserId) return false;
          if (!item.instagram && !item.telefone) return false;
          const chave = `${empresaId}:${item.chaveIdentidade}`;
          return !jaConsultados.has(chave);
        })
        .slice(
          0,
          Math.min(
            CAPTAZAP_AVATARES_POR_CICLO,
            CAPTAZAP_AVATARES_MAX_AUTO_POR_EMPRESA - jaConsultados.size,
          ),
        );

      for (const item of candidatos) {
        jaConsultados.add(`${empresaId}:${item.chaveIdentidade}`);
      }

      if (!candidatos.length) return;

      // WhatsApp e Instagram têm provedores de identidade separados.
      // O ramo WhatsApp permanece exatamente na rota antiga; Instagram nunca
      // passa por normalização de telefone nem pela Z-API.
      const resultados = [];
      for (
        let inicio = 0;
        inicio < candidatos.length;
        inicio += CAPTAZAP_AVATARES_CONCORRENCIA
      ) {
        const lote = candidatos.slice(
          inicio,
          inicio + CAPTAZAP_AVATARES_CONCORRENCIA,
        );
        const loteResultados = await Promise.all(
          lote.map(async (item) => {
            if (item.instagram) {
              const perfil = await buscarPerfilInstagramCaptaZap({
                empresaId,
                externalUserId: item.externalUserId,
              });

              return {
                ...item,
                profileUrl: perfil?.profileUrl || "",
                instagramName: perfil?.name || "",
                instagramUsername: perfil?.username || "",
              };
            }

            const profileUrl = await buscarAvatarContatoCaptaZap({
              empresaId,
              telefone: item.telefone,
            });

            return {
              ...item,
              profileUrl,
            };
          }),
        );

        resultados.push(...loteResultados);
        if (cancelado) return;
      }

      if (cancelado) return;

      const perfilMap = new Map(
        resultados.map((item) => [String(item.id), item]),
      );

      if (!perfilMap.size) return;

      const aplicarAvatar = (conversa) => {
        const id = String(conversa.id || conversa.conversa_id);
        const perfil = perfilMap.get(id);
        if (!perfil) return conversa;

        const profileUrl = perfil.profileUrl || "";
        const instagram = Boolean(perfil.instagram);
        const nomeAtual = String(conversa?.nome || "").trim();
        const nomeGenericoInstagram =
          instagram &&
          (!nomeAtual ||
            /^instagram\s*-\s*\d+$/i.test(nomeAtual) ||
            nomeAtual === perfil.externalUserId ||
            nomeAtual === `ig:${perfil.externalUserId}`);

        const nomeInstagram =
          perfil.instagramName ||
          (perfil.instagramUsername ? `@${perfil.instagramUsername}` : "");

        return {
          ...conversa,
          nome:
            nomeGenericoInstagram && nomeInstagram
              ? nomeInstagram
              : conversa.nome,
          nome_instagram: instagram ? perfil.instagramName || "" : conversa.nome_instagram,
          instagram_username: instagram
            ? perfil.instagramUsername || ""
            : conversa.instagram_username,
          username: instagram
            ? perfil.instagramUsername || conversa.username
            : conversa.username,
          foto: profileUrl || conversa.foto,
          avatar: profileUrl || conversa.avatar,
          avatar_url: profileUrl || conversa.avatar_url,
          profileUrl: profileUrl || conversa.profileUrl,
          lead: conversa.lead
            ? {
                ...conversa.lead,
                foto: conversa.lead.foto || profileUrl,
                avatar_url: conversa.lead.avatar_url || profileUrl,
                profileUrl: conversa.lead.profileUrl || profileUrl,
              }
            : conversa.lead,
        };
      };

      setConversasWhatsApp((atuais) => atuais.map(aplicarAvatar));
      setConversaSelecionada((atual) => (atual ? aplicarAvatar(atual) : atual));
    }

    // Foto de perfil é decorativa. Nunca deve concorrer com o carregamento das
    // mensagens. Busca poucas fotos, com baixa concorrência, só depois que a
    // lista já foi entregue ao operador.
    avatarAutoTimerRef.current = window.setTimeout(() => {
      avatarAutoTimerRef.current = null;
      carregarAvataresContatos();
    }, 1200);

    return () => {
      cancelado = true;
      if (avatarAutoTimerRef.current) {
        window.clearTimeout(avatarAutoTimerRef.current);
        avatarAutoTimerRef.current = null;
      }
    };
  }, [empresaId, conversasWhatsApp]);

  const recarregarMensagensDaConversaAberta = useCallback(
    async (conversa) => {
      if (!empresaId || !conversa) return;

      const leadId = conversa.lead_id || conversa.leadId || conversa.lead?.id;
      const telefone = conversa.numero || conversa.telefone || "";
      const zapiInstanceId = getConexaoInstanceIdConversaCaptaZap(conversa);
      const telefoneHistorico = getTelefoneHistoricoWhatsAppCaptaZap(conversa);
      const abriuPelaAbaContatos =
        deveCarregarHistoricoPorTelefoneCaptaZap(conversa);

      if (!leadId && !telefone) return;

      const chaveRecarregamento = [
        empresaId,
        leadId || "",
        zapiInstanceId,
        normalizarTelefone(telefoneHistorico || telefone) ||
          telefoneHistorico ||
          telefone,
      ].join(":");

      // SSE, envio e polling podem solicitar a mesma conversa quase ao mesmo
      // tempo. Se já existe uma leitura em andamento, NÃO descartamos o novo
      // sinal: marcamos uma reconciliação pendente para rodar logo depois.
      if (
        recarregamentosMensagensEmAndamentoRef.current.has(chaveRecarregamento)
      ) {
        recarregamentosMensagensPendentesRef.current.add(chaveRecarregamento);
        return;
      }

      recarregamentosMensagensEmAndamentoRef.current.add(chaveRecarregamento);

      try {
        const params = new URLSearchParams();
        params.set("empresa_id", String(empresaId));

        // Regra CaptaZap:
        // - histórico do WhatsApp sempre pertence ao TELEFONE;
        // - lead/contexto serve para CRM, agendamento, tarefas e fechamento.
        // Quando a origem é a aba Contatos, não enviamos lead_id para a rota
        // de mensagens para evitar que o backend filtre por uma lead específica
        // e esconda conversas antigas do mesmo WhatsApp.
        if (leadId && !abriuPelaAbaContatos)
          params.set("lead_id", String(leadId));
        if (telefoneHistorico || telefone) {
          params.set("telefone", telefoneHistorico || telefone);
        }
        if (zapiInstanceId) params.set("zapi_instance_id", zapiInstanceId);

        let data = await requestJson(
          `/whatsapp/captazap/mensagens-v2?${params.toString()}&limit=${CAPTAZAP_MENSAGENS_PAGE_SIZE}`,
        );

        let mensagens = extrairMensagensRespostaCaptaZap(data);

        // Histórico de ligações persistido: entra cronologicamente no mesmo chat.
        try {
          const paramsLigacoes = new URLSearchParams({
            empresa_id: String(empresaId),
            telefone: String(telefoneHistorico || telefone || ""),
          });
          if (zapiInstanceId) {
            paramsLigacoes.set("zapi_instance_id", zapiInstanceId);
          }

          const historicoLigacoes = await requestJson(
            `/whatsapp/calls/history?${paramsLigacoes.toString()}`,
          );

          const eventosLigacao = (Array.isArray(historicoLigacoes?.calls)
            ? historicoLigacoes.calls
            : []
          ).map(ligacaoCaptaZapParaMensagem);

          mensagens = ordenarMensagensConversaCaptaZap(
            [...mensagens, ...eventosLigacao],
            500,
          );
        } catch {
          // Mensagens continuam funcionando mesmo se o histórico de chamadas falhar.
        }

        if (abriuPelaAbaContatos && mensagens.length === 0 && telefone) {
          const paramsFallback = new URLSearchParams();
          paramsFallback.set("empresa_id", String(empresaId));
          paramsFallback.set("telefone", telefone);
          if (zapiInstanceId) {
            paramsFallback.set("zapi_instance_id", zapiInstanceId);
          }

          data = await requestJson(
            `/whatsapp/captazap/mensagens-v2?${paramsFallback.toString()}&limit=${CAPTAZAP_MENSAGENS_PAGE_SIZE}`,
          );

          mensagens = extrairMensagensRespostaCaptaZap(data);
        }
        const contextoLeads = montarContextoLeadsConversa(data);

        setConversaSelecionada((atual) => {
          if (!atual) return atual;

          const atualId = atual.id || atual.conversa_id;
          const conversaId = conversa.id || conversa.conversa_id;

          if (String(atualId) !== String(conversaId)) return atual;

          const leadContextoAtual =
            atual.lead_contexto_id ||
            atual.leadContextoId ||
            atual.contexto_lead_id ||
            atual.contextoLeadId ||
            atual.lead_id ||
            atual.leadId ||
            atual.lead?.id ||
            null;
          const leadAtualizada =
            getLeadVinculadaPorId(
              {
                leads_vinculadas:
                  contextoLeads.leads_vinculadas || atual.leads_vinculadas,
              },
              leadContextoAtual,
            ) || atual.lead;

          return normalizarConversaCaptazap({
            ...atual,
            ...contextoLeads,
            lead_contexto_id:
              leadContextoAtual || contextoLeads.lead_contexto_id,
            leadContextoId: leadContextoAtual || contextoLeads.leadContextoId,
            contexto_lead_id:
              leadContextoAtual || contextoLeads.lead_contexto_id,
            contextoLeadId: leadContextoAtual || contextoLeads.leadContextoId,
            lead_id: leadContextoAtual || atual.lead_id,
            leadId: leadContextoAtual || atual.leadId,
            lead: leadAtualizada,
            mensagens,
            naoLidas: 0,
            _mensagens_has_more: Boolean(
              data?.paginacao?.has_more ?? data?.paginacao?.hasMore ?? false,
            ),
            _mensagens_cursor:
              data?.paginacao?.previous_cursor ||
              data?.paginacao?.previousCursor ||
              null,
          });
        });

        await requestJson("/whatsapp/captazap/marcar-lidas", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            empresa_id: empresaId,
            lead_id: leadId || null,
            telefone: telefone || null,
            zapi_instance_id: zapiInstanceId || null,
          }),
        }).catch(() => null);

        setConversasWhatsApp((atuais) =>
          atuais.map((item) =>
            isMesmaConversaCaptazap(item, {
              lead_id: leadId,
              telefone,
              zapi_instance_id: zapiInstanceId || null,
            })
              ? { ...item, naoLidas: 0 }
              : item,
          ),
        );
      } catch (err) {
        console.error("❌ Erro ao recarregar conversa aberta:", err);
      } finally {
        recarregamentosMensagensEmAndamentoRef.current.delete(
          chaveRecarregamento,
        );

        const precisaReconciliarNovamente =
          recarregamentosMensagensPendentesRef.current.delete(
            chaveRecarregamento,
          );

        if (precisaReconciliarNovamente) {
          const conversaAtual = conversaSelecionadaRef.current;

          if (
            conversaAtual &&
            isMesmaConversaCaptazap(conversaAtual, conversa)
          ) {
            window.setTimeout(() => {
              recarregarMensagensDaConversaAberta(conversaAtual).catch(
                () => null,
              );
            }, 0);
          }
        }
      }
    },
    [empresaId],
  );


  const carregarMensagensAnterioresConversa = useCallback(async () => {
    const conversa = conversaSelecionadaRef.current;
    if (!empresaId || !conversa) return [];

    const cursor = conversa?._mensagens_cursor || null;
    const hasMore = Boolean(conversa?._mensagens_has_more);

    if (!hasMore || !cursor?.cursor_em || !cursor?.cursor_id) {
      return [];
    }

    if (conversa?._carregando_mensagens_anteriores) return [];

    const telefone =
      getTelefoneHistoricoWhatsAppCaptaZap(conversa) ||
      conversa?.telefone ||
      conversa?.numero ||
      "";
    const zapiInstanceId = getConexaoInstanceIdConversaCaptaZap(conversa);
    const leadId =
      conversa?._captazap_chamado_lead_id ||
      conversa?.lead_contexto_id ||
      conversa?.leadContextoId ||
      conversa?.lead_id ||
      conversa?.leadId ||
      conversa?.lead?.id ||
      null;

    if (!telefone && !leadId) return [];

    setConversaSelecionada((atual) =>
      atual
        ? {
            ...atual,
            _carregando_mensagens_anteriores: true,
          }
        : atual,
    );

    try {
      const params = new URLSearchParams({
        empresa_id: String(empresaId),
        limit: String(CAPTAZAP_MENSAGENS_PAGE_SIZE),
        cursor_em: String(cursor.cursor_em),
        cursor_id: String(cursor.cursor_id),
      });

      if (telefone) params.set("telefone", String(telefone));
      if (leadId) params.set("lead_id", String(leadId));
      if (zapiInstanceId) params.set("zapi_instance_id", zapiInstanceId);

      const data = await requestJson(
        `/whatsapp/captazap/mensagens-v2?${params.toString()}`,
      );
      const antigas = extrairMensagensRespostaCaptaZap(data);
      const paginacao = data?.paginacao || {};

      setConversaSelecionada((atual) => {
        if (!atual || !isMesmaConversaCaptazap(atual, conversa)) return atual;

        return normalizarConversaCaptazap({
          ...atual,
          ...montarContextoLeadsConversa(data || {}),
          mensagens: ordenarMensagensConversaCaptaZap(
            [
              ...antigas,
              ...(Array.isArray(atual.mensagens) ? atual.mensagens : []),
            ],
            500,
          ),
          _mensagens_has_more: Boolean(
            paginacao.has_more ?? paginacao.hasMore ?? false,
          ),
          _mensagens_cursor:
            paginacao.previous_cursor ||
            paginacao.previousCursor ||
            null,
          _carregando_mensagens_anteriores: false,
        });
      });

      return antigas;
    } catch (error) {
      console.warn("CaptaZap | falha ao carregar mensagens anteriores:", error);
      setConversaSelecionada((atual) =>
        atual
          ? {
              ...atual,
              _carregando_mensagens_anteriores: false,
            }
          : atual,
      );
      return [];
    }
  }, [empresaId]);

  const recarregarConversasCaptazapRealtime = useCallback(async () => {
    if (!empresaId) return;

    const abaAtual = abaQueueListRef.current || "minhas";
    if (!CAPTAZAP_ABAS_OPERACIONAIS_PAGINADAS.has(abaAtual)) {
      // Mesmo em Contatos/Follow-up, os badges operacionais precisam refletir
      // a mudança sem esperar o polling de segurança.
      carregarContadoresOperacaoV2({ force: true }).catch(() => null);
      return;
    }

    // A reconciliação nunca carrega toda a operação. Ela atualiza somente a
    // primeira página da aba visível; páginas antigas permanecem acessíveis pelo
    // scroll e a busca continua sendo remota.
    if (recarregamentoConversasEmAndamentoRef.current) {
      recarregamentoConversasPendenteRef.current = true;
      return;
    }

    recarregamentoConversasEmAndamentoRef.current = true;

    try {
      await carregarConversasOperacionaisV2({
        aba: abaAtual,
        reset: true,
        silencioso: true,
      });
      // Realtime: a lista visível acabou de ser reconciliada pela fonte oficial.
      // Atualiza também os badges imediatamente; não deixa o contador esperar
      // o throttle normal de 10s nem o polling de segurança.
      carregarContadoresOperacaoV2({ force: true }).catch(() => null);
    } catch (error) {
      console.warn(
        "CaptaZap | não foi possível reconciliar a página operacional:",
        error,
      );
    } finally {
      recarregamentoConversasEmAndamentoRef.current = false;

      if (recarregamentoConversasPendenteRef.current) {
        recarregamentoConversasPendenteRef.current = false;
        window.setTimeout(() => {
          recarregarConversasCaptazapRealtime().catch(() => null);
        }, 0);
      }
    }
  }, [
    empresaId,
    carregarConversasOperacionaisV2,
    carregarContadoresOperacaoV2,
  ]);

  const agendarRecarregarConversasCaptazapRealtime = useCallback(() => {
    if (!empresaId) return;

    if (refreshConversasRealtimeTimerRef.current) {
      window.clearTimeout(refreshConversasRealtimeTimerRef.current);
    }

    refreshConversasRealtimeTimerRef.current = window.setTimeout(() => {
      refreshConversasRealtimeTimerRef.current = null;
      recarregarConversasCaptazapRealtime();
    }, 650);
  }, [empresaId, recarregarConversasCaptazapRealtime]);

  useEffect(() => {
    if (!empresaId) return undefined;

    const base = API_BASE || "";
    const url = `${base}/whatsapp/realtime/subscribe?empresa_id=${empresaId}`;
    const eventSource = new EventSource(url);

    const parsePayload = (event) => {
      try {
        return JSON.parse(event?.data || "{}");
      } catch {
        return {};
      }
    };

    // `whatsapp:media_ready` NÃO é uma nova mensagem. Ele só informa que a mídia
    // da mensagem já existente terminou de ser localizada/salva. Tratar esse
    // evento como `whatsapp:new_message` fazia o normalizador inventar
    // `direcao: enviada` quando o payload vinha sem direção/autoria, invertendo
    // figurinhas recebidas para o lado direito e também mexendo indevidamente
    // na classificação do QueueList.
    const aplicarMidiaProntaRealtime = (payloadBruto = {}) => {
      const payload = payloadBruto?.mensagem || payloadBruto || {};
      const mensagemId = payload?.id || payload?.whatsapp_id || null;
      const mensagemMsgid =
        payload?.msgid ||
        payload?.messageId ||
        payload?.message_id ||
        null;

      if (!mensagemId && !mensagemMsgid) return;

      const mesmaMensagem = (mensagem = {}) => {
        const idAtual = mensagem?.id || mensagem?.whatsapp_id || null;
        const msgidAtual =
          mensagem?.msgid ||
          mensagem?.messageId ||
          mensagem?.message_id ||
          null;

        if (
          mensagemId &&
          idAtual &&
          String(mensagemId) === String(idAtual)
        ) {
          return true;
        }

        return Boolean(
          mensagemMsgid &&
            msgidAtual &&
            String(mensagemMsgid) === String(msgidAtual),
        );
      };

      const patchMidia = (mensagem = {}) => {
        if (!mesmaMensagem(mensagem)) return mensagem;

        return {
          ...mensagem,
          midia_url:
            payload.midia_url ??
            payload.media_url ??
            payload.mediaUrl ??
            mensagem.midia_url,
          media_url:
            payload.media_url ??
            payload.midia_url ??
            payload.mediaUrl ??
            mensagem.media_url,
          mediaUrl:
            payload.mediaUrl ??
            payload.midia_url ??
            payload.media_url ??
            mensagem.mediaUrl,
          mime_type:
            payload.mime_type ??
            payload.mimeType ??
            mensagem.mime_type,
          tipo_midia:
            payload.tipo_midia ??
            payload.tipoMidia ??
            mensagem.tipo_midia,
          tamanho_bytes:
            payload.tamanho_bytes ??
            payload.tamanhoBytes ??
            mensagem.tamanho_bytes,
          duracao_ms:
            payload.duracao_ms ??
            payload.duracaoMs ??
            mensagem.duracao_ms,
        };
      };

      const aplicarEmConversa = (conversa) => {
        if (!conversa || !Array.isArray(conversa.mensagens)) return conversa;

        let alterou = false;
        const mensagens = conversa.mensagens.map((mensagem) => {
          const atualizada = patchMidia(mensagem);
          if (atualizada !== mensagem) alterou = true;
          return atualizada;
        });

        if (!alterou) return conversa;

        // Não altera direção, autoria, responsável, aba, não-lidas nem preview.
        return {
          ...conversa,
          mensagens,
        };
      };

      setConversasWhatsApp((atuais) =>
        (Array.isArray(atuais) ? atuais : []).map(aplicarEmConversa),
      );
      setConversasCriadasLocal((atuais) =>
        (Array.isArray(atuais) ? atuais : []).map(aplicarEmConversa),
      );
      setConversaSelecionada((atual) => {
        const atualizado = atual ? aplicarEmConversa(atual) : atual;
        conversaSelecionadaRef.current = atualizado;
        return atualizado;
      });
    };

    const processarEventoMensagem = async (
      event,
      { tocarSom = false, incrementarNaoLidas = false } = {},
    ) => {
      const payload = parsePayload(event);
      const conversaAtual = conversaSelecionadaRef.current;
      const zapiInstancePayload = getConexaoInstanceIdConversaCaptaZap(payload);

      const mesmaConversaAberta =
        conversaAtual && isMesmaConversaCaptazap(conversaAtual, payload);

      const mensagemRecebida = deveTocarSomMensagem(payload);
      const conversaEncontradaParaSom = encontrarConversaSomCaptaZap(payload, [
        ...(conversasWhatsAppRef.current || []),
        ...(conversasCriadasLocalRef.current || []),
      ]);

      // Em um primeiro contato o item ainda não existe no estado local quando
      // o SSE chega. Sem este fallback, a mensagem entra na Fila, mas nenhum
      // operador recebe som, toast ou alerta do Chrome. Uma conversa sem
      // responsável é operacionalmente fila compartilhada.
      const conversaSom =
        conversaEncontradaParaSom ||
        (mensagemRecebida && !getResponsavelIdSomCaptaZap(payload)
          ? {
              ...payload,
              aba_captazap: "fila",
              em_fila: true,
              fila_atendimento: true,
              aguardando_atendimento: true,
              _fila_global: true,
              _fila_equipe: true,
            }
          : null);

      const podeTocarSom = Boolean(
        mensagemRecebida &&
        deveTocarSomParaUsuarioCaptaZap({
          payload,
          conversa: conversaSom,
          usuarioId: usuarioIdLogadoRef.current,
          usuarioNome: usuarioNomeLogadoRef.current,
        }),
      );
      const eventoDuplicado =
        tocarSom && podeTocarSom ? somMensagemJaProcessado(payload) : false;
      const deveIncrementarNaoLidas =
        incrementarNaoLidas && mensagemRecebida && !eventoDuplicado;

      if (tocarSom && podeTocarSom && !eventoDuplicado) {
        tocarSomMensagemRecebida();
      }

      const conversaAbertaEmFoco = Boolean(
        mesmaConversaAberta && janelaCaptaZapEstaEmFoco(),
      );

      if (
        mensagemRecebida &&
        podeTocarSom &&
        !eventoDuplicado &&
        !mesmaConversaAberta
      ) {
        mostrarNotificacaoNovaMensagem(payload, { conversa: conversaSom });
      }

      // A notificação do Chrome também deve aparecer quando a conversa está
      // aberta, mas o CaptaZap está em segundo plano ou minimizado.
      if (
        mensagemRecebida &&
        podeTocarSom &&
        !eventoDuplicado &&
        !conversaAbertaEmFoco
      ) {
        mostrarNotificacaoChrome(payload, { conversa: conversaSom });
      }

      // Importante:
      // Não chamar carregarDadosCRM() aqui.
      // Ele recarrega todos os cards e causa o efeito visual de "piscar".
      // Atualizamos apenas a conversa impactada pelo evento realtime.
      setConversasWhatsApp((atuais) => {
        const telefonePayload =
          payload.telefone || payload.phone || payload.numero || "";
        const leadPayload = payload.lead_id || payload.leadId || null;
        const textoPayloadBruto =
          payload.texto ||
          payload.mensagem ||
          payload.message ||
          payload.legenda ||
          getPreviewMensagemRealtime(payload);
        const mensagemRecuperadaDraMalu = isMensagemRecuperadoDraMaluCaptaZap({
          ...payload,
          texto: textoPayloadBruto,
        });
        const textoPayload = mensagemRecuperadaDraMalu
          ? CAPTAZAP_DRA_MALU_RECUPERADO_TEXTO_CHAT
          : textoPayloadBruto;
        const previewPayload = mensagemRecuperadaDraMalu
          ? CAPTAZAP_DRA_MALU_RECUPERADO_BADGE
          : textoPayload;

        if (!telefonePayload && !leadPayload) return atuais;

        const direcaoPayload = String(
          payload.direcao || payload.direction || "",
        ).toLowerCase();

        const mensagemRealtime = {
          ...normalizarMensagemCaptazap(payload),
          id:
            payload.id ||
            payload.msgid ||
            payload.messageId ||
            `rt-msg-${Date.now()}`,
          texto: textoPayload || "",
          mensagem: textoPayload || "",
          direcao:
            direcaoPayload || (mensagemRecebida ? "recebida" : "enviada"),
          autor: mensagemRecebida ? "cliente" : "operador",
          de: mensagemRecebida ? "cliente" : "operador",
          fromMe: !mensagemRecebida,
          datahora:
            payload.datahora ||
            payload.created_at ||
            payload.timestamp ||
            new Date().toISOString(),
        };

        const alvoRealtime = {
          lead_id: leadPayload,
          leadId: leadPayload,
          telefone: telefonePayload,
          numero: telefonePayload,
          zapi_instance_id: zapiInstancePayload || null,
          zapiInstanceId: zapiInstancePayload || null,
          conexao_instance_id: zapiInstancePayload || null,
          conexaoInstanceId: zapiInstancePayload || null,
        };
        const conversaExistente =
          (Array.isArray(atuais) ? atuais : []).find((item) =>
            isMesmaConversaCaptazap(item, alvoRealtime),
          ) || null;

        // Responsável operacional vem de `captazap_chamados`.
        // NÃO usar captador/dono da Lead nem operador autor da mensagem como
        // fallback de responsabilidade. Se o evento de mensagem não trouxer
        // responsável, preservamos o estado já conhecido e a reconciliação
        // autoritativa logo abaixo confirma a fonte oficial.
        const temResponsavelOperacionalPayload = [
          "responsavel_id",
          "usuario_responsavel_id",
          "atendente_id",
        ].some((campo) =>
          Object.prototype.hasOwnProperty.call(payload, campo),
        );

        const responsavelPayload = temResponsavelOperacionalPayload
          ? Number(
              payload.responsavel_id ||
                payload.usuario_responsavel_id ||
                payload.atendente_id ||
                0,
            ) || null
          : Number(conversaExistente?.responsavel_id || 0) || null;

        const temFilaProtecaoPayload = [
          "fila_protecao",
          "filaProtecao",
          "_fila_protecao",
        ].some((campo) =>
          Object.prototype.hasOwnProperty.call(payload, campo),
        );

        const filaProtecaoPayload = temFilaProtecaoPayload
          ? [
              payload.fila_protecao,
              payload.filaProtecao,
              payload._fila_protecao,
            ].some(
              (valor) =>
                valor === true ||
                valor === 1 ||
                String(valor || "").toLowerCase() === "true",
            )
          : Boolean(
              conversaExistente?.fila_protecao === true ||
                conversaExistente?.filaProtecao === true ||
                Number(conversaExistente?.fila_protecao || 0) === 1 ||
                Number(conversaExistente?.filaProtecao || 0) === 1,
            );
        const motivoProtecaoPayload =
          payload.fila_protecao_motivo ||
          payload.filaProtecaoMotivo ||
          null;
        const statusChamadoPayload = String(
          payload.status_chamado ||
            payload.chamado_status ||
            payload.chamada_status ||
            "",
        )
          .trim()
          .toLowerCase();
        const statusChamadoPayloadValido = [
          "aberto",
          "fechado",
          "transferido",
        ].includes(statusChamadoPayload);
        const novaEntradaFila = Boolean(
          mensagemRecebida && (!responsavelPayload || filaProtecaoPayload),
        );

        const payloadConversa = {
          id: `whatsapp-${leadPayload || normalizarTelefone(telefonePayload)}`,
          conversa_id: `whatsapp-${leadPayload || normalizarTelefone(telefonePayload)}`,
          empresa_id: payload.empresa_id || empresaId,
          zapi_instance_id: zapiInstancePayload || null,
          zapiInstanceId: zapiInstancePayload || null,
          conexao_instance_id: zapiInstancePayload || null,
          conexaoInstanceId: zapiInstancePayload || null,
          lead_id: leadPayload,
          leadId: leadPayload,
          nome:
            payload.nome ||
            payload.nome_lead ||
            telefonePayload ||
            "Contato sem nome",
          numero: telefonePayload,
          telefone: telefonePayload,
          canal: payload.canal || "whatsapp",
          status: novaEntradaFila
            ? "aguardando_atendimento"
            : conversaExistente?.status || "em_atendimento",
          status_chamado: statusChamadoPayloadValido
            ? statusChamadoPayload
            : conversaExistente?.status_chamado ||
              conversaExistente?.chamado_status ||
              conversaExistente?.chamada_status ||
              "aberto",
          chamado_status: statusChamadoPayloadValido
            ? statusChamadoPayload
            : conversaExistente?.chamado_status ||
              conversaExistente?.status_chamado ||
              conversaExistente?.chamada_status ||
              "aberto",
          chamada_status: statusChamadoPayloadValido
            ? statusChamadoPayload
            : conversaExistente?.chamada_status ||
              conversaExistente?.status_chamado ||
              conversaExistente?.chamado_status ||
              "aberto",
          aba_captazap: novaEntradaFila
            ? "fila"
            : payload.aba_captazap ||
              conversaExistente?.aba_captazap ||
              undefined,
          em_fila: novaEntradaFila,
          fila_atendimento: novaEntradaFila,
          aguardando_atendimento: novaEntradaFila,
          _fila_global: novaEntradaFila,
          _fila_equipe: novaEntradaFila,
          fila_protecao: filaProtecaoPayload,
          filaProtecao: filaProtecaoPayload,
          fila_protecao_motivo:
            payload.fila_protecao_motivo ||
            payload.filaProtecaoMotivo ||
            null,
          filaProtecaoMotivo:
            payload.filaProtecaoMotivo ||
            payload.fila_protecao_motivo ||
            null,
          fila_protecao_label:
            payload.fila_protecao_label ||
            payload.filaProtecaoLabel ||
            "",
          filaProtecaoLabel:
            payload.filaProtecaoLabel ||
            payload.fila_protecao_label ||
            "",
          responsavel_id: responsavelPayload,
          responsavel_nome:
            payload.responsavel_nome ||
            conversaExistente?.responsavel_nome ||
            "",
          // Captador/dono da Lead é dado comercial e não muda quando o chamado
          // muda de responsável operacional.
          captador_id:
            conversaExistente?.captador_id ||
            payload.captador_id ||
            null,
          captador_nome:
            conversaExistente?.captador_nome ||
            payload.captador_nome ||
            "",
          preview: previewPayload || "Nova mensagem",
          ultima_mensagem: previewPayload || "Nova mensagem",
          ultimaMensagem: previewPayload || "Nova mensagem",
          recuperado_dra_malu: mensagemRecuperadaDraMalu || undefined,
          recuperadoDraMalu: mensagemRecuperadaDraMalu || undefined,
          _recuperado_dra_malu: mensagemRecuperadaDraMalu || undefined,
          badge_recuperacao_dra_malu: mensagemRecuperadaDraMalu
            ? CAPTAZAP_DRA_MALU_RECUPERADO_BADGE
            : undefined,
          direcao: mensagemRealtime.direcao,
          ultima_direcao: mensagemRealtime.direcao,
          ultimaDirecao: mensagemRealtime.direcao,
          ultima_mensagem_id:
            payload.id ||
            payload.whatsapp_id ||
            payload.msgid ||
            payload.messageId ||
            payload.message_id ||
            null,
          ultimaMensagemId:
            payload.id ||
            payload.whatsapp_id ||
            payload.msgid ||
            payload.messageId ||
            payload.message_id ||
            null,
          ultimaMensagemEm: mensagemRealtime.datahora,
          ultima_mensagem_em: mensagemRealtime.datahora,
          datahora: mensagemRealtime.datahora,
          hora: formatHoraMensagemLocal(mensagemRealtime.datahora),
          naoLidas: mesmaConversaAberta ? 0 : deveIncrementarNaoLidas ? 1 : 0,
          conversa_ativa: true,
          sem_conversa: false,
          mensagens: [mensagemRealtime],
          _captazap_real: true,
        };

        let encontrou = false;

        const atualizadas = atuais.map((item) => {
          if (!isMesmaConversaCaptazap(item, payloadConversa)) return item;

          encontrou = true;

          const naoLidasAtuais = Number(item.naoLidas || 0);

          const mensagensAtualizadas = mesclarMensagensConversa(
            item.mensagens || [],
            mensagemRealtime,
          );

          const novaRespostaEmChamadoFechado =
            Boolean(
              mensagemRecebida &&
                filaProtecaoPayload &&
                motivoProtecaoPayload === "retorno_apos_fechamento",
            );
          const responsavelReabertura =
            getResponsavelReaberturaChamadoFechado(item);
          const statusChamadoExistente = getStatusChamadoVisual(item);
          const statusChamadoAtualizado = novaRespostaEmChamadoFechado
            ? "aberto"
            : statusChamadoPayloadValido
              ? statusChamadoPayload
              : statusChamadoExistente;

          return {
            ...item,
            ...payloadConversa,
            nome: item.nome || payloadConversa.nome,
            lead: item.lead || payload.lead || null,
            foto:
              item.foto ||
              payload.profileUrl ||
              payload.avatar_url ||
              payload.foto ||
              "",
            avatar:
              item.avatar ||
              payload.profileUrl ||
              payload.avatar_url ||
              payload.foto ||
              "",
            avatar_url:
              item.avatar_url ||
              payload.profileUrl ||
              payload.avatar_url ||
              payload.foto ||
              "",
            profileUrl:
              item.profileUrl ||
              payload.profileUrl ||
              payload.avatar_url ||
              payload.foto ||
              "",

            // Se o paciente respondeu um chamado fechado, ele reabre e entra
            // imediatamente na Fila de Proteção SEM responsável operacional.
            // O responsável antigo fica somente como referência histórica.
            status: novaRespostaEmChamadoFechado
              ? "aguardando_atendimento"
              : statusChamadoAtualizado === "fechado"
                ? "fechado"
                : payloadConversa.status,
            status_chamado: statusChamadoAtualizado,
            chamado_status: statusChamadoAtualizado,
            chamada_status: statusChamadoAtualizado,

            conversa_ativa: statusChamadoAtualizado === "fechado" ? false : true,
            conversaAtiva: statusChamadoAtualizado === "fechado" ? false : true,
            sem_conversa: statusChamadoAtualizado === "fechado" ? true : false,
            semConversa: statusChamadoAtualizado === "fechado" ? true : false,
            aba_captazap: novaRespostaEmChamadoFechado
              ? "fila"
              : statusChamadoAtualizado === "fechado"
                ? "fechados"
                : payloadConversa.aba_captazap,
            em_fila: novaRespostaEmChamadoFechado
              ? true
              : statusChamadoAtualizado === "fechado"
                ? false
                : payloadConversa.em_fila,
            fila_atendimento: novaRespostaEmChamadoFechado
              ? true
              : statusChamadoAtualizado === "fechado"
                ? false
                : payloadConversa.fila_atendimento,
            aguardando_atendimento: novaRespostaEmChamadoFechado
              ? true
              : statusChamadoAtualizado === "fechado"
                ? false
                : payloadConversa.aguardando_atendimento,
            _fila_global: novaRespostaEmChamadoFechado
              ? true
              : statusChamadoAtualizado === "fechado"
                ? false
                : payloadConversa._fila_global,
            _fila_equipe: novaRespostaEmChamadoFechado
              ? true
              : statusChamadoAtualizado === "fechado"
                ? false
                : payloadConversa._fila_equipe,
            fila_protecao: novaRespostaEmChamadoFechado
              ? true
              : payloadConversa.fila_protecao,
            filaProtecao: novaRespostaEmChamadoFechado
              ? true
              : payloadConversa.filaProtecao,
            fila_protecao_motivo: novaRespostaEmChamadoFechado
              ? "retorno_apos_fechamento"
              : payloadConversa.fila_protecao_motivo,
            filaProtecaoMotivo: novaRespostaEmChamadoFechado
              ? "retorno_apos_fechamento"
              : payloadConversa.filaProtecaoMotivo,
            fila_protecao_label: novaRespostaEmChamadoFechado
              ? "Retorno após fechamento"
              : payloadConversa.fila_protecao_label,
            filaProtecaoLabel: novaRespostaEmChamadoFechado
              ? "Retorno após fechamento"
              : payloadConversa.filaProtecaoLabel,

            cliente_respondeu_apos_fechamento: novaRespostaEmChamadoFechado
              ? true
              : false,
            respondeu_apos_fechamento: novaRespostaEmChamadoFechado
              ? true
              : false,
            nova_mensagem_apos_fechamento: novaRespostaEmChamadoFechado
              ? true
              : false,
            novaRespostaAposFechamento: novaRespostaEmChamadoFechado
              ? true
              : false,
            _fechado_com_resposta: novaRespostaEmChamadoFechado
              ? true
              : false,
            // A reabertura agora é automática no backend; não existe etapa
            // manual de "reabrir" para liberar o atendimento.
            reabertura_pendente: false,
            reaberturaPendente: false,

            responsavel_anterior_id: novaRespostaEmChamadoFechado
              ? responsavelReabertura.id || item.responsavel_id || null
              : item.responsavel_anterior_id || null,
            responsavel_anterior_nome: novaRespostaEmChamadoFechado
              ? responsavelReabertura.nome || item.responsavel_nome || ""
              : item.responsavel_anterior_nome || "",

            // Fila de Proteção preserva o responsável anterior como referência.
            // A flag fila_protecao retira a conversa de Minhas até alguém clicar
            // em Assumir; portanto não precisamos apagar a autoria operacional.
            responsavel_id: novaRespostaEmChamadoFechado
              ? responsavelReabertura.id || item.responsavel_id || responsavelPayload || null
              : item.responsavel_id || responsavelPayload || null,
            responsavel_nome: novaRespostaEmChamadoFechado
              ? responsavelReabertura.nome || item.responsavel_nome || payload.responsavel_nome || ""
              : item.responsavel_nome || payload.responsavel_nome || "",
            // Captador/dono da Lead permanece comercialmente intacto.
            // Assumir, reabrir ou transferir chamado altera somente `responsavel_*`.
            captador_id:
              item.captador_id ||
              payload.captador_id ||
              null,
            captador_nome:
              item.captador_nome ||
              payload.captador_nome ||
              "",

            mensagens: mensagensAtualizadas,
            naoLidas: mesmaConversaAberta
              ? 0
              : deveIncrementarNaoLidas
                ? naoLidasAtuais + 1
                : naoLidasAtuais,
          };
        });

        const listaFinal = encontrou
          ? atualizadas
          : [payloadConversa, ...atuais];

        return ordenarConversasPorUltimaAtividadeCaptaZap(listaFinal);
      });

      // Mensageria V2: evento SSE atualiza a conversa aberta diretamente.
      // Não fazemos novo GET de histórico nem recarregamos a lista inteira.
      if (mesmaConversaAberta) {
        const textoAbertoBruto =
          payload.texto ||
          payload.mensagem ||
          payload.message ||
          payload.legenda ||
          getPreviewMensagemRealtime(payload);
        const recuperadoDraMaluAberto = isMensagemRecuperadoDraMaluCaptaZap({
          ...payload,
          texto: textoAbertoBruto,
        });
        const textoAberto = recuperadoDraMaluAberto
          ? CAPTAZAP_DRA_MALU_RECUPERADO_TEXTO_CHAT
          : textoAbertoBruto;
        const direcaoAberta = String(
          payload.direcao || payload.direction || "",
        ).toLowerCase();

        const mensagemAberta = normalizarMensagemCaptazap({
          ...payload,
          id:
            payload.id ||
            payload.msgid ||
            payload.messageId ||
            `rt-open-${Date.now()}`,
          texto: textoAberto || "",
          mensagem: textoAberto || "",
          direcao:
            direcaoAberta || (mensagemRecebida ? "recebida" : "enviada"),
          autor: mensagemRecebida
            ? "cliente"
            : payload.autor || payload.de || "operador",
          de: mensagemRecebida
            ? "cliente"
            : payload.de || payload.autor || "operador",
          datahora:
            payload.datahora ||
            payload.created_at ||
            payload.timestamp ||
            new Date().toISOString(),
        });

        setConversaSelecionada((atual) => {
          if (!atual || !isMesmaConversaCaptazap(atual, payload)) {
            return atual;
          }

          return normalizarConversaCaptazap({
            ...atual,
            preview: textoAberto || atual.preview,
            ultima_mensagem: textoAberto || atual.ultima_mensagem,
            ultimaMensagem: textoAberto || atual.ultimaMensagem,
            ultimaMensagemEm:
              mensagemAberta.datahora || atual.ultimaMensagemEm,
            ultima_mensagem_em:
              mensagemAberta.datahora || atual.ultima_mensagem_em,
            direcao: mensagemAberta.direcao,
            ultima_direcao: mensagemAberta.direcao,
            ultimaDirecao: mensagemAberta.direcao,
            naoLidas: 0,
            mensagens: mesclarMensagensConversa(
              atual.mensagens || [],
              mensagemAberta,
            ),
          });
        });

        if (mensagemRecebida) {
          requestJson("/whatsapp/captazap/marcar-lidas", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              empresa_id: empresaId,
              lead_id:
                payload.lead_id ||
                payload.leadId ||
                conversaAtual?.lead_id ||
                conversaAtual?.leadId ||
                null,
              telefone:
                payload.telefone ||
                payload.phone ||
                payload.numero ||
                conversaAtual?.telefone ||
                conversaAtual?.numero ||
                null,
              zapi_instance_id:
                getConexaoInstanceIdConversaCaptaZap(payload) ||
                getConexaoInstanceIdConversaCaptaZap(conversaAtual) ||
                null,
            }),
          }).catch(() => null);
        }
      }

      // A atualização otimista acima deixa a conversa instantânea. Em seguida,
      // reconciliamos a primeira página da aba visível com `captazap_chamados`
      // (fonte oficial de responsável/Fila). O debounce agrupa rajadas de mensagens,
      // atualiza os badges e evita depender de F5 ou do polling de 60s.
      agendarRecarregarConversasCaptazapRealtime();
    };

    const onNovaMensagem = async (event) => {
      await processarEventoMensagem(event, {
        tocarSom: true,
        incrementarNaoLidas: true,
      });
    };

    const onMediaReady = (event) => {
      aplicarMidiaProntaRealtime(parsePayload(event));
    };

    const onLidas = (event) => {
      const payload = parsePayload(event);

      setConversasWhatsApp((atuais) =>
        atuais.map((item) =>
          isMesmaConversaCaptazap(item, payload)
            ? { ...item, naoLidas: 0 }
            : item,
        ),
      );

      setConversaSelecionada((atual) =>
        atual && isMesmaConversaCaptazap(atual, payload)
          ? { ...atual, naoLidas: 0 }
          : atual,
      );
    };

    const onChamadoUpdate = (event) => {
      const payload = parsePayload(event);
      const chamada = payload.chamada || payload || {};
      const statusChamada = String(chamada.status_chamado || "")
        .trim()
        .toLowerCase();
      const motivoFechamentoPayload =
        chamada.motivo_fechamento || payload.motivo_fechamento || "";
      const observacaoFechamentoPayload =
        chamada.observacao_fechamento || payload.observacao_fechamento || "";
      const fechamentoFollowupPayload = Boolean(
        normalizar(motivoFechamentoPayload).includes("follow") ||
        normalizar(observacaoFechamentoPayload).includes("follow") ||
        normalizar(payload.motivo || payload.cancelado_motivo || "").includes(
          "fechamento_followup",
        ),
      );

      const filaProtecaoPayload = [
        payload.fila_protecao,
        payload.filaProtecao,
        chamada.fila_protecao,
        chamada.filaProtecao,
      ].some(
        (valor) =>
          valor === true ||
          valor === 1 ||
          String(valor || "").toLowerCase() === "true",
      );
      const filaProtecaoMotivoPayload =
        payload.fila_protecao_motivo ||
        payload.filaProtecaoMotivo ||
        chamada.fila_protecao_motivo ||
        chamada.filaProtecaoMotivo ||
        null;
      const filaProtecaoLabelPayload =
        payload.fila_protecao_label ||
        payload.filaProtecaoLabel ||
        chamada.fila_protecao_label ||
        chamada.filaProtecaoLabel ||
        (filaProtecaoMotivoPayload === "retorno_apos_fechamento"
          ? "Retorno após fechamento"
          : filaProtecaoMotivoPayload === "sem_resposta_3h"
            ? "Sem resposta há mais de 3h"
            : "");

      const temResponsavelIdPayload = Object.prototype.hasOwnProperty.call(
        chamada,
        "responsavel_id",
      );
      const temResponsavelNomePayload = Object.prototype.hasOwnProperty.call(
        chamada,
        "responsavel_nome",
      );

      const alvo = {
        lead_id: payload.lead_id || chamada.lead_id || chamada.lead_contexto_id,
        leadId: payload.lead_id || chamada.lead_id || chamada.lead_contexto_id,
        lead_contexto_id: payload.lead_contexto_id || chamada.lead_contexto_id,
        leadContextoId: payload.lead_contexto_id || chamada.lead_contexto_id,
        telefone: payload.telefone || chamada.telefone,
        numero: payload.telefone || chamada.telefone,
        zapi_instance_id:
          payload.zapi_instance_id ||
          payload.zapiInstanceId ||
          chamada.zapi_instance_id ||
          chamada.zapiInstanceId ||
          null,
      };

      const aplicarChamada = (item) => {
        if (!isMesmaConversaCaptazap(item, alvo)) return item;

        const motivoFechamento =
          motivoFechamentoPayload || item.motivo_fechamento || "";
        const observacaoFechamento =
          observacaoFechamentoPayload || item.observacao_fechamento || "";
        const fechamentoFollowup = Boolean(
          fechamentoFollowupPayload ||
          item._followup_fechado ||
          item._fechamento_followup ||
          normalizar(motivoFechamento).includes("follow") ||
          normalizar(observacaoFechamento).includes("follow"),
        );

        const base = {
          ...item,
          status_chamado: chamada.status_chamado || item.status_chamado,
          chamado_status: chamada.status_chamado || item.chamado_status,
          chamada_status: chamada.status_chamado || item.chamada_status,
          chamado_id: chamada.id || item.chamado_id,
          id_chamado: chamada.id || item.id_chamado || item.chamado_id || null,
          lead_contexto_id:
            chamada.lead_contexto_id ||
            payload.lead_contexto_id ||
            item.lead_contexto_id ||
            null,
          leadContextoId:
            chamada.lead_contexto_id ||
            payload.lead_contexto_id ||
            item.leadContextoId ||
            null,
          // Importante: null vindo do backend significa "sem responsável".
          // Não usar `|| item.responsavel_id`, pois isso ressuscita o dono antigo
          // e foi exatamente o que deixava retornos presos fora da Fila.
          responsavel_id: temResponsavelIdPayload
            ? chamada.responsavel_id || null
            : item.responsavel_id || null,
          responsavel_nome: temResponsavelNomePayload
            ? chamada.responsavel_nome || ""
            : item.responsavel_nome || "",
          captador_id: temResponsavelIdPayload
            ? chamada.responsavel_id || null
            : item.captador_id || null,
          captador_nome: temResponsavelNomePayload
            ? chamada.responsavel_nome || ""
            : item.captador_nome || "",
          fechado_em: chamada.fechado_em || item.fechado_em || null,
          fechadoEm: chamada.fechado_em || item.fechadoEm || null,
          fechado_por_id: chamada.fechado_por_id || item.fechado_por_id || null,
          fechado_por_nome:
            chamada.fechado_por_nome || item.fechado_por_nome || "",
          motivo_fechamento: motivoFechamento,
          motivoFechamento,
          observacao_fechamento: observacaoFechamento,
          observacaoFechamento,
        };

        if (statusChamada === "fechado") {
          return {
            ...base,
            status: "fechado",
            status_chamado: "fechado",
            chamado_status: "fechado",
            chamada_status: "fechado",
            conversa_ativa: false,
            conversaAtiva: false,
            sem_conversa: true,
            semConversa: true,
            aba_captazap: "fechados",
            em_fila: false,
            fila_atendimento: false,
            aguardando_atendimento: false,
            _fila_global: false,
            _fila_equipe: false,
            _fila_resgate: false,
            naoLidas: 0,
            cliente_respondeu_apos_fechamento: false,
            respondeu_apos_fechamento: false,
            nova_mensagem_apos_fechamento: false,
            novaRespostaAposFechamento: false,
            _fechado_com_resposta: false,
            reabertura_pendente: false,
            reaberturaPendente: false,
            _followup_fechado: fechamentoFollowup,
            _fechamento_followup: fechamentoFollowup,
            fechamento_followup: fechamentoFollowup,
            fechamentoFollowup,
            tags: Array.from(
              new Set(
                [
                  ...(Array.isArray(item.tags) ? item.tags : []),
                  "Chamado fechado",
                  fechamentoFollowup ? "Follow-up fechado" : "",
                ].filter(Boolean),
              ),
            ),
          };
        }

        if (statusChamada === "aberto") {
          const semResponsavelAtual = !base.responsavel_id;
          const emFilaAtual = Boolean(
            filaProtecaoPayload || semResponsavelAtual,
          );

          return {
            ...base,
            status: emFilaAtual ? "aguardando_atendimento" : "em_atendimento",
            status_chamado: "aberto",
            chamado_status: "aberto",
            chamada_status: "aberto",
            conversa_ativa: true,
            conversaAtiva: true,
            sem_conversa: false,
            semConversa: false,
            aba_captazap: emFilaAtual ? "fila" : "minhas",
            em_fila: emFilaAtual,
            fila_atendimento: emFilaAtual,
            aguardando_atendimento: emFilaAtual,
            _fila_global: emFilaAtual,
            _fila_equipe: emFilaAtual,
            _fila_resgate: Boolean(
              filaProtecaoPayload &&
                (payload.responsavel_anterior_id ||
                  payload.responsavel_anterior_nome),
            ),
            fila_protecao: filaProtecaoPayload,
            filaProtecao: filaProtecaoPayload,
            _fila_protecao: filaProtecaoPayload,
            fila_protecao_motivo: filaProtecaoMotivoPayload,
            filaProtecaoMotivo: filaProtecaoMotivoPayload,
            fila_protecao_label: filaProtecaoLabelPayload,
            filaProtecaoLabel: filaProtecaoLabelPayload,
            responsavel_anterior_id:
              payload.responsavel_anterior_id ||
              item.responsavel_anterior_id ||
              null,
            responsavel_anterior_nome:
              payload.responsavel_anterior_nome ||
              item.responsavel_anterior_nome ||
              "",
            fechado_em: null,
            fechadoEm: null,
            fechado_por_id: null,
            fechado_por_nome: "",
            motivo_fechamento: "",
            motivoFechamento: "",
            observacao_fechamento: "",
            observacaoFechamento: "",
            _followup_fechado: false,
            _fechamento_followup: false,
            fechamento_followup: false,
            fechamentoFollowup: false,
            // O retorno após fechamento já foi reaberto automaticamente.
            reabertura_pendente: false,
            reaberturaPendente: false,
          };
        }

        return {
          ...base,
          aba_captazap:
            chamada.status_chamado === "aberto" && chamada.responsavel_id
              ? "minhas"
              : item.aba_captazap,
          em_fila:
            chamada.status_chamado === "aberto" && chamada.responsavel_id
              ? false
              : item.em_fila,
          fila_atendimento:
            chamada.status_chamado === "aberto" && chamada.responsavel_id
              ? false
              : item.fila_atendimento,
          _fila_global:
            chamada.status_chamado === "aberto" && chamada.responsavel_id
              ? false
              : item._fila_global,
          _fila_equipe:
            chamada.status_chamado === "aberto" && chamada.responsavel_id
              ? false
              : item._fila_equipe,
        };
      };

      setConversasWhatsApp((atuais) =>
        ordenarConversasPorUltimaAtividadeCaptaZap(
          (atuais || []).map(aplicarChamada),
        ),
      );
      setConversasCriadasLocal((atuais) =>
        ordenarConversasPorUltimaAtividadeCaptaZap(
          (atuais || []).map(aplicarChamada),
        ),
      );
      setConversasAcompanhamentoFixadas((atuais) =>
        ordenarConversasPorUltimaAtividadeCaptaZap(
          (atuais || []).map(aplicarChamada),
        ),
      );

      setConversaSelecionada((atual) =>
        atual ? aplicarChamada(atual) : atual,
      );

      agendarRecarregarConversasCaptazapRealtime();

      // O estado local e a reconciliação leve da fila acima já refletem
      // fechamento/reabertura. Não recarregar o CRM completo aqui.
    };

    const aplicarAlteracaoMensagem = (payloadBruto = {}) => {
      const payload = payloadBruto?.mensagem || payloadBruto || {};
      const mensagemAtualizada = normalizarMensagemCaptazap(payload);

      const mensagemId = mensagemAtualizada?.id || payload?.id || null;
      const mensagemMsgid =
        mensagemAtualizada?.msgid ||
        mensagemAtualizada?.messageId ||
        mensagemAtualizada?.message_id ||
        payload?.msgid ||
        payload?.messageId ||
        payload?.message_id ||
        null;

      if (!mensagemId && !mensagemMsgid) return;

      const mesmaMensagem = (mensagem = {}) => {
        const idAtual = mensagem.id || mensagem.whatsapp_id || null;
        const msgidAtual =
          mensagem.msgid ||
          mensagem.messageId ||
          mensagem.message_id ||
          mensagem.whatsapp_id ||
          null;

        if (mensagemId && idAtual && String(idAtual) === String(mensagemId)) {
          return true;
        }

        if (
          mensagemMsgid &&
          msgidAtual &&
          String(msgidAtual) === String(mensagemMsgid)
        ) {
          return true;
        }

        return false;
      };

      const aplicarEmConversa = (conversa) => {
        if (!conversa || !Array.isArray(conversa.mensagens)) return conversa;

        let alterou = false;

        const mensagens = conversa.mensagens.map((mensagem) => {
          if (!mesmaMensagem(mensagem)) return mensagem;

          alterou = true;

          return {
            ...mensagem,
            ...mensagemAtualizada,
            texto:
              mensagemAtualizada.texto ??
              mensagemAtualizada.mensagem ??
              mensagem.texto ??
              "",
            mensagem:
              mensagemAtualizada.mensagem ??
              mensagemAtualizada.texto ??
              mensagem.mensagem ??
              "",
            status: mensagemAtualizada.status || mensagem.status,
          };
        });

        if (!alterou) return conversa;

        const apagada =
          String(mensagemAtualizada.status || "").toLowerCase() === "apagada";

        return {
          ...conversa,
          mensagens,
          preview: apagada
            ? conversa.preview === mensagemAtualizada.texto
              ? "Mensagem apagada"
              : conversa.preview
            : mensagemAtualizada.texto || conversa.preview,
          ultima_mensagem: apagada
            ? conversa.ultima_mensagem
            : mensagemAtualizada.texto || conversa.ultima_mensagem,
          ultimaMensagem: apagada
            ? conversa.ultimaMensagem
            : mensagemAtualizada.texto || conversa.ultimaMensagem,
        };
      };

      setConversasWhatsApp((atuais) => atuais.map(aplicarEmConversa));
      setConversasCriadasLocal((atuais) => atuais.map(aplicarEmConversa));
      setConversaSelecionada((atual) =>
        atual ? aplicarEmConversa(atual) : atual,
      );
    };

    const onStatusMensagem = (event) => {
      const payload = parsePayload(event);
      const status = String(payload?.status || "").trim().toLowerCase();
      if (!status) return;

      const identificadores = [
        payload?.id,
        payload?.whatsapp_id,
        payload?.msgid,
        payload?.messageId,
        payload?.message_id,
        payload?.zaapId,
      ]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean);

      if (!identificadores.length) return;

      const agora = Date.now();
      const pendentes = statusMensagensPendentesRef.current;

      // Evita crescimento indefinido. Cinco minutos cobrem a corrida entre
      // DeliveryCallback/MessageStatusCallback e a resposta do POST de envio.
      for (const [key, value] of pendentes.entries()) {
        if (agora - Number(value?.recebidoEm || 0) > 5 * 60 * 1000) {
          pendentes.delete(key);
        }
      }

      const patchStatus = {
        status,
        recebidoEm: agora,
      };

      if (Object.prototype.hasOwnProperty.call(payload, "erro")) {
        patchStatus.erro = payload.erro ?? null;
      } else if (Object.prototype.hasOwnProperty.call(payload, "error")) {
        patchStatus.erro = payload.error ?? null;
      }

      identificadores.forEach((id) => pendentes.set(id, patchStatus));

      const idsSet = new Set(identificadores);
      const mesmaMensagemStatus = (mensagem = {}) => {
        const idsMensagem = [
          mensagem?.id,
          mensagem?.whatsapp_id,
          mensagem?.msgid,
          mensagem?.messageId,
          mensagem?.message_id,
          mensagem?.zaapId,
          mensagem?.wa_message_id,
        ]
          .map((value) => String(value ?? "").trim())
          .filter(Boolean);

        return idsMensagem.some((id) => idsSet.has(id));
      };

      const aplicarStatusEmConversa = (conversa) => {
        if (!conversa || !Array.isArray(conversa.mensagens)) return conversa;

        let alterou = false;
        const mensagens = conversa.mensagens.map((mensagem) => {
          if (!mesmaMensagemStatus(mensagem)) return mensagem;
          alterou = true;

          const atualizada = {
            ...mensagem,
            status,
          };

          // O status_update é um PATCH: não toca em direção, fromMe, autoria,
          // texto ou mídia. Isso impede uma confirmação de entrega de inverter
          // a bolha do operador para o lado do paciente.
          if (Object.prototype.hasOwnProperty.call(patchStatus, "erro")) {
            atualizada.erro = patchStatus.erro;
            atualizada.error = patchStatus.erro;
          }

          if (payload?.msgid) atualizada.msgid = payload.msgid;
          if (payload?.messageId) atualizada.messageId = payload.messageId;
          if (payload?.zaapId) atualizada.zaapId = payload.zaapId;

          return atualizada;
        });

        return alterou ? { ...conversa, mensagens } : conversa;
      };

      setConversasWhatsApp((atuais) =>
        (Array.isArray(atuais) ? atuais : []).map(aplicarStatusEmConversa),
      );
      setConversasCriadasLocal((atuais) =>
        (Array.isArray(atuais) ? atuais : []).map(aplicarStatusEmConversa),
      );
      setConversaSelecionada((atual) => {
        const atualizado = atual ? aplicarStatusEmConversa(atual) : atual;
        conversaSelecionadaRef.current = atualizado;
        return atualizado;
      });
    };

    const onMensagemEditada = (event) => {
      aplicarAlteracaoMensagem(parsePayload(event));
    };

    const onMensagemApagada = (event) => {
      aplicarAlteracaoMensagem(parsePayload(event));
    };

    const onFollowupCancelado = (event) => {
      window.dispatchEvent(
        new CustomEvent("captazap:followup_cancelado", {
          detail: parsePayload(event),
        }),
      );
    };

    const onMensagemAlteradaLocal = (event) => {
      aplicarAlteracaoMensagem(event?.detail || {});
    };

    // v67.6: o backend é a fonte da verdade do nome do contato.
    // `nome` é o cadastro manual; `nome_whatsapp` é apenas o senderName da Z-API.
    // O evento atualiza todas as cópias locais da conversa para que um refresh,
    // webhook ou outra aba não faça o nome recém-salvo voltar para o telefone.
    const onContatoUpdate = (event) => {
      const payload = parsePayload(event);
      const contato = payload?.contato || payload?.data || payload || {};
      const telefoneContato =
        contato?.telefone_normalizado ||
        contato?.telefone ||
        payload?.telefone ||
        payload?.phone ||
        "";
      const identidadeContato = normalizarIdentidadeWhatsappComparacaoCaptaZap(
        telefoneContato,
      );

      if (!identidadeContato) return;

      const nomeManualRecebido = String(contato?.nome || "").trim();
      const nomeWhatsappRecebido = String(
        contato?.nome_whatsapp || contato?.nomeWhatsapp || "",
      ).trim();
      const classificacaoRecebida = String(
        contato?.dra_malu_classificacao || "",
      ).trim();

      const aplicarContato = (item) => {
        if (!item) return item;

        const identidadeItem = normalizarIdentidadeWhatsappComparacaoCaptaZap(
          item?.telefone_normalizado || item?.numero || item?.telefone || "",
        );
        if (!identidadeItem || identidadeItem !== identidadeContato) return item;

        const nomeManualAtual = String(
          item?.contato_nome || item?.contatoNome || "",
        ).trim();
        const nomeWhatsappAtual = String(
          item?.nome_whatsapp || item?.nomeWhatsapp || "",
        ).trim();
        const nomeManual = nomeManualRecebido || nomeManualAtual;
        const nomeWhatsapp = nomeWhatsappRecebido || nomeWhatsappAtual;

        const leadAtual = getLeadPrincipalConversaCardSafe(item);
        const nomeLeadAtual = getNomeLeadBuscaCaptaZap(leadAtual);
        const nomeAtual = String(item?.nome || "").trim();
        const nomeAtualValido =
          nomeAtual &&
          !["contato sem nome", "novo contato", "whatsapp"].includes(
            normalizar(nomeAtual),
          ) &&
          (/[a-zA-ZÀ-ÿ]/.test(nomeAtual) ||
            normalizarTelefone(nomeAtual) !== normalizarTelefone(telefoneContato))
            ? nomeAtual
            : "";

        return normalizarConversaCaptazap({
          ...item,
          contato_id: contato?.id || item?.contato_id || null,
          contatoId: contato?.id || item?.contatoId || null,
          contato_nome: nomeManual,
          contatoNome: nomeManual,
          nome_whatsapp: nomeWhatsapp,
          nomeWhatsapp,
          dra_malu_classificacao:
            classificacaoRecebida || item?.dra_malu_classificacao || "padrao",
          nome:
            nomeLeadAtual ||
            nomeManual ||
            nomeWhatsapp ||
            nomeAtualValido ||
            telefoneContato ||
            "Contato sem nome",
        });
      };

      setConversasWhatsApp((atuais) => (atuais || []).map(aplicarContato));
      setConversasCriadasLocal((atuais) =>
        (atuais || []).map(aplicarContato),
      );
      setContatosAvulsos((atuais) => {
        const lista = Array.isArray(atuais) ? atuais : [];
        let encontrou = false;
        const atualizados = lista.map((item) => {
          const atualizado = aplicarContato(item);
          if (atualizado !== item) encontrou = true;
          return atualizado;
        });

        if (encontrou) return atualizados;

        return upsertConversa(
          atualizados,
          contatoAvulsoToConversaCaptaZap({
            contato: {
              ...contato,
              telefone: telefoneContato,
              telefone_normalizado: telefoneContato,
              nome: nomeManualRecebido || null,
              nome_whatsapp: nomeWhatsappRecebido || null,
            },
            empresaId,
            empresaNome: "",
          }),
        );
      });
      setConversaSelecionada((atual) => {
        const atualizado = atual ? aplicarContato(atual) : atual;
        conversaSelecionadaRef.current = atualizado;
        return atualizado;
      });
    };

    const onPinUpdate = (event) => {
      const payload = parsePayload(event);
      const chave = getChaveConversaFixadaCaptaZap(payload);
      if (!chave) return;

      setConversasFixadas((atuais) => {
        const lista = Array.isArray(atuais) ? atuais : [];
        const semAtual = lista.filter(
          (item) => getChaveConversaFixadaCaptaZap(item) !== chave,
        );
        if (payload?.fixado === false || payload?.action === "unpin") {
          return semAtual;
        }
        return [payload, ...semAtual];
      });

      carregarConversasFixadas().catch(() => null);
    };

    eventSource.addEventListener("whatsapp:new_message", onNovaMensagem);
    eventSource.addEventListener("whatsapp:media_ready", onMediaReady);
    eventSource.addEventListener("whatsapp:message_edited", onMensagemEditada);
    eventSource.addEventListener("whatsapp:message_deleted", onMensagemApagada);
    eventSource.addEventListener("whatsapp:status_update", onStatusMensagem);
    eventSource.addEventListener("captazap:lidas", onLidas);
    eventSource.addEventListener("captazap:chamado_update", onChamadoUpdate);
    eventSource.addEventListener("captazap:contato_update", onContatoUpdate);
    eventSource.addEventListener("captazap:pin_update", onPinUpdate);
    eventSource.addEventListener(
      "captazap:followup_cancelado",
      onFollowupCancelado,
    );

    window.addEventListener(
      "captazap:mensagem_atualizada",
      onMensagemAlteradaLocal,
    );

    eventSource.onerror = () => {
      // O navegador reconecta automaticamente.
    };

    return () => {
      eventSource.removeEventListener("whatsapp:new_message", onNovaMensagem);
      eventSource.removeEventListener("whatsapp:media_ready", onMediaReady);
      eventSource.removeEventListener(
        "whatsapp:message_edited",
        onMensagemEditada,
      );
      eventSource.removeEventListener(
        "whatsapp:message_deleted",
        onMensagemApagada,
      );
      eventSource.removeEventListener(
        "whatsapp:status_update",
        onStatusMensagem,
      );
      eventSource.removeEventListener("captazap:lidas", onLidas);
      eventSource.removeEventListener(
        "captazap:chamado_update",
        onChamadoUpdate,
      );
      eventSource.removeEventListener(
        "captazap:contato_update",
        onContatoUpdate,
      );
      eventSource.removeEventListener("captazap:pin_update", onPinUpdate);
      eventSource.removeEventListener(
        "captazap:followup_cancelado",
        onFollowupCancelado,
      );
      window.removeEventListener(
        "captazap:mensagem_atualizada",
        onMensagemAlteradaLocal,
      );

      if (refreshConversasRealtimeTimerRef.current) {
        window.clearTimeout(refreshConversasRealtimeTimerRef.current);
        refreshConversasRealtimeTimerRef.current = null;
      }

      eventSource.close();
    };
  }, [
    agendarRecarregarConversasCaptazapRealtime,
    carregarContadoresOperacaoV2,
    carregarConversasFixadas,
    empresaId,
    mostrarNotificacaoChrome,
    mostrarNotificacaoNovaMensagem,
    recarregarMensagensDaConversaAberta,
  ]);

  // Fallback de atualização automática:
  // O tempo real principal usa SSE (/whatsapp/realtime/subscribe).
  // Em alguns computadores/redes o EventSource pode não entregar eventos
  // imediatamente; este polling leve evita depender de F5 para ver novas mensagens.
  useEffect(() => {
    if (!empresaId) return undefined;

    let cancelado = false;
    let rodando = false;

    const atualizarCaptaZapEmSegundoPlano = async () => {
      if (cancelado || rodando) return;

      // Evita bater na API quando a aba está em segundo plano.
      if (typeof document !== "undefined" && document.hidden) return;

      rodando = true;
      try {
        await recarregarConversasCaptazapRealtime();

        // Mensagens chegam instantaneamente pelo SSE. Para redes que perderem
        // eventos, reconciliamos a conversa aberta de forma bem mais espaçada.
        const conversaAberta = conversaSelecionadaRef.current;
        const agora = Date.now();
        if (
          conversaAberta &&
          agora - ultimoPollingMensagensAbertasRef.current >=
            CAPTAZAP_POLLING_MENSAGEM_ABERTA_MS
        ) {
          ultimoPollingMensagensAbertasRef.current = agora;
          await recarregarMensagensDaConversaAberta(conversaAberta);
        }
      } catch (error) {
        console.warn(
          "CaptaZap | polling de atualização automática falhou:",
          error,
        );
      } finally {
        rodando = false;
      }
    };

    const timer = window.setInterval(
      atualizarCaptaZapEmSegundoPlano,
      CAPTAZAP_POLLING_FALLBACK_MS,
    );

    return () => {
      cancelado = true;
      window.clearInterval(timer);
    };
  }, [
    empresaId,
    recarregarConversasCaptazapRealtime,
    recarregarMensagensDaConversaAberta,
  ]);

  // Recuperação imediata ao voltar para a aba/janela:
  // não cria polling novo e não aumenta carga contínua. Apenas faz uma
  // reconciliação leve quando o navegador volta a ficar visível/em foco.
  useEffect(() => {
    if (!empresaId) return undefined;

    const sincronizarAoRetomar = () => {
      if (typeof document !== "undefined" && document.hidden) return;

      if (sincronizacaoRetomadaTimerRef.current) {
        window.clearTimeout(sincronizacaoRetomadaTimerRef.current);
      }

      sincronizacaoRetomadaTimerRef.current = window.setTimeout(async () => {
        sincronizacaoRetomadaTimerRef.current = null;

        await recarregarConversasCaptazapRealtime().catch(() => null);

        const conversaAberta = conversaSelecionadaRef.current;
        if (conversaAberta) {
          ultimoPollingMensagensAbertasRef.current = Date.now();
          await recarregarMensagensDaConversaAberta(conversaAberta).catch(
            () => null,
          );
        }
      }, 180);
    };

    window.addEventListener("focus", sincronizarAoRetomar);
    document.addEventListener("visibilitychange", sincronizarAoRetomar);

    return () => {
      window.removeEventListener("focus", sincronizarAoRetomar);
      document.removeEventListener("visibilitychange", sincronizarAoRetomar);

      if (sincronizacaoRetomadaTimerRef.current) {
        window.clearTimeout(sincronizacaoRetomadaTimerRef.current);
        sincronizacaoRetomadaTimerRef.current = null;
      }
    };
  }, [
    empresaId,
    recarregarConversasCaptazapRealtime,
    recarregarMensagensDaConversaAberta,
  ]);

  useEffect(() => {
    const termoOriginal = String(busca || "").trim();
    const termoNormalizado = normalizar(termoOriginal);

    if (buscaContatosCRMTimerRef.current) {
      window.clearTimeout(buscaContatosCRMTimerRef.current);
      buscaContatosCRMTimerRef.current = null;
    }

    if (!empresaId || termoNormalizado.length < 3) {
      setLeadsCRMBuscaRemota([]);
      setBuscandoContatosCRM(false);
      return undefined;
    }

    const cacheKey = `${empresaId}:${termoNormalizado}`;
    const cached = buscaContatosCRMCacheRef.current.get(cacheKey);
    if (cached) {
      setLeadsCRMBuscaRemota(cached);
      setBuscandoContatosCRM(false);
      return undefined;
    }

    let cancelado = false;
    buscaContatosCRMTimerRef.current = window.setTimeout(async () => {
      setBuscandoContatosCRM(true);
      try {
        const leadsEncontradas = await buscarLeadsCRMRemotasCaptaZap({
          empresaId,
          termo: termoOriginal,
        });

        if (cancelado) return;

        const filtradas = normalizarLeadsVinculadasCaptazap(leadsEncontradas)
          .filter((lead) => pertenceEmpresa(lead, empresaId))
          .filter((lead) =>
            leadCombinaComBuscaContatoCaptaZap(lead, termoNormalizado),
          );

        buscaContatosCRMCacheRef.current.set(cacheKey, filtradas);
        setLeadsCRMBuscaRemota(filtradas);
      } catch (error) {
        if (!cancelado) {
          console.warn("CaptaZap | busca remota de contatos falhou:", error);
          buscaContatosCRMCacheRef.current.set(cacheKey, []);
          setLeadsCRMBuscaRemota([]);
        }
      } finally {
        if (!cancelado) setBuscandoContatosCRM(false);
      }
    }, 320);

    return () => {
      cancelado = true;
      if (buscaContatosCRMTimerRef.current) {
        window.clearTimeout(buscaContatosCRMTimerRef.current);
        buscaContatosCRMTimerRef.current = null;
      }
    };
  }, [busca, empresaId]);

  const dadosMock = useMemo(() => {
    return criarMockConversas({ empresaId, empresaNome });
  }, [empresaId, empresaNome]);

  const dadosCRMNormalizados = useMemo(() => {
    const tarefasPorLead = agruparPorLeadId(tarefas);
    const interacoesPorLead = agruparPorLeadId(interacoes);
    const agendamentosPorLead = agruparPorLeadId(agendamentos);
    const transferenciasPorLead = agruparTransferenciasPorLeadId(
      solicitacoesTransferencia,
    );

    const leadsBase =
      leadsCRMComBuscaRemota.length > 0
        ? leadsCRMComBuscaRemota
        : isDev
          ? criarMockLeads({ empresaId, empresaNome, usuario })
          : [];

    const leadsEnriquecidas = leadsBase
      .filter((lead) => pertenceEmpresa(lead, empresaId))
      .map((lead) => {
        const leadId = getLeadId(lead);
        const leadTarefas = tarefasPorLead.get(String(leadId)) || [];
        const leadInteracoes = interacoesPorLead.get(String(leadId)) || [];
        const leadAgendamentos = agendamentosPorLead.get(String(leadId)) || [];
        const transferenciaPendente =
          transferenciasPorLead.get(String(leadId)) || null;
        const apareceNoCaptaZap = leadApareceNoCaptaZap(lead);

        return {
          ...lead,
          tarefas: leadTarefas,
          interacoes: leadInteracoes,
          agendamentos: leadAgendamentos,
          _captaZap_visivel: apareceNoCaptaZap,
          _captaZap_reativavel: !apareceNoCaptaZap,
          _solicitacao_transferencia_pendente: transferenciaPendente,
          transferencia_pendente: Boolean(transferenciaPendente),
          ultima_interacao: getUltimoItem(leadInteracoes, [
            "criado_em",
            "created_at",
            "data",
            "data_interacao",
          ]),
          proxima_tarefa: getProximaTarefa(leadTarefas),
          _ultimo_agendamento: getUltimoItem(leadAgendamentos, [
            "data_agendamento",
            "created_at",
            "criado_em",
          ]),
        };
      });

    const contatosTodos = leadsEnriquecidas.map((lead) =>
      leadToContatoConversa({
        lead,
        empresaId,
        empresaNome,
        usuarios,
        fontes,
        campanhas,
        procedimentos,
        regioes,
        dentistas,
      }),
    );

    const leadsVisiveis = leadsEnriquecidas.filter(
      (lead) => lead._captaZap_visivel,
    );

    // A aba Contatos é a carteira CRM.
    // Não pode depender de _captaZap_visivel, senão leads antigas/perdidas/inativas
    // somem da busca/lista e o captador perde a referência para iniciar conversa.
    const contatos = contatosTodos;

    return {
      leads: leadsEnriquecidas,
      leadsVisiveis,
      contatos,
      contatosTodos,
    };
  }, [
    agendamentos,
    campanhas,
    dentistas,
    empresaId,
    empresaNome,
    fontes,
    interacoes,
    isDev,
    leadsCRMComBuscaRemota,
    procedimentos,
    regioes,
    solicitacoesTransferencia,
    tarefas,
    usuario,
    usuarios,
  ]);

  const draMaluAtivosEnriquecidos = useMemo(() => {
    const conversas = Array.isArray(conversasWhatsApp) ? conversasWhatsApp : [];

    return (Array.isArray(draMaluAtivos) ? draMaluAtivos : []).map((item) => {
      const telefoneItem = normalizarTelefone(item?.telefone || item?.numero || "").slice(-11);
      const conversa = conversas.find((c) => {
        const telefoneConversa = normalizarTelefone(
          c?.telefone || c?.numero || c?.whatsapp || "",
        ).slice(-11);
        return Boolean(telefoneItem && telefoneConversa && telefoneItem === telefoneConversa);
      });

      const avatar = getAvatarConversa(conversa || {});
      const nomeConversa = String(conversa?.nome || conversa?.nome_lead || "").trim();
      const nomeAtual = String(item?.nome || "").trim();
      const nomeEhGenerico = !nomeAtual || nomeAtual === item?.telefone || /^contato$/i.test(nomeAtual);

      return {
        ...item,
        nome: nomeEhGenerico && nomeConversa ? nomeConversa : nomeAtual || nomeConversa || "Contato",
        foto: item?.foto || avatar || "",
        avatar: item?.avatar || avatar || "",
        avatar_url: item?.avatar_url || avatar || "",
        profileUrl: item?.profileUrl || avatar || "",
      };
    });
  }, [draMaluAtivos, conversasWhatsApp]);

  const followupsOperacionaisUsuario = useMemo(
    () => [
      ...(Array.isArray(followupsChamados) ? followupsChamados : []),
      // A recuperação pela Dra. Malu é uma operação compartilhada. Todos que
      // acessam o CaptaZap enxergam esses contatos no Follow-up, enquanto o
      // card informa claramente qual era o captador de origem.
      ...draMaluAtivosEnriquecidos,
    ],
    [followupsChamados, draMaluAtivosEnriquecidos],
  );

  const followupsOperacionaisEmpresa = useMemo(
    () => [
      ...(Array.isArray(followupsChamadosEmpresa)
        ? followupsChamadosEmpresa
        : []),
      ...draMaluAtivosEnriquecidos,
    ],
    [followupsChamadosEmpresa, draMaluAtivosEnriquecidos],
  );

  const followupIndexUsuario = useMemo(
    () => criarIndiceFollowupsCaptaZap(followupsOperacionaisUsuario),
    [followupsOperacionaisUsuario],
  );

  const followupIndexEmpresa = useMemo(
    () => criarIndiceFollowupsCaptaZap(followupsOperacionaisEmpresa),
    [followupsOperacionaisEmpresa],
  );

  const pendenciasAgendaConversas = useMemo(
    () =>
      calcularPendenciasAgendaCaptaZap({
        agendamentos,
        leads: dadosCRMNormalizados.leads || [],
        empresaId,
        empresaNome,
        usuarios,
        fontes,
        campanhas,
        procedimentos,
        regioes,
        dentistas,
        usuarioId: usuarioIdLogado,
        usuarioNome: usuarioNomeLogado,
      }),
    [
      agendamentos,
      campanhas,
      dadosCRMNormalizados.leads,
      dentistas,
      empresaId,
      empresaNome,
      fontes,
      procedimentos,
      regioes,
      usuarios,
      usuarioIdLogado,
      usuarioNomeLogado,
    ],
  );

  const conversasBase = useMemo(() => {
    const conversasMockDev = isDev
      ? [...dadosMock.minhasConversas, ...dadosMock.filaDeEspera]
      : [];

    const todasConversasAbertas = [
      ...conversasWhatsApp,
      ...conversasMockDev,
      ...conversasCriadasLocal,
    ];

    const contextoAtendimento = {
      usuarioId: usuarioIdLogado,
      usuarioNome: usuarioNomeLogado,
      usuarios,
      podeVerAtendimentosEquipe: usuarioPodeVerEquipeCaptaZap,
    };

    const conversasComLead = todasConversasAbertas
      .map((conversa) =>
        enriquecerConversaComLead({
          conversa,
          leads: dadosCRMNormalizados.leads,
          usuarios,
          fontes,
          campanhas,
          procedimentos,
          regioes,
          dentistas,
        }),
      )
      .map((conversa) =>
        prepararReaberturaAutomaticaAposFechamento(
          conversa,
          contextoAtendimento,
        ),
      )
      .map((conversa) =>
        aplicarResumoFollowupNaConversaCaptaZap(
          conversa,
          followupIndexEmpresa,
          followupRegrasAtivas,
        ),
      );

    const conversasOperacionais = conversasComLead.filter((conversa) =>
      getTemConversaAtiva(conversa),
    );

    return {
      minhasConversas: conversasOperacionais.filter((conversa) =>
        isConversaMinhaDoUsuario(conversa, contextoAtendimento),
      ),
      minhasEquipeConversas: usuarioPodeVerEquipeCaptaZap
        ? conversasOperacionais
            .filter((conversa) =>
              isConversaMinhaDaEquipe(conversa, contextoAtendimento),
            )
            .map((conversa) =>
              marcarConversaMinhaEquipeSupervisao(
                conversa,
                contextoAtendimento,
              ),
            )
        : [],
      filaDeEspera: conversasOperacionais
        .filter((conversa) =>
          isConversaNaFilaDeAtendimento(conversa, contextoAtendimento),
        )
        .map((conversa) =>
          marcarConversaFilaEquipe(conversa, contextoAtendimento),
        ),
      pendenciaAgenda: pendenciasAgendaConversas,
      followUp: followupsOperacionaisUsuario.filter((item) => {
        if (item?._dra_malu_em_recuperacao || item?.dra_malu_em_recuperacao) {
          return true;
        }
        return (
          String(
            item?._follow_up_status || item?.status || "pendente",
          ).toLowerCase() === "pendente"
        );
      }),
      fechados: conversasComLead.filter(isConversaFechadaHistorico),
      // Performance: não enriquece a base inteira de contatos aqui.
      // O enriquecimento de follow-up para CRM/busca acontece depois do filtro e do slice,
      // evitando processar milhares de leads em toda renderização/envio de mensagem.
      contatos:
        dedupeConversas([
          ...(dadosCRMNormalizados.contatosTodos ||
            dadosCRMNormalizados.contatos ||
            []),
          ...(contatosAvulsos || []),
        ]),
    };
  }, [
    campanhas,
    conversasWhatsApp,
    dadosCRMNormalizados.contatos,
    dadosCRMNormalizados.leads,
    dadosMock.filaDeEspera,
    dadosMock.minhasConversas,
    dentistas,
    fontes,
    procedimentos,
    regioes,
    followupsOperacionaisUsuario,
    pendenciasAgendaConversas,
    followupIndexEmpresa,
    followupRegrasAtivas,
    conversasCriadasLocal,
    contatosAvulsos,
    isDev,
    usuarios,
    usuarioIdLogado,
    usuarioNomeLogado,
    usuarioPodeVerEquipeCaptaZap,
  ]);

  const listasFiltradas = useMemo(() => {
    const termo = normalizar(busca);

    const filtrar = (lista, options = {}) => {
      const { ignorarFiltroCanal = false } = options;

      return lista.filter((item) => {
        const itemEmpresaId = item.empresa_id || item.empresaId;

        const itemPertenceEmpresa =
          empresaId &&
          (String(itemEmpresaId) === String(empresaId) ||
            (!itemEmpresaId && isDev));

        const bateCanal = ignorarFiltroCanal
          ? canalSelecionado === "favoritos"
            ? item.favorito
            : true
          : canalSelecionado === "todos"
            ? true
            : canalSelecionado === "favoritos"
              ? item.favorito
              : item.canal === canalSelecionado;

        const instanciaItem = getConexaoInstanceIdConversaCaptaZap(item);
        const bateConexao =
          !instanciaItem ||
          Boolean(conexaoSelecionada && instanciaItem === conexaoSelecionada);

        const bateBusca =
          !termo || normalizar(montarBuscaItem(item)).includes(termo);

        return itemPertenceEmpresa && bateCanal && bateConexao && bateBusca;
      });
    };

    const contatosCRMBase = Array.isArray(dadosCRMNormalizados.contatosTodos)
      ? dadosCRMNormalizados.contatosTodos
      : Array.isArray(conversasBase.contatos)
        ? conversasBase.contatos
        : [];

    const contatosBase = termo
      ? [
          ...contatosCRMBase,
          ...(Array.isArray(conversasBase.minhasConversas)
            ? conversasBase.minhasConversas
            : []),
          ...(Array.isArray(conversasBase.minhasEquipeConversas)
            ? conversasBase.minhasEquipeConversas
            : []),
          ...(Array.isArray(conversasBase.filaDeEspera)
            ? conversasBase.filaDeEspera
            : []),
          ...(Array.isArray(conversasBase.followUp)
            ? conversasBase.followUp
            : []),
          ...(Array.isArray(conversasBase.fechados)
            ? conversasBase.fechados
            : []),
          ...(Array.isArray(conversasWhatsApp) ? conversasWhatsApp : []),
          ...(Array.isArray(conversasCriadasLocal)
            ? conversasCriadasLocal
            : []),
          ...(conversaSelecionada ? [conversaSelecionada] : []),
        ]
      : contatosCRMBase;

    // A aba Contatos é a carteira CRM. Ela não pode sumir quando o filtro
    // superior estiver em WhatsApp/Instagram/etc., porque muitos contatos do
    // CRM chegam como captaCRM, lead, vazio ou sem canal padronizado.
    // Mantemos apenas o filtro Favoritos quando essa opção estiver selecionada.
    const contatosFiltrados = dedupeConversas(
      filtrar(contatosBase, { ignorarFiltroCanal: true }),
    );
    const limiteContatos = termo
      ? MAX_CONTATOS_RENDER_COM_BUSCA
      : MAX_CONTATOS_RENDER;

    const aplicarFollowupContato = (contato) => {
      const contatoComResumo = aplicarResumoFollowupNaConversaCaptaZap(
        contato,
        followupIndexEmpresa,
        followupRegrasAtivas,
      );

      return aplicarContextoFechadoFollowupNoContatoCaptaZap(
        contatoComResumo,
        conversasBase.fechados || [],
      );
    };

    // Performance: enriquece apenas o lote renderizado. Antes o componente
    // processava toda a carteira CRM a cada render/busca, travando digitação,
    // apagar texto e troca para a aba Contatos.
    const contatosVisiveis = contatosFiltrados
      .slice(0, limiteContatos)
      .map(aplicarFollowupContato);

    // Base completa para a busca interna da aba Contatos.
    // Importante: não pode depender do termo do cabeçalho nem do lote de 220 renderizado,
    // senão contatos antigos do CRM (ex.: Guilherme Moreira Ramos) somem da busca local do QueueList.
    // Mantemos sem enrich pesado para não travar; o enrich completo fica apenas nos contatos renderizados.
    const contatosBuscaGlobalEnriquecidos = dedupeConversas(
      contatosCRMBase,
    ).map((contato) => ({
      ...contato,
      _captazap_origem_busca_crm_global: true,
    }));

    return {
      minhasConversas: ordenarConversasPorUltimaAtividadeCaptaZap(
        filtrar(conversasBase.minhasConversas),
      ),
      minhasEquipeConversas: ordenarConversasPorUltimaAtividadeCaptaZap(
        filtrar(conversasBase.minhasEquipeConversas || []),
      ),
      filaDeEspera: ordenarConversasPorUltimaAtividadeCaptaZap(
        filtrar(conversasBase.filaDeEspera),
      ),
      pendenciaAgenda: ordenarConversasPorUltimaAtividadeCaptaZap(
        filtrar(conversasBase.pendenciaAgenda || [], {
          ignorarFiltroCanal: true,
        }),
      ),
      followUp: ordenarConversasPorUltimaAtividadeCaptaZap(
        filtrar(conversasBase.followUp || []),
      ),
      fechados: ordenarConversasPorUltimaAtividadeCaptaZap(
        filtrar(conversasBase.fechados || []),
      ),
      contatos: contatosVisiveis,
      contatosBuscaGlobal: contatosBuscaGlobalEnriquecidos,
      totalContatosFiltrados: contatosFiltrados.length,
      contatosLimitados: contatosFiltrados.length > limiteContatos,
      buscaGlobalAtiva: Boolean(termo),
      buscandoContatosCRM,
    };
  }, [
    busca,
    buscandoContatosCRM,
    canalSelecionado,
    conexaoSelecionada,
    conversasBase,
    followupIndexEmpresa,
    followupRegrasAtivas,
    dadosCRMNormalizados.contatosTodos,
    conversasWhatsApp,
    conversasCriadasLocal,
    conversaSelecionada,
    empresaId,
    isDev,
  ]);

  const minhasConversasComAcompanhamento = useMemo(() => {
    let lista = Array.isArray(listasFiltradas.minhasConversas)
      ? [...listasFiltradas.minhasConversas]
      : [];

    const fixadasDaConexao = (
      Array.isArray(conversasAcompanhamentoFixadas)
        ? conversasAcompanhamentoFixadas
        : []
    ).filter((conversa) => {
      const instancia = getConexaoInstanceIdConversaCaptaZap(conversa);
      if (conexaoSelecionada && instancia && instancia !== conexaoSelecionada) {
        return false;
      }
      if (canalSelecionado !== "todos" && canalSelecionado !== "favoritos") {
        if (String(conversa?.canal || "whatsapp") !== String(canalSelecionado)) {
          return false;
        }
      }
      return true;
    });

    for (const conversa of fixadasDaConexao) {
      lista = upsertConversa(lista, conversa);
    }

    return ordenarConversasPorUltimaAtividadeCaptaZap(lista);
  }, [
    listasFiltradas.minhasConversas,
    conversasAcompanhamentoFixadas,
    conexaoSelecionada,
    canalSelecionado,
  ]);

  const handleBuscarContatosCRMQueueList = useCallback(
    async (termoBusca) => {
      const termoOriginal = String(termoBusca || "").trim();
      const termoNormalizado = normalizar(termoOriginal);

      if (!empresaId || termoNormalizado.length < 3) {
        setLeadsCRMBuscaRemota([]);
        setBuscandoContatosCRM(false);
        return;
      }

      const cacheKey = `${empresaId}:${termoNormalizado}`;
      const cached = buscaContatosCRMCacheRef.current.get(cacheKey);
      if (cached) {
        setLeadsCRMBuscaRemota(cached);
        setBuscandoContatosCRM(false);
        return;
      }

      setBuscandoContatosCRM(true);
      try {
        const leadsEncontradas = await buscarLeadsCRMRemotasCaptaZap({
          empresaId,
          termo: termoOriginal,
        });

        const filtradas = normalizarLeadsVinculadasCaptazap(leadsEncontradas)
          .filter((lead) => pertenceEmpresa(lead, empresaId))
          .filter((lead) =>
            leadCombinaComBuscaContatoCaptaZap(lead, termoNormalizado),
          );

        buscaContatosCRMCacheRef.current.set(cacheKey, filtradas);
        setLeadsCRMBuscaRemota(filtradas);
      } catch (error) {
        console.warn("CaptaZap | busca CRM pelo QueueList falhou:", error);
        buscaContatosCRMCacheRef.current.set(cacheKey, []);
        setLeadsCRMBuscaRemota([]);
      } finally {
        setBuscandoContatosCRM(false);
      }
    },
    [empresaId],
  );

  const handleBuscarOperacaoGlobalQueueList = useCallback(
    async (termoBusca, opcoes = {}) => {
      const termoOriginal = String(termoBusca || "").trim();
      const termoNormalizado = normalizar(termoOriginal);
      const abaBusca = String(
        opcoes?.aba || abaQueueListRef.current || "operacao",
      )
        .trim()
        .toLowerCase();

      if (!empresaId || termoNormalizado.length < 3) {
        setResultadosBuscaOperacao([]);
        setBuscandoBuscaOperacao(false);
        return;
      }

      const conexaoBusca = String(
        conexaoSelecionadaRef.current || "",
      ).trim();

      const locais = dedupeConversas([
        ...(Array.isArray(conversasWhatsApp) ? conversasWhatsApp : []),
        ...(Array.isArray(conversasCriadasLocal) ? conversasCriadasLocal : []),
        ...(Array.isArray(conversasBase.minhasConversas)
          ? conversasBase.minhasConversas
          : []),
        ...(Array.isArray(conversasBase.minhasEquipeConversas)
          ? conversasBase.minhasEquipeConversas
          : []),
        ...(Array.isArray(conversasBase.filaDeEspera)
          ? conversasBase.filaDeEspera
          : []),
        ...(Array.isArray(conversasBase.followUp)
          ? conversasBase.followUp
          : []),
        ...(Array.isArray(conversasBase.fechados)
          ? conversasBase.fechados
          : []),
        ...(conversaSelecionada ? [conversaSelecionada] : []),
      ])
        .filter((item) => {
          const instanciaItem = getConexaoInstanceIdConversaCaptaZap(item);
          return !instanciaItem || !conexaoBusca || instanciaItem === conexaoBusca;
        })
        .filter((item) =>
          normalizar(montarBuscaItem(item)).includes(termoNormalizado),
        )
        .map((item) =>
          marcarResultadoBuscaOperacaoCaptaZap(item, {
            usuarioId: usuarioIdLogado,
            usuarioNome: usuarioNomeLogado,
          }),
        );

      if (locais.length > 0) {
        setResultadosBuscaOperacao(locais);
      }

      const cacheKey = `${empresaId}:${conexaoBusca || "sem-conexao"}:${abaBusca}:${termoNormalizado}`;
      if (buscaOperacaoCacheRef.current.has(cacheKey)) {
        const cached = buscaOperacaoCacheRef.current.get(cacheKey) || [];
        setResultadosBuscaOperacao(dedupeConversas([...locais, ...cached]));
        setBuscandoBuscaOperacao(false);
        return;
      }

      setBuscandoBuscaOperacao(true);
      try {
        const remotos = await buscarConversasOperacaoCaptaZap({
          empresaId,
          termo: termoOriginal,
          aba: abaBusca,
          usuarioId: usuarioIdLogado,
          zapiInstanceId: conexaoBusca,
        });

        const filtrados = dedupeConversas(remotos)
          .filter((item) => pertenceEmpresa(item, empresaId))
          .filter((item) =>
            normalizar(montarBuscaItem(item)).includes(termoNormalizado),
          )
          .map((item) =>
            marcarResultadoBuscaOperacaoCaptaZap(item, {
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
            }),
          );

        buscaOperacaoCacheRef.current.set(cacheKey, filtrados);
        setResultadosBuscaOperacao(dedupeConversas([...locais, ...filtrados]));
      } catch (error) {
        console.warn("CaptaZap | busca operacional global falhou:", error);
        buscaOperacaoCacheRef.current.set(cacheKey, []);
        setResultadosBuscaOperacao(locais);
      } finally {
        setBuscandoBuscaOperacao(false);
      }
    },
    [
      empresaId,
      conversasWhatsApp,
      conversasCriadasLocal,
      conversasBase,
      conversaSelecionada,
      usuarioIdLogado,
      usuarioNomeLogado,
    ],
  );

  const totalMinhas =
    totaisOperacaoV2.minhas ?? listasFiltradas.minhasConversas.length;
  const totalMinhasEquipe = listasFiltradas.minhasEquipeConversas?.length || 0;
  const totalFila =
    totaisOperacaoV2.fila ?? listasFiltradas.filaDeEspera.length;
  const totalFollowUp = listasFiltradas.followUp?.length || 0;
  const totalFechados =
    totaisOperacaoV2.fechados ?? listasFiltradas.fechados?.length ?? 0;
  const totalContatos =
    dadosCRMNormalizados.contatosTodos?.length ||
    dadosCRMNormalizados.contatos.length;
  const totalConversas = totalMinhas + totalFila;
  const totalLeads = dadosCRMNormalizados.leadsVisiveis?.length || 0;
  const totalLeadsCRM = dadosCRMNormalizados.leads.length;
  const tempoMedio = empresaId ? "2m 18s" : "-";

  const producaoCaptadorHoje = useMemo(
    () =>
      calcularProducaoCaptadorHojeCaptaZap({
        empresaId,
        usuarioId: usuarioIdLogado,
        leads: dadosCRMNormalizados.leads || [],
        agendamentos,
        recebimentosFinanceiros,
      }),
    [
      agendamentos,
      dadosCRMNormalizados.leads,
      empresaId,
      recebimentosFinanceiros,
      usuarioIdLogado,
    ],
  );

  const resumoFluxoFollowup = useMemo(() => {
    const total = Array.isArray(followupsChamados)
      ? followupsChamados.length
      : 0;

    if (!total) return "Nenhum follow-up pendente";

    return `${total} follow-up${total === 1 ? "" : "s"} pendente${
      total === 1 ? "" : "s"
    }`;
  }, [followupsChamados]);

  const primeiraConversa =
    listasFiltradas.filaDeEspera[0] ||
    listasFiltradas.pendenciaAgenda?.[0] ||
    listasFiltradas.followUp?.[0] ||
    listasFiltradas.minhasConversas[0] ||
    listasFiltradas.contatos[0];

  const chamadoFechadoSelecionado =
    conversaSelecionada &&
    getStatusChamadoVisual(conversaSelecionada) === "fechado";

  const precisaAbrirNovoChamadoSelecionada = Boolean(chamadoFechadoSelecionado);

  const somenteVisualizacaoSupervisaoSelecionada = Boolean(
    conversaSelecionada?._captazap_visualizacao_supervisao ||
    conversaSelecionada?._somente_visualizacao_supervisao ||
    conversaSelecionada?.somente_visualizacao_supervisao,
  );

  const contatoCRMConsultaSelecionada = Boolean(
    conversaSelecionada && isContatoCRMConsultaCaptaZap(conversaSelecionada),
  );

  const buscaOperacionalSelecionada = Boolean(
    conversaSelecionada?._captazap_busca_operacional ||
    conversaSelecionada?._captazap_pode_assumir ||
    conversaSelecionada?._captazap_bloqueado_por_responsavel,
  );

  const precisaAssumirParaResponderSelecionada = Boolean(
    conversaSelecionada &&
    precisaAssumirParaResponderCaptaZap(conversaSelecionada, {
      usuarioId: usuarioIdLogado,
      usuarioNome: usuarioNomeLogado,
    }),
  );

  const envioBloqueadoSelecionada = Boolean(
    precisaAbrirNovoChamadoSelecionada ||
    somenteVisualizacaoSupervisaoSelecionada ||
    precisaAssumirParaResponderSelecionada,
  );

  const identidadeChatSelecionada = useMemo(() => {
    const conversa = conversaSelecionada || {};

    const responsavelId =
      conversa._captazap_operador_visualizacao_id ||
      conversa._captazap_responsavel_original_id ||
      getResponsavelConversaId(conversa);

    const responsavelNome =
      conversa._captazap_operador_visualizacao_nome ||
      conversa._captazap_responsavel_original_nome ||
      getResponsavelConversaNome(conversa);

    const emSupervisao = Boolean(
      conversa._captazap_modo_visualizacao === "supervisao" ||
      conversa._captazap_visualizacao_supervisao ||
      conversa._somente_visualizacao_supervisao ||
      conversa.somente_visualizacao_supervisao,
    );

    return {
      modo: emSupervisao ? "supervisao" : "atendimento",
      somenteVisualizacao: emSupervisao,

      // Quem está olhando a tela.
      visualizadorId: usuarioIdLogado || null,
      visualizadorNome:
        usuarioNomeLogado || getNomeUsuarioSistema(usuario) || "",

      // Quem é o operador/captador real do chamado.
      // Em supervisão, este usuário define o lado direito das mensagens da equipe.
      operadorId: emSupervisao
        ? responsavelId || null
        : usuarioIdLogado || null,
      operadorNome: emSupervisao
        ? responsavelNome || "Captador responsável"
        : usuarioNomeLogado || getNomeUsuarioSistema(usuario) || "",

      responsavelId: responsavelId || null,
      responsavelNome: responsavelNome || "",
    };
  }, [conversaSelecionada, usuario, usuarioIdLogado, usuarioNomeLogado]);

  useEffect(() => {
    let cancelado = false;

    async function carregarMensagensConversa() {
      // O histórico do chat precisa seguir o chamado/conversa operacional.
      // Quando o usuário vem da aba Contatos, a lead clicada é contexto de CRM;
      // ela não pode apagar os IDs do atendimento real nem limitar a busca.
      if (!empresaId || !conversaSelecionada) return;

      const conversaId =
        conversaSelecionada.id || conversaSelecionada.conversa_id;
      const conversaOperacionalId =
        conversaSelecionada._captazap_conversa_operacional_id ||
        conversaSelecionada.conversa_id ||
        conversaSelecionada.conversaId ||
        conversaSelecionada.id ||
        null;
      const chamadoOperacionalId =
        conversaSelecionada._captazap_chamado_operacional_id ||
        conversaSelecionada.chamado_id ||
        conversaSelecionada.id_chamado ||
        conversaSelecionada.chamada_id ||
        null;
      const leadContextoId =
        conversaSelecionada.lead_contexto_id ||
        conversaSelecionada.leadContextoId ||
        conversaSelecionada.contexto_lead_id ||
        conversaSelecionada.contextoLeadId ||
        conversaSelecionada.lead_id ||
        conversaSelecionada.leadId ||
        conversaSelecionada.lead?.id ||
        null;
      const leadHistoricoId =
        conversaSelecionada._captazap_chamado_lead_id ||
        conversaSelecionada._captazap_lead_operacional_id ||
        (!deveCarregarHistoricoPorTelefoneCaptaZap(conversaSelecionada)
          ? leadContextoId
          : null);
      const telefoneHistorico =
        getTelefoneHistoricoWhatsAppCaptaZap(conversaSelecionada);
      const telefone = telefoneHistorico || "";
      const zapiInstanceId = getConexaoInstanceIdConversaCaptaZap(
        conversaSelecionada,
      );
      const abriuPelaAbaContatos =
        deveCarregarHistoricoPorTelefoneCaptaZap(conversaSelecionada);

      if (
        !leadContextoId &&
        !leadHistoricoId &&
        !telefone &&
        !conversaOperacionalId &&
        !chamadoOperacionalId
      ) {
        return;
      }

      const pertenceSelecaoAtual = (atual = {}) => {
        if (!atual) return false;

        const atualId = atual.id || atual.conversa_id;
        const atualOperacionalId =
          atual._captazap_conversa_operacional_id ||
          atual.conversa_id ||
          atual.conversaId ||
          atual.id;
        const atualTelefone = getTelefoneHistoricoWhatsAppCaptaZap(atual);
        const atualInstancia = getConexaoInstanceIdConversaCaptaZap(atual);

        return Boolean(
          (conversaId && String(atualId) === String(conversaId)) ||
          (conversaOperacionalId &&
            atualOperacionalId &&
            String(atualOperacionalId) === String(conversaOperacionalId)) ||
          (telefone &&
            atualTelefone &&
            (!zapiInstanceId ||
              !atualInstancia ||
              zapiInstanceId === atualInstancia) &&
            normalizarTelefoneLeadBrasil(atualTelefone) ===
              normalizarTelefoneLeadBrasil(telefone)),
        );
      };

      setConversaSelecionada((atual) => {
        if (!pertenceSelecaoAtual(atual)) return atual;

        return {
          ...atual,
          _carregando_mensagens: true,
          _erro_carregar_mensagens: "",
        };
      });

      try {
        const tentativas = [];
        const chavesTentativas = new Set();

        const adicionarTentativa = (campos = {}) => {
          const params = new URLSearchParams();
          params.set("empresa_id", String(empresaId));
          if (zapiInstanceId) params.set("zapi_instance_id", zapiInstanceId);
          let temFiltroHistorico = false;

          Object.entries(campos).forEach(([key, value]) => {
            if (value !== undefined && value !== null && String(value).trim()) {
              params.set(key, String(value));
              temFiltroHistorico = true;
            }
          });

          if (!temFiltroHistorico) return;

          const chave = params.toString();
          if (!chavesTentativas.has(chave)) {
            chavesTentativas.add(chave);
            tentativas.push(params);
          }
        };

        const telefonesTentativa =
          getTelefonesHistoricoCandidatosCaptaZap(conversaSelecionada);
        const telefonesParaBuscar = telefonesTentativa.length
          ? telefonesTentativa
          : telefone
            ? [telefone]
            : [];

        if (abriuPelaAbaContatos) {
          // 1) Melhor cenário: abrir pelo chamado/conversa real encontrado nas listas.
          telefonesParaBuscar.forEach((telefoneBusca) => {
            adicionarTentativa({
              conversa_id: conversaOperacionalId,
              chamado_id: chamadoOperacionalId,
              lead_id: leadHistoricoId,
              telefone: telefoneBusca,
            });
          });

          // 2) Fallback por lead operacional do chamado, não pela lead CRM clicada.
          telefonesParaBuscar.forEach((telefoneBusca) => {
            adicionarTentativa({
              lead_id: leadHistoricoId,
              telefone: telefoneBusca,
            });
          });

          // 3) Fallback puro por telefone/numero/phone. Alguns backends antigos
          // só leem um desses nomes; tentamos todos sem depender do lead CRM.
          telefonesParaBuscar.forEach((telefoneBusca) => {
            adicionarTentativa({ telefone: telefoneBusca });
            adicionarTentativa({ numero: telefoneBusca });
            adicionarTentativa({ phone: telefoneBusca });
          });

          // 4) Último recurso: contexto CRM clicado, sem substituir a conversa operacional.
          if (
            leadContextoId &&
            (!leadHistoricoId ||
              String(leadContextoId) !== String(leadHistoricoId))
          ) {
            telefonesParaBuscar.forEach((telefoneBusca) => {
              adicionarTentativa({
                lead_id: leadContextoId,
                telefone: telefoneBusca,
              });
            });
          }
        } else {
          adicionarTentativa({
            conversa_id: conversaOperacionalId,
            chamado_id: chamadoOperacionalId,
            lead_id: leadContextoId,
            telefone,
          });
          adicionarTentativa({ lead_id: leadContextoId, telefone });
          adicionarTentativa({ telefone });
        }

        let data = null;
        let mensagens = [];

        for (const params of tentativas) {
          data = await requestJson(
            `/whatsapp/captazap/mensagens-v2?${params.toString()}&limit=${CAPTAZAP_MENSAGENS_PAGE_SIZE}`,
          );

          if (cancelado) return;

          mensagens = extrairMensagensRespostaCaptaZap(data);

          if (mensagens.length > 0) break;
        }

        const contextoLeads = montarContextoLeadsConversa(data || {});

        setConversaSelecionada((atual) => {
          if (!pertenceSelecaoAtual(atual)) return atual;

          const mensagensAtuais = Array.isArray(atual.mensagens)
            ? atual.mensagens
            : [];
          const mensagensFinais = mensagens.length
            ? mensagens
            : mensagensAtuais;
          const leadContextoAtual =
            atual.lead_contexto_id ||
            atual.leadContextoId ||
            atual.contexto_lead_id ||
            atual.contextoLeadId ||
            leadContextoId ||
            atual.lead_id ||
            atual.leadId ||
            atual.lead?.id ||
            null;
          const leadAtualizada =
            getLeadVinculadaPorId(
              {
                leads_vinculadas:
                  contextoLeads.leads_vinculadas || atual.leads_vinculadas,
              },
              leadContextoAtual,
            ) || atual.lead;

          const historicoEncontrado = mensagensFinais.length > 0;

          return normalizarConversaCaptazap({
            ...atual,
            ...contextoLeads,
            _captazap_conversa_operacional_id:
              conversaOperacionalId || atual._captazap_conversa_operacional_id,
            _captazap_chamado_operacional_id:
              chamadoOperacionalId || atual._captazap_chamado_operacional_id,
            _captazap_chamado_lead_id:
              leadHistoricoId || atual._captazap_chamado_lead_id,
            lead_contexto_id:
              leadContextoAtual || contextoLeads.lead_contexto_id,
            leadContextoId: leadContextoAtual || contextoLeads.leadContextoId,
            contexto_lead_id:
              leadContextoAtual || contextoLeads.lead_contexto_id,
            contextoLeadId: leadContextoAtual || contextoLeads.leadContextoId,
            lead_id: leadContextoAtual || atual.lead_id,
            leadId: leadContextoAtual || atual.leadId,
            lead: leadAtualizada,
            mensagens: mensagensFinais,
            conversa_ativa:
              historicoEncontrado || abriuPelaAbaContatos
                ? true
                : atual.conversa_ativa,
            conversaAtiva:
              historicoEncontrado || abriuPelaAbaContatos
                ? true
                : atual.conversaAtiva,
            sem_conversa:
              historicoEncontrado || abriuPelaAbaContatos
                ? false
                : atual.sem_conversa,
            semConversa:
              historicoEncontrado || abriuPelaAbaContatos
                ? false
                : atual.semConversa,
            _historico_whatsapp_carregado: historicoEncontrado,
            _historico_whatsapp_tentado: true,
            _mensagens_has_more: Boolean(
              data?.paginacao?.has_more ?? data?.paginacao?.hasMore ?? false,
            ),
            _mensagens_cursor:
              data?.paginacao?.previous_cursor ||
              data?.paginacao?.previousCursor ||
              null,
            _carregando_mensagens: false,
            _erro_carregar_mensagens: "",
          });
        });

        await requestJson("/whatsapp/captazap/marcar-lidas", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            empresa_id: empresaId,
            conversa_id: conversaOperacionalId || null,
            chamado_id: chamadoOperacionalId || null,
            lead_id: leadHistoricoId || leadContextoId || null,
            telefone: telefone || null,
            zapi_instance_id: zapiInstanceId || null,
          }),
        }).catch(() => null);

        if (!cancelado) {
          setConversasWhatsApp((atuais) =>
            (atuais || []).map((item) =>
              isMesmaConversaCaptazap(item, {
                id: conversaOperacionalId || conversaId,
                conversa_id: conversaOperacionalId || conversaId,
                chamado_id: chamadoOperacionalId,
                lead_id: leadHistoricoId || leadContextoId,
                telefone,
              })
                ? normalizarConversaCaptazap({
                    ...item,
                    ...contextoLeads,
                    lead_contexto_id:
                      item.lead_contexto_id ||
                      item.leadContextoId ||
                      contextoLeads.lead_contexto_id,
                    leadContextoId:
                      item.leadContextoId ||
                      item.lead_contexto_id ||
                      contextoLeads.leadContextoId,
                    naoLidas: 0,
                    mensagens: mensagens.length ? mensagens : item.mensagens,
                    _mensagens_has_more: Boolean(
                      data?.paginacao?.has_more ??
                        data?.paginacao?.hasMore ??
                        item?._mensagens_has_more ??
                        false,
                    ),
                    _mensagens_cursor:
                      data?.paginacao?.previous_cursor ||
                      data?.paginacao?.previousCursor ||
                      item?._mensagens_cursor ||
                      null,
                  })
                : item,
            ),
          );
        }
      } catch (err) {
        console.error("❌ CaptaZap carregar mensagens reais:", err);

        if (!cancelado) {
          setConversaSelecionada((atual) => {
            if (!pertenceSelecaoAtual(atual)) return atual;

            return {
              ...atual,
              _carregando_mensagens: false,
              _erro_carregar_mensagens:
                err?.message || "Erro ao carregar mensagens.",
            };
          });
        }
      }
    }

    carregarMensagensConversa();

    return () => {
      cancelado = true;
    };
  }, [
    empresaId,
    conversaSelecionada?.id,
    conversaSelecionada?.conversa_id,
    conversaSelecionada?._captazap_conversa_operacional_id,
    conversaSelecionada?._captazap_chamado_operacional_id,
    conversaSelecionada?._captazap_chamado_lead_id,
    conversaSelecionada?.lead_contexto_id,
    conversaSelecionada?.leadContextoId,
    conversaSelecionada?.lead_id,
    conversaSelecionada?.leadId,
    conversaSelecionada?.lead?.id,
    conversaSelecionada?.numero,
    conversaSelecionada?.telefone,
    conversaSelecionada?.telefone1,
    conversaSelecionada?.celular,
    conversaSelecionada?.whatsapp,
  ]);

  const handleSelecionarLeadContexto = useCallback(
    async (leadId, conversaBase = conversaSelecionadaRef.current) => {
      const leadContextoId = Number(leadId || 0);

      if (!empresaId || !conversaBase || !leadContextoId) return;

      const chamadoId =
        conversaBase.chamado_id ||
        conversaBase.id_chamado ||
        conversaBase.chamada_id ||
        null;

      const leadSelecionada = getLeadVinculadaPorId(
        conversaBase,
        leadContextoId,
      );

      if (
        leadSelecionada &&
        !leadPertenceAoTelefoneCaptaZap(
          leadSelecionada,
          getTelefonePrincipalConversaCaptaZap(conversaBase),
        )
      ) {
        window.alert(
          "Este paciente não pertence ao WhatsApp desta conversa. O contexto não foi alterado.",
        );
        return;
      }

      salvarContextoPacienteCaptaZap(conversaBase, leadContextoId, empresaId);

      const conversaBaseComContexto = aplicarContextoPacientePersistidoCaptaZap(
        {
          ...conversaBase,
          lead_contexto_id: leadContextoId,
          leadContextoId: leadContextoId,
          lead: leadSelecionada || conversaBase.lead,
        },
        empresaId,
      );

      const aplicarContextoLocal = (item) => {
        if (!item || !isMesmaConversaCaptazap(item, conversaBase)) return item;

        return normalizarConversaCaptazap({
          ...item,
          lead_contexto_id: leadContextoId,
          leadContextoId: leadContextoId,
          lead_id: item.lead_id || leadContextoId,
          leadId: item.leadId || leadContextoId,
          lead: leadSelecionada || item.lead,
        });
      };

      setConversaSelecionada((atual) => aplicarContextoLocal(atual));
      setConversasWhatsApp((atuais) =>
        (atuais || []).map(aplicarContextoLocal),
      );
      setConversasCriadasLocal((atuais) =>
        (atuais || []).map(aplicarContextoLocal),
      );

      if (!chamadoId) return;

      setSalvandoContextoLead(true);

      try {
        const data = await requestJson(
          `/whatsapp/captazap/chamados/${chamadoId}/contexto-lead`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              empresa_id: empresaId,
              lead_contexto_id: leadContextoId,
            }),
          },
        );

        const chamada = data?.chamada || {};

        const aplicarRetorno = (item) => {
          if (!item || !isMesmaConversaCaptazap(item, conversaBase))
            return item;

          return normalizarConversaCaptazap({
            ...item,
            chamado_id: chamada.id || item.chamado_id || chamadoId,
            id_chamado: chamada.id || item.id_chamado || chamadoId,
            lead_contexto_id: chamada.lead_contexto_id || leadContextoId,
            leadContextoId: chamada.lead_contexto_id || leadContextoId,
            lead_id: item.lead_id || chamada.lead_id || leadContextoId,
            leadId: item.leadId || chamada.lead_id || leadContextoId,
            lead: leadSelecionada || item.lead,
          });
        };

        setConversaSelecionada((atual) => aplicarRetorno(atual));
        setConversasWhatsApp((atuais) => (atuais || []).map(aplicarRetorno));
        setConversasCriadasLocal((atuais) =>
          (atuais || []).map(aplicarRetorno),
        );
      } catch (error) {
        console.error("❌ CaptaZap alterar contexto da lead:", error);
        window.alert(
          error?.message ||
            "Não foi possível salvar o paciente em contexto agora.",
        );
      } finally {
        setSalvandoContextoLead(false);
      }
    },
    [empresaId],
  );

  const handleSelecionarConversa = useCallback(
    async (conversa) => {
      if (!conversa) return;

      let conversaParaSelecionar = conversa;

      const instanciaSelecionadaAtual = String(
        conexaoSelecionadaRef.current || "",
      ).trim();

      if (
        instanciaSelecionadaAtual &&
        !getConexaoInstanceIdConversaCaptaZap(conversaParaSelecionar)
      ) {
        conversaParaSelecionar = {
          ...conversaParaSelecionar,
          zapi_instance_id: instanciaSelecionadaAtual,
          conexao_instance_id: instanciaSelecionadaAtual,
        };
      }

      if (deveCarregarHistoricoPorTelefoneCaptaZap(conversaParaSelecionar)) {
        const telefoneContato = getTelefoneHistoricoWhatsAppCaptaZap(
          conversaParaSelecionar,
        );
        const atendimentoExistente = encontrarAtendimentoPorTelefoneCaptaZap({
          telefone: telefoneContato,
          listas: conversasBase,
          extras: [
            ...(conversasWhatsApp || []),
            ...(conversasCriadasLocal || []),
          ],
          zapiInstanceId: instanciaSelecionadaAtual,
        });

        if (atendimentoExistente) {
          const responsavelOriginalId =
            getResponsavelConversaId(atendimentoExistente);
          const responsavelOriginalNome =
            getResponsavelConversaNome(atendimentoExistente);
          const leadClicada =
            conversaParaSelecionar.lead ||
            conversaParaSelecionar.lead_dados ||
            conversaParaSelecionar.leadDados ||
            null;
          const leadClicadaId =
            getLeadId(leadClicada) ||
            conversaParaSelecionar.lead_id ||
            conversaParaSelecionar.leadId ||
            conversaParaSelecionar.id_lead ||
            null;
          const leadsMescladas = normalizarLeadsVinculadasCaptazap([
            ...(Array.isArray(atendimentoExistente.leads_vinculadas)
              ? atendimentoExistente.leads_vinculadas
              : []),
            ...(Array.isArray(atendimentoExistente.leadsVinculadas)
              ? atendimentoExistente.leadsVinculadas
              : []),
            ...(Array.isArray(conversaParaSelecionar.leads_vinculadas)
              ? conversaParaSelecionar.leads_vinculadas
              : []),
            ...(Array.isArray(conversaParaSelecionar.leadsVinculadas)
              ? conversaParaSelecionar.leadsVinculadas
              : []),
            leadClicada,
            atendimentoExistente.lead,
          ]);

          const conversaOperacionalId =
            atendimentoExistente.conversa_id ||
            atendimentoExistente.conversaId ||
            atendimentoExistente.id ||
            null;
          const chamadoOperacionalId =
            atendimentoExistente.chamado_id ||
            atendimentoExistente.id_chamado ||
            atendimentoExistente.chamada_id ||
            null;
          const leadOperacionalId =
            atendimentoExistente.lead_id ||
            atendimentoExistente.leadId ||
            atendimentoExistente.lead?.id ||
            null;
          const mensagensAtendimento = Array.isArray(
            atendimentoExistente.mensagens,
          )
            ? atendimentoExistente.mensagens
            : [];
          const mensagensContato = Array.isArray(
            conversaParaSelecionar.mensagens,
          )
            ? conversaParaSelecionar.mensagens
            : [];
          const telefoneOperacional =
            getTelefoneHistoricoWhatsAppCaptaZap(atendimentoExistente) ||
            telefoneContato;

          conversaParaSelecionar = normalizarConversaCaptazap({
            // Dados do contato primeiro; dados operacionais depois.
            // Isso evita que o card da aba Contatos sobrescreva mensagens,
            // chamado_id, conversa_id e status que vieram de Minhas/Fila.
            ...conversaParaSelecionar,
            ...atendimentoExistente,
            id: conversaOperacionalId || conversaParaSelecionar.id,
            conversa_id:
              conversaOperacionalId || conversaParaSelecionar.conversa_id,
            chamado_id:
              chamadoOperacionalId || conversaParaSelecionar.chamado_id,
            id_chamado:
              chamadoOperacionalId || conversaParaSelecionar.id_chamado,
            nome:
              leadClicada?.nome_lead ||
              leadClicada?.nome ||
              conversaParaSelecionar.nome ||
              atendimentoExistente.nome ||
              atendimentoExistente.numero ||
              atendimentoExistente.telefone,
            lead:
              leadClicada ||
              conversaParaSelecionar.lead ||
              atendimentoExistente.lead,
            leads_vinculadas: leadsMescladas,
            leadsVinculadas: leadsMescladas,

            // lead_id/leadContextoId continuam apontando para o paciente clicado,
            // mas guardamos a lead operacional do chamado em campo separado para
            // carregar o histórico corretamente.
            lead_id:
              leadClicadaId ||
              conversaParaSelecionar.lead_id ||
              atendimentoExistente.lead_id,
            leadId:
              leadClicadaId ||
              conversaParaSelecionar.leadId ||
              atendimentoExistente.leadId,
            lead_contexto_id:
              leadClicadaId ||
              conversaParaSelecionar.lead_contexto_id ||
              conversaParaSelecionar.leadContextoId ||
              atendimentoExistente.lead_contexto_id ||
              atendimentoExistente.leadContextoId ||
              null,
            leadContextoId:
              leadClicadaId ||
              conversaParaSelecionar.leadContextoId ||
              conversaParaSelecionar.lead_contexto_id ||
              atendimentoExistente.leadContextoId ||
              atendimentoExistente.lead_contexto_id ||
              null,
            numero: telefoneOperacional || conversaParaSelecionar.numero,
            telefone: telefoneOperacional || conversaParaSelecionar.telefone,
            mensagens: mensagensAtendimento.length
              ? mensagensAtendimento
              : mensagensContato,
            conversa_ativa: true,
            conversaAtiva: true,
            sem_conversa: false,
            semConversa: false,
            _captazap_origem_contatos: true,
            _contato_crm_consulta: true,
            _captazap_contato_com_chamado_existente: true,
            _forcar_historico_por_telefone: true,
            _captazap_conversa_operacional_id: conversaOperacionalId,
            _captazap_chamado_operacional_id: chamadoOperacionalId,
            _captazap_chamado_lead_id: leadOperacionalId,
            _captazap_lead_operacional_id: leadOperacionalId,
            _captazap_telefone_historico: telefoneOperacional,
            _captazap_responsavel_original_id: responsavelOriginalId || null,
            _captazap_responsavel_original_nome: responsavelOriginalNome || "",
          });
        }
      }

      if (deveCarregarHistoricoPorTelefoneCaptaZap(conversaParaSelecionar)) {
        conversaParaSelecionar = {
          ...conversaParaSelecionar,
          conversa_ativa: true,
          conversaAtiva: true,
          sem_conversa: false,
          semConversa: false,
          _carregando_mensagens: true,
          _erro_carregar_mensagens: "",
          _captazap_telefone_historico:
            conversaParaSelecionar._captazap_telefone_historico ||
            getTelefoneHistoricoWhatsAppCaptaZap(conversaParaSelecionar),
        };
      }

      // A conversa selecionada pode vir da busca/Contatos e ser uma cópia mais
      // antiga do chamado. Reaplica o estado atual da Dra. Malu antes de abrir
      // o ChatPanel para não exibir por engano a antiga régua de Lead Fria.
      const chamadoSelecionado = String(
        conversaParaSelecionar?.chamado_id ||
          conversaParaSelecionar?.chamadoId ||
          conversaParaSelecionar?.id_chamado ||
          "",
      );
      const telefoneSelecionado = normalizarTelefone(
        conversaParaSelecionar?.telefone ||
          conversaParaSelecionar?.numero ||
          conversaParaSelecionar?.whatsapp ||
          "",
      ).slice(-11);

      const draMaluAtivoSelecionado = (
        Array.isArray(draMaluAtivosEnriquecidos)
          ? draMaluAtivosEnriquecidos
          : []
      ).find((item) => {
        const chamadoAtivo = String(
          item?.chamado_id || item?.chamadoId || item?.id_chamado || "",
        );
        if (
          chamadoSelecionado &&
          chamadoAtivo &&
          chamadoSelecionado === chamadoAtivo
        ) {
          return true;
        }

        const telefoneAtivo = normalizarTelefone(
          item?.telefone || item?.numero || item?.telefone_norm || "",
        ).slice(-11);

        return Boolean(
          telefoneSelecionado &&
            telefoneAtivo &&
            telefoneSelecionado === telefoneAtivo,
        );
      });

      if (draMaluAtivoSelecionado) {
        conversaParaSelecionar = {
          ...conversaParaSelecionar,
          _dra_malu_em_recuperacao: true,
          dra_malu_em_recuperacao: true,
          _dra_malu_virtual_followup: true,
          origem_automacao: "dra_malu_ia",
          dra_malu_atendimento_id:
            draMaluAtivoSelecionado.atendimento_id ||
            draMaluAtivoSelecionado.id ||
            conversaParaSelecionar.dra_malu_atendimento_id ||
            null,
          dra_malu_status:
            draMaluAtivoSelecionado.dra_malu_status ||
            conversaParaSelecionar.dra_malu_status ||
            null,
          dra_malu_etapa: Number(
            draMaluAtivoSelecionado.dra_malu_etapa ||
              draMaluAtivoSelecionado.followup_ordem ||
              conversaParaSelecionar.dra_malu_etapa ||
              0,
          ),
          dra_malu_total_etapas: Number(
            draMaluAtivoSelecionado.dra_malu_total_etapas ||
              conversaParaSelecionar.dra_malu_total_etapas ||
              0,
          ),
          dra_malu_etapa_titulo:
            draMaluAtivoSelecionado.dra_malu_etapa_titulo ||
            draMaluAtivoSelecionado.titulo ||
            conversaParaSelecionar.dra_malu_etapa_titulo ||
            "Recuperação automática",
          dra_malu_entrou_em:
            draMaluAtivoSelecionado.criado_em ||
            conversaParaSelecionar.dra_malu_entrou_em ||
            null,
          dra_malu_captador_origem_id:
            draMaluAtivoSelecionado.captador_id ||
            draMaluAtivoSelecionado.responsavel_id ||
            conversaParaSelecionar.dra_malu_captador_origem_id ||
            null,
          dra_malu_captador_origem_nome:
            draMaluAtivoSelecionado.captador_nome ||
            draMaluAtivoSelecionado.responsavel_nome ||
            conversaParaSelecionar.dra_malu_captador_origem_nome ||
            "",
          precisa_responder: false,
          precisaResponder: false,
        };
      }

      if (conversaParaSelecionar?._follow_up_pendente) {
        setConversaSelecionada(
          normalizarConversaCaptazap(conversaParaSelecionar),
        );
        return;
      }

      // Chamado fechado, mesmo com nova resposta do paciente, NÃO reabre ao clicar.
      // Ele apenas abre em modo consulta/bloqueado. O operador precisa clicar
      // explicitamente em "Reabrir chamado" para liberar o envio.
      // Chamado fechado sem nova resposta continua somente consulta.
      if (getStatusChamadoVisual(conversaParaSelecionar) === "fechado") {
        setConversaSelecionada(
          normalizarConversaCaptazap(conversaParaSelecionar),
        );
        return;
      }

      // Abrir/selecionar uma conversa agora é somente visualização.
      // Não assume, não reabre e não transfere responsável automaticamente.
      // A mudança de responsável só pode acontecer por ação explícita:
      // botão "Assumir", botão "Reabrir chamado", transferência ou envio permitido.
      setConversaSelecionada(
        normalizarConversaCaptazap(conversaParaSelecionar),
      );
      return;
    },
    [
      conversasBase,
      conversasCriadasLocal,
      conversasWhatsApp,
      draMaluAtivosEnriquecidos,
      empresaId,
      usuarioIdLogado,
      usuarioNomeLogado,
      usuarios,
      usuarioPodeVerEquipeCaptaZap,
    ],
  );

  const handleVisualizarConversaSupervisao = useCallback(
    (conversa, options = {}) => {
      if (!conversa) return;

      const opcoesSupervisao = {
        ...(conversa?._captazap_supervisao_options || {}),
        ...(options || {}),
      };

      const leadIdBusca = String(
        opcoesSupervisao.leadId ||
          opcoesSupervisao.lead_id ||
          opcoesSupervisao.lead_contexto_id ||
          opcoesSupervisao.leadContextoId ||
          getLeadContextoIdConversa(conversa) ||
          conversa.lead_contexto_id ||
          conversa.leadContextoId ||
          conversa.lead_id ||
          conversa.leadId ||
          conversa.lead?.id ||
          "",
      );
      const telefoneBusca = normalizarTelefone(
        opcoesSupervisao.telefone ||
          conversa.telefone ||
          conversa.numero ||
          conversa.phone ||
          conversa.lead?.telefone1 ||
          conversa.lead?.telefone ||
          conversa.lead?.celular ||
          "",
      );
      const telefoneBuscaCurto = telefoneBusca.slice(-11) || telefoneBusca;
      const chamadoIdBusca = String(
        conversa.chamado_id ||
          conversa.chamadoId ||
          conversa.id_chamado ||
          conversa.ticket_id ||
          conversa.conversa_id ||
          conversa.conversaId ||
          "",
      );

      const candidatosOperacionais = [
        ...(conversasBase.minhasConversas || []),
        ...(conversasBase.minhasEquipeConversas || []),
        ...(conversasBase.filaDeEspera || []),
        ...(conversasBase.followUp || []),
        ...(conversasBase.fechados || []),
        ...(conversasBase.contatos || []),
      ].filter(Boolean);

      const conversaOperacionalSupervisao = candidatosOperacionais.find(
        (item) => {
          const chamadoCandidato = String(
            item.chamado_id ||
              item.chamadoId ||
              item.id_chamado ||
              item.ticket_id ||
              item.conversa_id ||
              item.conversaId ||
              "",
          );
          if (
            chamadoIdBusca &&
            chamadoCandidato &&
            chamadoCandidato === chamadoIdBusca
          ) {
            return true;
          }

          const leadCandidato = String(
            getLeadContextoIdConversa(item) ||
              item.lead_contexto_id ||
              item.leadContextoId ||
              item.lead_id ||
              item.leadId ||
              item.lead?.id ||
              "",
          );
          if (leadIdBusca && leadCandidato && leadCandidato === leadIdBusca) {
            return true;
          }

          const telefoneCandidato = normalizarTelefone(
            item.telefone ||
              item.numero ||
              item.phone ||
              item.whatsapp ||
              item.lead?.telefone1 ||
              item.lead?.telefone ||
              item.lead?.celular ||
              "",
          );

          return Boolean(
            telefoneBuscaCurto &&
            telefoneCandidato &&
            telefoneCandidato.endsWith(telefoneBuscaCurto),
          );
        },
      );

      const conversaBaseSupervisao = conversaOperacionalSupervisao
        ? {
            ...conversa,
            ...conversaOperacionalSupervisao,
            lead: {
              ...(conversa.lead || {}),
              ...(conversaOperacionalSupervisao.lead || {}),
            },
            ultimo_agendamento:
              conversa.ultimo_agendamento ||
              conversaOperacionalSupervisao.ultimo_agendamento,
            _ultimo_agendamento:
              conversa._ultimo_agendamento ||
              conversaOperacionalSupervisao._ultimo_agendamento,
            _supervisao_caso_original: conversa,
          }
        : conversa;

      const responsavelId = getResponsavelConversaId(conversaBaseSupervisao);
      const responsavelNome = getResponsavelConversaNome(
        conversaBaseSupervisao,
      );
      const leadIdSupervisao =
        opcoesSupervisao.leadId ||
        opcoesSupervisao.lead_id ||
        opcoesSupervisao.lead_contexto_id ||
        opcoesSupervisao.leadContextoId ||
        getLeadContextoIdConversa(conversaBaseSupervisao) ||
        conversaBaseSupervisao.lead_contexto_id ||
        conversaBaseSupervisao.leadContextoId ||
        conversaBaseSupervisao.lead_id ||
        conversaBaseSupervisao.leadId ||
        conversaBaseSupervisao.lead?.id ||
        null;

      const telefoneSupervisao =
        opcoesSupervisao.telefone ||
        conversaBaseSupervisao.telefone ||
        conversaBaseSupervisao.numero ||
        conversaBaseSupervisao.phone ||
        conversaBaseSupervisao.lead?.telefone1 ||
        conversaBaseSupervisao.lead?.telefone ||
        conversaBaseSupervisao.lead?.celular ||
        "";

      const conversaSupervisao = normalizarConversaCaptazap({
        ...conversaBaseSupervisao,
        lead_contexto_id:
          conversa.lead_contexto_id ||
          conversa.leadContextoId ||
          leadIdSupervisao,
        leadContextoId:
          conversa.leadContextoId ||
          conversa.lead_contexto_id ||
          leadIdSupervisao,
        lead_id: conversa.lead_id || conversa.leadId || leadIdSupervisao,
        leadId: conversa.leadId || conversa.lead_id || leadIdSupervisao,
        telefone: conversa.telefone || telefoneSupervisao,
        numero: conversa.numero || telefoneSupervisao,

        // Modo Supervisão: o usuário logado é apenas visualizador.
        // Ele não assume, não reabre, não transfere e não vira autor das mensagens.
        _captazap_modo_visualizacao: "supervisao",
        _captazap_visualizacao_supervisao: true,
        _somente_visualizacao_supervisao: true,
        somente_visualizacao_supervisao: true,

        // Ações pedidas pela tela de Supervisão. O CaptaZap precisa receber isso
        // para abrir o modal de agendamento/tarefas depois que a conversa for selecionada.
        _captazap_supervisao_options: opcoesSupervisao,
        _captazap_abrir_agendamento: Boolean(opcoesSupervisao.abrirAgendamento),
        _captazap_abrir_followup: Boolean(opcoesSupervisao.abrirFollowup),
        _captazap_abrir_checklist: Boolean(
          opcoesSupervisao.abrirChecklist || opcoesSupervisao.abrirTarefas,
        ),
        _captazap_abrir_interacoes: Boolean(
          opcoesSupervisao.abrirInteracoes ||
          opcoesSupervisao.abrirHistoricoLead ||
          opcoesSupervisao.abrirHistorico,
        ),

        // Identidade do supervisor que abriu a tela para auditoria/UI.
        _captazap_visualizador_id: usuarioIdLogado || null,
        _captazap_visualizador_nome: usuarioNomeLogado || "",

        // Identidade operacional original do atendimento.
        // O ChatPanel/MessageBubble deve usar estes campos para decidir lado/autor,
        // não o supervisor logado.
        _captazap_operador_visualizacao_id: responsavelId || null,
        _captazap_operador_visualizacao_nome: responsavelNome || "",
        _captazap_responsavel_original_id: responsavelId || null,
        _captazap_responsavel_original_nome: responsavelNome || "",
      });

      const deveExecutarAcao = Boolean(
        opcoesSupervisao.abrirAgendamento ||
        opcoesSupervisao.abrirFollowup ||
        opcoesSupervisao.abrirChecklist ||
        opcoesSupervisao.abrirTarefas ||
        opcoesSupervisao.abrirInteracoes ||
        opcoesSupervisao.abrirHistoricoLead ||
        opcoesSupervisao.abrirHistorico,
      );

      setSupervisaoEquipeAberta(false);
      setConversaSelecionada(conversaSupervisao);
      setConversasCriadasLocal((atuais) =>
        upsertConversa(atuais || [], conversaSupervisao),
      );

      if (deveExecutarAcao) {
        setAcaoSupervisaoPendente({
          ...opcoesSupervisao,
          leadId: leadIdSupervisao,
          lead_id: leadIdSupervisao,
          lead_contexto_id: leadIdSupervisao,
          telefone: telefoneSupervisao,
        });
      } else {
        setAcaoSupervisaoPendente(null);
      }
    },
    [conversasBase, usuarioIdLogado, usuarioNomeLogado],
  );

  const handleAbrirNovoOuAssumirChamado = useCallback(
    async (conversa) => {
      if (!conversa) return;

      const chamadoFechado = getStatusChamadoVisual(conversa) === "fechado";
      const contatoCRMConsulta = isContatoCRMConsultaCaptaZap(conversa);
      const conversaAindaSemChamado = !getTemConversaAtiva(conversa);
      const precisaAssumirParaResponder = precisaAssumirParaResponderCaptaZap(
        conversa,
        {
          usuarioId: usuarioIdLogado,
          usuarioNome: usuarioNomeLogado,
        },
      );

      if (!empresaId || !usuarioIdLogado) {
        window.alert(
          chamadoFechado
            ? "Não foi possível identificar o usuário para reabrir este chamado."
            : "Não foi possível identificar o usuário para assumir este chamado.",
        );
        return;
      }

      // MULTICONEXÃO: "Assumir" precisa usar a mesma regra do envio.
      // Antes, uma conversa/contato sem instância podia gerar `tel:...` no
      // /captazap/assumir e, segundos depois, o /enviar descobrir a única
      // conexão e trabalhar com `inst:...:tel:...`, deixando dois chamados.
      const instanciaConversa = getConexaoInstanceIdConversaCaptaZap(conversa);
      const instanciaSelecionada = String(
        conexaoSelecionadaRef.current || "",
      ).trim();
      const instanciaUnica =
        conexoesWhatsApp.length === 1
          ? String(conexoesWhatsApp[0]?.zapi_instance_id || "").trim()
          : "";
      const instanciaParaAssumir =
        instanciaConversa || instanciaSelecionada || instanciaUnica;

      if (!instanciaParaAssumir && conexoesWhatsApp.length > 1) {
        window.alert(
          "Selecione a conexão WhatsApp antes de assumir este atendimento.",
        );
        return;
      }

      const conversaParaAssumir = instanciaParaAssumir
        ? normalizarConversaCaptazap({
            ...conversa,
            zapi_instance_id: instanciaParaAssumir,
            zapiInstanceId: instanciaParaAssumir,
            conexao_instance_id: instanciaParaAssumir,
            conexaoInstanceId: instanciaParaAssumir,
          })
        : conversa;

      // Contato aberto pela aba Contatos/CRM:
      // pode consultar histórico por telefone, mas para responder precisa assumir
      // ou criar o chamado operacional primeiro.
      if (
        !chamadoFechado &&
        (contatoCRMConsulta ||
          conversaAindaSemChamado ||
          precisaAssumirParaResponder)
      ) {
        if (assumirAtendimentoLockRef.current) return;
        assumirAtendimentoLockRef.current = true;
        setAssumindoAtendimento(true);
        try {
          const data = await assumirConversaCaptaZap({
            empresaId,
            conversa: conversaParaAssumir,
            usuarioId: usuarioIdLogado,
            usuarioNome: usuarioNomeLogado,
            acao: "assumir",
          });

          const chamada = data?.chamada || {};
          const conversaAssumida = aplicarResponsavelConversa(
            {
              ...conversaParaAssumir,
              chamado_id: chamada.id || conversa.chamado_id || null,
              id_chamado: chamada.id || conversa.id_chamado || null,
              status: "em_atendimento",
              status_chamado: chamada.status_chamado || "aberto",
              chamado_status: chamada.status_chamado || "aberto",
              chamada_status: chamada.status_chamado || "aberto",
              responsavel_id: chamada.responsavel_id || usuarioIdLogado || null,
              responsavel_nome:
                chamada.responsavel_nome || usuarioNomeLogado || "",
              captador_id: chamada.responsavel_id || usuarioIdLogado || null,
              captador_nome:
                chamada.responsavel_nome || usuarioNomeLogado || "",
              conversa_ativa: true,
              conversaAtiva: true,
              sem_conversa: false,
              semConversa: false,
              aba_captazap: "minhas",
              em_fila: false,
              fila_atendimento: false,
              aguardando_atendimento: false,
              _fila_global: false,
              _fila_equipe: false,
              _fila_resgate: false,
              fila_protecao: false,
              filaProtecao: false,
              _fila_protecao: false,
              fila_protecao_motivo: null,
              filaProtecaoMotivo: null,
              fila_protecao_label: "",
              filaProtecaoLabel: "",
              _captazap_origem_contatos: false,
              _contato_crm_consulta: false,
            },
            {
              usuarioId: usuarioIdLogado,
              usuarioNome: usuarioNomeLogado,
            },
          );

          setConversaSelecionada(conversaAssumida);
          setConversasWhatsApp((atuais) =>
            upsertConversa(atuais, conversaAssumida),
          );
          setConversasCriadasLocal((atuais) =>
            upsertConversa(atuais, conversaAssumida),
          );
          await recarregarMensagensDaConversaAberta(conversaAssumida).catch(
            () => null,
          );
        } catch (error) {
          console.warn(
            "CaptaZap | não foi possível assumir contato CRM:",
            error,
          );
          window.alert(
            error?.message ||
              "Não foi possível assumir este chamado agora. Atualize a fila e tente novamente.",
          );
        } finally {
          assumirAtendimentoLockRef.current = false;
          setAssumindoAtendimento(false);
        }

        return;
      }

      // Conversa normal: abrir/selecionar não assume automaticamente.
      if (!chamadoFechado) {
        handleSelecionarConversa(conversa);
        return;
      }

      // Regra operacional atualizada:
      // chamado FECHADO não mantém bloqueio de posse do responsável antigo.
      // Qualquer captador autenticado pode reabrir e assumir o mesmo chamado.
      // Chamado ABERTO com outro responsável continua protegido nas regras normais
      // de fila/minhas e na rota de assumir.

      if (assumirAtendimentoLockRef.current) return;
      assumirAtendimentoLockRef.current = true;
      setAssumindoAtendimento(true);
      try {
        const data = await assumirConversaCaptaZap({
          empresaId,
          conversa: conversaParaAssumir,
          usuarioId: usuarioIdLogado,
          usuarioNome: usuarioNomeLogado,
          acao: "reabrir_chamado",
        });

        const chamada = data?.chamada || {};
        const agora = new Date().toISOString();
        const tagsSemFechado = (conversa.tags || []).filter(
          (tag) => normalizar(tag) !== "chamado fechado",
        );

        const conversaReaberta = aplicarResponsavelConversa(
          {
            ...conversaParaAssumir,
            chamado_id:
              chamada.id || conversa.chamado_id || conversa.id_chamado || null,
            id_chamado:
              chamada.id || conversa.id_chamado || conversa.chamado_id || null,

            // Regra definitiva:
            // não cria novo chamado. Reabre o MESMO chamado fechado quando um
            // operador decide retomar o atendimento, com ou sem nova mensagem do contato.
            novo_chamado: false,
            novoChamado: false,
            criar_novo_chamado: false,
            chamado_anterior_id: null,
            chamado_origem_id: null,

            status: "em_atendimento",
            status_chamado: chamada.status_chamado || "aberto",
            chamado_status: chamada.status_chamado || "aberto",
            chamada_status: chamada.status_chamado || "aberto",

            conversa_ativa: true,
            conversaAtiva: true,
            sem_conversa: false,
            semConversa: false,

            fila_atendimento: false,
            aguardando_atendimento: false,
            em_fila: false,
            _fila_global: false,
            _fila_equipe: false,
            _fila_resgate: false,
            fila_protecao: false,
            filaProtecao: false,
            _fila_protecao: false,
            fila_protecao_motivo: null,
            filaProtecaoMotivo: null,
            fila_protecao_label: "",
            filaProtecaoLabel: "",
            aba_captazap: "minhas",

            responsavel_id: chamada.responsavel_id || usuarioIdLogado || null,
            responsavel_nome:
              chamada.responsavel_nome || usuarioNomeLogado || "",
            captador_id: chamada.responsavel_id || usuarioIdLogado || null,
            captador_nome: chamada.responsavel_nome || usuarioNomeLogado || "",

            // Limpa TODOS os aliases de fechamento. O status visual consulta
            // mais de um nome de campo; deixar um alias antigo fazia a tela
            // continuar classificando o chamado como fechado após a API abrir.
            fechado_em: null,
            fechadoEm: null,
            data_fechamento: null,
            dataFechamento: null,
            closed_at: null,
            closedAt: null,
            fechado_por_id: null,
            fechado_por_nome: "",
            motivo_fechamento: "",
            motivoFechamento: "",
            observacao_fechamento: "",
            observacaoFechamento: "",

            aberto_em: chamada.aberto_em || agora,
            abertoEm: chamada.aberto_em || chamada.abertoEm || agora,
            opened_at: chamada.aberto_em || chamada.abertoEm || agora,
            openedAt: chamada.aberto_em || chamada.abertoEm || agora,
            aberto_por_id: chamada.aberto_por_id || usuarioIdLogado || null,
            aberto_por_nome: chamada.aberto_por_nome || usuarioNomeLogado || "",

            // A resposta desta API já é a fonte autoritativa do chamado.
            _captazap_status_chamado_reconciliado: true,
            _captazap_status_chamado_reconciliado_em: agora,

            cliente_respondeu_apos_fechamento: false,
            respondeu_apos_fechamento: false,
            nova_mensagem_apos_fechamento: false,
            novaRespostaAposFechamento: false,
            _fechado_com_resposta: false,
            reabertura_pendente: false,
            reaberturaPendente: false,

            tags: Array.from(
              new Set([
                ...tagsSemFechado,
                "Chamado reaberto",
                "Em atendimento",
              ]),
            ),

            timeline: [
              {
                tipo: "Chamado reaberto",
                descricao: `Chamado reaberto por ${
                  usuarioNomeLogado || "usuário"
                } após nova mensagem do paciente.`,
                data: "Agora",
              },
              ...(Array.isArray(conversa.timeline) ? conversa.timeline : []),
            ],
          },
          {
            usuarioId: usuarioIdLogado,
            usuarioNome: usuarioNomeLogado,
          },
        );

        setConversaSelecionada(conversaReaberta);

        setConversasWhatsApp((atuais) =>
          upsertConversa(atuais, conversaReaberta),
        );

        setConversasCriadasLocal((atuais) =>
          upsertConversa(atuais, conversaReaberta),
        );

        agendarRecarregarConversasCaptazapRealtime();
      } catch (error) {
        console.warn(
          "CaptaZap | backend não confirmou reabertura do chamado:",
          error,
        );
        window.alert(
          error?.message ||
            "Não foi possível reabrir este chamado agora. Atualize a fila e tente novamente.",
        );
      } finally {
        assumirAtendimentoLockRef.current = false;
        setAssumindoAtendimento(false);
      }
    },
    [
      agendarRecarregarConversasCaptazapRealtime,
      empresaId,
      handleSelecionarConversa,
      conexoesWhatsApp,
      usuarioIdLogado,
      usuarioNomeLogado,
    ],
  );

  const handleSolicitarFechamentoChamado = useCallback((conversa) => {
    if (!conversa) return;

    setBloqueioFechamentoChamado(null);
    setConversaFechamentoChamado(conversa);
    setModalFecharChamadoAberto(true);
  }, []);

  const handleFecharChamado = useCallback(
    async (
      conversa,
      motivo = "Atendimento concluído",
      { observacao = "", tipo_fechamento = "" } = {},
    ) => {
      if (!empresaId || !conversa) return;

      const leadId =
        getLeadContextoIdConversa(conversa) ||
        conversa.lead_id ||
        conversa.leadId ||
        conversa.lead?.id;
      const telefone = conversa.numero || conversa.telefone || "";

      if (!validarContextoPacienteConversa(conversa, "fechar o chamado")) {
        return false;
      }

      if (!leadId && !telefone) {
        window.alert("Não foi possível identificar o chamado para fechar.");
        return;
      }

      try {
        setBloqueioFechamentoChamado(null);

        const fechadoEmLocal = new Date().toISOString();

        const data = await requestJson("/whatsapp/captazap/fechar", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            empresa_id: empresaId,
            zapi_instance_id:
              getConexaoInstanceIdConversaCaptaZap(conversa) || null,
            lead_id: leadId || null,
            lead_contexto_id:
              getLeadContextoIdConversa(conversa) || leadId || null,
            telefone,
            usuario_id: usuarioIdLogado,
            usuario_nome: usuarioNomeLogado,
            motivo,
            motivo_fechamento:
              tipo_fechamento === "followup"
                ? "Fechamento de follow-up"
                : motivo,
            observacao,
            tipo_fechamento,
            fechado_em: fechadoEmLocal,
          }),
        });

        const chamada = data?.chamada || {};
        const fechamentoFollowup =
          String(tipo_fechamento || "").toLowerCase() === "followup" ||
          normalizar(motivo).includes("fechamento de follow") ||
          normalizar(chamada.motivo_fechamento).includes(
            "fechamento de follow",
          );

        const conversaFechada = {
          ...conversa,
          status: "fechado",
          status_chamado: "fechado",
          chamado_status: "fechado",
          chamada_status: "fechado",
          chamado_id: chamada.id || conversa.chamado_id || null,
          fechado_em: chamada.fechado_em || chamada.fechadoEm || fechadoEmLocal,
          fechadoEm: chamada.fechadoEm || chamada.fechado_em || fechadoEmLocal,
          data_fechamento:
            chamada.data_fechamento ||
            chamada.fechado_em ||
            chamada.fechadoEm ||
            fechadoEmLocal,
          dataFechamento:
            chamada.dataFechamento ||
            chamada.fechadoEm ||
            chamada.fechado_em ||
            fechadoEmLocal,
          fechado_por_id: chamada.fechado_por_id || usuarioIdLogado,
          fechado_por_nome: chamada.fechado_por_nome || usuarioNomeLogado,
          motivo_fechamento: fechamentoFollowup
            ? "Fechamento de follow-up"
            : chamada.motivo_fechamento || motivo,
          motivoFechamento: fechamentoFollowup
            ? "Fechamento de follow-up"
            : chamada.motivo_fechamento || motivo,
          observacao_fechamento:
            chamada.observacao_fechamento ||
            observacao ||
            conversa.observacao_fechamento ||
            "",
          observacaoFechamento:
            chamada.observacao_fechamento ||
            observacao ||
            conversa.observacaoFechamento ||
            "",
          _followup_fechado:
            fechamentoFollowup || conversa._followup_fechado || false,
          _fechamento_followup:
            fechamentoFollowup || conversa._fechamento_followup || false,
          fechamento_followup:
            fechamentoFollowup || conversa.fechamento_followup || false,
          fechamentoFollowup:
            fechamentoFollowup || conversa.fechamentoFollowup || false,

          // Chamado fechado some das listas operacionais.
          conversa_ativa: false,
          conversaAtiva: false,
          sem_conversa: true,
          semConversa: true,
          fila_atendimento: false,
          aguardando_atendimento: false,
          em_fila: false,
          _fila_global: false,
          _fila_equipe: false,
          _fila_resgate: false,
          aba_captazap: "fechados",
          naoLidas: 0,

          // Importante:
          // Se o operador fechou o chamado conscientemente, limpamos qualquer
          // marcação antiga de "cliente respondeu após fechamento".
          // Caso contrário, o chamado fechado pode continuar aparecendo na Fila
          // mesmo tendo sido fechado depois da última mensagem do cliente.
          cliente_respondeu_apos_fechamento: false,
          respondeu_apos_fechamento: false,
          nova_mensagem_apos_fechamento: false,
          novaRespostaAposFechamento: false,
          _fechado_com_resposta: false,

          tags: Array.from(
            new Set(
              [
                ...(conversa.tags || []),
                "Chamado fechado",
                fechamentoFollowup ? "Follow-up fechado" : "",
              ].filter(Boolean),
            ),
          ),
          timeline: [
            {
              tipo: fechamentoFollowup
                ? "Follow-up fechado"
                : "Chamado fechado",
              descricao: fechamentoFollowup
                ? `Follow-up fechado por ${
                    usuarioNomeLogado || "usuário"
                  } em ${formatDataHoraCaptaZap(fechadoEmLocal)}. Justificativa: ${
                    observacao || motivo
                  }.`
                : `Chamado fechado por ${
                    usuarioNomeLogado || "usuário"
                  } em ${formatDataHoraCaptaZap(fechadoEmLocal)}. Motivo: ${motivo}.`,
              data: formatDataHoraCaptaZap(fechadoEmLocal),
            },
            ...(Array.isArray(conversa.timeline) ? conversa.timeline : []),
          ],
        };

        setConversaSelecionada(null);

        // Mantém a conversa fechada no estado para aparecer em "Fechados/Histórico",
        // mas ela não entra mais em Minhas/Fila porque getTemConversaAtiva retorna false.
        setConversasWhatsApp((atuais) =>
          upsertConversa(atuais, conversaFechada),
        );

        setConversasCriadasLocal((atuais) =>
          upsertConversa(atuais, conversaFechada),
        );

        agendarRecarregarConversasCaptazapRealtime();

        return true;
      } catch (error) {
        console.error("❌ CaptaZap fechar chamado:", error);

        const data = error?.data || {};
        const code = error?.code || data?.code || data?.codigo || "";
        const fechamentoBloqueado =
          code === "CAPTAZAP_FECHAMENTO_BLOQUEADO_LEAD_AGENDADA";

        if (fechamentoBloqueado) {
          setBloqueioFechamentoChamado({
            mensagem:
              data?.error ||
              error?.message ||
              "Este chamado possui agendamento. Para fechar, registre uma interação e uma tarefa futura na lead.",
            precisa_interacao: Boolean(data?.precisa_interacao),
            precisa_tarefa_futura: Boolean(data?.precisa_tarefa_futura),
            detalhes: data?.detalhes || null,
          });

          return false;
        }

        window.alert(
          error?.message || "Não foi possível fechar o chamado agora.",
        );

        return false;
      }
    },
    [
      agendarRecarregarConversasCaptazapRealtime,
      empresaId,
      usuarioIdLogado,
      usuarioNomeLogado,
    ],
  );

  const handleConfirmarFechamentoChamado = useCallback(
    async ({ motivo, observacao } = {}) => {
      const conversa =
        conversaFechamentoChamado || conversaSelecionadaRef.current;

      if (!conversa || fechamentoChamadoLockRef.current) return;

      const ultimaFoiPaciente = Boolean(
        conversa?.precisa_responder === true ||
          conversa?.precisa_responder === 1 ||
          conversa?.precisaResponder === true ||
          conversa?.precisaResponder === 1 ||
          String(
            conversa?.ultima_mensagem_direcao ||
              conversa?.ultimaMensagemDirecao ||
              conversa?.ultimaDirecao ||
              "",
          )
            .toLowerCase()
            .includes("recebid"),
      );

      if (ultimaFoiPaciente) {
        const confirmou = window.confirm(
          "A última mensagem foi enviada pelo paciente. Confirma que o atendimento está concluído e não precisa de resposta?",
        );
        if (!confirmou) return;
      }

      fechamentoChamadoLockRef.current = true;
      setFechandoChamado(true);

      const followupAtivo = temFollowupAtivoParaFechamentoCaptaZap(
        conversa,
        followupsChamadosEmpresa,
      );
      const motivoFinal = followupAtivo
        ? "Fechamento de follow-up"
        : String(motivo || "Atendimento concluído").trim();
      const observacaoFinal = String(observacao || "").trim();
      const tipoFechamentoFinal = followupAtivo ? "followup" : "";

      if (followupAtivo && observacaoFinal.length < 30) {
        window.alert(
          "Informe uma justificativa com pelo menos 30 caracteres para fechar um chamado em follow-up.",
        );
        fechamentoChamadoLockRef.current = false;
        setFechandoChamado(false);
        return;
      }

      try {
        const fechado = await handleFecharChamado(conversa, motivoFinal, {
          observacao: observacaoFinal,
          tipo_fechamento: tipoFechamentoFinal,
        });

        if (!fechado) return;

        setModalFecharChamadoAberto(false);
        setConversaFechamentoChamado(null);
        setBloqueioFechamentoChamado(null);
      } finally {
        fechamentoChamadoLockRef.current = false;
        setFechandoChamado(false);
      }
    },
    [conversaFechamentoChamado, followupsChamadosEmpresa, handleFecharChamado],
  );

  const handleAbrirNovoContato = useCallback((dadosPrevios = {}) => {
    setNovoContatoForm({
      nome: dadosPrevios.nome || "",
      telefone: dadosPrevios.telefone || "",
      mensagem: dadosPrevios.mensagem || "",
    });
    setErroNovoContato("");
    setModalNovoContatoAberto(true);
  }, []);

  // Chegou aqui vindo do Funil Comercial (rota separada, sem acesso ao
  // estado vivo de conversas)? Lê os parâmetros da URL e abre/inicia a
  // conversa pedida, depois limpa a URL pra não repetir se a página for
  // recarregada.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const telefone = params.get("captazap_telefone");
    const nome = params.get("captazap_nome");
    const modo = params.get("captazap_modo");
    const leadId = params.get("captazap_lead_id");

    if (!telefone && !modo) return;

    if (modo === "iniciar") {
      handleAbrirNovoContato({ nome: nome || "", telefone: telefone || "" });
    } else if (modo === "ver" && telefone) {
      const responsavelId = params.get("captazap_responsavel_id");
      const leadJaEhMinha =
        responsavelId && String(responsavelId) === String(usuarioIdLogado);

      const conversaAlvo = {
        telefone,
        numero: telefone,
        nome: nome || telefone,
        lead_id: leadId || null,
        empresa_id: empresaId,
        _forcar_historico_por_telefone: true,
      };

      if (leadJaEhMinha) {
        // Lead já é do usuário logado — pode assumir automaticamente,
        // não é "roubar" o atendimento de ninguém.
        handleAbrirNovoOuAssumirChamado(conversaAlvo);
      } else {
        // Lead de outro responsável (ou sem responsável definido) — só
        // abre a conversa para consulta/histórico. O usuário decide se
        // quer assumir clicando manualmente no botão "Assumir chamado".
        handleSelecionarConversa(conversaAlvo);
      }
    }

    // Limpa os parâmetros da URL sem recarregar a página
    navigate("/captazap", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleConfirmarNovoContato = useCallback(
    async (formData = {}) => {
      if (!empresaId) {
        setErroNovoContato("Selecione uma empresa antes de iniciar contato.");
        return false;
      }

      const nome = String(formData.nome || "").trim();
      const telefoneRaw = String(formData.telefone || "").trim();
      const texto = String(formData.mensagem || "").trim();
      const classificacaoDraMalu = [
        "padrao",
        "parceiro_fornecedor",
        "nao_automatizar",
      ].includes(String(formData.dra_malu_classificacao || "padrao"))
        ? String(formData.dra_malu_classificacao || "padrao")
        : "padrao";
      const telefoneWhatsApp = normalizarTelefoneWhatsappBrasil(telefoneRaw);

      if (
        !telefoneWhatsApp ||
        normalizarTelefone(telefoneWhatsApp).length < 12
      ) {
        setErroNovoContato(
          "Informe um WhatsApp válido com DDD. Exemplo: 61999999999.",
        );
        return false;
      }

      const usuarioId = getUsuarioId(usuario);
      const usuarioNome = getNomeUsuarioSistema(usuario);
      const agora = new Date().toISOString();

      setSalvandoNovoContato(true);
      setErroNovoContato("");

      try {
        const contatoSalvoResp = await requestJson(
          "/whatsapp/captazap/contatos",
          {
            method: "POST",
            body: JSON.stringify({
              empresa_id: empresaId,
              telefone: telefoneWhatsApp,
              nome,
              dra_malu_classificacao: classificacaoDraMalu,
              usuario_id: usuarioId || null,
              usuario_nome: usuarioNome || "",
            }),
          },
        );

        const contatoSalvo =
          contatoSalvoResp?.contato || contatoSalvoResp?.data || null;

        if (!texto) {
          const contatoSemMensagem = contatoAvulsoToConversaCaptaZap({
            contato: {
              ...(contatoSalvo || {}),
              empresa_id: contatoSalvo?.empresa_id || empresaId,
              telefone:
                contatoSalvo?.telefone ||
                contatoSalvo?.telefone_normalizado ||
                telefoneWhatsApp,
              telefone_normalizado:
                contatoSalvo?.telefone_normalizado || telefoneWhatsApp,
              nome:
                contatoSalvo?.nome ||
                contatoSalvo?.nome_whatsapp ||
                nome ||
                "",
              contato_nome: contatoSalvo?.nome || "",
              nome_whatsapp: contatoSalvo?.nome_whatsapp || "",
              dra_malu_classificacao:
                contatoSalvo?.dra_malu_classificacao || classificacaoDraMalu,
              criado_em: contatoSalvo?.criado_em || agora,
              atualizado_em: contatoSalvo?.atualizado_em || agora,
            },
            empresaId,
            empresaNome,
          });

          setContatosAvulsos((prev) =>
            upsertConversa(prev || [], contatoSemMensagem),
          );
          setConversaSelecionada(contatoSemMensagem);
          conversaSelecionadaRef.current = contatoSemMensagem;
          setModalNovoContatoAberto(false);
          setNovoContatoForm({
            nome: "",
            telefone: "",
            mensagem: "",
            dra_malu_classificacao: "padrao",
          });
          agendarRecarregarConversasCaptazapRealtime();
          return true;
        }

        const instanciaNovoContato = String(
          conexaoSelecionadaRef.current ||
            (conexoesWhatsApp.length === 1
              ? conexoesWhatsApp[0]?.zapi_instance_id || ""
              : ""),
        ).trim();

        if (!instanciaNovoContato) {
          window.alert(
            "Selecione uma conexão WhatsApp antes de iniciar uma nova conversa.",
          );
          return false;
        }

        const mensagemCriada = await requestJson("/whatsapp/enviar", {
          method: "POST",
          body: JSON.stringify({
            empresa_id: empresaId,
            zapi_instance_id: instanciaNovoContato || null,
            telefone: telefoneWhatsApp,
            texto,
            mensagem: texto,
            autor: "operador",
            status: "enviado",
            direcao: "enviada",
            lead_id: null,
            origem: "novo_contato",
            contato_id: contatoSalvo?.id || null,
            contato_nome: contatoSalvo?.nome || nome || "",
            dra_malu_classificacao:
              contatoSalvo?.dra_malu_classificacao || classificacaoDraMalu,
            usuario_id: usuarioId || null,
            usuario_nome: usuarioNome || "",
          }),
        });

        let chamada = null;

        if (usuarioId) {
          const assumirResp = await requestJson("/whatsapp/captazap/assumir", {
            method: "POST",
            body: JSON.stringify({
              empresa_id: empresaId,
              zapi_instance_id: instanciaNovoContato || null,
              telefone: telefoneWhatsApp,
              usuario_id: usuarioId,
              usuario_nome: usuarioNome,
              acao: "assumir_por_envio",
            }),
          }).catch((error) => {
            console.warn(
              "CaptaZap | contato iniciado, mas assumir chamado falhou:",
              error,
            );
            return null;
          });

          chamada = assumirResp?.chamada || null;
        }

        const mensagemNormalizada = {
          ...(mensagemCriada?.mensagem || mensagemCriada || {}),
          id:
            mensagemCriada?.id ||
            mensagemCriada?.mensagem?.id ||
            `local-contato-msg-${Date.now()}`,
          empresa_id: empresaId,
          zapi_instance_id: instanciaNovoContato || null,
          zapiInstanceId: instanciaNovoContato || null,
          telefone: telefoneWhatsApp,
          numero: telefoneWhatsApp,
          texto,
          mensagem: texto,
          autor: "operador",
          de: "operador",
          direcao: "enviada",
          fromMe: true,
          datahora: mensagemCriada?.datahora || agora,
          status: mensagemCriada?.status || "enviado",
          canal: "whatsapp",
        };

        const novaConversa = normalizarConversaCaptazap({
          id:
            chamada?.id ||
            chamada?.chamado_id ||
            `contato-${normalizarTelefone(telefoneWhatsApp)}`,
          conversa_id:
            chamada?.id ||
            chamada?.chamado_id ||
            `contato-${normalizarTelefone(telefoneWhatsApp)}`,
          chamado_id: chamada?.id || chamada?.chamado_id || null,
          empresa_id: empresaId,
          zapi_instance_id: instanciaNovoContato || null,
          zapiInstanceId: instanciaNovoContato || null,
          empresa_nome: empresaNome,
          nome: nome || telefoneWhatsApp,
          contato_id: contatoSalvo?.id || null,
          contatoId: contatoSalvo?.id || null,
          contato_nome: contatoSalvo?.nome || nome || "",
          contatoNome: contatoSalvo?.nome || nome || "",
          nome_whatsapp: contatoSalvo?.nome_whatsapp || "",
          nomeWhatsapp: contatoSalvo?.nome_whatsapp || "",
          dra_malu_classificacao:
            contatoSalvo?.dra_malu_classificacao || classificacaoDraMalu,
          telefone: telefoneWhatsApp,
          numero: telefoneWhatsApp,
          canal: "whatsapp",
          lead_id: null,
          lead: null,
          status: "aberto",
          status_chamado: chamada?.status_chamado || "aberto",
          responsavel_id: chamada?.responsavel_id || usuarioId || null,
          responsavel_nome: chamada?.responsavel_nome || usuarioNome || "",
          captador_id: chamada?.responsavel_id || usuarioId || null,
          captador_nome: chamada?.responsavel_nome || usuarioNome || "",
          aberto_em: chamada?.aberto_em || agora,
          ultima_mensagem_id:
            chamada?.ultima_mensagem_id || mensagemNormalizada.id || null,
          ultima_mensagem_em:
            chamada?.ultima_mensagem_em ||
            mensagemNormalizada.datahora ||
            agora,
          ultima_mensagem_direcao: "enviada",
          preview: texto,
          mensagem: texto,
          conversa_ativa: true,
          sem_conversa: false,
          mensagens: [mensagemNormalizada],
          _captazap_contato_avulso: true,
          _captazap_origem_novo_contato: true,
          _captazap_badge_laranja: true,
          badge_tipo_cadastro: CAPTAZAP_SOMENTE_CONTATO_BADGE,
          tipo_cadastro_visual: "contato",
          timeline: [
            {
              tipo: "Novo contato",
              descricao: "Contato iniciado pelo CaptaZap sem criar lead.",
              data: "Agora",
            },
          ],
          tags: [CAPTAZAP_SOMENTE_CONTATO_TAG, "CaptaZap"],
        });

        setConversasCriadasLocal((prev) =>
          upsertConversa(prev || [], novaConversa),
        );
        setConversasWhatsApp((prev) =>
          upsertConversa(prev || [], novaConversa),
        );
        setContatosAvulsos((prev) =>
          upsertConversa(prev || [], {
            ...novaConversa,
            id:
              contatoSalvo?.id ||
              `contato-${normalizarTelefone(telefoneWhatsApp)}`,
            conversa_id:
              chamada?.id ||
              chamada?.chamado_id ||
              novaConversa.conversa_id,
            _captazap_origem_contatos: true,
            _captazap_contexto_fixo_contato: true,
          }),
        );
        setConversaSelecionada(novaConversa);
        conversaSelecionadaRef.current = novaConversa;
        setModalNovoContatoAberto(false);
        setNovoContatoForm({
          nome: "",
          telefone: "",
          mensagem: "",
        });
        agendarRecarregarConversasCaptazapRealtime();
        return true;
      } catch (error) {
        console.error("❌ CaptaZap novo contato:", error);
        setErroNovoContato(
          error?.message ||
            "Não foi possível cadastrar este contato agora. Verifique os dados e tente novamente.",
        );
        return false;
      } finally {
        setSalvandoNovoContato(false);
      }
    },
    [
      agendarRecarregarConversasCaptazapRealtime,
      conexoesWhatsApp,
      empresaId,
      empresaNome,
      usuario,
    ],
  );

  const handleAbrirCadastroNomeContato = useCallback((conversa) => {
    if (!conversa) return;

    setConversaNomeContato(conversa);
    setNomeContatoForm(getNomeContatoCadastradoCaptaZap(conversa));
    setClassificacaoContatoForm(
      String(conversa?.dra_malu_classificacao || "padrao"),
    );
    setErroNomeContato("");
    setModalNomeContatoAberto(true);
  }, []);

  const handleConfirmarNomeContato = useCallback(async () => {
    const conversa = conversaNomeContato || conversaSelecionadaRef.current;
    const telefoneRaw = conversa?.numero || conversa?.telefone || "";
    const telefone = normalizarTelefoneWhatsappBrasil(telefoneRaw);
    const nome = String(nomeContatoForm || "").trim();

    if (!empresaId || !telefone) {
      setErroNomeContato(
        "Não foi possível identificar a empresa e o telefone deste contato.",
      );
      return false;
    }

    if (nome.length < 2) {
      setErroNomeContato("Informe o nome do contato com pelo menos 2 caracteres.");
      return false;
    }

    if (nome.length > 160) {
      setErroNomeContato("O nome do contato pode ter no máximo 160 caracteres.");
      return false;
    }

    const usuarioId = getUsuarioId(usuario);
    const usuarioNome = getNomeUsuarioSistema(usuario);

    setSalvandoNomeContato(true);
    setErroNomeContato("");

    try {
      const resposta = await requestJson(
        "/whatsapp/captazap/contatos/nome",
        {
          method: "POST",
          body: JSON.stringify({
            empresa_id: empresaId,
            telefone,
            nome,
            dra_malu_classificacao: classificacaoContatoForm,
            usuario_id: usuarioId || null,
            usuario_nome: usuarioNome || "",
          }),
        },
      );

      const contato = resposta?.contato || resposta?.data || {};
      const telefoneAlvo = normalizarTelefone(
        contato?.telefone_normalizado || contato?.telefone || telefone,
      );

      const aplicarNome = (item) => {
        if (!item) return item;

        const telefoneItem = normalizarTelefone(
          item?.numero || item?.telefone || item?.telefone_normalizado || "",
        );

        if (!telefoneItem || telefoneItem !== telefoneAlvo) return item;

        return {
          ...item,
          nome,
          contato_nome: nome,
          contatoNome: nome,
          nome_whatsapp: contato?.nome_whatsapp || item?.nome_whatsapp || "",
          nomeWhatsapp: contato?.nome_whatsapp || item?.nomeWhatsapp || "",
          dra_malu_classificacao:
            contato?.dra_malu_classificacao || classificacaoContatoForm,
          contato_id: contato?.id || item?.contato_id || null,
          contatoId: contato?.id || item?.contatoId || null,
          _captazap_contato_avulso: true,
          _captazap_badge_laranja: true,
          badge_tipo_cadastro: CAPTAZAP_SOMENTE_CONTATO_BADGE,
          tipo_cadastro_visual: "contato",
          tags: Array.from(
            new Set([
              ...(Array.isArray(item?.tags) ? item.tags : []),
              CAPTAZAP_SOMENTE_CONTATO_TAG,
              "CaptaZap",
            ]),
          ),
        };
      };

      setConversasWhatsApp((prev) => (prev || []).map(aplicarNome));
      setConversasCriadasLocal((prev) => (prev || []).map(aplicarNome));
      setContatosAvulsos((prev) => {
        const atuais = prev || [];
        const encontrou = atuais.some((item) => {
          const telefoneItem = normalizarTelefone(
            item?.numero || item?.telefone || item?.telefone_normalizado || "",
          );
          return telefoneItem && telefoneItem === telefoneAlvo;
        });

        if (encontrou) return atuais.map(aplicarNome);

        return upsertConversa(
          atuais,
          contatoAvulsoToConversaCaptaZap({
            contato: {
              ...contato,
              nome,
              telefone,
              telefone_normalizado: telefone,
            },
            empresaId,
            empresaNome,
          }),
        );
      });
      setConversaSelecionada((atual) => {
        const atualizado = aplicarNome(atual);
        conversaSelecionadaRef.current = atualizado;
        return atualizado;
      });

      setModalNomeContatoAberto(false);
      setConversaNomeContato(null);
      setNomeContatoForm("");
      agendarRecarregarConversasCaptazapRealtime();
      return true;
    } catch (error) {
      console.error("❌ CaptaZap cadastro de nome do contato:", error);
      setErroNomeContato(
        error?.message || "Não foi possível salvar o nome do contato agora.",
      );
      return false;
    } finally {
      setSalvandoNomeContato(false);
    }
  }, [
    agendarRecarregarConversasCaptazapRealtime,
    conversaNomeContato,
    empresaId,
    empresaNome,
    nomeContatoForm,
    usuario,
  ]);

  const handleEnviarMensagem = async (
    entrada,
    conversaParam = null,
    context = {},
  ) => {
    const payloadMensagem =
      entrada && typeof entrada === "object"
        ? entrada
        : {
            texto: entrada,
          };

    const texto = String(
      payloadMensagem.texto ||
        payloadMensagem.mensagem ||
        payloadMensagem.message ||
        payloadMensagem.body ||
        "",
    ).trim();

    const midiaUrlEnvio = String(
      payloadMensagem.midia_url ||
        payloadMensagem.mediaUrl ||
        payloadMensagem.arquivo_url ||
        "",
    ).trim();
    const tipoMidiaEnvio = String(
      payloadMensagem.tipo_midia || payloadMensagem.tipoMidia || "",
    ).trim();
    const mensagemRapidaIdEnvio =
      payloadMensagem.mensagem_rapida_id || context.mensagem_rapida_id || null;
    const respostaMsgid =
      context.resposta_msgid ||
      context.reply_message_id ||
      payloadMensagem.resposta_msgid ||
      payloadMensagem.reply_message_id ||
      null;
    const respostaTexto = String(
      context.resposta_texto || payloadMensagem.resposta_texto || "",
    ).trim();
    const respostaTipo = String(
      context.resposta_tipo || payloadMensagem.resposta_tipo || "",
    ).trim();
    const respostaAutor = String(
      context.resposta_autor || payloadMensagem.resposta_autor || "",
    ).trim();

    let conversaBase =
      conversaParam ||
      context.conversa ||
      context.conversaSelecionada ||
      conversaSelecionadaRef.current;

    const empresaContextId =
      context.empresaId ||
      context.empresa_id ||
      payloadMensagem.empresa_id ||
      payloadMensagem.empresaId ||
      empresaId;

    if (conversaBase) {
      const contextoEnvioPersistidoBruto =
        context.lead_contexto_id ||
        context.leadContextoId ||
        context.leadId ||
        context.lead_id ||
        payloadMensagem.lead_contexto_id ||
        payloadMensagem.leadContextoId ||
        payloadMensagem.leadId ||
        payloadMensagem.lead_id ||
        getLeadContextoIdConversa(conversaBase) ||
        obterContextoPacienteCaptaZap(conversaBase, empresaContextId);

      const contextoEnvioPersistido = getLeadVinculadaSeguraPorIdCaptaZap(
        conversaBase,
        contextoEnvioPersistidoBruto,
      )
        ? contextoEnvioPersistidoBruto
        : getLeadContextoSeguroConversaCaptaZap(conversaBase);

      if (contextoEnvioPersistido) {
        salvarContextoPacienteCaptaZap(
          conversaBase,
          contextoEnvioPersistido,
          empresaContextId,
        );

        conversaBase = normalizarConversaCaptazap({
          ...conversaBase,
          lead_contexto_id: contextoEnvioPersistido,
          leadContextoId: contextoEnvioPersistido,
          contexto_lead_id: contextoEnvioPersistido,
          contextoLeadId: contextoEnvioPersistido,
        });
      } else {
        conversaBase = aplicarContextoPacientePersistidoCaptaZap(
          conversaBase,
          empresaContextId,
        );
      }
    }

    if (!empresaContextId || !conversaBase?.id || !texto) {
      console.warn("CaptaZap | envio bloqueado por dados incompletos", {
        temEmpresa: Boolean(empresaContextId),
        temConversa: Boolean(conversaBase?.id),
        temTexto: Boolean(texto),
        origem: context.origem || payloadMensagem.origem || "manual",
      });
      return;
    }

    const conversaEmpresaId = conversaBase.empresa_id || conversaBase.empresaId;

    if (
      conversaEmpresaId &&
      String(conversaEmpresaId) !== String(empresaContextId)
    ) {
      console.warn("CaptaZap | bloqueado envio por divergência de empresa", {
        empresaAtiva: empresaContextId,
        empresaConversa: conversaEmpresaId,
        conversaId: conversaBase.id,
      });
      return;
    }

    // MULTICONEXÃO: responder sempre pela mesma instância que originou
    // o atendimento. A aba "Todas" é apenas um filtro visual e nunca pode
    // escolher silenciosamente outra conexão para o envio.
    const instanciaEnvio = String(
      context.zapi_instance_id ||
        context.zapiInstanceId ||
        payloadMensagem.zapi_instance_id ||
        payloadMensagem.zapiInstanceId ||
        getConexaoInstanceIdConversaCaptaZap(conversaBase) ||
        (conexoesWhatsApp.length === 1
          ? conexoesWhatsApp[0]?.zapi_instance_id || ""
          : ""),
    ).trim();

    if (!instanciaEnvio && conexoesWhatsApp.length > 1) {
      console.error(
        "CaptaZap | envio bloqueado: conversa sem conexão definida em ambiente multiconexão",
        {
          conversaId: conversaBase?.id || null,
          chamadoId:
            conversaBase?.chamado_id || conversaBase?.id_chamado || null,
          telefone: conversaBase?.telefone || conversaBase?.numero || null,
        },
      );
      window.alert(
        "Não foi possível identificar por qual conexão este atendimento chegou. O envio foi bloqueado para evitar responder pelo número errado. Reabra o chamado pela lista antes de responder.",
      );
      return;
    }

    if (instanciaEnvio) {
      conversaBase = normalizarConversaCaptazap({
        ...conversaBase,
        zapi_instance_id: instanciaEnvio,
        zapiInstanceId: instanciaEnvio,
        conexao_instance_id: instanciaEnvio,
        conexaoInstanceId: instanciaEnvio,
      });
    }

    if (
      precisaAssumirParaResponderCaptaZap(conversaBase, {
        usuarioId: usuarioIdLogado,
        usuarioNome: usuarioNomeLogado,
      })
    ) {
      window.alert(
        "Para responder, primeiro clique em Assumir. Ao assumir, você passa a ser o responsável por este chamado.",
      );
      return;
    }

    if (!validarContextoPacienteConversa(conversaBase, "enviar mensagem")) {
      return;
    }

    const origemEnvio = String(
      context.origem || payloadMensagem.origem || "manual",
    ).trim();
    const envioMensagemRapidaManual =
      origemEnvio === "mensagem_rapida_manual" ||
      origemEnvio === "mensagem_rapida";

    const conversaAtiva = getTemConversaAtiva(conversaBase)
      ? conversaBase
      : criarConversaLocalAPartirDeLead({
          conversa: conversaBase,
          empresaId: empresaContextId,
          empresaNome,
        });

    // Neste ponto a posse já foi validada: o operador só envia se o chamado
    // estiver explicitamente sob sua responsabilidade. Assumir é uma ação
    // separada e nunca acontece silenciosamente por causa do envio.
    const conversaAtendimento = aplicarResponsavelConversa(conversaAtiva, {
      usuarioId: usuarioIdLogado,
      usuarioNome: usuarioNomeLogado,
    });

    const agoraEnvio = new Date().toISOString();

    const mensagemOperador = {
      id: `local-msg-${Date.now()}`,
      empresa_id: empresaContextId,
      zapi_instance_id: instanciaEnvio || null,
      zapiInstanceId: instanciaEnvio || null,
      usuario_id: usuarioIdLogado || null,
      usuario_nome: usuarioNomeLogado || "",
      operador_id: usuarioIdLogado || null,
      operador_nome: usuarioNomeLogado || "",
      de: "operador",
      autor: "operador",
      direcao: "enviada",
      fromMe: true,
      texto,
      mensagem: texto,
      datahora: agoraEnvio,
      hora: new Date(agoraEnvio).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      status: "enviando",
      origem: origemEnvio,
      resposta_msgid: respostaMsgid,
      resposta_texto: respostaTexto || null,
      resposta_tipo: respostaTipo || null,
      resposta_autor: respostaAutor || null,
      _local: true,
    };

    const followUpExecutadoLocal = {};

    const conversaAtualizada = {
      ...conversaAtendimento,
      conversa_ativa: true,
      conversaAtiva: true,
      sem_conversa: false,
      semConversa: false,
      status: "em_atendimento",
      status_chamado: "aberto",
      chamado_status: "aberto",
      chamada_status: "aberto",
      preview: texto,
      ultima_mensagem: texto,
      ultimaMensagem: texto,
      direcao: "enviada",
      ultima_direcao: "enviada",
      ultimaDirecao: "enviada",
      hora: "Agora",
      naoLidas: 0,
      mensagens: mesclarMensagensConversa(
        conversaAtendimento.mensagens || [],
        mensagemOperador,
      ),
      ...followUpExecutadoLocal,
    };

    setConversasCriadasLocal((prev) =>
      upsertConversa(prev, conversaAtualizada),
    );
    setConversasWhatsApp((prev) => upsertConversa(prev, conversaAtualizada));
    setConversaSelecionada(conversaAtualizada);

    const telefoneEnvio =
      context.telefone ||
      payloadMensagem.telefone ||
      payloadMensagem.numero ||
      conversaAtendimento.numero ||
      conversaAtendimento.telefone;
    const leadIdEnvioBruto =
      context.leadId ||
      context.lead_id ||
      context.lead_contexto_id ||
      payloadMensagem.leadId ||
      payloadMensagem.lead_id ||
      payloadMensagem.lead_contexto_id ||
      getLeadContextoIdConversa(conversaAtendimento) ||
      null;

    const leadIdEnvio = getLeadVinculadaSeguraPorIdCaptaZap(
      conversaAtendimento,
      leadIdEnvioBruto,
    )
      ? leadIdEnvioBruto
      : getLeadContextoSeguroConversaCaptaZap(conversaAtendimento);

    const leadContextoIdEnvioBruto =
      context.lead_contexto_id ||
      payloadMensagem.lead_contexto_id ||
      getLeadContextoIdConversa(conversaAtendimento) ||
      leadIdEnvio ||
      null;

    const leadContextoIdEnvio = getLeadVinculadaSeguraPorIdCaptaZap(
      conversaAtendimento,
      leadContextoIdEnvioBruto,
    )
      ? leadContextoIdEnvioBruto
      : leadIdEnvio || null;

    try {
      // v66: se a conversa veio da Fila (normal ou Proteção), confirma a posse
      // ANTES do envio. O estado visual já foi atualizado de forma otimista
      // acima, então não há atraso para o operador; o await apenas elimina a
      // corrida entre /assumir e /enviar no backend. Se a confirmação falhar,
      // o envio continua e o backend de /enviar faz a reconciliação de posse.
      const precisaConfirmarPosseAntesEnvio = Boolean(
        isFilaProtecaoCaptaZap(conversaBase) ||
          isConversaNaFilaDeAtendimento(conversaBase, {
            usuarioId: usuarioIdLogado,
            usuarioNome: usuarioNomeLogado,
          }) ||
          !(getResponsavelConversaId(conversaBase) ||
            getResponsavelConversaNome(conversaBase)),
      );

      if (precisaConfirmarPosseAntesEnvio) {
        await assumirConversaCaptaZap({
          empresaId: empresaContextId,
          conversa: conversaBase,
          usuarioId: usuarioIdLogado,
          usuarioNome: usuarioNomeLogado,
          acao: "assumir_por_envio",
        }).catch((error) => {
          console.warn(
            "CaptaZap | posse não confirmou antes do envio; /enviar fará a reconciliação:",
            error,
          );
          return null;
        });
      }

      const enviadaResp = await requestJson("/whatsapp/enviar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          telefone: telefoneEnvio,
          zapi_instance_id:
            instanciaEnvio ||
            getConexaoInstanceIdConversaCaptaZap(conversaAtendimento) ||
            null,
          canal:
            conversaAtendimento?.canal ||
            payloadMensagem?.canal ||
            (String(
              instanciaEnvio ||
                getConexaoInstanceIdConversaCaptaZap(conversaAtendimento) ||
                "",
            )
              .toLowerCase()
              .startsWith("instagram:")
              ? "instagram"
              : "whatsapp"),
          external_user_id:
            conversaAtendimento?.external_user_id ||
            conversaAtendimento?.externalUserId ||
            payloadMensagem?.external_user_id ||
            payloadMensagem?.externalUserId ||
            null,
          external_conversation_id:
            conversaAtendimento?.external_conversation_id ||
            conversaAtendimento?.externalConversationId ||
            payloadMensagem?.external_conversation_id ||
            payloadMensagem?.externalConversationId ||
            null,
          external_account_id:
            conversaAtendimento?.external_account_id ||
            conversaAtendimento?.externalAccountId ||
            payloadMensagem?.external_account_id ||
            payloadMensagem?.externalAccountId ||
            null,
          texto,
          mensagem: texto,
          autor: "operador",
          de: "operador",
          lead_id: leadIdEnvio,
          lead_contexto_id: leadContextoIdEnvio,
          status: "enviado",
          direcao: "enviada",
          empresa_id: empresaContextId,
          usuario_id: usuarioIdLogado || null,
          usuario_nome: usuarioNomeLogado || "",
          operador_id: usuarioIdLogado || null,
          operador_nome: usuarioNomeLogado || "",
          origem: envioMensagemRapidaManual
            ? "mensagem_rapida_manual"
            : origemEnvio,
          origem_envio: envioMensagemRapidaManual
            ? "mensagem_rapida_manual"
            : origemEnvio,
          mensagem_rapida_manual: envioMensagemRapidaManual ? 1 : 0,
          aplicar_followup: envioMensagemRapidaManual ? 0 : undefined,
          aplicar_automacao: envioMensagemRapidaManual ? 0 : undefined,
          resposta_msgid: respostaMsgid,
          resposta_texto: respostaTexto || null,
          resposta_tipo: respostaTipo || null,
          resposta_autor: respostaAutor || null,
          ...(midiaUrlEnvio
            ? {
                midia_url: midiaUrlEnvio,
                tipo_midia: tipoMidiaEnvio || "imagem",
                mensagem_rapida_id: mensagemRapidaIdEnvio,
              }
            : {}),
        }),
      });

      const enviada =
        enviadaResp?.mensagem ||
        enviadaResp?.whatsapp ||
        enviadaResp?.data?.mensagem ||
        enviadaResp?.data ||
        enviadaResp;

      const mensagemSalvaNormalizada = normalizarMensagemCaptazap({
        ...enviada,
        telefone: enviada?.telefone || telefoneEnvio,
        texto: enviada?.texto || enviada?.mensagem || texto,
        mensagem: enviada?.mensagem || enviada?.texto || texto,
        empresa_id: enviada?.empresa_id || empresaContextId,
        zapi_instance_id:
          getConexaoInstanceIdConversaCaptaZap(enviada) || instanciaEnvio || null,
        zapiInstanceId:
          getConexaoInstanceIdConversaCaptaZap(enviada) || instanciaEnvio || null,
        lead_id: enviada?.lead_id ?? leadIdEnvio ?? null,
        lead_contexto_id:
          enviada?.lead_contexto_id ?? leadContextoIdEnvio ?? null,
        direcao: enviada?.direcao || "enviada",
        autor: enviada?.autor || "operador",
        de: enviada?.de || "operador",
        fromMe: true,
        usuario_id: enviada?.usuario_id || usuarioIdLogado || null,
        usuario_nome: enviada?.usuario_nome || usuarioNomeLogado || "",
        operador_id: enviada?.operador_id || usuarioIdLogado || null,
        operador_nome: enviada?.operador_nome || usuarioNomeLogado || "",
        datahora: enviada?.datahora || enviada?.created_at || agoraEnvio,
        resposta_msgid: enviada?.resposta_msgid || respostaMsgid,
        resposta_texto: enviada?.resposta_texto || respostaTexto || null,
        resposta_tipo: enviada?.resposta_tipo || respostaTipo || null,
        resposta_autor: enviada?.resposta_autor || respostaAutor || null,
      });

      let mensagemSalva = {
        ...mensagemSalvaNormalizada,
        id:
          mensagemSalvaNormalizada.id ||
          mensagemSalvaNormalizada.whatsapp_id ||
          mensagemSalvaNormalizada.msgid ||
          mensagemOperador.id,
        texto: mensagemSalvaNormalizada.texto || texto,
        mensagem: mensagemSalvaNormalizada.mensagem || texto,
        status: mensagemSalvaNormalizada.status || enviada?.status || "enviado",
        direcao: mensagemSalvaNormalizada.direcao || "enviada",
        autor: mensagemSalvaNormalizada.autor || "operador",
        de: mensagemSalvaNormalizada.de || "operador",
        fromMe: true,
      };

      // O DeliveryCallback pode chegar milissegundos antes da resposta deste
      // POST. Nesse caso o SSE já foi recebido quando ainda existia apenas a
      // bolha local `local-msg-*`. Reaplicamos aqui o status pendente usando
      // qualquer identificador persistido, sem depender de outro callback/F5.
      const idsMensagemSalva = [
        mensagemSalva?.id,
        mensagemSalva?.whatsapp_id,
        mensagemSalva?.msgid,
        mensagemSalva?.messageId,
        mensagemSalva?.message_id,
        mensagemSalva?.zaapId,
        mensagemSalva?.wa_message_id,
      ]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean);

      let statusPendente = null;
      for (const idStatus of idsMensagemSalva) {
        const encontrado = statusMensagensPendentesRef.current.get(idStatus);
        if (encontrado) {
          statusPendente = encontrado;
          break;
        }
      }

      if (statusPendente?.status) {
        mensagemSalva = {
          ...mensagemSalva,
          status: statusPendente.status,
          ...(Object.prototype.hasOwnProperty.call(statusPendente, "erro")
            ? {
                erro: statusPendente.erro,
                error: statusPendente.erro,
              }
            : {}),
        };

        idsMensagemSalva.forEach((idStatus) =>
          statusMensagensPendentesRef.current.delete(idStatus),
        );
      }

      if (
        conversaAtendimento?._followup_chamado_id &&
        !envioMensagemRapidaManual
      ) {
        try {
          const followupResp = await requestJson(
            `/api/followup-whatsapp/${conversaAtendimento._followup_chamado_id}/realizar`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                usuario_id: usuarioIdLogado || null,
                resultado: "mensagem_enviada",
                observacao: "Follow-up realizado pelo CaptaZap.",
              }),
            },
          );

          setFollowupsChamados((atuais) =>
            (atuais || []).filter(
              (item) =>
                String(item?._followup_chamado_id || item?.id) !==
                String(conversaAtendimento._followup_chamado_id),
            ),
          );

          window.dispatchEvent(
            new CustomEvent("captazap:followup_executado", {
              detail: {
                followup_id: conversaAtendimento._followup_chamado_id,
                empresa_id: empresaContextId,
                telefone: telefoneEnvio,
                resultado: "mensagem_enviada",
                proxima_etapa: followupResp?.proxima_etapa || null,
              },
            }),
          );

          // Evita recarregar CRM completo no caminho quente do envio.
          // A etapa já foi removida localmente e a próxima sincronização leve
          // atualiza a fila sem travar o chat.
        } catch (error) {
          console.warn(
            "CaptaZap | follow-up enviado, mas não foi marcado como realizado:",
            error,
          );
        }
      }

      const aplicarMensagemSalva = (atual) => {
        if (!atual) return atual;

        const atualizado = aplicarResponsavelConversa(atual, {
          usuarioId: usuarioIdLogado,
          usuarioNome: usuarioNomeLogado,
        });

        const mensagensSemLocal = (atualizado.mensagens || []).filter(
          (msg) => msg.id !== mensagemOperador.id,
        );

        return {
          ...atualizado,
          ...followUpExecutadoLocal,
          conversa_ativa: true,
          conversaAtiva: true,
          sem_conversa: false,
          semConversa: false,
          preview: texto,
          ultima_mensagem: texto,
          ultimaMensagem: texto,
          direcao: "enviada",
          ultima_direcao: "enviada",
          ultimaDirecao: "enviada",
          hora: "Agora",
          naoLidas: 0,
          mensagens: mesclarMensagensConversa(mensagensSemLocal, mensagemSalva),
        };
      };

      const conversaSalva = aplicarMensagemSalva(conversaAtualizada);

      setConversaSelecionada((atual) => aplicarMensagemSalva(atual));

      setConversasWhatsApp((atuais) =>
        upsertConversa(
          (atuais || []).map((item) =>
            isMesmaConversaCaptazap(item, conversaAtualizada)
              ? aplicarMensagemSalva(item)
              : item,
          ),
          conversaSalva,
        ),
      );

      setConversasCriadasLocal((atuais) =>
        upsertConversa(
          (atuais || []).map((item) =>
            isMesmaConversaCaptazap(item, conversaAtualizada)
              ? aplicarMensagemSalva(item)
              : item,
          ),
          conversaSalva,
        ),
      );

      // Mensageria V2: a resposta do /whatsapp/enviar já contém a mensagem
      // persistida e o SSE mantém o estado em tempo real. Não fazemos um novo
      // GET de histórico após cada envio.
    } catch (err) {
      console.error("❌ CaptaZap enviar mensagem real:", err);

      setConversaSelecionada((atual) => {
        if (!atual) return atual;

        return {
          ...atual,
          mensagens: (atual.mensagens || []).map((msg) =>
            msg.id === mensagemOperador.id ? { ...msg, status: "erro" } : msg,
          ),
        };
      });
    }
  };

  const handleIniciarConversa = async (conversa, context = {}) => {
    const empresaContextId = context.empresaId || empresaId;

    if (!empresaContextId || !conversa) return;

    const telefone = context.telefone || conversa.numero || conversa.telefone;
    const telefoneNormalizado = normalizarTelefone(telefone);

    if (!telefoneNormalizado) {
      console.warn(
        "CaptaZap | não foi possível iniciar conversa sem telefone",
        {
          conversa,
          context,
        },
      );
      return;
    }

    const instanciaSelecionadaAtual = String(
      conexaoSelecionadaRef.current || "",
    ).trim();

    if (!instanciaSelecionadaAtual) {
      window.alert("Selecione uma conexão WhatsApp antes de iniciar a conversa.");
      return;
    }

    const conversaExistente = encontrarConversaAbertaPorTelefone({
      telefone,
      listas: conversasBase,
      zapiInstanceId: instanciaSelecionadaAtual,
    });

    if (conversaExistente) {
      setConversaSelecionada(conversaExistente);
      return;
    }

    const novaConversa = prepararConversaReativadaSeNecessario(
      normalizarConversaCaptazap({
        ...criarConversaLocalAPartirDeLead({
          conversa,
          empresaId: empresaContextId,
          empresaNome,
        }),
        zapi_instance_id: instanciaSelecionadaAtual,
        zapiInstanceId: instanciaSelecionadaAtual,
        conexao_instance_id: instanciaSelecionadaAtual,
        conexaoInstanceId: instanciaSelecionadaAtual,
      }),
    );

    setConversasCriadasLocal((prev) => upsertConversa(prev, novaConversa));
    setConversaSelecionada(novaConversa);

    console.log("CaptaZap | iniciar conversa com lead", {
      empresaId: empresaContextId,
      leadId: context.leadId || conversa.lead_id || conversa.leadId,
      telefone,
      canal: context.canal || conversa.canal || "whatsapp",
    });
  };

  const handleConverterLead = async (conversa, context = {}) => {
    const empresaContextId = context.empresaId || empresaId;

    if (!empresaContextId || !conversa?.id) return;

    const conversaEmpresaId = conversa.empresa_id || conversa.empresaId;

    if (
      conversaEmpresaId &&
      String(conversaEmpresaId) !== String(empresaContextId)
    ) {
      console.warn(
        "CaptaZap | bloqueado converter lead por divergência de empresa",
        {
          empresaAtiva: empresaContextId,
          empresaConversa: conversaEmpresaId,
          conversaId: conversa.id,
        },
      );
      return;
    }

    const telefoneConversaBruto =
      context.telefone ||
      context.numero ||
      conversa.numero ||
      conversa.telefone ||
      conversa.whatsapp ||
      conversa.phone ||
      "";

    const leadExistente = conversa.lead || null;
    const leadExistenteId =
      context.leadId ||
      conversa.lead_id ||
      conversa.leadId ||
      leadExistente?.id;
    const leadExistenteEhDaConversa = leadExistenteId
      ? leadPertenceAoTelefoneCaptaZap(
          leadExistente || {},
          telefoneConversaBruto,
        )
      : false;

    if (leadExistenteId && leadExistenteEhDaConversa) {
      window.alert("Esta conversa já está vinculada a uma lead.");
      return;
    }

    if (leadExistenteId && !leadExistenteEhDaConversa) {
      console.warn("CaptaZap | lead divergente ignorada na conversão", {
        leadExistenteId,
        telefoneConversa: telefoneConversaBruto,
        lead: leadExistente,
      });
    }

    const telefoneLeadOriginal = normalizarTelefoneLeadBrasil(
      telefoneConversaBruto,
    );
    const telefoneWhatsAppOriginal = normalizarTelefoneWhatsappBrasil(
      telefoneConversaBruto,
    );
    const conversaIdOriginal =
      context.conversaId ||
      context.conversa_id ||
      conversa.conversa_id ||
      conversa.conversaId ||
      conversa.id ||
      null;

    if (!telefoneLeadOriginal) {
      window.alert(
        "Não foi possível converter este contato porque ele não tem telefone válido.",
      );
      return;
    }

    if (!telefoneLeadBrasilValido(telefoneLeadOriginal)) {
      window.alert(
        "Telefone inválido na conversa. Use DDD + 9 + número. Exemplo: 61996368800. Se vier com 55, o sistema remove automaticamente.",
      );
      return;
    }

    const conversaSeguraParaConversao = normalizarConversaCaptazap({
      ...conversa,
      lead: leadExistenteEhDaConversa ? leadExistente : null,
      lead_id: leadExistenteEhDaConversa ? leadExistenteId : null,
      leadId: leadExistenteEhDaConversa ? leadExistenteId : null,
      lead_contexto_id: leadExistenteEhDaConversa ? leadExistenteId : null,
      leadContextoId: leadExistenteEhDaConversa ? leadExistenteId : null,
      nome: leadExistenteEhDaConversa
        ? conversa.nome
        : conversa.preview || conversa.ultima_mensagem || telefoneLeadOriginal,
    });

    setConversaConverterLead({
      ...conversaSeguraParaConversao,
      numero: telefoneWhatsAppOriginal || telefoneConversaBruto,
      telefone: telefoneLeadOriginal,
      telefone_normalizado: telefoneLeadOriginal,
      _converter_context: {
        ...context,
        empresaId: empresaContextId,
        conversaId: conversaIdOriginal,
        conversa_id: conversaIdOriginal,
        telefone: telefoneLeadOriginal,
        telefoneLeadOriginal,
        telefoneWhatsApp: telefoneWhatsAppOriginal,
        telefoneWhatsAppOriginal,
        chaveConversao: [
          empresaContextId,
          conversaIdOriginal ||
            `tel:${telefoneWhatsAppOriginal || telefoneLeadOriginal}`,
        ]
          .filter(Boolean)
          .join(":"),
      },
    });
    setModalConverterLeadAberto(true);
  };

  const handleConfirmarConverterLead = async (formData) => {
    const conversa = conversaConverterLead;
    const context = conversa?._converter_context || {};
    const empresaContextId = context.empresaId || empresaId;

    if (!empresaContextId || !conversa) return;

    const telefoneConversaOriginal =
      context.telefoneLeadOriginal ||
      context.telefone ||
      conversa.telefone ||
      conversa.numero ||
      conversa.whatsapp ||
      conversa.phone ||
      "";

    const telefoneLead = normalizarTelefoneLeadBrasil(telefoneConversaOriginal);
    const telefoneWhatsApp = normalizarTelefoneWhatsappBrasil(
      context.telefoneWhatsAppOriginal ||
        context.telefoneWhatsApp ||
        telefoneConversaOriginal,
    );
    const telefoneFormulario = normalizarTelefoneLeadBrasil(
      formData.telefone1 || "",
    );

    if (!telefoneLead) {
      window.alert(
        "Não foi possível converter este contato porque a conversa não tem telefone válido.",
      );
      return;
    }

    if (!telefoneLeadBrasilValido(telefoneLead)) {
      window.alert(
        "Telefone inválido na conversa. Use DDD + 9 + número. Exemplo: 61996368800. Se vier com 55, o sistema remove automaticamente.",
      );
      return;
    }

    // Segurança crítica: o formulário não pode trocar a identidade da conversa.
    // A lead deve nascer do telefone congelado no momento em que o operador
    // clicou em converter. Isso evita reaproveitamento de estado de outro contato.
    if (
      telefoneFormulario &&
      telefoneLead &&
      telefoneFormulario !== telefoneLead
    ) {
      window.alert(
        `Bloqueado por segurança: o telefone do formulário (${telefoneFormulario}) é diferente do telefone da conversa aberta (${telefoneLead}). Feche e abra novamente a conversão.`,
      );
      return;
    }

    const nomeLead =
      String(formData.nome_lead || "").trim() || conversa.nome || telefoneLead;

    setSalvandoConversaoLead(true);

    try {
      const usuarioId = getUsuarioId(usuario);
      const usuarioNome = getNomeUsuarioSistema(usuario);
      const dataLead = getDataLeadHoje();

      const payload = {
        empresa_id: empresaContextId,
        empresaId: empresaContextId,
        id_empresa: empresaContextId,

        data_lead: dataLead,
        data: dataLead,
        data_cadastro: dataLead,
        criado_em: new Date().toISOString(),

        conversa_id: context.conversaId || conversa.conversa_id || conversa.id,
        conversaId: context.conversaId || conversa.conversa_id || conversa.id,

        nome_lead: nomeLead,
        nome: nomeLead,

        telefone: telefoneLead,
        telefone1: telefoneLead,
        celular: telefoneLead,
        whatsapp: telefoneLead,
        numero: telefoneLead,
        telefone_whatsapp: telefoneWhatsApp,
        numero_whatsapp: telefoneWhatsApp,
        telefone_origem_captazap: telefoneLead,
        telefone_whatsapp_origem_captazap: telefoneWhatsApp,

        canal: context.canal || conversa.canal || "whatsapp",
        origem: formData.origem || conversa.origem || "WhatsApp",
        fonte:
          formData.fonte ||
          formData.nome_fonte ||
          formData.origem ||
          conversa.origem ||
          "WhatsApp",

        status_negociacao: "Prospectando",
        status_agendamento: formData.status_agendamento || "Sem agendamento",
        potencial: formData.potencial || "",

        id_procedimento: formData.id_procedimento || null,
        procedimento: formData.procedimento || "",
        nome_procedimento: formData.nome_procedimento || "",

        id_fonte_lead: formData.id_fonte_lead || null,
        id_fonte: formData.id_fonte_lead || null,
        nome_fonte: formData.nome_fonte || formData.fonte || "",

        id_regiao: formData.id_regiao || null,
        regiao: formData.regiao || formData.nome_regiao || "",
        nome_regiao: formData.nome_regiao || formData.regiao || "",

        id_campanha: formData.id_campanha || null,
        campanha: formData.campanha || formData.nome_campanha || "",
        nome_campanha: formData.nome_campanha || formData.campanha || "",

        observacao: formData.observacao || "",
        observacoes: formData.observacao || "",
        anotacao: formData.observacao || "",

        usuario_criador_id: usuarioId,
        captador_id: usuarioId,
        id_usuario: usuarioId,
        usuario_responsavel_id: usuarioId,
        responsavel_id: usuarioId,

        usuario_criador_nome: usuarioNome,
        captador_nome: usuarioNome,
        usuario_nome: usuarioNome,
        responsavel_nome: usuarioNome,

        origem_conversao: "captazap",
        conversa_origem_id:
          context.conversaId ||
          context.conversa_id ||
          conversa.conversa_id ||
          conversa.id,

        tags: ["CaptaZap", "WhatsApp", "Convertido do atendimento"],
      };

      validarPayloadCriacaoLeadCaptaZap(payload);

      console.log("CaptaZap | payload converter lead", payload);

      const response = await criarLeadPeloCaptaZap(payload);

      const leadCriada = normalizarLeadCriadaDoCaptaZap({
        response,
        payload,
        conversa,
        empresaId: empresaContextId,
      });

      const leadId = getLeadId(leadCriada);

      if (!leadId) {
        throw new Error(
          "A lead foi criada, mas o backend não retornou o ID da lead.",
        );
      }

      await vincularConversaLeadCaptaZap({
        empresaId: empresaContextId,
        conversa,
        telefone: telefoneWhatsApp || telefoneLead,
        leadId,
        usuarioId,
        usuarioNome,
      }).catch((error) => {
        console.warn(
          "CaptaZap | lead criada, mas vínculo automático da conversa falhou:",
          error,
        );
      });

      const conversaAtualizada = {
        ...conversa,
        id: conversa.id,
        empresa_id: conversa.empresa_id || empresaContextId,
        lead_id: leadId,
        leadId: leadId,
        lead: leadCriada,
        nome: leadCriada.nome_lead || leadCriada.nome || nomeLead,
        numero: telefoneWhatsApp || telefoneLead,
        telefone: telefoneWhatsApp || telefoneLead,
        telefone_normalizado: telefoneWhatsApp || telefoneLead,
        status: "em_atendimento",
        status_chamado: "aberto",
        chamado_status: "aberto",
        chamada_status: "aberto",
        aba_captazap: "minhas",
        em_fila: false,
        fila_atendimento: false,
        aguardando_atendimento: false,
        _fila_global: false,
        _fila_equipe: false,
        _fila_resgate: false,
        naoLidas: 0,
        status_negociacao: leadCriada.status_negociacao || "Prospectando",
        origem: leadCriada.fonte || leadCriada.origem || payload.origem,
        preview: conversa.preview || "Lead criada pelo CaptaZap",
        conversa_ativa: true,
        sem_conversa: false,
        lead_reativavel: false,
        reativacao_pendente: false,
        // Conversa recém-convertida fica em atendimento com quem converteu.
        captador_id: usuarioId,
        captador_nome: usuarioNome,
        responsavel_id: usuarioId,
        responsavel_nome: usuarioNome,
        usuario_responsavel_id: usuarioId,
        usuario_responsavel_nome: usuarioNome,
        atendente_id: usuarioId,
        atendente_nome: usuarioNome,
        operador_id: usuarioId,
        operador_nome: usuarioNome,
        _captazap_assumida_por: usuarioId || usuarioNome || null,
        _captazap_assumida_por_nome: usuarioNome || "",
        lead_captador_id: usuarioId,
        lead_captador_nome: usuarioNome,
        dono_lead_id: usuarioId,
        dono_lead_nome: usuarioNome,
        _captazap_contato_avulso: false,
        _captazap_badge_laranja: false,
        badge_tipo_cadastro: "Lead CRM",
        tipo_cadastro_visual: "lead",
        tags: Array.from(
          new Set([
            ...(conversa.tags || []).filter(
              (tag) =>
                tag !== CAPTAZAP_SOMENTE_CONTATO_TAG &&
                tag !== CAPTAZAP_SOMENTE_CONTATO_BADGE &&
                tag !== "Contato",
            ),
            "Lead CRM",
            "Convertido do CaptaZap",
          ]),
        ),
        timeline: [
          {
            tipo: "Conversão",
            descricao: "Contato convertido em lead pelo CaptaZap.",
            data: "Agora",
          },
          ...(Array.isArray(conversa.timeline) ? conversa.timeline : []),
        ],
      };

      // Regra crítica do CaptaZap:
      // converter contato em lead NÃO pode devolver a conversa para a Fila.
      // Antes da conversão, o chamado pode estar identificado por telefone.
      // Depois da conversão, ele passa a ser identificado por lead_id.
      // Por isso assumimos novamente usando o lead_id novo antes de recarregar o CRM.
      await assumirConversaCaptaZap({
        empresaId: empresaContextId,
        conversa: conversaAtualizada,
        usuarioId,
        usuarioNome,
        acao: "converter_lead",
      }).catch((error) => {
        console.warn(
          "CaptaZap | lead convertida, mas não foi possível preservar o responsável do chamado:",
          error,
        );
      });

      setLeadsCRM((atuais) => {
        const lista = Array.isArray(atuais) ? atuais : [];
        const semDuplicar = lista.filter(
          (lead) => String(getLeadId(lead)) !== String(leadId),
        );

        return [leadCriada, ...semDuplicar];
      });

      setConversasWhatsApp((atuais) =>
        upsertConversa(atuais, conversaAtualizada),
      );

      setConversasCriadasLocal((atuais) =>
        upsertConversa(atuais, conversaAtualizada),
      );

      setConversaSelecionada(conversaAtualizada);

      setModalConverterLeadAberto(false);
      setConversaConverterLead(null);

      // A lead e a conversa já foram atualizadas no estado local. Apenas
      // reconcilia a fila, sem baixar novamente toda a carteira CRM.
      agendarRecarregarConversasCaptazapRealtime();
    } catch (error) {
      console.error("❌ CaptaZap converter contato em lead:", error);
      window.alert(
        error?.message ||
          "Não foi possível converter este contato em lead agora.",
      );
    } finally {
      setSalvandoConversaoLead(false);
    }
  };

  const handleAgendarTarefa = (conversa, context = {}) => {
    const empresaContextId = context.empresaId || empresaId;

    if (!empresaContextId || !conversa?.id) return;

    const conversaEmpresaId = conversa.empresa_id || conversa.empresaId;

    if (
      conversaEmpresaId &&
      String(conversaEmpresaId) !== String(empresaContextId)
    ) {
      console.warn(
        "CaptaZap | bloqueado abrir agendamento por divergência de empresa",
        {
          empresaAtiva: empresaContextId,
          empresaConversa: conversaEmpresaId,
          conversaId: conversa.id,
        },
      );
      return;
    }

    if (!validarContextoPacienteConversa(conversa, "agendar")) return;

    const leadId =
      context.leadId ||
      context.lead_contexto_id ||
      getLeadContextoIdConversa(conversa) ||
      conversa.lead_id ||
      conversa.leadId ||
      conversa.lead?.id;
    const leadBase =
      getLeadVinculadaPorId(conversa, leadId) ||
      conversa.lead ||
      leadsCRM.find((lead) => String(getLeadId(lead)) === String(leadId));

    if (!leadId || !leadBase) {
      window.alert(
        "Para agendar, primeiro a conversa precisa estar vinculada a uma lead.",
      );
      return;
    }

    setLeadAgendamentoSelecionada({
      ...leadBase,
      id: leadId,
      empresa_id: leadBase.empresa_id || empresaContextId,
      nome_lead: leadBase.nome_lead || leadBase.nome || conversa.nome,
      telefone1:
        leadBase.telefone1 ||
        leadBase.telefone ||
        leadBase.celular ||
        conversa.numero,
    });
    setModalAgendamentoAberto(true);
  };

  const abrirAgendamentoSupervisao = useCallback(
    (conversa, context = {}) => {
      const base = conversa || conversaSelecionadaRef.current || {};
      const empresaContextId =
        context.empresaId ||
        context.empresa_id ||
        base.empresa_id ||
        base.empresaId ||
        empresaId;

      const leadId =
        context.leadId ||
        context.lead_id ||
        context.lead_contexto_id ||
        context.leadContextoId ||
        getLeadContextoIdConversa(base) ||
        base.lead_contexto_id ||
        base.leadContextoId ||
        base.lead_id ||
        base.leadId ||
        base.lead?.id ||
        null;

      const leadBase =
        getLeadVinculadaPorId(base, leadId) ||
        base.lead ||
        leadsCRM.find((lead) => String(getLeadId(lead)) === String(leadId));

      const telefone =
        context.telefone ||
        base.telefone ||
        base.numero ||
        leadBase?.telefone1 ||
        leadBase?.telefone ||
        leadBase?.celular ||
        "";

      const nome =
        leadBase?.nome_lead ||
        leadBase?.nome ||
        base.nome ||
        base.nome_lead ||
        "Paciente";

      if (!leadId && !leadBase) {
        window.alert(
          "Não foi possível abrir os agendamentos porque este chamado não trouxe lead vinculada.",
        );
        return false;
      }

      const ultimoAgendamento =
        context.ultimo_agendamento ||
        base.ultimo_agendamento ||
        base._ultimo_agendamento ||
        leadBase?.ultimo_agendamento ||
        leadBase?._ultimo_agendamento ||
        null;
      const agendamentosLead = [
        ...(Array.isArray(context.agendamentos) ? context.agendamentos : []),
        ...(Array.isArray(base.agendamentos) ? base.agendamentos : []),
        ...(Array.isArray(leadBase?.agendamentos) ? leadBase.agendamentos : []),
        ...(ultimoAgendamento ? [ultimoAgendamento] : []),
      ]
        .filter(Boolean)
        .filter((item, index, arr) => {
          const key = item?.id
            ? `id:${item.id}`
            : `${item?.data_agendamento || ""}|${item?.status_agendamento || ""}`;
          return (
            arr.findIndex((candidate) => {
              const candidateKey = candidate?.id
                ? `id:${candidate.id}`
                : `${candidate?.data_agendamento || ""}|${candidate?.status_agendamento || ""}`;
              return candidateKey === key;
            }) === index
          );
        })
        .sort((a, b) => {
          const ida = Number(a?.id || 0);
          const idb = Number(b?.id || 0);
          if (ida !== idb) return idb - ida;
          return (
            new Date(b?.data_agendamento || b?.criado_em || 0).getTime() -
            new Date(a?.data_agendamento || a?.criado_em || 0).getTime()
          );
        });
      const ultimoAgendamentoFinal =
        agendamentosLead[0] || ultimoAgendamento || null;
      const statusAgendamentoAtual =
        context.status_agendamento ||
        ultimoAgendamentoFinal?.status_agendamento ||
        base.status_agendamento ||
        leadBase?.status_agendamento ||
        "";
      const dataAgendamentoAtual =
        context.data_agendamento ||
        ultimoAgendamentoFinal?.data_agendamento ||
        base.data_agendamento ||
        leadBase?.data_agendamento ||
        "";

      setLeadAgendamentoSelecionada({
        ...(leadBase || {}),
        id: leadId || getLeadId(leadBase),
        empresa_id: leadBase?.empresa_id || empresaContextId,
        nome_lead: nome,
        telefone1: telefone,
        telefone,
        celular: leadBase?.celular || telefone,
        status_agendamento: statusAgendamentoAtual,
        data_agendamento: dataAgendamentoAtual,
        ultimo_agendamento: ultimoAgendamentoFinal,
        _ultimo_agendamento: ultimoAgendamentoFinal,
        agendamentos: agendamentosLead,
        chamado_id:
          base.chamado_id || base.chamadoId || context.chamado_id || null,
        conversa_id:
          base.conversa_id ||
          base.conversaId ||
          base.id ||
          context.conversa_id ||
          null,
        _captazap_visualizacao_supervisao: true,
        _somente_visualizacao_supervisao: true,
      });
      setModalAgendamentoAberto(true);
      return true;
    },
    [empresaId, leadsCRM],
  );

  const atualizarAgendamentoNaLead = (ultimoAgendamento) => {
    if (!ultimoAgendamento) return;

    const leadId = ultimoAgendamento.lead_id || ultimoAgendamento.id_lead;
    if (!leadId) return;

    setAgendamentos((atuais) => {
      const lista = Array.isArray(atuais) ? atuais : [];
      const agendamentoId = ultimoAgendamento.id;

      if (!agendamentoId) return [ultimoAgendamento, ...lista];

      const existe = lista.some(
        (item) => String(item.id) === String(agendamentoId),
      );

      if (existe) {
        return lista.map((item) =>
          String(item.id) === String(agendamentoId) ? ultimoAgendamento : item,
        );
      }

      return [ultimoAgendamento, ...lista];
    });

    setLeadsCRM((atuais) =>
      (atuais || []).map((lead) =>
        String(getLeadId(lead)) === String(leadId)
          ? {
              ...lead,
              _ultimo_agendamento: ultimoAgendamento,
              ultimo_agendamento: ultimoAgendamento,
              status_agendamento:
                ultimoAgendamento.status_agendamento || lead.status_agendamento,
              data_agendamento:
                ultimoAgendamento.data_agendamento || lead.data_agendamento,
            }
          : lead,
      ),
    );

    setConversaSelecionada((atual) => {
      if (!atual) return atual;

      const atualLeadId =
        atual.lead_id || atual.leadId || atual.lead?.id || atual.lead?.lead_id;

      if (String(atualLeadId) !== String(leadId)) return atual;

      return {
        ...atual,
        ultimo_agendamento: ultimoAgendamento,
        _ultimo_agendamento: ultimoAgendamento,
        lead: atual.lead
          ? {
              ...atual.lead,
              _ultimo_agendamento: ultimoAgendamento,
              ultimo_agendamento: ultimoAgendamento,
              status_agendamento:
                ultimoAgendamento.status_agendamento ||
                atual.lead.status_agendamento,
              data_agendamento:
                ultimoAgendamento.data_agendamento ||
                atual.lead.data_agendamento,
            }
          : atual.lead,
      };
    });
  };

  const recarregarAgendamentosCaptaZap = async () => {
    if (!empresaId) return;

    try {
      const data = await requestJson(`/agendamentos/empresa/${empresaId}`);
      setAgendamentos(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      console.error("CaptaZap | erro ao recarregar agendamentos:", err);
    }
  };

  const getLeadDaConversa = (conversa, context = {}) => {
    const leadId =
      context.leadId ||
      context.lead_id ||
      context.lead_contexto_id ||
      context.leadContextoId ||
      getLeadContextoIdConversa(conversa) ||
      conversa?.lead_contexto_id ||
      conversa?.leadContextoId ||
      conversa?.lead_id ||
      conversa?.leadId ||
      conversa?.lead?.id;

    if (
      !leadId &&
      !validarContextoPacienteConversa(conversa, "usar esta ação do CRM")
    ) {
      return null;
    }

    const leadBase =
      getLeadVinculadaPorId(conversa, leadId) ||
      conversa?.lead ||
      context.lead ||
      leadsCRM.find((lead) => String(getLeadId(lead)) === String(leadId)) ||
      {};

    if (!leadId) return null;

    const telefoneContexto =
      context.telefone ||
      conversa?.telefone ||
      conversa?.numero ||
      conversa?.phone ||
      leadBase.telefone1 ||
      leadBase.telefone ||
      leadBase.celular ||
      "";

    const ultimoAgendamento =
      context.ultimo_agendamento ||
      conversa?.ultimo_agendamento ||
      conversa?._ultimo_agendamento ||
      leadBase.ultimo_agendamento ||
      leadBase._ultimo_agendamento ||
      null;
    const agendamentosLead = [
      ...(Array.isArray(context.agendamentos) ? context.agendamentos : []),
      ...(Array.isArray(conversa?.agendamentos) ? conversa.agendamentos : []),
      ...(Array.isArray(leadBase.agendamentos) ? leadBase.agendamentos : []),
      ...(ultimoAgendamento ? [ultimoAgendamento] : []),
    ].filter(Boolean);

    return {
      ...leadBase,
      id: leadBase.id || leadId,
      empresa_id: leadBase.empresa_id || context.empresaId || empresaId,
      nome_lead:
        leadBase.nome_lead ||
        leadBase.nome ||
        context.nome ||
        conversa?.nome ||
        "Paciente",
      telefone1:
        leadBase.telefone1 ||
        leadBase.telefone ||
        leadBase.celular ||
        telefoneContexto,
      telefone: leadBase.telefone || leadBase.telefone1 || telefoneContexto,
      celular: leadBase.celular || telefoneContexto,
      status_agendamento:
        context.status_agendamento ||
        ultimoAgendamento?.status_agendamento ||
        conversa?.status_agendamento ||
        leadBase.status_agendamento ||
        "",
      data_agendamento:
        context.data_agendamento ||
        ultimoAgendamento?.data_agendamento ||
        conversa?.data_agendamento ||
        leadBase.data_agendamento ||
        "",
      ultimo_agendamento: ultimoAgendamento,
      _ultimo_agendamento: ultimoAgendamento,
      agendamentos: agendamentosLead,
      chamado_id:
        conversa?._captazap_chamado_operacional_id ||
        conversa?.chamado_id ||
        conversa?.chamadoId ||
        context.chamado_id ||
        null,
      conversa_id:
        conversa?._captazap_conversa_operacional_id ||
        conversa?.conversa_id ||
        conversa?.conversaId ||
        conversa?.id ||
        context.conversa_id ||
        null,
      telefone_historico:
        conversa?._captazap_telefone_historico ||
        getTelefoneHistoricoWhatsAppCaptaZap(conversa) ||
        telefoneContexto ||
        null,
      _captazap_conversa_operacional_id:
        conversa?._captazap_conversa_operacional_id || null,
      _captazap_chamado_operacional_id:
        conversa?._captazap_chamado_operacional_id || null,
      _captazap_chamado_lead_id: conversa?._captazap_chamado_lead_id || null,
    };
  };

  const handleAbrirTarefasLead = (conversa, context = {}) => {
    const leadSelecionada = getLeadDaConversa(conversa, context);

    if (!leadSelecionada) {
      window.alert(
        "Para gerenciar tarefas, primeiro a conversa precisa estar vinculada a uma lead.",
      );
      return;
    }

    const listaTarefas = (tarefas || []).filter(
      (tarefa) => String(tarefa?.lead_id) === String(leadSelecionada.id),
    );

    setLeadTarefaSelecionada(leadSelecionada);
    setTarefasDaLeadSelecionada(listaTarefas);
    setModalTarefaAberto(true);
  };

  const handleAbrirInteracoesLead = (conversa, context = {}) => {
    const leadSelecionada = getLeadDaConversa(conversa, context);

    if (!leadSelecionada) {
      window.alert(
        "Para ver interações, primeiro a conversa precisa estar vinculada a uma lead.",
      );
      return;
    }

    const listaInteracoes = (interacoes || []).filter(
      (interacao) =>
        String(interacao?.id_lead ?? interacao?.lead_id) ===
        String(leadSelecionada.id),
    );

    setLeadInteracaoSelecionada(leadSelecionada);
    setInteracoesDaLeadSelecionada(listaInteracoes);
    setModalInteracoesAberto(true);
  };

  useEffect(() => {
    if (!acaoSupervisaoPendente || !conversaSelecionada) return;

    const acao = acaoSupervisaoPendente;
    setAcaoSupervisaoPendente(null);

    if (acao.abrirAgendamento) {
      abrirAgendamentoSupervisao(conversaSelecionada, acao);
    }

    if (acao.abrirChecklist || acao.abrirTarefas) {
      handleAbrirTarefasLead(conversaSelecionada, acao);
    }

    if (
      acao.abrirInteracoes ||
      acao.abrirHistoricoLead ||
      acao.abrirHistorico
    ) {
      handleAbrirInteracoesLead(conversaSelecionada, acao);
    }
  }, [acaoSupervisaoPendente, conversaSelecionada, abrirAgendamentoSupervisao]);

  useEffect(() => {
    const onAbrirAgendamentoSupervisao = (event) => {
      const detail = event?.detail || {};
      const conversa = detail.conversa || detail.caso || null;
      if (!conversa) return;

      handleVisualizarConversaSupervisao(conversa, {
        ...(detail.info || {}),
        origem: "supervisao",
        abrirAgendamento: true,
        leadId: detail.lead_id || detail.leadId || null,
        lead_id: detail.lead_id || detail.leadId || null,
        telefone: detail.telefone || "",
        somenteLeitura: true,
        bloqueado: true,
      });
    };

    const onAbrirChamadoSupervisao = (event) => {
      const detail = event?.detail || {};
      const conversa = detail.conversa || detail.caso || null;
      if (!conversa) return;

      handleVisualizarConversaSupervisao(conversa, {
        ...(detail.info || {}),
        origem: "supervisao",
        abrirChamado: true,
        abrirAgendamento: Boolean(detail.abrirAgendamento),
        leadId: detail.lead_id || detail.leadId || null,
        lead_id: detail.lead_id || detail.leadId || null,
        telefone: detail.telefone || conversa.telefone || conversa.numero || "",
        somenteLeitura: true,
        bloqueado: true,
      });
    };

    const onAbrirHistoricoLeadSupervisao = (event) => {
      const detail = event?.detail || {};
      const conversa = detail.conversa || detail.caso || null;
      if (!conversa) return;

      handleVisualizarConversaSupervisao(conversa, {
        ...(detail.info || {}),
        origem: "supervisao",
        modo: "historico_lead",
        abrirHistoricoLead: true,
        abrirInteracoes: true,
        abrirChat: false,
        abrirConversa: false,
        abrirChamado: false,
        leadId:
          detail.lead_id ||
          detail.leadId ||
          conversa.lead_id ||
          conversa.leadId ||
          null,
        lead_id:
          detail.lead_id ||
          detail.leadId ||
          conversa.lead_id ||
          conversa.leadId ||
          null,
        telefone: detail.telefone || conversa.telefone || conversa.numero || "",
        lead: detail.lead || conversa.lead || null,
        somenteLeitura: true,
        bloqueado: true,
      });
    };

    window.addEventListener(
      "captazap:supervisao:abrir-agendamento",
      onAbrirAgendamentoSupervisao,
    );
    window.addEventListener(
      "captazap:supervisao:abrir-chamado",
      onAbrirChamadoSupervisao,
    );
    window.addEventListener(
      "captazap:supervisao:abrir-historico-lead",
      onAbrirHistoricoLeadSupervisao,
    );

    return () => {
      window.removeEventListener(
        "captazap:supervisao:abrir-agendamento",
        onAbrirAgendamentoSupervisao,
      );
      window.removeEventListener(
        "captazap:supervisao:abrir-chamado",
        onAbrirChamadoSupervisao,
      );
      window.removeEventListener(
        "captazap:supervisao:abrir-historico-lead",
        onAbrirHistoricoLeadSupervisao,
      );
    };
  }, [handleVisualizarConversaSupervisao]);

  const recarregarTarefasCaptaZap = async () => {
    if (!empresaId) return;

    try {
      const data = await requestJson(`/todas-tarefas?empresa_id=${empresaId}`);
      const lista = Array.isArray(data) ? data : [];
      setTarefas(lista);

      if (leadTarefaSelecionada?.id) {
        setTarefasDaLeadSelecionada(
          lista.filter(
            (tarefa) =>
              String(tarefa?.lead_id) === String(leadTarefaSelecionada.id),
          ),
        );
      }
    } catch (err) {
      console.error("CaptaZap | erro ao recarregar tarefas:", err);
    }
  };

  const formatarDataHoraCurtaCaptaZap = (value) => {
    const data = new Date(value);
    if (Number.isNaN(data.getTime())) return String(value || "");
    return data.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const sincronizarFollowupAgendamentoCaptaZap = async (agendamento = {}) => {
    const status = String(agendamento?.status_agendamento || "").trim();
    const statusNormalizado = status
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    const participa = [
      "faltou",
      "cancelado",
      "agendado",
      "reagendado",
      "compareceu",
    ].some((item) => statusNormalizado.includes(item));

    if (!participa) return null;

    const leadAtual = leadAgendamentoSelecionada || {};
    const leadId =
      agendamento?.lead_id ||
      agendamento?.id_lead ||
      leadAtual?.id ||
      leadAtual?.lead_id ||
      leadAtual?.id_lead ||
      null;

    const empresaContextId =
      agendamento?.empresa_id ||
      leadAtual?.empresa_id ||
      leadAtual?.empresaId ||
      empresaId ||
      null;

    if (!leadId || !empresaContextId) return null;

    try {
      const resultado = await requestJson(
        "/whatsapp/captazap/followup-agendamento/sincronizar",
        {
          method: "POST",
          body: JSON.stringify({
            empresa_id: empresaContextId,
            lead_id: leadId,
            agendamento_id:
              agendamento?.id || agendamento?.agendamento_id || null,
            status_agendamento: status,
            data_agendamento: agendamento?.data_agendamento || null,
            observacao:
              agendamento?.observacoes ||
              agendamento?.observacao ||
              agendamento?.descricao ||
              "",
            usuario_id:
              usuario?.id ||
              usuario?.usuario_id ||
              agendamento?.usuario_id ||
              null,
            usuario_nome:
              usuario?.nome || usuario?.name || agendamento?.usuario_nome || "",
            telefone:
              leadAtual?.telefone1 ||
              leadAtual?.telefone ||
              leadAtual?.celular ||
              agendamento?.telefone ||
              "",
          }),
        },
      );

      if (resultado?.followup_agendamento) {
        const tarefaCriada = Boolean(resultado?.tarefa?.criada);
        const tarefaExistente = Boolean(resultado?.tarefa?.existente);
        const vencimento = resultado?.ciclo?.proximo_vencimento_em;

        if (tarefaCriada) {
          toast.info(
            `Follow-up / Agendamento criado${vencimento ? ` · vence ${formatarDataHoraCurtaCaptaZap(vencimento)}` : ""}.`,
          );
        } else if (tarefaExistente) {
          toast.info("Follow-up / Agendamento já existia para este evento.");
        } else {
          toast.warn(
            "Ciclo de Follow-up / Agendamento criado, mas a tarefa precisa ser conferida.",
          );
        }
      } else if (resultado?.acao === "ciclo_encerrado") {
        const encerrados = Number(resultado?.encerramento?.encerrados || 0);
        if (encerrados > 0) {
          toast.success(
            "Follow-up / Agendamento encerrado por novo status da agenda.",
          );
        }
      }

      window.dispatchEvent(
        new CustomEvent("captazap:followup_agendamento_atualizar", {
          detail: resultado,
        }),
      );

      await recarregarTarefasCaptaZap();
      return resultado;
    } catch (err) {
      console.error(
        "CaptaZap | erro ao sincronizar Follow-up / Agendamento:",
        err,
      );
      toast.warn(
        err?.message ||
          "Agendamento salvo, mas não foi possível sincronizar o Follow-up / Agendamento.",
      );
      return null;
    }
  };

  const handleAdicionarTag = (conversa, context = {}) => {
    const empresaContextId = context.empresaId || empresaId;

    if (!empresaContextId || !conversa?.id) return;

    const conversaEmpresaId = conversa.empresa_id || conversa.empresaId;

    if (
      conversaEmpresaId &&
      String(conversaEmpresaId) !== String(empresaContextId)
    ) {
      console.warn(
        "CaptaZap | bloqueado adicionar tag por divergência de empresa",
        {
          empresaAtiva: empresaContextId,
          empresaConversa: conversaEmpresaId,
          conversaId: conversa.id,
        },
      );
      return;
    }

    console.log("CaptaZap | adicionar tag", {
      empresaId: empresaContextId,
      conversaId: context.conversaId || conversa.id,
      telefone: context.telefone || conversa.numero,
      leadId: context.leadId || conversa.lead_id || conversa.leadId || null,
    });
  };

  const handleVincularLead = (conversa, context = {}) => {
    const empresaContextId = context.empresaId || empresaId;

    if (!empresaContextId || !conversa) return;

    const conversaEmpresaId = conversa.empresa_id || conversa.empresaId;

    if (
      conversaEmpresaId &&
      String(conversaEmpresaId) !== String(empresaContextId)
    ) {
      console.warn(
        "CaptaZap | bloqueado abrir lead por divergência de empresa",
        {
          empresaAtiva: empresaContextId,
          empresaConversa: conversaEmpresaId,
          conversaId: conversa.id,
        },
      );
      return;
    }

    console.log("CaptaZap | abrir lead vinculado", {
      empresaId: empresaContextId,
      leadId: context.leadId || conversa.lead_id || conversa.leadId,
      conversaId: context.conversaId || conversa.id,
    });
  };

  const handleAbrirTransferirAtendimento = useCallback((conversa) => {
    if (!conversa) return;

    setConversaTransferirAtendimento(conversa);
    setModalTransferirAtendimentoAberto(true);
  }, []);

  const aplicarTransferenciaAtendimentoLocal = useCallback(
    ({ conversa, chamada, destinoUsuarioId, destinoUsuarioNome }) => {
      if (!conversa || !destinoUsuarioId) return;

      const leadId =
        conversa.lead_id || conversa.leadId || conversa.lead?.id || null;
      const telefone = conversa.telefone || conversa.numero || "";
      const destinoEhUsuarioLogado =
        usuarioIdLogado && String(destinoUsuarioId) === String(usuarioIdLogado);
      const responsavelAnteriorNome =
        conversa.responsavel_anterior_nome ||
        conversa.responsavelAnteriorNome ||
        getResponsavelConversaNome(conversa) ||
        "";
      const eraFila = Boolean(
        isFilaProtecaoCaptaZap(conversa) ||
          String(conversa?.aba_captazap || "").toLowerCase() === "fila" ||
          conversa?.em_fila === true ||
          conversa?._fila_global === true ||
          conversa?._fila_equipe === true,
      );
      const motivoFila = String(
        conversa.fila_protecao_label ||
          conversa.filaProtecaoLabel ||
          (conversa.fila_protecao_motivo === "sem_resposta_3h"
            ? "Sem resposta há mais de 3h"
            : conversa.fila_protecao_motivo === "retorno_apos_fechamento"
              ? "Retorno após fechamento"
              : conversa.fila_protecao_motivo === "recuperado_dra_malu"
                ? "Recuperado pela Dra. Malu"
                : ""),
      ).trim();

      const instanciaConversa =
        getConexaoInstanceIdConversaCaptaZap(conversa) ||
        getConexaoInstanceIdConversaCaptaZap(chamada || {}) ||
        "";

      const alvo = {
        id: conversa.id,
        conversa_id: conversa.conversa_id,
        lead_id: leadId,
        leadId,
        telefone,
        numero: telefone,
        zapi_instance_id: instanciaConversa || null,
      };

      const aplicar = (item) => {
        if (!isMesmaConversaCaptazap(item, alvo)) return item;

        const statusChamado = chamada?.status_chamado || "aberto";
        const chamadoId = chamada?.id || item.chamado_id || item.id_chamado;
        const responsavelNome =
          chamada?.responsavel_nome || destinoUsuarioNome || "";

        return {
          ...item,
          ...(chamada || {}),
          chamado_id: chamadoId || null,
          id_chamado: chamadoId || item.id_chamado || null,
          status: "em_atendimento",
          status_chamado: statusChamado,
          chamado_status: statusChamado,
          chamada_status: statusChamado,
          responsavel_id: destinoUsuarioId,
          responsavel_nome: responsavelNome,
          captador_id: destinoUsuarioId,
          captador_nome: responsavelNome,
          usuario_responsavel_id: destinoUsuarioId,
          usuario_responsavel_nome: responsavelNome,
          atendente_id: destinoUsuarioId,
          atendente_nome: responsavelNome,
          operador_id: destinoUsuarioId,
          operador_nome: responsavelNome,
          _captazap_assumida_por: destinoUsuarioId,
          _captazap_assumida_por_nome: responsavelNome,
          fila_protecao: false,
          filaProtecao: false,
          _fila_protecao: false,
          fila_protecao_motivo: null,
          filaProtecaoMotivo: null,
          fila_protecao_label: "",
          filaProtecaoLabel: "",
          transferido_para_tipo: "usuario",
          transferido_para_usuario_id: destinoUsuarioId,
          transferido_para_usuario_nome: responsavelNome,
          transferido_em: chamada?.transferido_em || new Date().toISOString(),
          conversa_ativa: true,
          conversaAtiva: true,
          sem_conversa: false,
          semConversa: false,
          aba_captazap: destinoEhUsuarioLogado ? "minhas" : "outras",
          em_fila: false,
          fila_atendimento: false,
          aguardando_atendimento: false,
          _fila_global: false,
          _fila_equipe: false,
          _fila_resgate: false,
          tags: Array.from(
            new Set(
              [
                ...(item.tags || []),
                "Transferido",
                responsavelNome ? `Para ${responsavelNome}` : "",
              ].filter(Boolean),
            ),
          ),
          timeline: [
            {
              tipo: eraFila ? "Transferência da Fila" : "Transferência",
              descricao: `${
                eraFila ? "Atendimento transferido da Fila" : "Atendimento transferido"
              } por ${usuarioNomeLogado || "usuário atual"} para ${
                responsavelNome || `usuário ${destinoUsuarioId}`
              }.${
                responsavelAnteriorNome
                  ? ` Responsável anterior: ${responsavelAnteriorNome}.`
                  : ""
              }${motivoFila ? ` Motivo da Fila: ${motivoFila}.` : ""}`,
              data: "Agora",
            },
            ...(Array.isArray(item.timeline) ? item.timeline : []),
          ],
        };
      };

      setConversasWhatsApp((atuais) => atuais.map(aplicar));
      setConversasCriadasLocal((atuais) => atuais.map(aplicar));

      setConversaSelecionada((atual) => {
        if (!atual || !isMesmaConversaCaptazap(atual, alvo)) return atual;

        return destinoEhUsuarioLogado ? aplicar(atual) : null;
      });
    },
    [usuarioIdLogado, usuarioNomeLogado],
  );

  const handleConfirmarTransferirAtendimento = useCallback(
    async ({ destino_usuario_id, destino_usuario_nome, motivo } = {}) => {
      const conversa = conversaTransferirAtendimento;

      if (!empresaId || !conversa || !usuarioIdLogado) return;

      const destinoUsuarioId = Number(destino_usuario_id || 0);

      if (!destinoUsuarioId) {
        window.alert("Selecione o captador que vai receber o atendimento.");
        return;
      }

      if (String(destinoUsuarioId) === String(usuarioIdLogado)) {
        window.alert("Selecione outro captador para receber o atendimento.");
        return;
      }

      if (
        !validarContextoPacienteConversa(conversa, "transferir o atendimento")
      ) {
        return;
      }

      const leadId =
        getLeadContextoIdConversa(conversa) ||
        conversa.lead_id ||
        conversa.leadId ||
        conversa.lead?.id;
      const telefone = conversa.telefone || conversa.numero || "";

      if (!leadId && !telefone) {
        window.alert(
          "Não foi possível identificar esta conversa para transferência.",
        );
        return;
      }

      if (transferenciaAtendimentoLockRef.current) return;
      transferenciaAtendimentoLockRef.current = true;
      setTransferindoAtendimento(true);

      try {
        const data = await requestJson("/whatsapp/captazap/transferir", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            empresa_id: empresaId,
            zapi_instance_id:
              getConexaoInstanceIdConversaCaptaZap(conversa) || null,
            lead_id: leadId || null,
            lead_contexto_id:
              getLeadContextoIdConversa(conversa) || leadId || null,
            telefone,
            usuario_id: usuarioIdLogado,
            usuario_nome: usuarioNomeLogado,
            destino_tipo: "usuario",
            destino_usuario_id: destinoUsuarioId,
            destino_usuario_nome: destino_usuario_nome || "",
            motivo: motivo || "",
          }),
        });

        aplicarTransferenciaAtendimentoLocal({
          conversa,
          chamada: data?.chamada || null,
          destinoUsuarioId,
          destinoUsuarioNome:
            destino_usuario_nome || data?.destino?.usuario_nome || "",
        });

        setModalTransferirAtendimentoAberto(false);
        setConversaTransferirAtendimento(null);

        // A transferência já foi aplicada localmente. Atualiza somente a
        // fila para reconciliar outros operadores, sem recarregar todo o CRM.
        agendarRecarregarConversasCaptazapRealtime();
      } catch (error) {
        console.error("❌ CaptaZap transferir atendimento:", error);
        window.alert(
          error?.message || "Não foi possível transferir o atendimento agora.",
        );
      } finally {
        transferenciaAtendimentoLockRef.current = false;
        setTransferindoAtendimento(false);
      }
    },
    [
      agendarRecarregarConversasCaptazapRealtime,
      aplicarTransferenciaAtendimentoLocal,
      conversaTransferirAtendimento,
      empresaId,
      usuarioIdLogado,
      usuarioNomeLogado,
    ],
  );

  const handleSolicitarTransferencia = (conversa, context = {}) => {
    const empresaContextId = context.empresaId || empresaId;

    if (!empresaContextId || !conversa?.id) return;

    const conversaEmpresaId = conversa.empresa_id || conversa.empresaId;

    if (
      conversaEmpresaId &&
      String(conversaEmpresaId) !== String(empresaContextId)
    ) {
      console.warn(
        "CaptaZap | bloqueado solicitar transferência por divergência de empresa",
        {
          empresaAtiva: empresaContextId,
          empresaConversa: conversaEmpresaId,
          conversaId: conversa.id,
        },
      );
      return;
    }

    if (
      !validarContextoPacienteConversa(
        conversa,
        "solicitar transferência da lead",
      )
    ) {
      return;
    }

    const leadId =
      context.leadId ||
      context.lead_contexto_id ||
      getLeadContextoIdConversa(conversa) ||
      conversa.lead_id ||
      conversa.leadId ||
      conversa.lead?.id;

    const leadBase =
      getLeadVinculadaPorId(conversa, leadId) ||
      conversa.lead ||
      leadsCRM.find((lead) => String(getLeadId(lead)) === String(leadId));

    if (!leadId || !leadBase) {
      handleAbrirTransferirAtendimento(conversa);
      return;
    }

    setLeadTransferenciaSelecionada({
      ...leadBase,
      id: leadId,
      empresa_id: leadBase.empresa_id || empresaContextId,
      nome_lead: leadBase.nome_lead || leadBase.nome || conversa.nome,
      telefone1:
        leadBase.telefone1 ||
        leadBase.telefone ||
        leadBase.celular ||
        conversa.numero,
    });
    setModalTransferenciaAberto(true);
  };

  const aplicarTransferenciaPendenteLocal = (leadId) => {
    if (!leadId) return;

    const pendenciaLocal = {
      id: `captazap-local-${leadId}`,
      lead_id: leadId,
      status: "pendente",
      empresa_id: empresaId || null,
    };

    setSolicitacoesTransferencia((atuais) => {
      const lista = Array.isArray(atuais) ? atuais : [];
      const jaExiste = lista.some(
        (item) =>
          String(item?.lead_id || "") === String(leadId) &&
          String(item?.status || "").toLowerCase() === "pendente",
      );

      return jaExiste ? lista : [pendenciaLocal, ...lista];
    });

    setLeadsCRM((atuais) =>
      (atuais || []).map((lead) =>
        String(getLeadId(lead)) === String(leadId)
          ? {
              ...lead,
              transferencia_pendente: true,
              _solicitacao_transferencia_pendente:
                lead._solicitacao_transferencia_pendente || pendenciaLocal,
            }
          : lead,
      ),
    );

    setConversaSelecionada((atual) => {
      if (!atual) return atual;

      const atualLeadId =
        atual.lead_id || atual.leadId || atual.lead?.id || atual.lead?.lead_id;

      if (String(atualLeadId) !== String(leadId)) return atual;

      return {
        ...atual,
        transferencia_pendente: true,
        lead: atual.lead
          ? {
              ...atual.lead,
              transferencia_pendente: true,
              _solicitacao_transferencia_pendente:
                atual.lead._solicitacao_transferencia_pendente ||
                pendenciaLocal,
            }
          : atual.lead,
      };
    });
  };

  const handleAbrirNotificacaoNovaMensagem = useCallback(
    (notificacao) => {
      if (!notificacao) return;

      removerNotificacaoNovaMensagem(notificacao.id);

      const alvo = {
        telefone: notificacao.telefone,
        numero: notificacao.telefone,
        lead_id: notificacao.lead_id || notificacao.leadId,
        leadId: notificacao.lead_id || notificacao.leadId,
        chamado_id: notificacao.chamado_id || notificacao.chamadoId,
        id_chamado: notificacao.chamado_id || notificacao.chamadoId,
        zapi_instance_id:
          notificacao.zapi_instance_id || notificacao.zapiInstanceId || null,
        zapiInstanceId:
          notificacao.zapi_instance_id || notificacao.zapiInstanceId || null,
      };

      const conversaEncontrada = encontrarConversaPorNotificacaoCaptaZap(alvo, [
        ...(conversasWhatsApp || []),
        ...(conversasCriadasLocal || []),
        ...(Array.isArray(conversasBase?.minhasConversas)
          ? conversasBase.minhasConversas
          : []),
        ...(Array.isArray(conversasBase?.filaDeEspera)
          ? conversasBase.filaDeEspera
          : []),
        ...(Array.isArray(conversasBase?.followUp)
          ? conversasBase.followUp
          : []),
        ...(Array.isArray(conversasBase?.fechados)
          ? conversasBase.fechados
          : []),
        ...(Array.isArray(conversasBase?.contatos)
          ? conversasBase.contatos
          : []),
      ]);

      if (conversaEncontrada) {
        handleSelecionarConversa(conversaEncontrada);
        return;
      }

      handleSelecionarConversa(
        normalizarConversaCaptazap({
          id: `toast-${notificacao.lead_id || normalizarTelefone(notificacao.telefone)}`,
          conversa_id: `toast-${notificacao.lead_id || normalizarTelefone(notificacao.telefone)}`,
          lead_id: notificacao.lead_id || null,
          leadId: notificacao.lead_id || null,
          chamado_id: notificacao.chamado_id || null,
          id_chamado: notificacao.chamado_id || null,
          zapi_instance_id:
            notificacao.zapi_instance_id || notificacao.zapiInstanceId || null,
          zapiInstanceId:
            notificacao.zapi_instance_id || notificacao.zapiInstanceId || null,
          nome: notificacao.nome || notificacao.telefone || "Novo contato",
          telefone: notificacao.telefone,
          numero: notificacao.telefone,
          canal: "whatsapp",
          status_chamado: "aberto",
          chamado_status: "aberto",
          preview: notificacao.texto || "Nova mensagem",
          ultima_mensagem: notificacao.texto || "Nova mensagem",
          ultima_mensagem_direcao: "recebida",
          ultima_mensagem_autor: "cliente",
          datahora: notificacao.criado_em,
          ultima_mensagem_em: notificacao.criado_em,
          naoLidas: 1,
          conversa_ativa: true,
          conversaAtiva: true,
          sem_conversa: false,
          semConversa: false,
        }),
      );
    },
    [
      conversasBase,
      conversasCriadasLocal,
      conversasWhatsApp,
      handleSelecionarConversa,
      removerNotificacaoNovaMensagem,
    ],
  );

  useEffect(() => {
    abrirNotificacaoChromeRef.current = handleAbrirNotificacaoNovaMensagem;

    return () => {
      abrirNotificacaoChromeRef.current = null;
    };
  }, [handleAbrirNotificacaoNovaMensagem]);

  const handleSelecionarConexao = useCallback(
    (instanceId = "") => {
      const proxima = String(instanceId || "").trim();
      if (!proxima) return;

      if (proxima === conexaoSelecionadaRef.current) return;

      conexaoSelecionadaRef.current = proxima;
      setConexaoSelecionada(proxima);
      setConversaSelecionada(null);
      setResultadosBuscaOperacao([]);
      buscaOperacaoCacheRef.current?.clear?.();

      carregandoPaginaConversasV2Ref.current.clear();
      paginasConversasV2Ref.current = {
        minhas: { carregada: false, hasMore: true, cursor: null },
        fila: { carregada: false, hasMore: true, cursor: null },
        fechados: { carregada: false, hasMore: true, cursor: null },
      };
      setPaginacaoConversasV2({
        minhas: { carregada: false, hasMore: true, cursor: null, loading: false },
        fila: { carregada: false, hasMore: true, cursor: null, loading: false },
        fechados: { carregada: false, hasMore: true, cursor: null, loading: false },
      });
      setTotaisOperacaoV2({ minhas: null, fila: null, fechados: null });
      setConversasWhatsApp([]);

      const abaAtual = CAPTAZAP_ABAS_OPERACIONAIS_PAGINADAS.has(
        abaQueueListRef.current,
      )
        ? abaQueueListRef.current
        : "minhas";

      carregarConversasOperacionaisV2({
        aba: abaAtual,
        reset: true,
      }).catch(() => null);
      carregarContadoresOperacaoV2({ force: true }).catch(() => null);
    },
    [carregarConversasOperacionaisV2, carregarContadoresOperacaoV2],
  );

  const conexaoAtivaCabecalho =
    conexoesWhatsApp.find(
      (item) =>
        String(item?.zapi_instance_id || "") === String(conexaoSelecionada),
    ) || null;

  // Tema visual acompanha a conexão selecionada sem alterar sua lógica.
  // WhatsApp/CaptaZap = verde, segunda conexão = azul, Instagram = identidade Instagram.
  const conexaoAtivaNome = getNomeConexaoWhatsAppCaptaZap(
    conexaoAtivaCabecalho || {},
    Math.max(
      0,
      conexoesWhatsApp.findIndex(
        (item) =>
          String(item?.zapi_instance_id || "") === String(conexaoSelecionada),
      ),
    ),
  );
  const conexaoAtivaNomeNormalizado = String(conexaoAtivaNome || "")
    .trim()
    .toLowerCase();
  const conexaoAtivaInstanceId = String(conexaoSelecionada || "").trim();
  const instagramConexaoAtiva = Boolean(
    String(conexaoAtivaCabecalho?.canal || "").toLowerCase() === "instagram" ||
      conexaoAtivaInstanceId.toLowerCase().startsWith("instagram:"),
  );
  const captaZapConexaoAtiva = Boolean(
    !instagramConexaoAtiva &&
      (conexaoAtivaNomeNormalizado.includes("captazap") ||
        conexaoAtivaNomeNormalizado.includes("capta zap")),
  );
  const azulConexaoAtiva = Boolean(
    !instagramConexaoAtiva && !captaZapConexaoAtiva,
  );
  // Mantido como alias de compatibilidade visual para trechos antigos do componente.
  const segundaConexaoAtiva = azulConexaoAtiva;

  const canalAtivoCabecalho =
    CHANNELS.find((channel) => channel.key === canalSelecionado) || CHANNELS[0];
  const CanalAtivoCabecalhoIcon = canalAtivoCabecalho.icon;

  if (!empresaId) {
    return <EmpresaNaoSelecionada />;
  }

  return (
    <div className="relative flex h-[100dvh] max-h-[100dvh] min-h-0 w-full overflow-hidden overscroll-none bg-[#F4F7FB] text-slate-950">
      <AmbientBackground />
      <MetaHomologacaoViewer empresaId={empresaId} requestJson={requestJson} />

      <NotificacoesNovaMensagemCaptaZap
        notificacoes={notificacoesNovaMensagem}
        onClose={removerNotificacaoNovaMensagem}
        onOpen={handleAbrirNotificacaoNovaMensagem}
      />

      {chamadasRecebidas.length ? (
        <div className="fixed right-5 top-5 z-[120] flex w-[min(390px,calc(100vw-40px))] flex-col gap-3">
          {chamadasRecebidas.map((chamada) => {
            const estado = String(chamada?.estado || "ringing").toLowerCase();
            const ativa = ["accepted", "preaccepted", "active"].includes(estado);
            const recusando = estado === "rejecting";
            const finalizada = [
              "ended",
              "missed",
              "rejected",
              "claimed",
              "dismissed",
              "failed",
            ].includes(estado);
            const titulo =
              estado === "missed"
                ? "Chamada não atendida"
                : estado === "rejected"
                  ? "Chamada recusada"
                  : ativa
                    ? "Chamada em andamento"
                    : finalizada
                      ? "Chamada encerrada"
                      : "Chamada recebida";
            const telefoneExibicao =
              formatTelefoneChamadaCaptaZap(chamada?.telefone) ||
              chamada?.telefone ||
              "Número não identificado";

            return (
              <div
                key={chamada.chave}
                className="overflow-hidden rounded-[24px] border border-emerald-200 bg-white shadow-[0_22px_55px_rgba(15,23,42,0.28)]"
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="relative h-14 w-14 shrink-0">
                      {chamada?.foto ? (
                        <img
                          src={chamada.foto}
                          alt={chamada?.nome || "Contato"}
                          className="h-14 w-14 rounded-2xl object-cover ring-2 ring-white shadow-md"
                        />
                      ) : (
                        <div
                          className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
                            ativa
                              ? "bg-emerald-600 text-white"
                              : finalizada
                                ? "bg-slate-100 text-slate-500"
                                : "animate-pulse bg-emerald-100 text-emerald-700"
                          }`}
                        >
                          <Phone size={23} />
                        </div>
                      )}

                      {!finalizada ? (
                        <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-white ring-2 ring-white shadow-sm">
                          <Phone size={12} />
                        </span>
                      ) : null}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] font-black uppercase tracking-[0.12em] text-emerald-700">
                          {titulo}
                        </div>

                        {chamada?.emFila ? (
                          <span className="shrink-0 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-black text-amber-800 ring-1 ring-amber-200">
                            NA FILA
                          </span>
                        ) : chamada?.responsavelNome ? (
                          <span className="shrink-0 rounded-full bg-blue-100 px-2 py-1 text-[10px] font-black text-blue-800 ring-1 ring-blue-200">
                            EM ATENDIMENTO
                          </span>
                        ) : null}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (chamada?.conversaAlvo) {
                            handleSelecionarConversa(chamada.conversaAlvo);
                          }
                        }}
                        className="mt-0.5 block w-full truncate text-left text-[17px] font-black text-slate-950 hover:text-emerald-700"
                        title={
                          chamada?.conversaAlvo
                            ? "Abrir conversa deste paciente"
                            : undefined
                        }
                      >
                        {chamada?.nome || telefoneExibicao}
                      </button>

                      {chamada?.nome ? (
                        <div className="truncate text-xs font-semibold text-slate-500">
                          {telefoneExibicao}
                        </div>
                      ) : null}

                      <div className="mt-1.5 inline-flex max-w-full items-center gap-1.5 rounded-lg bg-slate-100 px-2 py-1 text-[10px] font-black text-slate-600">
                        <FaWhatsapp className="shrink-0 text-[#25D366]" />
                        <span className="truncate">
                          {chamada?.conexaoNome || "WhatsApp"}
                        </span>
                      </div>

                      {ativa && chamada?.atendidaEm ? (
                        <div className="mt-2 text-sm font-black tabular-nums text-emerald-700">
                          {formatDuracaoLigacaoCaptaZap(
                            Math.max(
                              0,
                              Math.floor(
                                (relogioLigacoesAgora - chamada.atendidaEm) / 1000,
                              ),
                            ),
                          )}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        Captador
                      </span>
                      <span className="truncate text-right text-sm font-black text-slate-900">
                        {chamada?.captadorNome ||
                          (chamada?.identificacaoCarregada
                            ? "Não identificado"
                            : "Identificando...")}
                      </span>
                    </div>

                    <div className="h-px bg-slate-200" />

                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                        Responsável CaptaZap
                      </span>
                      <span
                        className={`truncate text-right text-sm font-black ${
                          chamada?.emFila
                            ? "text-amber-700"
                            : "text-blue-800"
                        }`}
                      >
                        {!chamada?.identificacaoCarregada &&
                        !chamada?.responsavelNome
                          ? "Identificando..."
                          : chamada?.emFila
                            ? "Fila / sem responsável"
                            : chamada?.responsavelNome || "Não identificado"}
                      </span>
                    </div>
                  </div>
                </div>

                {!finalizada ? (
                  <div className="grid grid-cols-2 gap-2 border-t border-slate-100 bg-slate-50/80 p-3">
                    {!ativa ? (
                      <button
                        type="button"
                        onClick={() => handleAtenderChamadaRecebida(chamada)}
                        className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-black text-white shadow-sm transition hover:bg-emerald-700"
                      >
                        <Phone size={16} />
                        Atender
                      </button>
                    ) : (
                      <div className="inline-flex h-11 items-center justify-center rounded-2xl bg-emerald-50 px-4 text-sm font-black text-emerald-700 ring-1 ring-emerald-200">
                        Em ligação
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => handleRecusarChamadaRecebida(chamada)}
                      disabled={recusando}
                      className={`inline-flex h-11 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-black text-white shadow-sm transition ${
                        recusando
                          ? "cursor-wait bg-rose-400"
                          : "bg-rose-600 hover:bg-rose-700"
                      }`}
                    >
                      <PhoneOff size={16} />
                      {recusando ? "Recusando..." : ativa ? "Encerrar" : "Recusar"}
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <header
          data-captazap-header-version={CAPTAZAP_CABECALHO_RECOLHIVEL_VERSION}
          className={`z-10 shrink-0 border-b border-blue-200 bg-white/95 shadow-[0_8px_24px_rgba(30,64,175,0.07)] backdrop-blur-xl transition-[padding] duration-300 ${
            cabecalhoRecolhido ? "px-4 py-2" : "px-5 py-1.5"
          }`}
        >
          {cabecalhoRecolhido ? (
            <div className="flex min-h-10 items-center gap-3">
              <button
                type="button"
                onClick={() => setCabecalhoRecolhido(false)}
                className="group flex min-w-0 items-center gap-2.5 rounded-2xl px-1.5 py-0.5 text-left transition hover:bg-blue-50"
                title="Expandir cabeçalho do CaptaZap"
                aria-label="Expandir cabeçalho do CaptaZap"
                aria-expanded="false"
              >
                <img
                  src={LOGO_CAPTAZAP_ICON}
                  alt="CaptaZap"
                  className="h-9 w-9 shrink-0 rounded-xl object-contain drop-shadow-[0_6px_14px_rgba(249,115,22,0.20)]"
                />

                <div className="hidden min-w-0 sm:block">
                  <div className="truncate text-sm font-black leading-tight text-slate-950">
                    CaptaZap
                  </div>
                  <div className="max-w-[220px] truncate text-[10px] font-semibold leading-tight text-slate-500">
                    {empresaNome}
                  </div>
                </div>
              </button>

              <span className="hidden items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700 ring-1 ring-emerald-200 md:inline-flex">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Conectado
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold text-blue-700 ring-1 ring-blue-200">
                <CanalAtivoCabecalhoIcon size={13} />
                {canalAtivoCabecalho.label}
              </span>

              <span className="hidden items-center gap-1.5 rounded-full bg-violet-50 px-2.5 py-1 text-[10px] font-bold text-violet-700 ring-1 ring-violet-200 md:inline-flex">
                <ArrowRightLeft size={12} />
                {conexaoAtivaCabecalho?.nome || "Selecione uma conexão"}
              </span>

              {loadingCRM ? (
                <span className="hidden items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-1 text-[10px] font-bold text-slate-500 ring-1 ring-slate-200 lg:inline-flex">
                  <RefreshCw size={12} className="animate-spin" />
                  Sincronizando
                </span>
              ) : null}

              <div className="ml-auto flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  title={
                    permissaoNotificacoesChrome === "denied"
                      ? "Alertas bloqueados no Chrome"
                      : notificacoesChromeAtivas
                        ? "Alertas do Chrome ativos"
                        : "Ativar alertas do Chrome"
                  }
                  onClick={handleAlternarNotificacoesChrome}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-2xl border shadow-sm transition hover:-translate-y-0.5 ${
                    permissaoNotificacoesChrome === "denied"
                      ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                      : notificacoesChromeAtivas
                        ? "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                        : "border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                  }`}
                >
                  {permissaoNotificacoesChrome === "denied" ? (
                    <BellOff size={16} />
                  ) : (
                    <BellRing
                      size={16}
                      className={
                        notificacoesChromeAtivas ? "animate-pulse" : ""
                      }
                    />
                  )}
                  {notificacoesChromeAtivas ? (
                    <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />
                  ) : null}
                </button>

                {usuarioPodeVerEquipeCaptaZap ? (
                  <HeaderAction
                    title="Abrir supervisão inteligente"
                    onClick={() =>
                      window.open(
                        "/captazap/funil-anuncios",
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    <ShieldCheck size={16} />
                  </HeaderAction>
                ) : null}

                <HeaderAction title="Atualizar" onClick={carregarDadosCRM}>
                  <RefreshCw size={16} />
                </HeaderAction>

                <button
                  type="button"
                  onClick={() => setCabecalhoRecolhido(false)}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-3 text-xs font-black text-blue-800 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-100"
                  title="Expandir cabeçalho"
                  aria-label="Expandir cabeçalho"
                  aria-expanded="false"
                >
                  <ChevronDown size={17} />
                  <span className="hidden lg:inline">Expandir</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex h-12 items-center gap-3">
                <div className="flex min-w-0 shrink-0 items-center gap-3">
                  <img
                    src={LOGO_CAPTAZAP_HORIZONTAL}
                    alt="CaptaZap"
                    className="h-10 w-auto max-w-[185px] object-contain drop-shadow-[0_5px_10px_rgba(15,23,42,0.10)] xl:h-11 xl:max-w-[205px]"
                  />

                  <div className="hidden min-w-0 max-w-[260px] items-center gap-2 lg:flex">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
                    <span className="truncate text-[11px] font-bold text-slate-600">
                      {empresaNome}
                    </span>
                    {loadingCRM ? (
                      <RefreshCw size={12} className="shrink-0 animate-spin text-slate-400" />
                    ) : null}
                  </div>
                </div>



                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                  <HeaderUserCard captador={captadorLogado} />

                  <HeaderNotificacoesChromeButton
                    ativo={notificacoesChromeAtivas}
                    permissao={permissaoNotificacoesChrome}
                    onClick={handleAlternarNotificacoesChrome}
                  />

                  {usuarioPodeVerEquipeCaptaZap ? (
                    <HeaderSupervisaoButton
                      totalEquipe={totalMinhasEquipe}
                      totalFila={totalFila}
                      onClick={() =>
                        window.open(
                          "/captazap/funil-anuncios",
                          "_blank",
                          "noopener,noreferrer",
                        )
                      }
                    />
                  ) : null}

                  <HeaderAction
                    title="Meu Dia"
                    onClick={() => setModalBomDiaProducaoAberto(true)}
                  >
                    <span className="text-[16px] leading-none">☀</span>
                  </HeaderAction>

                  <HeaderAction title="Atualizar" onClick={carregarDadosCRM}>
                    <RefreshCw size={15} />
                  </HeaderAction>

                  <HeaderAction
                    title="Recolher cabeçalho"
                    onClick={() => setCabecalhoRecolhido(true)}
                  >
                    <ChevronUp size={17} />
                  </HeaderAction>
                </div>
              </div>

              <div className="mt-1.5 flex min-h-9 items-center gap-2 border-t border-slate-100 pt-1.5">
                <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <div className="flex shrink-0 items-center gap-1">
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center text-slate-400"
                      title="Filtrar por conexão"
                    >
                      <ArrowRightLeft size={14} />
                    </span>

                    {conexoesWhatsApp.map((conexao, index) => {
                      const instanceId = String(
                        conexao?.zapi_instance_id || "",
                      ).trim();
                      const active = conexaoSelecionada === instanceId;
                      const canalConexao = String(
                        conexao?.canal ||
                          conexao?.tipo ||
                          conexao?.provider ||
                          conexao?.plataforma ||
                          "",
                      )
                        .trim()
                        .toLowerCase();
                      const isInstagram =
                        canalConexao === "instagram" ||
                        instanceId.toLowerCase().startsWith("instagram:");
                      const nomeConexao = getNomeConexaoWhatsAppCaptaZap(
                        conexao,
                        index,
                      );
                      const numeroConexao = numeroConexaoExibicaoCaptaZap({
                        ...conexao,
                        telefone: telefonesCadastradosPorInstancia[instanceId] || conexao?.telefone || "",
                      });
                      const nomeConexaoNormalizado = String(nomeConexao || "")
                        .trim()
                        .toLowerCase();
                      const isCaptaZap =
                        !isInstagram &&
                        (nomeConexaoNormalizado.includes("captazap") ||
                          nomeConexaoNormalizado.includes("capta zap"));
                      const IconeConexao = isInstagram ? FaInstagram : FaWhatsapp;
                      const totalAguardandoResposta = Math.max(
                        0,
                        Number(
                          pendenciasRespostaPorConexao.porInstancia?.[
                            instanceId
                          ] || 0,
                        ),
                      );

                      if (!instanceId) return null;

                      return (
                        <button
                          key={instanceId}
                          type="button"
                          onClick={() => handleSelecionarConexao(instanceId)}
                          title={
                            totalAguardandoResposta > 0
                              ? `${nomeConexao} · ${totalAguardandoResposta} aguardando resposta`
                              : nomeConexao
                          }
                          className={`flex min-h-9 max-w-[205px] shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-bold transition ${
                            isInstagram
                              ? active
                                ? "border-pink-500 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#FCAF45] text-white shadow-sm shadow-pink-500/25"
                                : "border-pink-200 bg-gradient-to-r from-purple-50 via-pink-50 to-orange-50 text-[#C13584] hover:border-pink-400 hover:from-purple-100 hover:via-pink-100 hover:to-orange-100"
                              : isCaptaZap
                                ? active
                                  ? "border-[#25D366] bg-gradient-to-r from-[#128C7E] via-[#25D366] to-[#5EEB8B] text-white shadow-sm shadow-emerald-500/25"
                                  : "border-emerald-200 bg-gradient-to-r from-emerald-50 via-green-50 to-lime-50 text-[#128C7E] hover:border-[#25D366] hover:from-emerald-100 hover:via-green-100 hover:to-lime-100"
                                : active
                                  ? "border-blue-500 bg-gradient-to-r from-[#1D4ED8] via-[#2563EB] to-[#60A5FA] text-white shadow-sm shadow-blue-500/25"
                                  : "border-blue-200 bg-gradient-to-r from-blue-50 via-sky-50 to-cyan-50 text-[#1D4ED8] hover:border-blue-400 hover:from-blue-100 hover:via-sky-100 hover:to-cyan-100"
                          }`}
                        >
                          <IconeConexao size={12} />
                          <span className="flex min-w-0 flex-col items-start leading-tight">
                            <span className="max-w-[155px] truncate">{nomeConexao}</span>
                            {numeroConexao ? <span className="text-[9px] font-medium opacity-85">{numeroConexao}</span> : null}
                          </span>
                          {totalAguardandoResposta > 0 ? (
                            <span
                              className={`inline-flex h-[17px] min-w-[17px] shrink-0 items-center justify-center rounded-full px-1 text-[9px] font-black leading-none shadow-sm ${
                                active
                                  ? isInstagram
                                    ? "bg-white text-[#C13584]"
                                    : isCaptaZap
                                      ? "bg-white text-[#128C7E]"
                                      : "bg-white text-[#1D4ED8]"
                                  : "bg-orange-500 text-white"
                              }`}
                            >
                              {totalAguardandoResposta > 99
                                ? "99+"
                                : totalAguardandoResposta}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  <span className="mx-1 h-5 w-px shrink-0 bg-slate-200" />

                  <div className="flex shrink-0 items-center gap-1">
                    {CHANNELS.map((channel) => {
                      const Icon = channel.icon;
                      const active = canalSelecionado === channel.key;

                      return (
                        <button
                          key={channel.key}
                          type="button"
                          onClick={() => setCanalSelecionado(channel.key)}
                          className={`flex h-7 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-[10px] font-bold transition ${
                            active
                              ? "border-slate-900 bg-slate-900 text-white"
                              : "border-transparent bg-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-800"
                          }`}
                        >
                          <Icon size={13} />
                          <span>{channel.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="hidden shrink-0 items-center gap-2 whitespace-nowrap text-[10px] font-semibold text-slate-500 xl:flex">
                  <span>
                    <strong className="font-black text-slate-800">{totalConversas}</strong> ativas
                  </span>
                  <span className="text-slate-300">•</span>
                  <span>
                    <strong className="font-black text-slate-800">{totalFila}</strong> fila
                  </span>
                  <span className="text-slate-300">•</span>
                  <span>
                    <strong className="font-black text-slate-800">{tempoMedio}</strong> resposta
                  </span>
                </div>
              </div>
            </>
          )}
        </header>

        <main className="relative min-h-0 flex-1 overflow-hidden overscroll-none bg-[#F4F7FB] p-5">
          <div className="relative h-full min-h-0 rounded-[34px] border border-blue-200/80 bg-white p-[2px] shadow-[0_22px_70px_rgba(30,64,175,0.16)]">
            <div
              className={`absolute inset-0 rounded-[34px] bg-gradient-to-br ${
                instagramConexaoAtiva
                  ? "from-[#833AB4]/45 via-[#E1306C]/18 to-[#FCAF45]/35"
                  : captaZapConexaoAtiva
                    ? "from-[#128C7E]/35 via-white to-[#25D366]/28"
                    : "from-[#1D4ED8]/38 via-white to-[#60A5FA]/30"
              }`}
            />

            <div
              className={`relative grid h-full min-h-0 overflow-hidden rounded-[32px] border border-slate-300 bg-white transition-[grid-template-columns] duration-300 ease-out ${
                painelDetalhesRecolhido
                  ? "grid-cols-[390px_minmax(0,1fr)_64px]"
                  : "grid-cols-[390px_minmax(0,1fr)_370px]"
              }`}
            >
              <div className="pointer-events-none absolute inset-0 z-20 rounded-[32px] ring-1 ring-inset ring-white/80" />

              <section
                className={`relative min-h-0 border-r transition-colors duration-200 ${
                  instagramConexaoAtiva
                    ? "border-pink-400 bg-[#FFF4FA] shadow-[8px_0_28px_rgba(225,48,108,0.14)]"
                    : captaZapConexaoAtiva
                      ? "border-emerald-400 bg-[#F1FFF7] shadow-[8px_0_28px_rgba(37,211,102,0.12)]"
                      : "border-blue-400 bg-[#F3F8FF] shadow-[8px_0_28px_rgba(37,99,235,0.12)]"
                }`}
              >
                <div
                  className={`absolute inset-x-0 top-0 z-20 h-[3px] bg-gradient-to-r ${
                    instagramConexaoAtiva
                      ? "from-[#833AB4] via-[#E1306C] to-[#FCAF45]"
                      : captaZapConexaoAtiva
                        ? "from-[#128C7E] via-[#25D366] to-[#5EEB8B]"
                        : "from-[#1D4ED8] via-[#2563EB] to-[#60A5FA]"
                  }`}
                />

                <div className="absolute right-14 top-3 z-40">
                  <button
                    type="button"
                    onClick={handleAbrirNovoContato}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-700 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
                    title="Novo contato"
                    aria-label="Novo contato"
                  >
                    <UserPlus size={15} strokeWidth={2.6} />
                  </button>
                </div>

                <div
                  className={`h-full min-h-0 ${
                    instagramConexaoAtiva
                      ? "captazap-queue-instagram"
                      : captaZapConexaoAtiva
                        ? "captazap-queue-whatsapp"
                        : "captazap-queue-azul"
                  }`}
                >
                  <style>{`
                    .captazap-queue-instagram > section {
                      background: #FFF7FB !important;
                    }
                    .captazap-queue-instagram > section > header {
                      background-color: #FFF0F7 !important;
                      background-image: linear-gradient(135deg, #FFF0F7 0%, #FFE2EF 52%, #FFF0DA 100%) !important;
                      border-color: rgba(225,48,108,.34) !important;
                      box-shadow: 0 16px 40px rgba(193,53,132,.12) !important;
                    }
                    .captazap-queue-instagram > section > header [class*="bg-amber-"],
                    .captazap-queue-instagram > section > header [class*="bg-orange-"] {
                      background-color: rgba(255,255,255,.72) !important;
                    }
                    .captazap-queue-instagram > section > header [class*="border-amber-"],
                    .captazap-queue-instagram > section > header [class*="border-orange-"] {
                      border-color: rgba(225,48,108,.35) !important;
                    }
                    .captazap-queue-instagram > section > header [class*="text-amber-"],
                    .captazap-queue-instagram > section > header [class*="text-orange-"] {
                      color: #A61E62 !important;
                    }

                    .captazap-queue-whatsapp > section {
                      background: #F7FFF9 !important;
                    }
                    .captazap-queue-whatsapp > section > header {
                      background-color: #ECFFF4 !important;
                      background-image: linear-gradient(135deg, #ECFFF4 0%, #DCFCE7 55%, #E9FFF0 100%) !important;
                      border-color: rgba(37,211,102,.35) !important;
                    }

                    .captazap-queue-azul > section {
                      background: #F7FAFF !important;
                    }
                    .captazap-queue-azul > section > header {
                      background-color: #EEF5FF !important;
                      background-image: linear-gradient(135deg, #EEF5FF 0%, #E0ECFF 55%, #F4F8FF 100%) !important;
                      border-color: rgba(37,99,235,.30) !important;
                    }
                  `}</style>
                  <QueueList
                    minhasConversas={minhasConversasComAcompanhamento}
                  filaDeEspera={listasFiltradas.filaDeEspera}
                  pendenciaAgendaConversas={
                    listasFiltradas.pendenciaAgenda || []
                  }
                  followUpConversas={listasFiltradas.followUp || []}
                  followUpRegras={followupRegrasAtivas}
                  followUpCadenciaResumo={resumoFluxoFollowup}
                  fechadosConversas={listasFiltradas.fechados || []}
                  totalFechados={totalFechados}
                  contatos={listasFiltradas.contatos}
                  contatosBuscaGlobal={
                    Array.isArray(dadosCRMNormalizados.contatosTodos) &&
                    dadosCRMNormalizados.contatosTodos.length > 0
                      ? dadosCRMNormalizados.contatosTodos
                      : listasFiltradas.contatosBuscaGlobal ||
                        listasFiltradas.contatos ||
                        []
                  }
                  buscaOperacionalGlobal={resultadosBuscaOperacao}
                  buscandoOperacaoGlobal={buscandoBuscaOperacao}
                  onBuscarOperacaoGlobal={handleBuscarOperacaoGlobalQueueList}
                  onBuscarContatosCRMGlobal={handleBuscarContatosCRMQueueList}
                  buscandoContatosCRMGlobal={buscandoContatosCRM}
                  totalContatosCRM={
                    dadosCRMNormalizados.contatosTodos?.length || 0
                  }
                  totalContatosVisiveis={
                    listasFiltradas.totalContatosFiltrados ||
                    listasFiltradas.contatos?.length ||
                    0
                  }
                  conversaSelecionada={conversaSelecionada}
                  usuarios={usuarios}
                  conexoesWhatsApp={conexoesWhatsApp}
                  conexaoSelecionada={conexaoSelecionada}
                  conversasFixadas={conversasFixadas}
                  usuarioAtualId={usuarioIdLogado}
                  usuarioAtualNome={usuarioNomeLogado}
                  onSelectConversa={handleSelecionarConversa}
                  onChangeAba={handleChangeAbaQueueList}
                  onCarregarMais={handleCarregarMaisQueueList}
                  paginacaoPorAba={paginacaoConversasV2}
                  totaisPorAba={totaisOperacaoV2}
                  onNovoContato={() => setModalNovoContatoAberto(true)}
                  onAbrirNovoContato={() => setModalNovoContatoAberto(true)}
                  onNewContact={() => setModalNovoContatoAberto(true)}
                  onNovoLead={() => setModalNovoContatoAberto(true)}
                  onCreateLead={() => setModalNovoContatoAberto(true)}
                  novoContatoLabel="Novo contato"
                  novoContatoTitle="Novo contato"
                    novoContatoIcon={UserPlus}
                  />
                </div>
              </section>

              <section
                className={`relative min-h-0 border-x transition-colors duration-200 ${
                  instagramConexaoAtiva
                    ? "border-pink-300 bg-[#FFF8FB] shadow-[inset_10px_0_28px_rgba(225,48,108,0.07),inset_-10px_0_28px_rgba(252,175,69,0.06)]"
                    : captaZapConexaoAtiva
                      ? "border-emerald-300 bg-[#F7FFFA] shadow-[inset_10px_0_28px_rgba(37,211,102,0.06),inset_-10px_0_28px_rgba(18,140,126,0.06)]"
                      : "border-blue-300 bg-[#F7FAFF] shadow-[inset_10px_0_28px_rgba(37,99,235,0.06),inset_-10px_0_28px_rgba(96,165,250,0.06)]"
                }`}
              >
                <div
                  className={`absolute inset-x-0 top-0 z-20 h-[3px] bg-gradient-to-r ${
                    instagramConexaoAtiva
                      ? "from-[#FCAF45] via-[#E1306C] to-[#833AB4]"
                      : captaZapConexaoAtiva
                        ? "from-[#5EEB8B] via-[#25D366] to-[#128C7E]"
                        : "from-[#60A5FA] via-[#2563EB] to-[#1D4ED8]"
                  }`}
                />

                {conversaSelecionada ? (
                  <div className="flex h-full min-h-0 flex-col">
                    <ChamadoStatusBar
                      conversa={conversaSelecionada}
                      usuarioId={usuarioIdLogado}
                      usuarioNome={usuarioNomeLogado}
                      somenteVisualizacao={
                        somenteVisualizacaoSupervisaoSelecionada
                      }
                      assumindoAtendimento={assumindoAtendimento}
                      onAssumir={() =>
                        handleAbrirNovoOuAssumirChamado(conversaSelecionada)
                      }
                      onFechar={() =>
                        handleSolicitarFechamentoChamado(conversaSelecionada)
                      }
                      onTransferir={() =>
                        handleAbrirTransferirAtendimento(conversaSelecionada)
                      }
                      onCadastrarNome={() =>
                        handleAbrirCadastroNomeContato(conversaSelecionada)
                      }
                    />

                    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                      <PacienteContextoBar
                        conversa={conversaSelecionada}
                        loading={salvandoContextoLead}
                        onSelecionarLead={(leadId) =>
                          handleSelecionarLeadContexto(
                            leadId,
                            conversaSelecionada,
                          )
                        }
                      />

                      <div
                        className={`min-h-0 flex-1 overflow-hidden ${
                          instagramConexaoAtiva
                            ? "[&>div]:!bg-[#FFF8FB]"
                            : captaZapConexaoAtiva
                              ? "[&>div]:!bg-[#F7FFFA]"
                              : "[&>div]:!bg-[#F7FAFF]"
                        }`}
                      >
                        <ChatPanel
                          conversa={conversaSelecionada}
                          empresaId={empresaId}
                          usuarios={usuarios}
                          dentistas={dentistas}
                          agendamentos={agendamentos}
                          followUpRegras={followupRegrasAtivas}
                          // Identidade de renderização do chat.
                          // Em Supervisão, currentUserId aponta para o captador responsável,
                          // não para o supervisor logado. Isso evita transformar mensagens antigas
                          // em "Você" do supervisor ou inverter cliente/captador.
                          modoVisualizacao={identidadeChatSelecionada.modo}
                          somenteVisualizacao={
                            identidadeChatSelecionada.somenteVisualizacao
                          }
                          isSupervisao={
                            identidadeChatSelecionada.somenteVisualizacao
                          }
                          currentUserId={identidadeChatSelecionada.operadorId}
                          currentUserName={
                            identidadeChatSelecionada.operadorNome
                          }
                          usuarioOperadorId={
                            identidadeChatSelecionada.operadorId
                          }
                          usuarioOperadorNome={
                            identidadeChatSelecionada.operadorNome
                          }
                          responsavelOperacionalId={
                            identidadeChatSelecionada.responsavelId
                          }
                          responsavelOperacionalNome={
                            identidadeChatSelecionada.responsavelNome
                          }
                          usuarioVisualizadorId={
                            identidadeChatSelecionada.visualizadorId
                          }
                          usuarioVisualizadorNome={
                            identidadeChatSelecionada.visualizadorNome
                          }
                          envioBloqueado={envioBloqueadoSelecionada}
                          inputDisabled={envioBloqueadoSelecionada}
                          disabled={envioBloqueadoSelecionada}
                          conversaFixada={conversaSelecionadaFixada}
                          conversaFixadaPorOutro={
                            conversaSelecionadaFixadaPorOutro
                          }
                          conversaFixadaPorNome={
                            conversaSelecionadaFixadaPorNome
                          }
                          podeFixarAcompanhamento={
                            usuarioPodeFixarAcompanhamentoCaptaZap
                          }
                          fixandoConversa={fixandoConversaSelecionada}
                          onToggleFixarConversa={handleToggleFixarConversa}
                          onIniciarLigacao={iniciarChamadaSaidaCaptaZap}
                          onLoadOlderMessages={
                            carregarMensagensAnterioresConversa
                          }
                          // Reutiliza a API autenticada já existente no CaptaZap.
                          metaTemplatesRequest={requestJson}
                          onSend={(texto, conversa, context) => {
                            if (somenteVisualizacaoSupervisaoSelecionada) {
                              window.alert(
                                "Este chamado foi aberto pela Supervisão em modo visualização. Nenhuma alteração será feita por aqui.",
                              );
                              return;
                            }

                            if (precisaAbrirNovoChamadoSelecionada) {
                              window.alert(
                                "Este chamado está fechado. Para responder, primeiro clique em Reabrir chamado.",
                              );
                              return;
                            }

                            const conversaEnvio =
                              conversa ||
                              conversaSelecionadaRef.current ||
                              conversaSelecionada;

                            return handleEnviarMensagem(texto, conversaEnvio, {
                              ...context,
                              origem:
                                context?.origem ||
                                context?.origem_envio ||
                                (context?.mensagem_rapida_id ||
                                context?.mensagemRapidaId
                                  ? "mensagem_rapida_manual"
                                  : "manual"),
                              lead_contexto_id:
                                context?.lead_contexto_id ||
                                context?.leadContextoId ||
                                context?.leadId ||
                                getLeadContextoIdConversa(conversaEnvio) ||
                                getLeadContextoIdConversa(
                                  conversaSelecionada,
                                ) ||
                                obterContextoPacienteCaptaZap(
                                  conversaEnvio || conversaSelecionada,
                                  empresaId,
                                ) ||
                                null,
                              leadId:
                                context?.leadId ||
                                context?.lead_contexto_id ||
                                context?.leadContextoId ||
                                getLeadContextoIdConversa(conversaEnvio) ||
                                getLeadContextoIdConversa(
                                  conversaSelecionada,
                                ) ||
                                obterContextoPacienteCaptaZap(
                                  conversaEnvio || conversaSelecionada,
                                  empresaId,
                                ) ||
                                null,
                            });
                          }}
                        />
                      </div>

                      {envioBloqueadoSelecionada ? (
                        <div className="pointer-events-auto absolute inset-x-4 bottom-4 z-30 rounded-[24px] border border-amber-200 bg-amber-50/95 p-4 shadow-[0_18px_45px_rgba(146,64,14,0.18)] backdrop-blur">
                          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                            <div className="min-w-0">
                              <div className="text-sm font-black text-amber-900">
                                {somenteVisualizacaoSupervisaoSelecionada
                                  ? "Modo visualização da Supervisão"
                                  : contatoCRMConsultaSelecionada ||
                                      buscaOperacionalSelecionada ||
                                      precisaAssumirParaResponderSelecionada
                                    ? "Histórico disponível · assuma para responder"
                                    : "Envio bloqueado até reabrir o chamado"}
                              </div>
                              <div className="mt-1 text-xs font-semibold leading-5 text-amber-700">
                                {somenteVisualizacaoSupervisaoSelecionada
                                  ? "Você está apenas visualizando este chamado pela Supervisão. Abrir por aqui não assume, não reabre, não transfere e não altera o atendimento."
                                  : contatoCRMConsultaSelecionada ||
                                      buscaOperacionalSelecionada ||
                                      precisaAssumirParaResponderSelecionada
                                    ? `${
                                        buscaOperacionalSelecionada
                                          ? "Este chamado foi localizado pela busca da operação. "
                                          : "Este contato veio da carteira CRM. As mensagens antigas são carregadas pelo telefone para consulta. "
                                      }${
                                        getResponsavelConversaNome(
                                          conversaSelecionada,
                                        )
                                          ? `Chamado atual: ${getResponsavelConversaNome(
                                              conversaSelecionada,
                                            )}. `
                                          : ""
                                      }Para responder, assuma o chamado.`
                                    : "Este chamado foi fechado, mas o paciente enviou nova mensagem. Para responder, clique em Reabrir chamado e continue o atendimento no mesmo histórico."}
                              </div>
                            </div>

                            {!somenteVisualizacaoSupervisaoSelecionada ? (
                              <button
                                type="button"
                                onClick={() =>
                                  handleAbrirNovoOuAssumirChamado(
                                    conversaSelecionada,
                                  )
                                }
                                disabled={assumindoAtendimento}
                                className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-2xl bg-amber-600 px-4 text-xs font-black text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-700 disabled:cursor-wait disabled:opacity-60 disabled:hover:translate-y-0"
                              >
                                {assumindoAtendimento ? (
                                  <RefreshCw size={14} className="animate-spin" />
                                ) : (
                                  <Plus size={14} />
                                )}
                                {assumindoAtendimento
                                  ? chamadoFechadoSelecionado
                                    ? "Reabrindo..."
                                    : "Assumindo..."
                                  : contatoCRMConsultaSelecionada ||
                                      buscaOperacionalSelecionada ||
                                      precisaAssumirParaResponderSelecionada
                                    ? "Assumir chamado"
                                    : "Reabrir chamado"}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <PremiumLaunchpad
                    empresaNome={empresaNome}
                    totalFila={totalFila}
                    totalMinhas={totalMinhas}
                    totalLeads={totalLeads}
                    totalContatos={totalContatos}
                    producaoCaptadorHoje={producaoCaptadorHoje}
                    onOpenFirst={() => {
                      if (!primeiraConversa) return;

                      const primeiraEmpresaId =
                        primeiraConversa.empresa_id ||
                        primeiraConversa.empresaId;

                      if (
                        primeiraEmpresaId &&
                        String(primeiraEmpresaId) !== String(empresaId)
                      ) {
                        return;
                      }

                      handleSelecionarConversa(primeiraConversa);
                    }}
                    onNovoContato={handleAbrirNovoContato}
                  />
                )}
              </section>

              <section
                className={`relative min-h-0 border-l transition-all duration-300 ${
                  instagramConexaoAtiva
                    ? "border-pink-400 bg-[#FFF4FA] shadow-[-8px_0_28px_rgba(225,48,108,0.14)]"
                    : captaZapConexaoAtiva
                      ? "border-emerald-400 bg-[#F1FFF7] shadow-[-8px_0_28px_rgba(37,211,102,0.12)]"
                      : "border-blue-400 bg-[#F3F8FF] shadow-[-8px_0_28px_rgba(37,99,235,0.12)]"
                } ${
                  painelDetalhesRecolhido
                    ? "overflow-hidden"
                    : "overflow-visible"
                }`}
              >
                <div
                  className={`absolute inset-x-0 top-0 z-20 h-[3px] bg-gradient-to-r ${
                    instagramConexaoAtiva
                      ? "from-[#833AB4] via-[#E1306C] to-[#FCAF45]"
                      : captaZapConexaoAtiva
                        ? "from-[#128C7E] via-[#25D366] to-[#5EEB8B]"
                        : "from-[#1D4ED8] via-[#2563EB] to-[#60A5FA]"
                  }`}
                />

                <button
                  type="button"
                  onClick={() => setPainelDetalhesRecolhido((atual) => !atual)}
                  className={`absolute z-40 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-base font-black leading-none text-slate-600 shadow-[0_10px_30px_rgba(15,23,42,0.16)] transition hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800 ${
                    painelDetalhesRecolhido
                      ? "left-1/2 top-4 h-11 w-11 -translate-x-1/2"
                      : "right-3 top-3 h-9 w-9"
                  }`}
                  title={
                    painelDetalhesRecolhido
                      ? "Mostrar dados da lead"
                      : "Recolher painel de detalhes"
                  }
                  aria-label={
                    painelDetalhesRecolhido
                      ? "Mostrar dados da lead"
                      : "Recolher painel de detalhes"
                  }
                >
                  {painelDetalhesRecolhido ? "‹" : "›"}
                </button>

                {painelDetalhesRecolhido ? (
                  <button
                    type="button"
                    onClick={() => setPainelDetalhesRecolhido(false)}
                    className={`flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-b text-slate-500 transition ${
                      instagramConexaoAtiva
                        ? "from-[#FFF4FA] via-[#FFE8F2] to-[#FFF1DF] hover:from-[#FFE8F2] hover:to-[#FFE2F0] hover:text-[#C13584]"
                        : captaZapConexaoAtiva
                          ? "from-[#F1FFF7] via-[#E7FAEF] to-[#DDF8E8] hover:from-[#E7FAEF] hover:to-[#D2F5E0] hover:text-[#128C7E]"
                          : "from-[#F3F8FF] via-[#EAF2FF] to-[#DFEBFF] hover:from-[#EAF2FF] hover:to-[#D7E7FF] hover:text-[#1D4ED8]"
                    }`}
                    title="Mostrar terceira tela"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-blue-200 bg-white text-xl font-black shadow-sm">
                      ‹
                    </span>
                    <span
                      className="whitespace-nowrap text-[11px] font-black uppercase tracking-[0.18em]"
                      style={{ writingMode: "vertical-rl" }}
                    >
                      Detalhes
                    </span>
                  </button>
                ) : conversaSelecionada ? (
                  <div
                    className={`h-full min-h-0 ${
                      instagramConexaoAtiva
                        ? "captazap-lead-details-instagram [&>*]:!bg-[#FFF4FA]"
                        : captaZapConexaoAtiva
                          ? "captazap-lead-details-whatsapp [&>*]:!bg-[#F1FFF7]"
                          : "captazap-lead-details-azul [&>*]:!bg-[#F3F8FF]"
                    }`}
                  >
                    <style>{`
                      .captazap-lead-details-instagram > * > [class*="bg-gradient"],
                      .captazap-lead-details-instagram > * > header,
                      .captazap-lead-details-instagram [class*="bg-gradient"][class*="from-blue-"],
                      .captazap-lead-details-instagram [class*="bg-gradient"][class*="via-blue-"],
                      .captazap-lead-details-instagram [class*="bg-blue-950"],
                      .captazap-lead-details-instagram [class*="bg-blue-900"],
                      .captazap-lead-details-instagram [class*="bg-blue-800"],
                      .captazap-lead-details-instagram [class*="bg-blue-700"],
                      .captazap-lead-details-instagram [class*="bg-indigo-"],
                      .captazap-lead-details-instagram [class*="from-[#172554]"],
                      .captazap-lead-details-instagram [class*="via-[#1e3a8a]"],
                      .captazap-lead-details-instagram [class*="to-[#3730a3]"] {
                        background-color: #C13584 !important;
                        background-image: linear-gradient(135deg, #833AB4 0%, #E1306C 52%, #FCAF45 100%) !important;
                        border-color: rgba(244, 114, 182, 0.78) !important;
                        box-shadow: 0 14px 34px rgba(193, 53, 132, 0.24) !important;
                      }

                      .captazap-lead-details-instagram [class*="border-blue-"] {
                        border-color: rgba(225,48,108,.32) !important;
                      }
                      .captazap-lead-details-instagram [class*="text-blue-700"],
                      .captazap-lead-details-instagram [class*="text-blue-800"],
                      .captazap-lead-details-instagram [class*="text-blue-900"] {
                        color: #A61E62 !important;
                      }

                      .captazap-lead-details-whatsapp [class*="bg-gradient"][class*="from-blue-"],
                      .captazap-lead-details-whatsapp [class*="bg-gradient"][class*="via-blue-"],
                      .captazap-lead-details-whatsapp [class*="bg-blue-950"],
                      .captazap-lead-details-whatsapp [class*="bg-blue-900"],
                      .captazap-lead-details-whatsapp [class*="bg-blue-800"] {
                        background-color: #128C7E !important;
                        background-image: linear-gradient(135deg, #075E54 0%, #128C7E 45%, #25D366 100%) !important;
                        border-color: rgba(52, 211, 153, 0.78) !important;
                        box-shadow: 0 14px 34px rgba(18, 140, 126, 0.22) !important;
                      }

                      .captazap-lead-details-azul [class*="bg-gradient"][class*="from-blue-"],
                      .captazap-lead-details-azul [class*="bg-gradient"][class*="via-blue-"],
                      .captazap-lead-details-azul [class*="bg-blue-950"],
                      .captazap-lead-details-azul [class*="bg-blue-900"],
                      .captazap-lead-details-azul [class*="bg-blue-800"] {
                        background-color: #1D4ED8 !important;
                        background-image: linear-gradient(135deg, #1E3A8A 0%, #2563EB 48%, #60A5FA 100%) !important;
                        border-color: rgba(96, 165, 250, 0.82) !important;
                        box-shadow: 0 14px 34px rgba(37, 99, 235, 0.22) !important;
                      }

                      .captazap-lead-details-instagram [class*="text-blue-100"],
                      .captazap-lead-details-instagram [class*="text-blue-200"],
                      .captazap-lead-details-whatsapp [class*="text-blue-100"],
                      .captazap-lead-details-whatsapp [class*="text-blue-200"],
                      .captazap-lead-details-azul [class*="text-blue-100"],
                      .captazap-lead-details-azul [class*="text-blue-200"] {
                        color: rgba(255,255,255,0.92) !important;
                      }
                    `}</style>
                    <LeadDetails
                    conversa={conversaSelecionada}
                    empresaId={empresaId}
                    usuarioLogado={usuario}
                    usuarios={usuarios}
                    fontes={fontes}
                    campanhas={campanhas}
                    procedimentos={procedimentos}
                    regioes={regioes}
                    dentistas={dentistas}
                    onConverterLead={
                      somenteVisualizacaoSupervisaoSelecionada
                        ? undefined
                        : handleConverterLead
                    }
                    onAgendarTarefa={
                      somenteVisualizacaoSupervisaoSelecionada
                        ? (conversa, context) =>
                            abrirAgendamentoSupervisao(conversa, {
                              ...(context || {}),
                              origem: "supervisao",
                              somenteLeitura: true,
                              bloqueado: true,
                            })
                        : handleAgendarTarefa
                    }
                    onAdicionarTag={
                      somenteVisualizacaoSupervisaoSelecionada
                        ? undefined
                        : handleAdicionarTag
                    }
                    onVincularLead={
                      somenteVisualizacaoSupervisaoSelecionada
                        ? undefined
                        : handleVincularLead
                    }
                    onSolicitarTransferencia={
                      somenteVisualizacaoSupervisaoSelecionada
                        ? undefined
                        : handleSolicitarTransferencia
                    }
                    onIniciarConversa={
                      somenteVisualizacaoSupervisaoSelecionada
                        ? undefined
                        : handleIniciarConversa
                    }
                    onAbrirTarefas={handleAbrirTarefasLead}
                    onAbrirInteracoes={handleAbrirInteracoesLead}
                    onSelecionarLeadContexto={handleSelecionarLeadContexto}
                  />
                  </div>
                ) : (
                  <CopilotPanel
                    empresaNome={empresaNome}
                    totalFila={totalFila}
                    totalMinhas={totalMinhas}
                    totalContatos={totalContatos}
                    totalLeads={totalLeads}
                    tempoMedio={tempoMedio}
                  />
                )}
              </section>
            </div>
          </div>
        </main>

        <BomDiaProducaoModal
          isOpen={modalBomDiaProducaoAberto}
          empresaId={empresaId}
          requestJson={requestJson}
          onClose={() => setModalBomDiaProducaoAberto(false)}
        />

        <SupervisaoEquipeCaptaZap
          isOpen={supervisaoEquipeAberta}
          empresaNome={empresaNome}
          conversasEquipe={listasFiltradas.minhasEquipeConversas || []}
          filaDeEspera={listasFiltradas.filaDeEspera || []}
          followUpConversas={listasFiltradas.followUp || []}
          fechadosConversas={listasFiltradas.fechados || []}
          usuarios={usuarios}
          usuarioLogado={usuario}
          onClose={() => setSupervisaoEquipeAberta(false)}
          onAtualizar={carregarDadosCRM}
          onSelecionarConversa={handleVisualizarConversaSupervisao}
          onVisualizarConversa={handleVisualizarConversaSupervisao}
          onTransferir={(conversa) => {
            setSupervisaoEquipeAberta(false);
            handleAbrirTransferirAtendimento(conversa);
          }}
        />

        <NovoContatoModal
          isOpen={modalNovoContatoAberto}
          form={novoContatoForm}
          setForm={setNovoContatoForm}
          loading={salvandoNovoContato}
          erro={erroNovoContato}
          empresaNome={empresaNome}
          onClose={() => {
            if (salvandoNovoContato) return;

            setModalNovoContatoAberto(false);
            setErroNovoContato("");
          }}
          onConfirm={handleConfirmarNovoContato}
        />

        <CadastrarNomeContatoModal
          isOpen={modalNomeContatoAberto}
          conversa={conversaNomeContato}
          nome={nomeContatoForm}
          setNome={setNomeContatoForm}
          classificacao={classificacaoContatoForm}
          setClassificacao={setClassificacaoContatoForm}
          loading={salvandoNomeContato}
          erro={erroNomeContato}
          onClose={() => {
            if (salvandoNomeContato) return;

            setModalNomeContatoAberto(false);
            setConversaNomeContato(null);
            setNomeContatoForm("");
            setClassificacaoContatoForm("padrao");
            setErroNomeContato("");
          }}
          onConfirm={handleConfirmarNomeContato}
        />

        <FecharChamadoModal
          isOpen={modalFecharChamadoAberto}
          conversa={conversaFechamentoChamado}
          loading={fechandoChamado}
          bloqueio={bloqueioFechamentoChamado}
          followupAtivo={temFollowupAtivoParaFechamentoCaptaZap(
            conversaFechamentoChamado,
            followupsChamadosEmpresa,
          )}
          onClose={() => {
            if (fechandoChamado) return;
            setModalFecharChamadoAberto(false);
            setConversaFechamentoChamado(null);
            setBloqueioFechamentoChamado(null);
          }}
          onAbrirInteracoes={() => {
            const conversa =
              conversaFechamentoChamado || conversaSelecionadaRef.current;

            if (!conversa) return;

            setModalFecharChamadoAberto(false);
            handleAbrirInteracoesLead(conversa);
          }}
          onAbrirTarefas={() => {
            const conversa =
              conversaFechamentoChamado || conversaSelecionadaRef.current;

            if (!conversa) return;

            setModalFecharChamadoAberto(false);
            handleAbrirTarefasLead(conversa);
          }}
          onConfirm={handleConfirmarFechamentoChamado}
        />

        <ConverterLeadModal
          isOpen={modalConverterLeadAberto}
          conversa={conversaConverterLead}
          loading={salvandoConversaoLead}
          procedimentos={procedimentos}
          fontes={fontes}
          regioes={regioes}
          campanhas={campanhas}
          usuarioLogado={usuario}
          onClose={() => {
            if (salvandoConversaoLead) return;

            setModalConverterLeadAberto(false);
            setConversaConverterLead(null);
          }}
          onConfirm={handleConfirmarConverterLead}
        />

        <AgendamentosModal
          isOpen={modalAgendamentoAberto}
          somenteVisualizacao={somenteVisualizacaoSupervisaoSelecionada}
          readOnly={somenteVisualizacaoSupervisaoSelecionada}
          modoVisualizacao={
            somenteVisualizacaoSupervisaoSelecionada
              ? "supervisao"
              : "atendimento"
          }
          onClose={() => {
            setModalAgendamentoAberto(false);
            setLeadAgendamentoSelecionada(null);
          }}
          lead={leadAgendamentoSelecionada}
          onUltimoAgendamento={atualizarAgendamentoNaLead}
          onAgendamentoSalvo={async (agendamentoSalvo) => {
            await recarregarAgendamentosCaptaZap();
            await sincronizarFollowupAgendamentoCaptaZap(agendamentoSalvo);
          }}
        />

        <TransferenciaLeadModal
          isOpen={modalTransferenciaAberto}
          onClose={() => {
            setModalTransferenciaAberto(false);
            setLeadTransferenciaSelecionada(null);
          }}
          lead={leadTransferenciaSelecionada}
          usuarios={usuarios}
          onTransferido={async () => {
            const leadId = leadTransferenciaSelecionada?.id;

            if (leadId) {
              aplicarTransferenciaPendenteLocal(leadId);
            }

            try {
              const data = await requestJson(
                `/leads/transferencias/solicitacoes?empresa_id=${empresaId}&status=pendente`,
              );
              setSolicitacoesTransferencia(Array.isArray(data) ? data : []);
            } catch (err) {
              console.error(
                "CaptaZap | erro ao recarregar transferências:",
                err,
              );
            }

            setModalTransferenciaAberto(false);
            setLeadTransferenciaSelecionada(null);
          }}
        />

        <TransferirAtendimentoModal
          isOpen={modalTransferirAtendimentoAberto}
          conversa={conversaTransferirAtendimento}
          usuarios={usuarios}
          usuarioLogado={usuario}
          loading={transferindoAtendimento}
          onClose={() => {
            if (transferindoAtendimento) return;

            setModalTransferirAtendimentoAberto(false);
            setConversaTransferirAtendimento(null);
          }}
          onConfirm={handleConfirmarTransferirAtendimento}
        />

        <TarefaModal
          isOpen={modalTarefaAberto}
          somenteVisualizacao={somenteVisualizacaoSupervisaoSelecionada}
          readOnly={somenteVisualizacaoSupervisaoSelecionada}
          modoVisualizacao={
            somenteVisualizacaoSupervisaoSelecionada
              ? "supervisao"
              : "atendimento"
          }
          onClose={async () => {
            setModalTarefaAberto(false);
            setLeadTarefaSelecionada(null);
            setTarefasDaLeadSelecionada([]);
            await recarregarTarefasCaptaZap();
          }}
          lead={leadTarefaSelecionada}
          tarefas={tarefasDaLeadSelecionada}
          onSalvarTarefa={async () => {
            await recarregarTarefasCaptaZap();
          }}
        />

        <InteracoesModal
          isOpen={modalInteracoesAberto}
          somenteVisualizacao={somenteVisualizacaoSupervisaoSelecionada}
          readOnly={somenteVisualizacaoSupervisaoSelecionada}
          modoVisualizacao={
            somenteVisualizacaoSupervisaoSelecionada
              ? "supervisao"
              : "atendimento"
          }
          onClose={() => {
            setModalInteracoesAberto(false);
            setLeadInteracaoSelecionada(null);
            setInteracoesDaLeadSelecionada([]);
          }}
          lead={leadInteracaoSelecionada}
          tarefas={tarefas}
          interacoesDaLead={interacoesDaLeadSelecionada}
          onChangeInteracoes={(novasInteracoesDaLead) => {
            setInteracoes((atuais) => [
              ...(atuais || []).filter(
                (interacao) =>
                  String(interacao?.id_lead ?? interacao?.lead_id) !==
                  String(leadInteracaoSelecionada?.id),
              ),
              ...(novasInteracoesDaLead || []),
            ]);
            setInteracoesDaLeadSelecionada(novasInteracoesDaLead || []);
          }}
        />
      </div>
    </div>
  );
}

function NotificacoesNovaMensagemCaptaZap({
  notificacoes = [],
  onClose,
  onOpen,
}) {
  const lista = Array.isArray(notificacoes) ? notificacoes : [];

  if (!lista.length) return null;

  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-[min(380px,calc(100vw-2rem))] flex-col gap-2">
      {lista.map((item) => (
        <div
          key={item.id}
          className="pointer-events-auto overflow-hidden rounded-2xl border border-blue-200 bg-white/96 shadow-[0_18px_55px_rgba(15,23,42,0.20)] ring-1 ring-blue-100 backdrop-blur-xl animate-[captazapToastIn_160ms_ease-out]"
        >
          <div className="flex gap-3 p-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-blue-900 text-white shadow-lg shadow-blue-900/20">
              <MessageSquare size={18} />
            </div>

            <button
              type="button"
              onClick={() => onOpen?.(item)}
              className="min-w-0 flex-1 text-left"
              title="Abrir conversa"
            >
              <div className="flex items-center gap-2">
                <p className="truncate text-sm font-extrabold text-slate-900">
                  Nova mensagem recebida
                </p>
                {item.emFila ? (
                  <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-700 ring-1 ring-amber-200">
                    Fila
                  </span>
                ) : null}
              </div>

              <p className="mt-0.5 truncate text-[13px] font-bold text-blue-900">
                {item.nome || formatTelefoneCurtoCaptaZap(item.telefone)}
              </p>

              <p className="mt-1 line-clamp-2 text-xs leading-snug text-slate-600">
                {item.texto || "Nova mensagem"}
              </p>

              {item.responsavel_nome ? (
                <p className="mt-1 truncate text-[11px] font-semibold text-slate-400">
                  Resp.: {item.responsavel_nome}
                </p>
              ) : null}
            </button>

            <button
              type="button"
              onClick={() => onClose?.(item.id)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-lg font-bold leading-none text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
              aria-label="Fechar aviso"
              title="Fechar"
            >
              ×
            </button>
          </div>

          <div className="h-1 origin-left animate-[captazapToastLife_6500ms_linear_forwards] bg-gradient-to-r from-blue-600 via-cyan-500 to-emerald-400" />
        </div>
      ))}
    </div>
  );
}

function getTelefoneKeyFollowupFechamentoCaptaZap(value = "") {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? digits.slice(-11) : "";
}

function getStatusFollowupFechamentoCaptaZap(item = {}) {
  return String(
    item._follow_up_status ||
      item.followup_status ||
      item.status_followup_status ||
      item.status_execucao_followup ||
      item.status ||
      "",
  )
    .trim()
    .toLowerCase();
}

function itemFollowupAtivoParaFechamentoCaptaZap(item = {}) {
  if (!item || typeof item !== "object") return false;

  const status = getStatusFollowupFechamentoCaptaZap(item);
  const statusAtivo = [
    "pendente",
    "em_andamento",
    "em andamento",
    "vencido",
    "pendente_vencido",
    "pendente vencido",
    "pendente_futuro",
    "pendente futuro",
    "aguardando",
    "programado",
  ].includes(status);

  if (!statusAtivo && !item._follow_up_pendente) return false;

  const temIdFollowupReal = Boolean(
    item._followup_chamado_id ||
    item._follow_up_id ||
    item.followup_id ||
    item.followupId ||
    item.id_followup ||
    item.chamado_followup_id ||
    // item.id só vale quando o objeto é explicitamente um follow-up normalizado,
    // nunca quando é apenas uma conversa/lead comum.
    (item.id &&
      (item.followup_ordem || item.protocolo_id || item.mensagem_rapida_id)),
  );

  return Boolean(
    item._follow_up_pendente || (temIdFollowupReal && statusAtivo),
  );
}

function followupPertenceAConversaFechamentoCaptaZap(
  conversa = {},
  followup = {},
) {
  if (!conversa || !followup) return false;

  const telConversa = getTelefoneKeyFollowupFechamentoCaptaZap(
    conversa.telefone || conversa.numero || conversa.phone || "",
  );
  const telFollowup = getTelefoneKeyFollowupFechamentoCaptaZap(
    followup.telefone || followup.numero || followup.phone || "",
  );

  if (telConversa && telFollowup && telConversa === telFollowup) return true;

  const chamadoConversa = String(
    conversa.chamado_id || conversa.id_chamado || conversa.protocolo_id || "",
  );
  const chamadoFollowup = String(
    followup.protocolo_id || followup.chamado_id || followup.id_chamado || "",
  );

  if (
    chamadoConversa &&
    chamadoFollowup &&
    chamadoConversa === chamadoFollowup
  ) {
    return true;
  }

  const followupIdConversa = String(
    conversa._followup_chamado_id ||
      conversa._follow_up_id ||
      conversa.followup_id ||
      conversa.followupId ||
      conversa.id_followup ||
      conversa.chamado_followup_id ||
      "",
  );
  const followupId = String(
    followup._followup_chamado_id ||
      followup._follow_up_id ||
      followup.followup_id ||
      followup.followupId ||
      followup.id_followup ||
      followup.chamado_followup_id ||
      followup.id ||
      "",
  );

  return Boolean(
    followupIdConversa && followupId && followupIdConversa === followupId,
  );
}

function temFollowupAtivoParaFechamentoCaptaZap(
  conversa = {},
  followupsExternos = [],
) {
  if (!conversa || typeof conversa !== "object") return false;

  if (itemFollowupAtivoParaFechamentoCaptaZap(conversa)) return true;

  const listasInternas = [
    conversa._followup_fluxo_completo,
    conversa._followups_fluxo,
    conversa._follow_up_fluxo,
    conversa._followups_historico,
    conversa._follow_up_historico,
    conversa.followups_historico,
    conversa.chamado_followups,
  ];

  const temAtivoEmListaInterna = listasInternas.some(
    (lista) =>
      Array.isArray(lista) &&
      lista.some(itemFollowupAtivoParaFechamentoCaptaZap),
  );

  if (temAtivoEmListaInterna) return true;

  return (Array.isArray(followupsExternos) ? followupsExternos : []).some(
    (followup) =>
      itemFollowupAtivoParaFechamentoCaptaZap(followup) &&
      followupPertenceAConversaFechamentoCaptaZap(conversa, followup),
  );
}

function aplicarContextoFechadoFollowupNoContatoCaptaZap(
  contato = {},
  fechados = [],
) {
  if (!contato || !Array.isArray(fechados) || fechados.length === 0) {
    return contato;
  }

  const telefoneContato = normalizarTelefone(
    contato.telefone || contato.numero || contato.lead?.telefone1 || "",
  ).slice(-11);
  const leadContato = String(
    contato.lead_id || contato.leadId || contato.lead?.id || "",
  ).trim();

  const fechado = fechados.find((item) => {
    if (!item) return false;

    const telefoneFechado = normalizarTelefone(
      item.telefone || item.numero || item.lead?.telefone1 || "",
    ).slice(-11);
    const leadFechado = String(
      item.lead_id || item.leadId || item.lead?.id || "",
    ).trim();

    const bateTelefone = Boolean(
      telefoneContato && telefoneFechado && telefoneContato === telefoneFechado,
    );
    const bateLead = Boolean(
      leadContato && leadFechado && leadContato === leadFechado,
    );

    if (!bateTelefone && !bateLead) return false;

    const texto = normalizar(
      [
        item.motivo_fechamento,
        item.motivoFechamento,
        item.observacao_fechamento,
        item.observacaoFechamento,
        Array.isArray(item.tags) ? item.tags.join(" ") : "",
      ]
        .filter(Boolean)
        .join(" "),
    );

    return (
      item._followup_fechado ||
      item._fechamento_followup ||
      item.fechamento_followup ||
      texto.includes("fechamento_followup") ||
      texto.includes("fechamento followup") ||
      texto.includes("fechamento de follow") ||
      texto.includes("follow-up fechado") ||
      texto.includes("followup fechado")
    );
  });

  if (!fechado) return contato;

  return {
    ...contato,
    status: "fechado",
    status_chamado: "fechado",
    chamado_status: "fechado",
    chamada_status: "fechado",
    chamado_id:
      fechado.chamado_id || fechado.id_chamado || contato.chamado_id || null,
    id_chamado:
      fechado.chamado_id || fechado.id_chamado || contato.id_chamado || null,
    fechado_em: fechado.fechado_em || contato.fechado_em || null,
    fechadoEm:
      fechado.fechadoEm || fechado.fechado_em || contato.fechadoEm || null,
    fechado_por_id: fechado.fechado_por_id || contato.fechado_por_id || null,
    fechado_por_nome:
      fechado.fechado_por_nome || contato.fechado_por_nome || "",
    motivo_fechamento:
      fechado.motivo_fechamento ||
      fechado.motivoFechamento ||
      "Fechamento de follow-up",
    motivoFechamento:
      fechado.motivoFechamento ||
      fechado.motivo_fechamento ||
      "Fechamento de follow-up",
    observacao_fechamento:
      fechado.observacao_fechamento ||
      fechado.observacaoFechamento ||
      contato.observacao_fechamento ||
      "Follow-up fechado pelo fluxo de encerramento da régua.",
    observacaoFechamento:
      fechado.observacaoFechamento ||
      fechado.observacao_fechamento ||
      contato.observacaoFechamento ||
      "Follow-up fechado pelo fluxo de encerramento da régua.",
    conversa_ativa: false,
    conversaAtiva: false,
    sem_conversa: true,
    semConversa: true,
    aba_captazap: "fechados",
    _followup_fechado: true,
    _fechamento_followup: true,
    fechamento_followup: true,
    fechamentoFollowup: true,
    tags: Array.from(
      new Set([
        ...(contato.tags || []),
        "Chamado fechado",
        "Follow-up fechado",
      ]),
    ),
  };
}

function PacienteContextoBar({ conversa, loading = false, onSelecionarLead }) {
  const leads = getLeadsVinculadasConversa(conversa);

  if (!leads.length) return null;

  const contextoId = getLeadContextoIdConversa(conversa);
  const telefoneCompartilhado = leads.length > 1;

  if (!telefoneCompartilhado && contextoId) return null;

  return (
    <div className="shrink-0 border-b border-amber-300/80 bg-gradient-to-r from-amber-50 via-orange-50/80 to-white px-3 py-2 shadow-[0_8px_20px_rgba(217,119,6,0.08)]">
      <div className="flex min-w-0 items-center gap-2">
        <span className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-amber-300 bg-white/90 px-2.5 text-[10px] font-black uppercase tracking-wide text-amber-800 shadow-sm">
          <Users size={12} />
          {telefoneCompartilhado
            ? `${leads.length} pacientes no mesmo WhatsApp`
            : "Paciente em contexto"}
        </span>

        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto py-0.5 pr-1">
          {leads.map((lead) => {
            const leadId = getLeadId(lead);
            const active = contextoId && String(contextoId) === String(leadId);
            const nome = lead.nome_lead || lead.nome || `Lead ${leadId}`;
            const status =
              lead.status_negociacao || lead.status_agendamento || "";

            return (
              <button
                key={leadId || nome}
                type="button"
                disabled={loading || !leadId}
                onClick={() => onSelecionarLead?.(leadId)}
                className={`inline-flex h-9 max-w-[210px] shrink-0 items-center gap-2 rounded-2xl border px-3 text-left shadow-sm transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 ${
                  active
                    ? "border-blue-700 bg-blue-700 text-white shadow-blue-900/20"
                    : "border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                }`}
                title={status ? `${nome} · ${status}` : nome}
              >
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${
                    active ? "bg-white" : "bg-amber-400"
                  }`}
                />
                <span className="min-w-0">
                  <span className="block truncate text-[11px] font-black leading-tight">
                    {nome}
                  </span>
                  {status ? (
                    <span
                      className={`block truncate text-[9px] font-semibold leading-tight ${
                        active ? "text-blue-100" : "text-slate-500"
                      }`}
                    >
                      {status}
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const MOTIVOS_FECHAMENTO_CHAMADO = [
  {
    value: "Atendimento concluído",
    label: "Atendimento concluído",
    description: "Paciente foi atendido e não há pendência neste chamado.",
    tone: "emerald",
  },
  {
    value: "Spam",
    label: "Spam",
    description:
      "Mensagem indesejada, propaganda, golpe ou conteúdo sem relação.",
    tone: "rose",
  },
  {
    value: "Sem interesse",
    label: "Sem interesse",
    description: "Contato informou que não deseja seguir atendimento agora.",
    tone: "amber",
  },
  {
    value: "Número errado",
    label: "Número errado",
    description: "Contato não é o paciente/lead ou informou engano.",
    tone: "slate",
  },
  {
    value: "Duplicado",
    label: "Duplicado",
    description: "Existe outro chamado/conversa tratando o mesmo assunto.",
    tone: "blue",
  },
  {
    value: "Transferido",
    label: "Transferido",
    description: "Atendimento foi direcionado para outro responsável/canal.",
    tone: "violet",
  },
  {
    value: "Outro",
    label: "Outro motivo",
    description: "Use quando nenhuma opção acima representar o fechamento.",
    tone: "slate",
  },
];

function TransferirAtendimentoModal({
  isOpen,
  conversa,
  usuarios = [],
  usuarioLogado,
  loading = false,
  onClose,
  onConfirm,
}) {
  const [destinoUsuarioId, setDestinoUsuarioId] = useState("");
  const [motivo, setMotivo] = useState("");

  const usuarioLogadoId = getUsuarioId(usuarioLogado);

  useEffect(() => {
    if (!isOpen) return;

    setDestinoUsuarioId("");
    setMotivo("");
  }, [isOpen, conversa?.id, conversa?.conversa_id]);

  const usuariosDisponiveis = useMemo(() => {
    return (usuarios || [])
      .filter((item) => {
        const id = getUsuarioId(item);

        if (!id) return false;
        if (usuarioLogadoId && String(id) === String(usuarioLogadoId)) {
          return false;
        }

        const ativo = item?.ativo ?? item?.active ?? item?.usuario_ativo;

        if (
          ativo === 0 ||
          ativo === false ||
          String(ativo || "").toLowerCase() === "false"
        ) {
          return false;
        }

        return true;
      })
      .sort((a, b) =>
        getNomeUsuarioSistema(a).localeCompare(
          getNomeUsuarioSistema(b),
          "pt-BR",
        ),
      );
  }, [usuarios, usuarioLogadoId]);

  if (!isOpen) return null;

  const destinoSelecionado = usuariosDisponiveis.find(
    (item) => String(getUsuarioId(item)) === String(destinoUsuarioId),
  );

  const nomeContato =
    conversa?.nome ||
    conversa?.nome_lead ||
    conversa?.lead?.nome_lead ||
    conversa?.numero ||
    conversa?.telefone ||
    "Contato sem nome";

  const telefone = conversa?.numero || conversa?.telefone || "";
  const temLead = Boolean(
    conversa?.lead_id || conversa?.leadId || conversa?.lead?.id,
  );

  const confirmar = () => {
    if (!destinoUsuarioId) {
      window.alert("Selecione o captador que vai receber o atendimento.");
      return;
    }

    onConfirm?.({
      destino_tipo: "usuario",
      destino_usuario_id: Number(destinoUsuarioId),
      destino_usuario_nome: getNomeUsuarioSistema(destinoSelecionado),
      motivo: motivo.trim(),
    });
  };

  return (
    <div className="fixed inset-0 z-[92] flex items-start justify-center overflow-y-auto bg-slate-950/55 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6">
      <div className="flex max-h-[92dvh] w-full max-w-lg flex-col overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.30)]">
        <div className="shrink-0 border-b border-slate-200 bg-gradient-to-br from-blue-950 via-blue-800 to-blue-700 px-5 py-5 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-blue-100 ring-1 ring-white/15">
                <ArrowRightLeft size={13} />
                Transferir atendimento
              </div>

              <h3 className="mt-3 text-xl font-black tracking-tight">
                Enviar conversa para outro captador
              </h3>

              <p className="mt-1 text-sm font-semibold leading-5 text-blue-100">
                {temLead
                  ? "A lead continua vinculada e o chamado passa para o novo responsável."
                  : "Funciona também quando ainda é só conversa, sem lead criada."}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
              Atendimento atual
            </div>
            <div className="mt-1 truncate text-sm font-black text-slate-950">
              {nomeContato}
            </div>
            <div className="mt-0.5 truncate text-xs font-semibold text-slate-500">
              {telefone || "Telefone não informado"}
            </div>
          </div>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
              Novo captador
            </span>
            <select
              value={destinoUsuarioId}
              onChange={(event) => setDestinoUsuarioId(event.target.value)}
              disabled={loading}
              className="h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <option value="">Selecione quem vai receber</option>
              {usuariosDisponiveis.map((item) => {
                const id = getUsuarioId(item);

                return (
                  <option key={id} value={id}>
                    {getNomeUsuarioSistema(item)}
                  </option>
                );
              })}
            </select>
          </label>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
              Motivo opcional
            </span>
            <textarea
              value={motivo}
              onChange={(event) => setMotivo(event.target.value)}
              disabled={loading}
              rows={3}
              placeholder="Ex.: repassando para captador responsável pelo turno."
              className="w-full resize-none rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </label>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-11 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-black text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={confirmar}
              disabled={loading || !destinoUsuarioId}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-blue-700 px-5 text-sm font-black text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
            >
              {loading ? "Transferindo..." : "Transferir atendimento"}
              <ArrowRightLeft size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FecharChamadoModal({
  isOpen,
  conversa,
  loading = false,
  bloqueio = null,
  followupAtivo = false,
  onClose,
  onConfirm,
  onAbrirInteracoes,
  onAbrirTarefas,
}) {
  const [motivo, setMotivo] = useState("Atendimento concluído");
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setMotivo(
      followupAtivo ? "Fechamento de follow-up" : "Atendimento concluído",
    );
    setObservacao("");
  }, [isOpen, conversa?.id, followupAtivo]);

  if (!isOpen) return null;

  const motivosDisponiveis = followupAtivo
    ? [
        {
          value: "Fechamento de follow-up",
          label: "Fechamento de follow-up",
          description:
            "Use esta opção para encerrar manualmente uma régua de follow-up. Informe uma justificativa clara.",
          tone: "amber",
        },
      ]
    : MOTIVOS_FECHAMENTO_CHAMADO;

  const motivoSelecionado =
    motivosDisponiveis.find((item) => item.value === motivo) ||
    motivosDisponiveis[0];

  const fechamentoBloqueado = Boolean(bloqueio);
  const precisaInteracao = Boolean(bloqueio?.precisa_interacao);
  const precisaTarefaFutura = Boolean(bloqueio?.precisa_tarefa_futura);

  const tons = {
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-800 ring-emerald-200",
    rose: "border-rose-200 bg-rose-50 text-rose-800 ring-rose-200",
    amber: "border-amber-200 bg-amber-50 text-amber-800 ring-amber-200",
    slate: "border-slate-200 bg-slate-50 text-slate-700 ring-slate-200",
    blue: "border-blue-200 bg-blue-50 text-blue-800 ring-blue-200",
    violet: "border-violet-200 bg-violet-50 text-violet-800 ring-violet-200",
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-slate-950/45 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-4 sm:py-6">
      <div className="flex max-h-[92dvh] w-full max-w-xl flex-col overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="shrink-0 border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50 px-5 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-blue-700 ring-1 ring-blue-200">
                <CheckCircle2 size={13} />
                Fechar chamado
              </div>

              <h3 className="mt-3 text-xl font-black tracking-tight text-slate-950">
                {followupAtivo
                  ? "Fechamento de follow-up"
                  : "Escolha o motivo do fechamento"}
              </h3>

              <p className="mt-1 text-sm leading-5 text-slate-500">
                {followupAtivo
                  ? "Este chamado possui follow-up ativo. Para fechar, informe uma justificativa clara com no mínimo 30 caracteres."
                  : "O chamado ficará registrado com este motivo. Se o cliente mandar nova mensagem depois, o responsável deverá reabrir o mesmo chamado."}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
            >
              ×
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
          {conversa ? (
            <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                Conversa
              </div>
              <div className="mt-1 truncate text-sm font-black text-slate-900">
                {conversa.nome || conversa.numero || "Contato sem nome"}
              </div>
              <div className="mt-0.5 truncate text-xs font-semibold text-slate-500">
                {conversa.numero ||
                  conversa.telefone ||
                  "Telefone não informado"}
              </div>
            </div>
          ) : null}

          {followupAtivo ? (
            <div className="mb-4 rounded-[24px] border border-orange-200 bg-orange-50 px-4 py-4 text-orange-900 shadow-sm">
              <div className="text-sm font-black">
                Chamado em follow-up ativo
              </div>
              <p className="mt-1 text-xs font-semibold leading-5 text-orange-800">
                O fechamento comum fica bloqueado. Use a opção exclusiva de
                fechamento de follow-up e registre a justificativa para
                auditoria.
              </p>
            </div>
          ) : null}

          {fechamentoBloqueado ? (
            <div className="mb-4 rounded-[24px] border border-amber-200 bg-amber-50 px-4 py-4 text-amber-900 shadow-sm">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 ring-1 ring-amber-200">
                  <ShieldCheck size={17} />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-black">
                    Não foi possível fechar ainda
                  </div>

                  <p className="mt-1 text-xs font-semibold leading-5 text-amber-800">
                    {bloqueio?.mensagem ||
                      "Esta lead possui agendamento. Antes de fechar o chamado, registre uma interação e uma tarefa futura."}
                  </p>

                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div
                      className={`rounded-2xl border px-3 py-2 text-xs font-black ${
                        precisaInteracao
                          ? "border-rose-200 bg-white text-rose-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {precisaInteracao ? "✗" : "✓"} Interação registrada
                    </div>

                    <div
                      className={`rounded-2xl border px-3 py-2 text-xs font-black ${
                        precisaTarefaFutura
                          ? "border-rose-200 bg-white text-rose-700"
                          : "border-emerald-200 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {precisaTarefaFutura ? "✗" : "✓"} Tarefa futura
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {precisaInteracao ? (
                      <button
                        type="button"
                        onClick={onAbrirInteracoes}
                        className="inline-flex h-9 items-center justify-center rounded-2xl bg-amber-700 px-3 text-xs font-black text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-800"
                      >
                        Registrar interação
                      </button>
                    ) : null}

                    {precisaTarefaFutura ? (
                      <button
                        type="button"
                        onClick={onAbrirTarefas}
                        className="inline-flex h-9 items-center justify-center rounded-2xl border border-amber-300 bg-white px-3 text-xs font-black text-amber-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-100"
                      >
                        Criar tarefa
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-2">
            {motivosDisponiveis.map((item) => {
              const active = item.value === motivo;

              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setMotivo(item.value)}
                  disabled={loading}
                  className={`rounded-2xl border px-4 py-3 text-left transition hover:-translate-y-0.5 ${
                    active
                      ? `${tons[item.tone]} ring-1`
                      : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-black">{item.label}</div>
                      <div className="mt-0.5 text-xs font-semibold opacity-75">
                        {item.description}
                      </div>
                    </div>

                    {active ? <CheckCircle2 size={17} /> : null}
                  </div>
                </button>
              );
            })}
          </div>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
              {followupAtivo
                ? "Justificativa obrigatória do follow-up"
                : "Observação opcional"}
            </span>
            <textarea
              value={observacao}
              onChange={(event) => setObservacao(event.target.value)}
              placeholder={
                followupAtivo
                  ? "Explique por que este follow-up está sendo encerrado. Mínimo 30 caracteres."
                  : motivoSelecionado.value === "Spam"
                    ? "Ex.: mensagem automática, propaganda, golpe, contato indevido..."
                    : "Detalhe o fechamento, se necessário."
              }
              rows={followupAtivo ? 4 : 3}
              disabled={loading}
              className={`w-full resize-none rounded-2xl border bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:ring-4 ${
                followupAtivo &&
                observacao.trim().length > 0 &&
                observacao.trim().length < 30
                  ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500/10"
                  : "border-slate-300 focus:border-blue-500 focus:ring-blue-500/10"
              }`}
            />
            {followupAtivo ? (
              <div
                className={`mt-1 text-[11px] font-bold ${
                  observacao.trim().length >= 30
                    ? "text-emerald-700"
                    : "text-amber-700"
                }`}
              >
                {observacao.trim().length}/30 caracteres mínimos para fechar
                follow-up.
              </div>
            ) : null}
          </label>
        </div>

        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-5 py-3 sm:px-6 sm:py-4">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-11 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-600 shadow-sm transition hover:bg-slate-50"
            >
              Cancelar
            </button>

            <button
              type="button"
              disabled={loading}
              onClick={() => {
                if (loading) return;
                const observacaoFinal = String(observacao || "").trim();

                if (followupAtivo && observacaoFinal.length < 30) {
                  window.alert(
                    "Informe uma justificativa com pelo menos 30 caracteres para fechar um chamado em follow-up.",
                  );
                  return;
                }

                onConfirm?.({
                  motivo: followupAtivo ? "Fechamento de follow-up" : motivo,
                  observacao: observacaoFinal,
                  tipo_fechamento: followupAtivo ? "followup" : "",
                });
              }}
              className={`flex h-11 items-center justify-center gap-2 rounded-2xl px-5 text-sm font-black text-white shadow-lg transition hover:-translate-y-0.5 ${
                followupAtivo
                  ? "bg-gradient-to-br from-amber-600 via-orange-600 to-orange-800 shadow-orange-900/20"
                  : "bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 shadow-blue-900/20"
              }`}
            >
              {loading
                ? "Fechando..."
                : followupAtivo
                  ? "Fechar follow-up"
                  : fechamentoBloqueado
                    ? "Tentar fechar novamente"
                    : "Fechar chamado"}
              <CheckCircle2 size={15} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function NovoContatoModal({
  isOpen = false,
  form = {},
  setForm,
  loading = false,
  erro = "",
  empresaNome = "",
  onClose,
  onConfirm,
}) {
  if (!isOpen) return null;

  const atualizarCampo = (campo) => (event) => {
    const value = event?.target?.value ?? "";
    setForm?.((atual) => ({ ...atual, [campo]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    await onConfirm?.(form);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="border-b border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-blue-50 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200">
                <Plus size={13} />
                Novo contato
              </div>

              <h3 className="mt-3 text-xl font-black tracking-tight text-slate-950">
                Cadastrar contato sem criar lead
              </h3>

              <p className="mt-1 text-sm leading-5 text-slate-500">
                Informe o WhatsApp. Se quiser, você também pode enviar a
                primeira mensagem agora. O contato será salvo no CaptaZap sem
                cadastro na base de leads
                {empresaNome ? ` · ${empresaNome}` : ""}.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Fechar novo contato"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="px-6 py-5">
          <div className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Nome do contato opcional
              </span>
              <input
                value={form.nome || ""}
                onChange={atualizarCampo("nome")}
                placeholder="Ex.: Maria, João, Responsável pelo paciente..."
                disabled={loading}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 disabled:bg-slate-100"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                WhatsApp / telefone
              </span>
              <input
                value={form.telefone || ""}
                onChange={atualizarCampo("telefone")}
                placeholder="Ex.: 61999999999"
                inputMode="tel"
                disabled={loading}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 disabled:bg-slate-100"
              />
              <span className="mt-1 block text-[11px] font-semibold text-slate-400">
                Use DDD + número. O sistema normaliza para WhatsApp Brasil.
              </span>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Dra. Malu
              </span>
              <select
                value={form.dra_malu_classificacao || "padrao"}
                onChange={atualizarCampo("dra_malu_classificacao")}
                disabled={loading}
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 disabled:bg-slate-100"
              >
                <option value="padrao">Padrão · Dra. Malu permitida</option>
                <option value="parceiro_fornecedor">Parceiro / Fornecedor · bloqueada</option>
                <option value="nao_automatizar">Não automatizar · bloqueada</option>
              </select>
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Primeira mensagem opcional
              </span>
              <textarea
                value={form.mensagem || ""}
                onChange={atualizarCampo("mensagem")}
                rows={4}
                maxLength={1000}
                placeholder="Deixe em branco para apenas cadastrar o contato..."
                disabled={loading}
                className="w-full resize-none rounded-[22px] border border-slate-300 bg-white px-3 py-3 text-sm font-semibold leading-6 text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 disabled:bg-slate-100"
              />
              <span className="mt-1 block text-right text-[10px] font-semibold text-slate-400">
                {String(form.mensagem || "").length}/1000
              </span>
            </label>
          </div>

          {erro ? (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold leading-5 text-red-700">
              {erro}
            </div>
          ) : null}

          <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-xs font-semibold leading-5 text-blue-800">
            Este botão não cria lead. Sem mensagem, ele apenas salva o contato.
            Com mensagem, também inicia a conversa no CaptaZap. Se virar
            oportunidade, depois você pode converter o contato em lead.
          </div>

          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-11 rounded-2xl border border-slate-300 bg-white px-4 text-xs font-black text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 text-xs font-black text-white shadow-[0_12px_26px_rgba(5,150,105,0.20)] transition hover:-translate-y-0.5 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <Plus size={14} />
              {loading
                ? "Salvando..."
                : String(form.mensagem || "").trim()
                  ? "Cadastrar e enviar"
                  : "Cadastrar contato"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConverterLeadModal({
  isOpen,
  conversa,
  loading = false,
  procedimentos = [],
  fontes = [],
  regioes = [],
  campanhas = [],
  usuarioLogado,
  onClose,
  onConfirm,
}) {
  const telefoneInicial =
    conversa?._converter_context?.telefone ||
    conversa?.numero ||
    conversa?.telefone ||
    "";

  const nomeInicial = getNomeInicialLeadConversao(conversa);
  const captadorNome = getNomeUsuarioSistema(usuarioLogado);
  const captadorId = getUsuarioId(usuarioLogado);

  const [form, setForm] = useState({
    nome_lead: nomeInicial,
    telefone1: normalizarTelefoneLeadBrasil(telefoneInicial),
    origem: conversa?.origem || "WhatsApp",
    status_negociacao: "Prospectando",
    status_agendamento: "Sem agendamento",
    id_procedimento: "",
    id_fonte_lead: "",
    id_regiao: "",
    id_campanha: "",
    potencial: "",
    observacao: "",
  });

  useEffect(() => {
    if (!isOpen) return;

    const telefone =
      conversa?._converter_context?.telefone ||
      conversa?.numero ||
      conversa?.telefone ||
      "";

    setForm({
      nome_lead: getNomeInicialLeadConversao(conversa),
      telefone1: normalizarTelefoneLeadBrasil(telefone),
      origem: conversa?.origem || "WhatsApp",
      status_negociacao: "Prospectando",
      status_agendamento: "Sem agendamento",
      id_procedimento: "",
      id_fonte_lead: "",
      id_regiao: "",
      id_campanha: "",
      potencial: "",
      observacao: "",
    });
  }, [isOpen, conversa]);

  if (!isOpen) return null;

  const procedimentoSelecionado = findById(procedimentos, form.id_procedimento);
  const fonteSelecionada = findById(fontes, form.id_fonte_lead);
  const regiaoSelecionada = findById(regioes, form.id_regiao);
  const campanhaSelecionada = findById(campanhas, form.id_campanha);

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nome = String(form.nome_lead || "").trim();
    const telefone = normalizarTelefoneLeadBrasil(form.telefone1);

    if (!nome) {
      window.alert("Informe o nome da lead.");
      return;
    }

    if (!telefone) {
      window.alert("Informe o telefone da lead.");
      return;
    }

    if (!telefoneLeadBrasilValido(telefone)) {
      window.alert(
        "Telefone inválido. Use DDD + 9 + número. Exemplo: 61996368800. Se vier com 55, o sistema remove automaticamente.",
      );
      return;
    }

    if (!form.id_procedimento) {
      window.alert("Selecione o procedimento da lead.");
      return;
    }

    if (!form.potencial) {
      window.alert("Selecione o potencial da lead.");
      return;
    }

    if (!form.id_fonte_lead) {
      window.alert("Selecione a fonte da lead.");
      return;
    }

    if (!form.id_regiao) {
      window.alert("Selecione a região da lead.");
      return;
    }

    if (!form.id_campanha) {
      window.alert("Selecione a campanha da lead.");
      return;
    }

    onConfirm?.({
      ...form,
      nome_lead: nome,
      telefone1: telefone,
      status_negociacao: "Prospectando",
      status_agendamento: "Sem agendamento",

      id_procedimento: form.id_procedimento,
      procedimento: getItemNome(procedimentoSelecionado, [
        "nome",
        "procedimento",
        "descricao",
      ]),
      nome_procedimento: getItemNome(procedimentoSelecionado, [
        "nome",
        "procedimento",
        "descricao",
      ]),

      id_fonte_lead: form.id_fonte_lead,
      fonte: getItemNome(fonteSelecionada, ["nome", "fonte", "descricao"]),
      nome_fonte: getItemNome(fonteSelecionada, ["nome", "fonte", "descricao"]),

      id_regiao: form.id_regiao,
      regiao: getItemNome(regiaoSelecionada, ["regiao", "nome", "descricao"]),
      nome_regiao: getItemNome(regiaoSelecionada, [
        "regiao",
        "nome",
        "descricao",
      ]),

      id_campanha: form.id_campanha,
      campanha: getItemNome(campanhaSelecionada, [
        "nome",
        "campanha",
        "descricao",
      ]),
      nome_campanha: getItemNome(campanhaSelecionada, [
        "nome",
        "campanha",
        "descricao",
      ]),
    });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
      <div className="max-h-[92dvh] w-full max-w-2xl overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="border-b border-slate-200 bg-gradient-to-br from-blue-50 via-white to-emerald-50 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200">
                <Plus size={13} />
                Converter contato
              </div>

              <h3 className="mt-3 text-xl font-black tracking-tight text-slate-950">
                Transformar conversa em lead
              </h3>

              <p className="mt-1 text-sm leading-5 text-slate-500">
                A lead entrará como{" "}
                <span className="font-black text-blue-700">Prospectando</span> e
                será atribuída ao captador que está convertendo.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              ×
            </button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="max-h-[calc(92dvh-128px)] overflow-y-auto px-6 py-5"
        >
          <div className="grid grid-cols-1 gap-4">
            <div className="rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3">
              <div className="text-[11px] font-black uppercase tracking-wide text-blue-700">
                Captador responsável
              </div>
              <div className="mt-1 text-sm font-black text-slate-900">
                {captadorNome}
              </div>
              {captadorId ? (
                <div className="mt-0.5 text-xs font-semibold text-slate-500">
                  ID do usuário: {captadorId}
                </div>
              ) : null}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Nome da lead
                </span>
                <input
                  value={form.nome_lead}
                  onChange={handleChange("nome_lead")}
                  placeholder="Nome do contato"
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  WhatsApp / telefone
                </span>
                <input
                  value={form.telefone1}
                  readOnly
                  placeholder="5561999999999"
                  className="h-11 w-full cursor-not-allowed rounded-2xl border border-slate-300 bg-slate-50 px-3 text-sm font-semibold text-slate-700 outline-none"
                />
                <span className="mt-1 block text-[11px] font-semibold leading-4 text-slate-400">
                  Telefone travado pela conversa para evitar converter o contato
                  errado.
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Negociação
                </span>
                <input
                  value="Prospectando"
                  disabled
                  className="h-11 w-full cursor-not-allowed rounded-2xl border border-blue-200 bg-blue-50 px-3 text-sm font-black text-blue-800 outline-none"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Potencial *
                </span>
                <select
                  value={form.potencial}
                  onChange={handleChange("potencial")}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="">Escolher potencial</option>
                  <option value="Alto">Alto</option>
                  <option value="Médio">Médio</option>
                  <option value="Baixo">Baixo</option>
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Procedimento *
                </span>
                <select
                  value={form.id_procedimento}
                  onChange={handleChange("id_procedimento")}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="">Escolher procedimento</option>
                  {procedimentos.map((item) => (
                    <option key={getItemId(item)} value={getItemId(item)}>
                      {getItemNome(item, ["nome", "procedimento", "descricao"])}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Fonte *
                </span>
                <select
                  value={form.id_fonte_lead}
                  onChange={handleChange("id_fonte_lead")}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="">Escolher fonte</option>
                  {fontes.map((item) => (
                    <option key={getItemId(item)} value={getItemId(item)}>
                      {getItemNome(item, ["nome", "fonte", "descricao"])}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Região *
                </span>
                <select
                  value={form.id_regiao}
                  onChange={handleChange("id_regiao")}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="">Escolher região</option>
                  {regioes.map((item) => (
                    <option key={getItemId(item)} value={getItemId(item)}>
                      {getItemNome(item, ["regiao", "nome", "descricao"])}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                  Campanha *
                </span>
                <select
                  value={form.id_campanha}
                  onChange={handleChange("id_campanha")}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                >
                  <option value="">Escolher campanha</option>
                  {campanhas.map((item) => (
                    <option key={getItemId(item)} value={getItemId(item)}>
                      {getItemNome(item, ["nome", "campanha", "descricao"])}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
                Observação inicial
              </span>
              <textarea
                value={form.observacao}
                onChange={handleChange("observacao")}
                placeholder="Ex.: Lead criada a partir de conversa recebida no CaptaZap."
                rows={3}
                className="w-full resize-none rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              />
            </label>
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-11 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={loading}
              className="flex h-11 items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 px-5 text-sm font-black text-white shadow-lg shadow-blue-900/20 transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Criando lead..." : "Criar lead e vincular"}
              <ArrowRight size={15} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CadastrarNomeContatoModal({
  isOpen = false,
  conversa,
  nome = "",
  setNome,
  classificacao = "padrao",
  setClassificacao,
  loading = false,
  erro = "",
  onClose,
  onConfirm,
}) {
  if (!isOpen) return null;

  const telefone =
    conversa?.numero || conversa?.telefone || conversa?.telefone_normalizado || "";
  const jaTemNome = Boolean(getNomeContatoCadastradoCaptaZap(conversa));

  const submit = async (event) => {
    event.preventDefault();
    await onConfirm?.();
  };

  return (
    <div className="fixed inset-0 z-[96] flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-md overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
        <div className="border-b border-orange-200 bg-gradient-to-br from-orange-50 via-white to-blue-50 px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-[11px] font-black uppercase tracking-wide text-orange-800 ring-1 ring-orange-300">
                <UserRound size={13} />
                Somente contato
              </div>

              <h3 className="mt-3 text-xl font-black tracking-tight text-slate-950">
                {jaTemNome ? "Editar contato" : "Cadastrar contato"}
              </h3>

              <p className="mt-1 text-sm leading-5 text-slate-500">
                Salve o nome e defina se este contato pode receber a régua da
                Dra. Malu, sem transformá-lo em lead.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Fechar cadastro de nome"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="px-6 py-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-[11px] font-black uppercase tracking-wide text-slate-500">
              WhatsApp
            </div>
            <div className="mt-1 truncate text-sm font-black text-slate-900">
              {formatTelefoneCurtoCaptaZap(telefone) ||
                telefone ||
                "Telefone não informado"}
            </div>
          </div>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
              Nome do contato
            </span>
            <input
              value={nome}
              onChange={(event) => setNome?.(event.target.value)}
              placeholder="Ex.: Maria da Silva"
              maxLength={160}
              autoFocus
              disabled={loading}
              className="h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 disabled:bg-slate-100"
            />
          </label>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs font-black uppercase tracking-wide text-slate-500">
              Dra. Malu
            </span>
            <select
              value={classificacao || "padrao"}
              onChange={(event) => setClassificacao?.(event.target.value)}
              disabled={loading}
              className="h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 disabled:bg-slate-100"
            >
              <option value="padrao">Padrão · Dra. Malu permitida</option>
              <option value="parceiro_fornecedor">Parceiro / Fornecedor · bloqueada</option>
              <option value="nao_automatizar">Não automatizar · bloqueada</option>
            </select>
          </label>

          {erro ? (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold leading-5 text-red-700">
              {erro}
            </div>
          ) : null}

          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="h-11 rounded-2xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-600 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={loading || String(nome || "").trim().length < 2}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-orange-600 px-5 text-sm font-black text-white shadow-lg shadow-orange-900/20 transition hover:-translate-y-0.5 hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
            >
              {loading ? "Salvando..." : "Salvar contato"}
              <CheckCircle2 size={15} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function getNomeContatoCadastradoCaptaZap(conversa = {}) {
  const telefone = normalizarTelefone(
    conversa?.numero || conversa?.telefone || conversa?.telefone_normalizado || "",
  );
  const candidatos = [
    conversa?.contato_nome,
    conversa?.contatoNome,
    conversa?.nome,
    conversa?.nome_contato,
  ];
  const nomesGenericos = new Set([
    "contato sem nome",
    "novo contato",
    "whatsapp",
    "cliente",
  ]);

  for (const candidato of candidatos) {
    const valor = String(candidato || "").trim();

    if (!valor || nomesGenericos.has(normalizar(valor))) continue;

    const digitos = normalizarTelefone(valor);
    const pareceApenasTelefone =
      digitos &&
      !/[a-zA-ZÀ-ÿ]/.test(valor) &&
      (digitos === telefone ||
        digitos.slice(-11) === telefone.slice(-11));

    if (!pareceApenasTelefone) return valor;
  }

  return "";
}

function ChamadoStatusBar({
  conversa,
  usuarioId,
  usuarioNome,
  somenteVisualizacao = false,
  assumindoAtendimento = false,
  onAssumir,
  onFechar,
  onTransferir,
  onCadastrarNome,
}) {
  const status = getStatusChamadoVisual(conversa);
  const responsavelId = getResponsavelConversaId(conversa);
  const responsavelNome = getResponsavelConversaNome(conversa);
  const filaProtecaoAtiva = isFilaProtecaoCaptaZap(conversa);
  const responsavelAnteriorNome = String(
    conversa?.responsavel_anterior_nome ||
      conversa?.responsavelAnteriorNome ||
      (filaProtecaoAtiva ? responsavelNome : "") ||
      "",
  ).trim();
  const filaProtecaoMotivo = String(
    conversa?.fila_protecao_motivo || conversa?.filaProtecaoMotivo || "",
  ).trim();
  const filaProtecaoLabel = String(
    conversa?.fila_protecao_label ||
      conversa?.filaProtecaoLabel ||
      (filaProtecaoMotivo === "sem_resposta_3h"
        ? "Sem resposta há mais de 3h"
        : filaProtecaoMotivo === "retorno_apos_fechamento"
          ? "Retorno após fechamento"
          : filaProtecaoMotivo === "recuperado_dra_malu"
            ? "Recuperado pela Dra. Malu"
            : "Fila de proteção"),
  ).trim();
  const isFila =
    filaProtecaoAtiva ||
    String(conversa?.aba_captazap || "").toLowerCase() === "fila" ||
    conversa?.em_fila === true ||
    conversa?._fila_global === true ||
    conversa?._fila_equipe === true;
  const minha = Boolean(
    !filaProtecaoAtiva &&
      isConversaMinhaDoUsuario(conversa, {
        usuarioId,
        usuarioNome,
      }),
  );
  const fechadoComResposta =
    status === "fechado" &&
    (isFila || isConversaPendenteRespostaCliente(conversa));
  const chamadoFechado = status === "fechado";
  const somenteContato = Boolean(
    !(conversa?.lead_id || conversa?.leadId || conversa?.lead?.id),
  );
  const nomeContatoCadastrado =
    getNomeContatoCadastradoCaptaZap(conversa);
  const podeCadastrarNome =
    somenteContato &&
    !somenteVisualizacao &&
    typeof onCadastrarNome === "function";
  // Regra operacional v67.5:
  // - chamado FECHADO sempre oferece Reabrir para qualquer operador autenticado;
  //   o responsável anterior é apenas histórico e não pode esconder o botão;
  // - chamado ABERTO que NÃO é meu sempre oferece Assumir;
  // - ninguém envia mensagem antes de assumir;
  // - se já sou o responsável de um chamado aberto, Assumir não é necessário;
  // - depois de assumir/reabrir, Transferir e Fechar ficam disponíveis normalmente;
  // - captador/dono da Lead CRM não é alterado por Assumir o chamado.
  const podeReabrirChamadoFechado = Boolean(chamadoFechado);

  const podeAssumir =
    !somenteVisualizacao &&
    ((status === "aberto" && !minha) ||
      status === "transferido" ||
      podeReabrirChamadoFechado);
  const podeTransferir =
    !somenteVisualizacao &&
    status === "aberto" &&
    (isFila || minha) &&
    typeof onTransferir === "function";
  const podeFechar =
    !somenteVisualizacao && status === "aberto" && minha;

  const config = getChamadoStatusConfig(status, {
    isFila,
    fechadoComResposta,
    fechadoEm:
      conversa?.fechado_em ||
      conversa?.fechadoEm ||
      conversa?.data_fechamento ||
      conversa?.dataFechamento ||
      "",
  });
  const entradaCaptaZap = formatDataEntradaCaptaZap(conversa);
  const descricaoCurta =
    status === "fechado" && fechadoComResposta
      ? "Nova resposta após fechamento"
      : status === "fechado"
        ? "Encerrado"
        : status === "transferido"
          ? "Transferido"
          : isFila
            ? "Aguardando atendimento"
            : "Em atendimento";

  return (
    <div className={`shrink-0 border-b px-2.5 py-1 ${config.shell}`}>
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          {podeCadastrarNome ? (
            <button
              type="button"
              onClick={onCadastrarNome}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-orange-300 bg-orange-50 text-orange-800 transition hover:bg-orange-100"
              title={nomeContatoCadastrado ? "Editar contato / Dra. Malu" : "Cadastrar contato / Dra. Malu"}
              aria-label={nomeContatoCadastrado ? "Editar contato / Dra. Malu" : "Cadastrar contato / Dra. Malu"}
            >
              <UserPlus size={13} strokeWidth={2.5} />
            </button>
          ) : null}

          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${config.iconShell}`}>
            {config.icon}
          </div>

          <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-px text-[9px] font-black uppercase tracking-wide ring-1 ${config.badge}`}>
            {config.label}
          </span>

          {somenteContato ? (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-orange-100 px-2 py-px text-[9px] font-black uppercase tracking-wide text-orange-800 ring-1 ring-orange-300">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-orange-500" />
              {CAPTAZAP_SOMENTE_CONTATO_BADGE}
            </span>
          ) : null}

          {somenteContato &&
          ["parceiro_fornecedor", "nao_automatizar"].includes(
            String(conversa?.dra_malu_classificacao || "padrao"),
          ) ? (
            <span className="hidden shrink-0 rounded-full bg-slate-100 px-1.5 py-px text-[9px] font-black text-slate-700 ring-1 ring-slate-300 xl:inline-flex">
              Dra. Malu bloqueada
            </span>
          ) : null}

          {isFila ? (
            <span className="hidden shrink-0 rounded-full bg-amber-50 px-1.5 py-px text-[9px] font-black uppercase text-amber-700 ring-1 ring-amber-200 lg:inline-flex">Fila</span>
          ) : null}
          {filaProtecaoAtiva && filaProtecaoLabel ? (
            <span
              className="hidden max-w-[180px] shrink-0 truncate rounded-full bg-rose-50 px-1.5 py-px text-[9px] font-black text-rose-700 ring-1 ring-rose-200 xl:inline-flex"
              title={filaProtecaoLabel}
            >
              {filaProtecaoLabel}
            </span>
          ) : null}
          {minha ? (
            <span className="hidden shrink-0 rounded-full bg-blue-50 px-1.5 py-px text-[9px] font-black uppercase text-blue-700 ring-1 ring-blue-200 lg:inline-flex">Minha</span>
          ) : null}

          <span className="hidden min-w-0 truncate text-[10px] font-semibold text-slate-600 md:inline">
            {descricaoCurta}
          </span>

          {entradaCaptaZap ? (
            <span className="hidden shrink-0 text-[9.5px] font-semibold text-slate-500 lg:inline">
              <span className="font-black text-blue-700">Entrada</span> {entradaCaptaZap}
            </span>
          ) : null}

          <span className="hidden shrink-0 text-slate-300 lg:inline">•</span>

          {filaProtecaoAtiva && responsavelAnteriorNome ? (
            <span
              className="min-w-0 truncate text-[9.5px] font-semibold text-amber-700"
              title={`Responsável preservado: ${responsavelAnteriorNome} · disponível na Fila para assumir ou transferir`}
            >
              <span className="font-black text-amber-800">Preservado:</span>{" "}
              {responsavelAnteriorNome}
            </span>
          ) : responsavelNome ? (
            <span className="min-w-0 truncate text-[9.5px] font-semibold text-slate-600" title={`Responsável: ${responsavelNome}`}>
              <span className="font-black text-slate-700">Resp.</span> {responsavelNome}
            </span>
          ) : (
            <span className="shrink-0 text-[9.5px] font-black text-amber-700">Sem responsável</span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {podeAssumir ? (
            <button
              type="button"
              onClick={onAssumir}
              disabled={assumindoAtendimento}
              className="inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-blue-700 px-2 text-[10px] font-black text-white transition hover:bg-blue-800 disabled:cursor-wait disabled:opacity-60"
              title={
                assumindoAtendimento
                  ? chamadoFechado
                    ? "Reabrindo chamado..."
                    : "Assumindo chamado..."
                  : chamadoFechado
                    ? "Reabrir chamado"
                    : "Assumir chamado"
              }
            >
              {assumindoAtendimento ? (
                <RefreshCw size={12} className="animate-spin" />
              ) : (
                <Headphones size={12} />
              )}
              <span className="hidden 2xl:inline">
                {assumindoAtendimento
                  ? chamadoFechado
                    ? "Reabrindo..."
                    : "Assumindo..."
                  : chamadoFechado
                    ? "Reabrir"
                    : "Assumir"}
              </span>
            </button>
          ) : null}

          {podeTransferir ? (
            <button
              type="button"
              onClick={onTransferir}
              className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 text-[10px] font-black text-blue-700 transition hover:bg-blue-100"
              title="Transferir chamado"
            >
              <ArrowRightLeft size={12} />
              <span className="hidden 2xl:inline">Transferir</span>
            </button>
          ) : null}

          {podeFechar ? (
            <button
              type="button"
              onClick={onFechar}
              className="inline-flex h-7 items-center justify-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 text-[10px] font-black text-emerald-700 transition hover:bg-emerald-100"
              title="Fechar chamado"
            >
              <CheckCircle2 size={12} />
              <span className="hidden 2xl:inline">Fechar</span>
            </button>
          ) : null}

          {somenteVisualizacao ? (
            <span className="inline-flex h-7 items-center rounded-lg border border-slate-200 bg-white px-2 text-[9px] font-black text-slate-500">Somente leitura</span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function isChamadoFechadoComRespostaCaptaZap(conversa = {}) {
  const status = getStatusChamadoVisual(conversa);

  if (status !== "fechado") return false;

  // Regra definitiva:
  // chamado fechado só entra no fluxo de reabertura quando existir mensagem do
  // cliente posterior ao fechamento, ou flag explícita de nova mensagem após
  // fechamento. Não use em_fila/_fila_global/naoLidas isoladamente, porque
  // esses campos podem ser restos do estado anterior ao fechamento.
  return getClienteRespondeuDepoisDeFechado(conversa);
}

function getResponsavelReaberturaChamadoFechado(conversa = {}) {
  return {
    id: getPrimeiroValor([
      conversa.fechado_por_id,
      conversa.responsavel_id,
      conversa.usuario_responsavel_id,
      conversa.atendente_id,
      conversa.operador_id,
      conversa.captador_id,
      conversa._captazap_assumida_por,
    ]),
    nome: getPrimeiroValor([
      conversa.fechado_por_nome,
      conversa.responsavel_nome,
      conversa.usuario_responsavel_nome,
      conversa.atendente_nome,
      conversa.operador_nome,
      conversa.captador_nome,
      conversa._captazap_assumida_por_nome,
    ]),
  };
}

function getMsDesdeFechamentoChamado(conversa = {}) {
  const dataFechamento = getDataFechamentoChamado(conversa);

  if (!dataFechamento) return null;

  const timestamp = dataFechamento.getTime();

  if (!Number.isFinite(timestamp)) return null;

  return Math.max(0, Date.now() - timestamp);
}

function getUsuarioAtivoPorResponsavel(responsavel = {}, usuarios = []) {
  if (!responsavel?.id && !responsavel?.nome) return null;

  if (!Array.isArray(usuarios) || !usuarios.length) {
    return { _ativoPresumido: true };
  }

  return (
    usuarios.find((usuario) => {
      const usuarioId = getUsuarioId(usuario);
      const usuarioNome = getNomeUsuarioSistema(usuario);

      const mesmoId =
        responsavel.id &&
        usuarioId &&
        String(responsavel.id) === String(usuarioId);
      const mesmoNome =
        responsavel.nome &&
        usuarioNome &&
        normalizarNomeComparacao(responsavel.nome) ===
          normalizarNomeComparacao(usuarioNome);

      if (!mesmoId && !mesmoNome) return false;

      const ativo =
        usuario?.ativo ??
        usuario?.active ??
        usuario?.usuario_ativo ??
        usuario?.status_ativo ??
        usuario?.is_active ??
        usuario?.status;

      if (
        ativo === 0 ||
        ativo === false ||
        String(ativo || "").toLowerCase() === "false" ||
        String(ativo || "").toLowerCase() === "inativo" ||
        String(ativo || "").toLowerCase() === "desativado" ||
        String(ativo || "").toLowerCase() === "desligado"
      ) {
        return false;
      }

      return true;
    }) || null
  );
}

function getDestinoReaberturaChamadoFechado(
  conversa = {},
  { usuarios = [] } = {},
) {
  const responsavel = getResponsavelReaberturaChamadoFechado(conversa);

  return {
    modo: "fila_protecao",
    motivo: "retorno_apos_fechamento",
    responsavel,
    minhas: false,
    fila: true,
  };
}

function getDescricaoReaberturaChamadoFechado(destino = {}) {
  const nomeAnterior = destino?.responsavel?.nome || "";

  return `Cliente respondeu após o fechamento. O chamado foi reaberto automaticamente e enviado para a Fila, aguardando alguém assumir.${
    nomeAnterior ? ` Responsável anterior: ${nomeAnterior}.` : ""
  }`;
}

function isUsuarioResponsavelPelaReabertura(
  conversa = {},
  { usuarioId, usuarioNome, usuarios = [] } = {},
) {
  const destino = getDestinoReaberturaChamadoFechado(conversa, { usuarios });
  const responsavel = destino.responsavel;

  if (!destino.minhas) return false;

  const mesmoId =
    usuarioId && responsavel.id && String(usuarioId) === String(responsavel.id);

  const mesmoNome =
    usuarioNome &&
    responsavel.nome &&
    normalizarNomeComparacao(usuarioNome) ===
      normalizarNomeComparacao(responsavel.nome);

  return Boolean(mesmoId || mesmoNome);
}

function prepararReaberturaAutomaticaAposFechamento(
  conversa = {},
  contextoAtendimento = {},
) {
  if (!isChamadoFechadoComRespostaCaptaZap(conversa)) return conversa;

  const destino = getDestinoReaberturaChamadoFechado(conversa, {
    usuarios: contextoAtendimento.usuarios,
  });

  const responsavel = destino.responsavel || { id: null, nome: "" };
  const tagsAtuais = Array.isArray(conversa.tags) ? conversa.tags : [];

  return {
    ...conversa,

    status: "aguardando_atendimento",
    status_chamado: "aberto",
    chamado_status: "aberto",
    chamada_status: "aberto",

    // Mas torna operacional para aparecer em Minhas/Fila.
    conversa_ativa: true,
    conversaAtiva: true,
    sem_conversa: false,
    semConversa: false,

    aba_captazap: "fila",
    em_fila: true,
    fila_atendimento: true,
    aguardando_atendimento: true,
    _fila_global: true,
    _fila_equipe: true,
    _fila_resgate: Boolean(responsavel.id || responsavel.nome),
    fila_protecao: true,
    filaProtecao: true,
    fila_protecao_motivo: "retorno_apos_fechamento",
    filaProtecaoMotivo: "retorno_apos_fechamento",
    fila_protecao_label: "Retorno após fechamento",
    filaProtecaoLabel: "Retorno após fechamento",

    naoLidas: Math.max(1, Number(conversa.naoLidas || 0)),
    // Fallback visual: o backend é quem reabre de verdade. Não exigir ação
    // manual do captador enquanto a reconciliação chega.
    reabertura_pendente: false,
    reaberturaPendente: false,
    cliente_respondeu_apos_fechamento: true,
    respondeu_apos_fechamento: true,
    nova_mensagem_apos_fechamento: true,
    novaRespostaAposFechamento: true,
    _fechado_com_resposta: true,

    // O responsável anterior permanece visível enquanto a conversa está na
    // Fila de Proteção. A própria flag de proteção impede que volte para Minhas
    // até alguém clicar em Assumir.
    responsavel_anterior_id: responsavel.id || conversa.responsavel_id || null,
    responsavel_anterior_nome:
      responsavel.nome || conversa.responsavel_nome || "",
    responsavel_id: responsavel.id || conversa.responsavel_id || null,
    responsavel_nome: responsavel.nome || conversa.responsavel_nome || "",
    captador_id:
      responsavel.id || conversa.captador_id || conversa.responsavel_id || null,
    captador_nome:
      responsavel.nome || conversa.captador_nome || conversa.responsavel_nome || "",
    usuario_responsavel_id:
      responsavel.id || conversa.usuario_responsavel_id || conversa.responsavel_id || null,
    usuario_responsavel_nome:
      responsavel.nome || conversa.usuario_responsavel_nome || conversa.responsavel_nome || "",
    atendente_id:
      responsavel.id || conversa.atendente_id || conversa.responsavel_id || null,
    atendente_nome:
      responsavel.nome || conversa.atendente_nome || conversa.responsavel_nome || "",
    operador_id:
      responsavel.id || conversa.operador_id || conversa.responsavel_id || null,
    operador_nome:
      responsavel.nome || conversa.operador_nome || conversa.responsavel_nome || "",

    tags: Array.from(
      new Set(
        [
          ...tagsAtuais,
          "Chamado fechado",
          "Nova resposta",
          "Aguardando reabertura",
        ].filter(Boolean),
      ),
    ),
    timeline: [
      {
        tipo: "Nova resposta em chamado fechado",
        descricao: getDescricaoReaberturaChamadoFechado(destino),
        data: "Agora",
      },
      ...(Array.isArray(conversa.timeline) ? conversa.timeline : []),
    ],
  };
}

function getStatusChamadoVisual(conversa = {}) {
  const status = String(
    conversa.status_chamado ||
      conversa.chamado_status ||
      conversa.statusChamado ||
      conversa.chamada_status ||
      "",
  )
    .trim()
    .toLowerCase();

  const fechadoEmRaw =
    conversa.fechado_em ||
    conversa.fechadoEm ||
    conversa.data_fechamento ||
    conversa.dataFechamento ||
    conversa.closed_at ||
    conversa.closedAt ||
    "";
  const abertoEmRaw =
    conversa.aberto_em ||
    conversa.abertoEm ||
    conversa.opened_at ||
    conversa.openedAt ||
    "";

  const fechadoEm = fechadoEmRaw ? parseDataCaptaZap(fechadoEmRaw) : null;
  const abertoEm = abertoEmRaw ? parseDataCaptaZap(abertoEmRaw) : null;
  const fechamentoEhMaisRecente = Boolean(
    fechadoEm &&
      (!abertoEm || fechadoEm.getTime() >= abertoEm.getTime()),
  );

  // Defesa de consistência: algumas origens do front (Contatos/Busca/Supervisão)
  // podem chegar sem status_chamado ou com um fallback antigo "aberto".
  // Se há fechamento real posterior à abertura, o fechamento vence.
  if (status === "fechado") return "fechado";
  if (status === "transferido") return "transferido";
  if (status === "aberto") {
    return fechamentoEhMaisRecente ? "fechado" : "aberto";
  }

  if (fechamentoEhMaisRecente) return "fechado";

  const temEvidenciaFechamento = Boolean(
    fechadoEmRaw ||
      conversa.fechado_por_id ||
      conversa.fechado_por_nome ||
      conversa.motivo_fechamento ||
      conversa.motivoFechamento,
  );

  if (temEvidenciaFechamento) return "fechado";

  // Compatibilidade com contatos ainda sem chamado: apenas nesses casos
  // continuamos tratando como aberto até a fonte operacional ser reconciliada.
  return "aberto";
}

function getChamadoStatusConfig(
  status,
  { isFila = false, fechadoComResposta = false, fechadoEm = "" } = {},
) {
  const dataFechamentoTexto = fechadoEm
    ? formatDataHoraCaptaZap(fechadoEm)
    : "";
  if (status === "fechado" && fechadoComResposta) {
    return {
      label: "Retorno após fechamento",
      description:
        "O cliente respondeu depois do fechamento. O atendimento deve voltar automaticamente para a Fila; este estado indica que a sincronização ainda está sendo reconciliada.",
      shell: "border-amber-200 bg-amber-50/90",
      badge: "bg-amber-100 text-amber-800 ring-amber-200",
      iconShell: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
      icon: <Inbox size={18} />,
    };
  }

  if (status === "fechado") {
    return {
      label: "Chamado fechado",
      description: dataFechamentoTexto
        ? `Encerrado em ${dataFechamentoTexto}. Se o cliente responder novamente, o chamado será reaberto automaticamente e enviado para a Fila.`
        : "Este atendimento foi encerrado. Se o cliente responder novamente, o chamado será reaberto automaticamente e enviado para a Fila.",
      shell: "border-slate-200 bg-slate-50/95",
      badge: "bg-slate-100 text-slate-700 ring-slate-200",
      iconShell: "bg-slate-100 text-slate-600 ring-1 ring-slate-200",
      icon: <CheckCircle2 size={18} />,
    };
  }

  if (status === "transferido") {
    return {
      label: "Chamado transferido",
      description:
        "Este chamado foi transferido e precisa ser assumido pelo destino.",
      shell: "border-violet-200 bg-violet-50/90",
      badge: "bg-violet-100 text-violet-800 ring-violet-200",
      iconShell: "bg-violet-100 text-violet-700 ring-1 ring-violet-200",
      icon: <ArrowRight size={18} />,
    };
  }

  if (isFila) {
    return {
      label: "Chamado aberto",
      description:
        "Chamado aberto aguardando alguém assumir para iniciar atendimento.",
      shell: "border-amber-200 bg-amber-50/90",
      badge: "bg-amber-100 text-amber-800 ring-amber-200",
      iconShell: "bg-amber-100 text-amber-700 ring-1 ring-amber-200",
      icon: <Clock3 size={18} />,
    };
  }

  return {
    label: "Chamado aberto",
    description: "Chamado em atendimento.",
    shell: "border-blue-200 bg-blue-50/80",
    badge: "bg-blue-100 text-blue-800 ring-blue-200",
    iconShell: "bg-blue-100 text-blue-700 ring-1 ring-blue-200",
    icon: <Headphones size={18} />,
  };
}

function PremiumLaunchpad({
  empresaNome,
  totalFila,
  totalMinhas,
  totalLeads,
  totalContatos,
  producaoCaptadorHoje,
  onOpenFirst,
  onNovoContato,
}) {
  return (
    <div className="flex h-full min-h-0 items-start justify-center overflow-y-auto px-3 py-2 sm:px-6 sm:py-3 lg:px-10">
      <div className="w-full max-w-2xl">
        <div className="relative overflow-visible rounded-[28px] border border-slate-300 bg-white p-4 shadow-[0_28px_80px_rgba(30,64,175,0.14)] sm:rounded-[32px] sm:p-5 lg:p-6">
          <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-blue-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 left-10 h-56 w-56 rounded-full bg-orange-400/20 blur-3xl" />

          <div className="relative">
            <div className="mx-auto flex h-14 w-14 items-center justify-center sm:h-16 sm:w-16 lg:h-[4.5rem] lg:w-[4.5rem]">
              <img
                src={LOGO_CAPTAZAP_ICON}
                alt="CaptaZap"
                className="h-14 w-14 object-contain drop-shadow-[0_14px_28px_rgba(249,115,22,0.30)] sm:h-16 sm:w-16 lg:h-[4.5rem] lg:w-[4.5rem]"
              />
            </div>

            <div className="mt-2 text-center sm:mt-3 lg:mt-4">
              <h2 className="text-[21px] font-semibold leading-tight tracking-tight text-slate-950 sm:text-[24px] lg:text-[26px]">
                Transforme conversas em receita
              </h2>

              <p className="mx-auto mt-2 max-w-md text-xs leading-5 text-slate-500 sm:text-sm sm:leading-6">
                Atendimento ativo para{" "}
                <span className="font-semibold text-slate-800">
                  {empresaNome}
                </span>
                . Responda contatos, consulte sua carteira de leads e inicie
                conversas pelo CaptaZap.
              </p>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:mt-4 lg:gap-2.5">
              <LaunchMetric
                icon={<Clock3 size={18} />}
                label="Fila"
                value={totalFila}
                text="novos contatos"
                tone="amber"
              />

              <LaunchMetric
                icon={<Headphones size={18} />}
                label="Atendimento"
                value={totalMinhas}
                text="conversas abertas"
                tone="blue"
              />

              <LaunchMetric
                icon={<TrendingUp size={18} />}
                label="Leads"
                value={totalLeads}
                text="base CRM"
                tone="emerald"
              />

              <LaunchMetric
                icon={<Phone size={18} />}
                label="Contatos"
                value={totalContatos}
                text="para iniciar"
                tone="violet"
              />
            </div>

            <div
              className="mt-3 rounded-[20px] border border-blue-100 bg-gradient-to-br from-blue-50 via-white to-amber-50/70 p-2.5 shadow-[0_14px_34px_rgba(30,64,175,0.09)] sm:mt-3 sm:rounded-[24px] sm:p-3"
              aria-label="Resumo da produção do captador logado somente no dia de hoje."
            >
              <div className="flex flex-col gap-0.5 text-center sm:gap-1">
                <div className="text-[10px] font-black uppercase tracking-[0.16em] text-blue-700/70 sm:text-[11px] sm:tracking-[0.18em]">
                  Sua produção hoje
                </div>
                <div className="text-[11px] font-semibold text-slate-600 sm:text-sm sm:text-slate-700">
                  Resultado do captador logado no dia
                </div>
              </div>

              <div className="mt-2 grid grid-cols-3 gap-1.5 sm:grid-cols-3 sm:gap-2 xl:grid-cols-6">
                <ProducaoHojeMetric
                  icon={<Plus size={13} />}
                  label="Leads"
                  value={producaoCaptadorHoje?.cadastrouHoje || 0}
                  tone="blue"
                  help="Leads cadastradas hoje pelo captador logado."
                  tooltipAlign="left"
                />
                <ProducaoHojeMetric
                  icon={<Activity size={13} />}
                  label="Produção"
                  value={producaoCaptadorHoje?.producaoHoje || 0}
                  tone="cyan"
                  help="Total de agendamentos e reagendamentos criados hoje pelo captador logado."
                />
                <ProducaoHojeMetric
                  icon={<Clock3 size={13} />}
                  label="Agenda"
                  value={producaoCaptadorHoje?.agendaHoje || 0}
                  tone="emerald"
                  help="Quantidade de pacientes na agenda de hoje vinculados ao captador logado."
                />
                <ProducaoHojeMetric
                  icon={<CheckCircle2 size={13} />}
                  label="Compareceu"
                  value={producaoCaptadorHoje?.compareceuHoje || 0}
                  tone="green"
                  help="Pacientes da agenda de hoje marcados como compareceu."
                />
                <ProducaoHojeMetric
                  icon={<TrendingUp size={13} />}
                  label="Fechou"
                  value={producaoCaptadorHoje?.fechamentosHoje || 0}
                  tone="amber"
                  help="Quantidade de recebimentos/fechamentos registrados hoje para leads do captador logado."
                />
                <ProducaoHojeMetric
                  icon={<Sparkles size={13} />}
                  label="Receita"
                  value={moedaCaptaZap(producaoCaptadorHoje?.receitaHoje || 0)}
                  tone="gold"
                  compact
                  help="Valor total recebido hoje em fechamentos vinculados ao captador logado."
                  tooltipAlign="right"
                />
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 sm:gap-3 lg:mt-4">
              <button
                type="button"
                onClick={onOpenFirst}
                className="flex h-10 flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 px-3 text-xs font-semibold text-white shadow-xl shadow-blue-900/25 transition hover:-translate-y-0.5 hover:from-blue-700 hover:via-blue-800 hover:to-blue-950 sm:h-11 sm:px-4 sm:text-sm"
              >
                Abrir próxima conversa
                <ArrowRight size={16} />
              </button>

              <button
                type="button"
                onClick={onNovoContato}
                className="flex h-10 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 sm:h-11 sm:px-4 sm:text-sm"
              >
                <Plus size={16} />
                Novo contato
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopilotPanel({
  empresaNome,
  totalFila,
  totalMinhas,
  totalContatos,
  totalLeads,
  tempoMedio,
}) {
  return (
    <aside className="flex h-full flex-col bg-white">
      <header className="border-b border-slate-300 px-5 py-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-950">
              Copiloto CRM
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Operação ativa em {empresaNome}.
            </p>
          </div>

          <div className="flex h-11 w-11 items-center justify-center">
            <img
              src={LOGO_CAPTAZAP_ICON}
              alt="CaptaZap"
              className="h-11 w-11 object-contain drop-shadow-[0_8px_16px_rgba(249,115,22,0.25)]"
            />
          </div>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="rounded-[28px] bg-gradient-to-br from-blue-600 via-blue-700 to-blue-950 p-5 text-white shadow-xl shadow-blue-900/25">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold">Canal operacional</div>
              <div className="mt-1 text-xs leading-5 text-blue-100">
                WhatsApp conectado para esta empresa.
              </div>
            </div>

            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-400/15 text-emerald-200 ring-1 ring-white/10">
              <CheckCircle2 size={19} />
            </span>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3">
            <DarkStat label="Fila" value={totalFila} />
            <DarkStat label="Minhas" value={totalMinhas} />
            <DarkStat label="Contatos" value={totalContatos} />
            <DarkStat label="Resposta" value={tempoMedio} />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <SoftStat
            icon={<TrendingUp size={16} />}
            label="Leads"
            value={totalLeads}
            tone="emerald"
          />

          <SoftStat
            icon={<Activity size={16} />}
            label="Conversão"
            value="32%"
            tone="violet"
          />
        </div>

        <section className="mt-5">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-900">
              Ações inteligentes
            </h4>

            <span className="rounded-full bg-orange-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-orange-700 ring-1 ring-orange-200">
              CRM
            </span>
          </div>

          <div className="space-y-2">
            <ActionShortcut
              icon={<Plus size={16} />}
              title="Criar lead manual"
              description="Registrar oportunidade nesta empresa"
            />

            <ActionShortcut
              icon={<Inbox size={16} />}
              title="Revisar fila"
              description="Priorizar contatos aguardando"
            />

            <ActionShortcut
              icon={<Phone size={16} />}
              title="Iniciar conversa"
              description="Abrir contato da carteira CRM"
            />

            <ActionShortcut
              icon={<Settings size={16} />}
              title="Automações"
              description="Templates, tags e distribuição"
            />
          </div>
        </section>

        <section className="mt-5 rounded-[28px] border border-orange-200 bg-orange-50 p-4">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-orange-700" />
            <h4 className="text-sm font-semibold text-slate-900">
              Recomendação
            </h4>
          </div>

          <p className="mt-2 text-xs leading-5 text-slate-600">
            Use a aba Contatos para acessar leads já cadastrados no CaptaCRM e
            iniciar uma conversa mesmo quando ainda não existir atendimento
            aberto.
          </p>
        </section>
      </div>
    </aside>
  );
}

async function buscarRegrasFollowupCaptaZap({ empresaId }) {
  if (!empresaId) return [];

  const data = await requestJson(
    `/api/followup-whatsapp/empresa/${empresaId}/regras`,
  );
  const lista = Array.isArray(data) ? data : data?.regras || data?.data || [];

  return (lista || [])
    .filter((item) => item && Number(item.followup_ordem || 0) > 0)
    .map((item) => ({
      ...item,
      id: item.id == null ? null : Number(item.id),
      followup_ordem: Number(item.followup_ordem || 0),
      followup_canal: String(
        item.followup_canal || item.canal || "whatsapp",
      ).toLowerCase(),
      titulo: item.titulo || `Tentativa ${item.followup_ordem || ""}`,
      texto: item.texto || "",
    }))
    .sort((a, b) => a.followup_ordem - b.followup_ordem);
}

async function buscarFollowupsChamadosCaptaZap({
  empresaId,
  usuarioId,
  usuarioNome,
} = {}) {
  if (!empresaId || !usuarioId) {
    return { followups: [], data: [] };
  }

  const montarParams = (status) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);

    // A fila operacional deve mostrar somente etapas vencidas/acionáveis.
    // A próxima etapa pode ficar registrada como pendente no banco, mas só volta
    // para Follow-up quando agendado_para chegar. Histórico e ChatPanel continuam
    // recebendo as etapas realizadas em consulta separada.
    if (status === "pendente") params.set("atrasados", "1");
    params.set("responsavel_id", String(usuarioId));
    params.set("usuario_id", String(usuarioId));

    if (usuarioNome) {
      params.set("responsavel_nome", String(usuarioNome));
    }

    return params;
  };

  // Precisamos dos pendentes para a aba Follow-up e também dos realizados
  // para sinalizar no QueueList/ChatPanel que o contato já está dentro da régua.
  const tentativas = await Promise.allSettled([
    requestJson(
      `/api/followup-whatsapp/empresa/${empresaId}?${montarParams("pendente").toString()}`,
    ),
    requestJson(
      `/api/followup-whatsapp/empresa/${empresaId}?${montarParams("realizado").toString()}`,
    ),
  ]);

  const followups = tentativas
    .flatMap((resultado) =>
      pickArray(resultado, ["followups", "data", "items", "registros"]),
    )
    .filter(Boolean);

  const unicos = new Map();

  for (const item of followups) {
    const key = String(
      item?.id ||
        `${item?.telefone || "sem-telefone"}-${item?.followup_ordem || "sem-ordem"}-${item?.status || "sem-status"}-${item?.agendado_para || item?.realizado_em || "sem-data"}`,
    );

    if (!unicos.has(key)) unicos.set(key, item);
  }

  return { followups: [...unicos.values()], data: [...unicos.values()] };
}

async function buscarFollowupsChamadosEmpresaCaptaZap({ empresaId } = {}) {
  if (!empresaId) {
    return { followups: [], data: [] };
  }

  const montarParams = (status) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);

    // A fila operacional deve mostrar somente etapas vencidas/acionáveis.
    // A próxima etapa pode ficar registrada como pendente no banco, mas só volta
    // para Follow-up quando agendado_para chegar. Histórico e ChatPanel continuam
    // recebendo as etapas realizadas em consulta separada.
    if (status === "pendente") params.set("atrasados", "1");
    return params;
  };

  const tentativas = await Promise.allSettled([
    requestJson(
      `/api/followup-whatsapp/empresa/${empresaId}?${montarParams("pendente").toString()}`,
    ),
    requestJson(
      `/api/followup-whatsapp/empresa/${empresaId}?${montarParams("realizado").toString()}`,
    ),
  ]);

  const followups = tentativas
    .flatMap((resultado) =>
      pickArray(resultado, ["followups", "data", "items", "registros"]),
    )
    .filter(Boolean);

  const unicos = new Map();

  for (const item of followups) {
    const key = String(
      item?.id ||
        `${item?.telefone || "sem-telefone"}-${item?.followup_ordem || "sem-ordem"}-${item?.status || "sem-status"}-${item?.agendado_para || item?.realizado_em || "sem-data"}`,
    );

    if (!unicos.has(key)) unicos.set(key, item);
  }

  return { followups: [...unicos.values()], data: [...unicos.values()] };
}

function followupPertenceAoResponsavelLogadoCaptaZap(
  item = {},
  { usuarioId, usuarioNome } = {},
) {
  if (!usuarioId && !usuarioNome) return false;

  const idsResponsavel = [
    item.responsavel_id,
    item.usuario_responsavel_id,
    item.atendente_id,
    item.operador_id,
    item.captador_id,
    item.executar_por_usuario_id,
    item.usuario_id,
    item.chamado?.responsavel_id,
    item.chamado?.usuario_responsavel_id,
    item.chamado?.atendente_id,
    item.chamado?.operador_id,
    item.chamado?.captador_id,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  if (usuarioId && idsResponsavel.some((id) => id === String(usuarioId))) {
    return true;
  }

  const nomesResponsavel = [
    item.responsavel_nome,
    item.usuario_responsavel_nome,
    item.atendente_nome,
    item.operador_nome,
    item.captador_nome,
    item.executar_por_usuario_nome,
    item.usuario_nome,
    item.chamado?.responsavel_nome,
    item.chamado?.usuario_responsavel_nome,
    item.chamado?.atendente_nome,
    item.chamado?.operador_nome,
    item.chamado?.captador_nome,
  ]
    .map((value) => normalizarNomeComparacao(value))
    .filter(Boolean);

  const usuarioNomeNormalizado = normalizarNomeComparacao(usuarioNome);

  if (
    usuarioNomeNormalizado &&
    nomesResponsavel.includes(usuarioNomeNormalizado)
  ) {
    return true;
  }

  return false;
}

function normalizarFollowupChamadoCaptaZap(
  item = {},
  { empresaId, usuarioId, usuarioNome } = {},
) {
  const telefone = item.telefone || item.numero || "";
  const ordem = Number(item.followup_ordem || 0);
  const canalFollowup = String(item.canal || "whatsapp").toLowerCase();
  const statusFollowup = String(item.status || "pendente").toLowerCase();
  const realizadoEm = item.realizado_em || item.realizadoEm || null;
  const canceladoEm = item.cancelado_em || item.canceladoEm || null;
  const titulo = item.titulo || `Follow-up ${ordem || ""}`.trim();
  const agendadoPara =
    item.agendado_para ||
    item.agendadoPara ||
    item.janela_inicio ||
    item.criado_em ||
    new Date().toISOString();

  const idBase = item.id || item.mensagem_rapida_id || telefone || Date.now();
  const id = `followup-${idBase}`;

  const descricao =
    canalFollowup === "ligacao"
      ? "Ligar para o paciente e registrar o resultado."
      : canalFollowup === "perdido"
        ? "Validar e marcar como perdido, se fizer sentido."
        : "Enviar a mensagem de follow-up manualmente pelo WhatsApp.";

  const janelaTexto =
    item.janela_inicio && item.janela_fim
      ? `${formatDataHoraCaptaZap(item.janela_inicio)} até ${formatHoraMensagemLocal(item.janela_fim)}`
      : "";

  return {
    ...item,
    id,
    conversa_id: id,
    empresa_id: item.empresa_id || empresaId,
    empresaId: item.empresa_id || empresaId,
    canal: "whatsapp",
    numero: telefone,
    telefone,
    nome: item.nome || item.chamado_nome || telefone || "Contato sem nome",
    status: "followup",
    status_chamado: "aberto",
    chamado_status: "aberto",
    chamada_status: "aberto",
    conversa_ativa: true,
    conversaAtiva: true,
    sem_conversa: false,
    semConversa: false,
    aba_captazap: "followup",
    responsavel_id:
      item.responsavel_id ||
      item.usuario_responsavel_id ||
      item.atendente_id ||
      item.operador_id ||
      item.captador_id ||
      item.chamado?.responsavel_id ||
      null,
    responsavel_nome:
      item.responsavel_nome ||
      item.usuario_responsavel_nome ||
      item.atendente_nome ||
      item.operador_nome ||
      item.captador_nome ||
      item.chamado?.responsavel_nome ||
      "",
    captador_id:
      item.captador_id ||
      item.responsavel_id ||
      item.usuario_responsavel_id ||
      item.atendente_id ||
      item.operador_id ||
      item.chamado?.responsavel_id ||
      null,
    captador_nome:
      item.captador_nome ||
      item.responsavel_nome ||
      item.usuario_responsavel_nome ||
      item.atendente_nome ||
      item.operador_nome ||
      item.chamado?.responsavel_nome ||
      "",
    _captazap_real: true,
    _follow_up_status: statusFollowup,
    _follow_up_pendente: statusFollowup === "pendente",
    _follow_up_realizado: statusFollowup === "realizado",
    _follow_up_cancelado: statusFollowup === "cancelado",
    _follow_up_realizado_em: realizadoEm,
    _follow_up_cancelado_em: canceladoEm,
    _follow_up_realizado_por: item.realizado_por || item.realizadoPor || null,
    _follow_up_resultado: item.resultado || "",
    _follow_up_observacao: item.observacao || "",
    _followup_chamado_id: item.id,
    _follow_up_id: item.id,
    _follow_up_etapa: ordem,
    _follow_up_ordem: ordem,
    _follow_up_regra_id: item.mensagem_rapida_id || null,
    _follow_up_canal: canalFollowup,
    _follow_up_titulo: titulo,
    _follow_up_etapa_label: titulo,
    _follow_up_short_label:
      canalFollowup === "ligacao"
        ? "Ligar"
        : canalFollowup === "perdido"
          ? "Perdido"
          : `FU ${ordem || ""}`.trim(),
    _follow_up_texto: item.texto || "",
    _follow_up_descricao: descricao,
    _follow_up_liberado_em: agendadoPara,
    _follow_up_prazo_label: formatDataHoraCaptaZap(agendadoPara),
    _follow_up_perdido_sugerido: canalFollowup === "perdido",
    status_followup: titulo,
    status_followup_status: statusFollowup,
    status_followup_detalhe: descricao,
    preview: item.texto || descricao,
    ultima_mensagem: item.texto || descricao,
    ultimaMensagem: item.texto || descricao,
    ultima_direcao: "enviada",
    ultimaDirecao: "enviada",
    direcao: "enviada",
    hora: formatDataHoraCaptaZap(agendadoPara),
    naoLidas: 0,
    mensagens: item.texto
      ? [
          {
            id: `followup-msg-${item.id || Date.now()}`,
            texto: item.texto,
            mensagem: item.texto,
            direcao: "enviada",
            autor: "sistema",
            de: "sistema",
            datahora: agendadoPara,
            status: statusFollowup,
          },
        ]
      : [],
    tags: Array.from(
      new Set(
        [
          "Follow-up",
          titulo,
          canalFollowup === "ligacao" ? "Ligação de Follow-up" : "",
          canalFollowup === "whatsapp" ? "WhatsApp de Follow-up" : "",
          janelaTexto,
        ].filter(Boolean),
      ),
    ),
  };
}

function criarIndiceFollowupsCaptaZap(followups = []) {
  const indice = {
    __followupIndex: true,
    porProtocolo: new Map(),
    porLead: new Map(),
    porTelefone: new Map(),
  };

  const adicionar = (mapa, chave, item) => {
    const key = String(chave || "").trim();
    if (!key) return;
    const lista = mapa.get(key) || [];
    lista.push(item);
    mapa.set(key, lista);
  };

  for (const item of Array.isArray(followups) ? followups : []) {
    if (!item || typeof item !== "object") continue;

    const protocolo = Number(
      item.protocolo_id || item.chamado_id || item.id_chamado || 0,
    );
    const leadId = String(item.lead_id || item.leadId || "").trim();
    const telefone = normalizarTelefone(
      item.telefone || item.numero || item.whatsapp || item.phone,
    );

    if (protocolo) adicionar(indice.porProtocolo, protocolo, item);
    if (leadId) adicionar(indice.porLead, leadId, item);

    if (telefone) {
      const variantes = montarVariantesTelefoneBuscaCaptaZap(telefone);
      for (const variante of variantes) {
        adicionar(indice.porTelefone, variante, item);
      }
      adicionar(indice.porTelefone, telefone.slice(-11), item);
    }
  }

  return indice;
}

function getFollowupsDaConversaCaptaZap(
  { telefone = "", leadId = "", protocoloId = 0 } = {},
  origemFollowups,
) {
  if (!origemFollowups) return [];

  if (!origemFollowups.__followupIndex) {
    const telefoneBusca = normalizarTelefone(telefone);
    const variantesBusca = montarVariantesTelefoneBuscaCaptaZap(telefoneBusca);

    return (Array.isArray(origemFollowups) ? origemFollowups : []).filter(
      (item) => {
        const telefoneItem = normalizarTelefone(
          item.telefone || item.numero || item.whatsapp || item.phone,
        );
        const leadItem = String(item.lead_id || item.leadId || "").trim();
        const protocoloItem = Number(
          item.protocolo_id || item.chamado_id || item.id_chamado || 0,
        );

        const bateTelefone =
          telefoneBusca &&
          telefoneItem &&
          variantesBusca.some((a) =>
            montarVariantesTelefoneBuscaCaptaZap(telefoneItem).some(
              (b) => a === b || a.endsWith(b) || b.endsWith(a),
            ),
          );

        const bateLead = leadId && leadItem && String(leadId) === leadItem;
        const bateProtocolo =
          protocoloId && protocoloItem && Number(protocoloId) === protocoloItem;

        return bateProtocolo || bateTelefone || bateLead;
      },
    );
  }

  const encontrados = [];
  const vistos = new Set();

  const adicionarLista = (lista = []) => {
    for (const item of lista || []) {
      const key = String(
        item?.id ||
          `${item?.telefone || "sem-telefone"}-${item?.followup_ordem || "sem-ordem"}-${item?.status || "sem-status"}-${item?.agendado_para || item?.realizado_em || "sem-data"}`,
      );
      if (vistos.has(key)) continue;
      vistos.add(key);
      encontrados.push(item);
    }
  };

  if (protocoloId) {
    adicionarLista(
      origemFollowups.porProtocolo.get(String(Number(protocoloId))),
    );
  }

  if (leadId) {
    adicionarLista(origemFollowups.porLead.get(String(leadId)));
  }

  const telefoneBusca = normalizarTelefone(telefone);
  if (telefoneBusca) {
    for (const variante of montarVariantesTelefoneBuscaCaptaZap(
      telefoneBusca,
    )) {
      adicionarLista(origemFollowups.porTelefone.get(variante));
    }
    adicionarLista(origemFollowups.porTelefone.get(telefoneBusca.slice(-11)));
  }

  return encontrados;
}

function aplicarResumoFollowupNaConversaCaptaZap(
  conversa = {},
  followups = [],
  regras = [],
) {
  const telefone = normalizarTelefone(
    conversa.telefone || conversa.numero || conversa.whatsapp || conversa.phone,
  );
  const leadConversa = String(
    conversa.lead_id || conversa.leadId || conversa.lead?.id || "",
  ).trim();
  const protocoloId = Number(
    conversa.protocolo_id ||
      conversa.chamado_id ||
      conversa.id_chamado ||
      conversa.chamada_id ||
      conversa.chamada?.id ||
      0,
  );

  const historico = getFollowupsDaConversaCaptaZap(
    {
      telefone,
      leadId: leadConversa,
      protocoloId,
    },
    followups,
  )
    .map((item) => ({
      ...item,
      id: item.id == null ? null : Number(item.id),
      followup_ordem: Number(item.followup_ordem || item._follow_up_ordem || 0),
      canal: String(
        item.canal ||
          item.followup_canal ||
          item._follow_up_canal ||
          "whatsapp",
      ).toLowerCase(),
      status: String(
        item._follow_up_status || item.status || "pendente",
      ).toLowerCase(),
    }))
    .filter((item) => item.followup_ordem > 0)
    .sort(
      (a, b) =>
        a.followup_ordem - b.followup_ordem ||
        Number(a.id || 0) - Number(b.id || 0),
    );

  const regrasAtivas = (regras || [])
    .map((regra) => ({
      ...regra,
      id: regra.id == null ? null : Number(regra.id),
      followup_ordem: Number(regra.followup_ordem || 0),
      followup_canal: String(
        regra.followup_canal || regra.canal || "whatsapp",
      ).toLowerCase(),
      titulo: regra.titulo || `Tentativa ${regra.followup_ordem || ""}`,
      texto: regra.texto || "",
    }))
    .filter((regra) => regra.followup_ordem > 0)
    .sort((a, b) => a.followup_ordem - b.followup_ordem);

  // Regras cadastradas sozinhas não são follow-up real.
  // Só enriquecemos o card/painel quando existe histórico/chamado_followup.
  if (!historico.length) return conversa;

  const historicoPorOrdem = new Map();
  historico.forEach((item) => {
    const ordem = Number(item.followup_ordem || 0);
    const atual = historicoPorOrdem.get(ordem);

    if (!atual) {
      historicoPorOrdem.set(ordem, item);
      return;
    }

    const peso = (status) =>
      status === "pendente"
        ? 4
        : status === "em_andamento"
          ? 3
          : status === "realizado"
            ? 2
            : 1;

    const statusAtual = String(atual.status || "").toLowerCase();
    const statusNovo = String(item.status || "").toLowerCase();
    const dataAtual = parseTimestampCaptaZap(
      atual.realizado_em || atual.agendado_para || atual.criado_em,
    );
    const dataNovo = parseTimestampCaptaZap(
      item.realizado_em || item.agendado_para || item.criado_em,
    );

    if (peso(statusNovo) > peso(statusAtual) || dataNovo > dataAtual) {
      historicoPorOrdem.set(ordem, item);
    }
  });

  // Se o histórico é de lead fria, as regrasAtivas da régua antiga não devem
  // sobrescrever canal/titulo — cada item do histórico já tem seus dados corretos.
  const historicoEhLeadFria = historico.some(
    (item) =>
      String(item.texto || "").includes("[followup_lead_fria]") ||
      Boolean(item?._dra_malu_virtual_followup) ||
      String(item?.origem_automacao || item?.origem || "")
        .toLowerCase()
        .includes("dra_malu"),
  );

  const fluxoCompleto =
    regrasAtivas.length && !historicoEhLeadFria
      ? regrasAtivas.map((regra) => {
          const item = historicoPorOrdem.get(Number(regra.followup_ordem));

          return {
            ...(item || {}),
            regra_id: regra.id,
            mensagem_rapida_id: item?.mensagem_rapida_id || regra.id,
            followup_ordem: regra.followup_ordem,
            _follow_up_ordem: regra.followup_ordem,
            canal: item?.canal || regra.followup_canal || "whatsapp",
            _follow_up_canal: item?.canal || regra.followup_canal || "whatsapp",
            titulo:
              regra.titulo ||
              item?.titulo ||
              `Tentativa ${regra.followup_ordem}`,
            _follow_up_titulo:
              regra.titulo ||
              item?.titulo ||
              `Tentativa ${regra.followup_ordem}`,
            texto: item?.texto || regra.texto || "",
            _follow_up_texto: item?.texto || regra.texto || "",
            status: item?.status || "futura",
            _follow_up_status: item?.status || "futura",
            agendado_para: item?.agendado_para || null,
            _follow_up_liberado_em: item?.agendado_para || null,
            realizado_em: item?.realizado_em || null,
            _follow_up_realizado_em: item?.realizado_em || null,
            realizado_por: item?.realizado_por || null,
            _follow_up_realizado_por: item?.realizado_por || null,
            resultado: item?.resultado || null,
            _follow_up_resultado: item?.resultado || null,
            observacao: item?.observacao || null,
            _follow_up_observacao: item?.observacao || null,
            _followup_chamado_id: item?.id || null,
            _follow_up_id: item?.id || null,
            _existe_no_banco: Boolean(item?.id),
          };
        })
      : historico;

  const pendente = fluxoCompleto.find((item) =>
    ["pendente", "em_andamento"].includes(
      String(item.status || "").toLowerCase(),
    ),
  );
  const ultimoRealizado = [...fluxoCompleto]
    .reverse()
    .find((item) => String(item.status || "").toLowerCase() === "realizado");
  const ultimoHistorico = historico.length
    ? historico[historico.length - 1]
    : null;
  const atual =
    pendente || ultimoRealizado || ultimoHistorico || fluxoCompleto[0];

  const statusAtual = String(
    atual?.status || conversa._follow_up_status || "",
  ).toLowerCase();

  const followupFechamento = [...historico].reverse().find((item) => {
    const motivoCancelamento = normalizar(
      item.cancelado_motivo ||
        item.canceladoMotivo ||
        item._follow_up_cancelado_motivo ||
        "",
    );
    const observacao = normalizar(
      item.observacao || item._follow_up_observacao || "",
    );

    return (
      motivoCancelamento.includes("fechamento_followup") ||
      motivoCancelamento.includes("fechamento followup") ||
      motivoCancelamento.includes("fechamento de follow") ||
      observacao.includes("fechamento_followup") ||
      observacao.includes("fechamento de follow")
    );
  });

  const textoFechamentoFollowup = normalizar(
    [
      conversa.motivo_fechamento,
      conversa.motivoFechamento,
      conversa.observacao_fechamento,
      conversa.observacaoFechamento,
      Array.isArray(conversa.tags) ? conversa.tags.join(" ") : "",
    ]
      .filter(Boolean)
      .join(" "),
  );

  const fechamentoFollowupDetectado = Boolean(
    followupFechamento ||
    conversa._followup_fechado ||
    conversa._fechamento_followup ||
    conversa.fechamento_followup ||
    textoFechamentoFollowup.includes("fechamento_followup") ||
    textoFechamentoFollowup.includes("fechamento followup") ||
    textoFechamentoFollowup.includes("fechamento de follow") ||
    textoFechamentoFollowup.includes("follow-up fechado") ||
    textoFechamentoFollowup.includes("followup fechado"),
  );

  return {
    ...conversa,
    ...(fechamentoFollowupDetectado
      ? {
          status: "fechado",
          status_chamado: "fechado",
          chamado_status: "fechado",
          chamada_status: "fechado",
          conversa_ativa: false,
          conversaAtiva: false,
          sem_conversa: true,
          semConversa: true,
          aba_captazap: "fechados",
          _followup_fechado: true,
          _fechamento_followup: true,
          fechamento_followup: true,
          fechamentoFollowup: true,
          motivo_fechamento:
            conversa.motivo_fechamento ||
            conversa.motivoFechamento ||
            "Fechamento de follow-up",
          motivoFechamento:
            conversa.motivoFechamento ||
            conversa.motivo_fechamento ||
            "Fechamento de follow-up",
          observacao_fechamento:
            conversa.observacao_fechamento ||
            conversa.observacaoFechamento ||
            followupFechamento?.observacao ||
            "Follow-up fechado pelo fluxo de encerramento da régua.",
          observacaoFechamento:
            conversa.observacaoFechamento ||
            conversa.observacao_fechamento ||
            followupFechamento?.observacao ||
            "Follow-up fechado pelo fluxo de encerramento da régua.",
          tags: Array.from(
            new Set([
              ...(conversa.tags || []),
              "Chamado fechado",
              "Follow-up fechado",
            ]),
          ),
        }
      : {}),
    _dra_malu_em_recuperacao: Boolean(
      conversa?._dra_malu_em_recuperacao ||
        conversa?.dra_malu_em_recuperacao ||
        pendente?._dra_malu_em_recuperacao ||
        pendente?.dra_malu_em_recuperacao,
    ),
    dra_malu_em_recuperacao: Boolean(
      conversa?._dra_malu_em_recuperacao ||
        conversa?.dra_malu_em_recuperacao ||
        pendente?._dra_malu_em_recuperacao ||
        pendente?.dra_malu_em_recuperacao,
    ),
    dra_malu_atendimento_id:
      conversa?.dra_malu_atendimento_id || pendente?.atendimento_id || null,
    dra_malu_status:
      conversa?.dra_malu_status || pendente?.dra_malu_status || null,
    dra_malu_etapa:
      Number(conversa?.dra_malu_etapa || pendente?.dra_malu_etapa || pendente?.followup_ordem || 0),
    dra_malu_total_etapas:
      Number(conversa?.dra_malu_total_etapas || pendente?.dra_malu_total_etapas || 0),
    dra_malu_etapa_titulo:
      conversa?.dra_malu_etapa_titulo || pendente?.dra_malu_etapa_titulo || pendente?.titulo || "",
    dra_malu_entrou_em:
      conversa?.dra_malu_entrou_em || pendente?.criado_em || null,
    dra_malu_captador_origem_id:
      conversa?.dra_malu_captador_origem_id || pendente?.captador_id || pendente?.responsavel_id || null,
    dra_malu_captador_origem_nome:
      conversa?.dra_malu_captador_origem_nome || pendente?.captador_nome || pendente?.responsavel_nome || "",

    _follow_up_em_fluxo: Boolean(fluxoCompleto.length),
    _followups_historico: historico,
    _followup_regras: regrasAtivas,
    _followup_fluxo_completo: fluxoCompleto,
    _followups_fluxo: fluxoCompleto,
    _follow_up_fluxo: fluxoCompleto,
    _follow_up_historico: historico,
    _followup_total_regua: regrasAtivas.length || fluxoCompleto.length,
    _follow_up_total_regras: regrasAtivas.length || fluxoCompleto.length,
    total_regras_followup: regrasAtivas.length || fluxoCompleto.length,
    _follow_up_total: regrasAtivas.length || fluxoCompleto.length,
    _follow_up_total_realizados: fluxoCompleto.filter(
      (item) => String(item.status || "").toLowerCase() === "realizado",
    ).length,
    _follow_up_total_pendentes: fluxoCompleto.filter((item) =>
      ["pendente", "em_andamento"].includes(
        String(item.status || "").toLowerCase(),
      ),
    ).length,
    _follow_up_status: pendente
      ? statusAtual
      : conversa._follow_up_status || "",
    _follow_up_pendente: Boolean(pendente),
    _follow_up_atual: pendente || null,
    _follow_up_ultimo_realizado: ultimoRealizado || null,
    _follow_up_proximo_pendente: pendente || null,
    _follow_up_id: pendente
      ? pendente?._follow_up_id || pendente?.id || conversa._follow_up_id
      : null,
    _followup_chamado_id: pendente
      ? pendente?._followup_chamado_id ||
        pendente?.id ||
        conversa._followup_chamado_id
      : null,
    _follow_up_ordem: pendente?.followup_ordem || null,
    _follow_up_etapa: pendente?.followup_ordem || null,
    _follow_up_canal: pendente?.canal || "",
    _follow_up_titulo: pendente?.titulo || "",
    _follow_up_etapa_label: pendente?.titulo || "",
    _follow_up_texto: pendente?.texto || "",
    _follow_up_liberado_em: pendente?.agendado_para || null,
    _follow_up_realizado_em:
      ultimoRealizado?.realizado_em || conversa._follow_up_realizado_em,
    _follow_up_realizado_por:
      ultimoRealizado?.realizado_por || conversa._follow_up_realizado_por,
    _follow_up_resultado:
      ultimoRealizado?.resultado || conversa._follow_up_resultado,
    _follow_up_observacao:
      ultimoRealizado?.observacao || conversa._follow_up_observacao,
  };
}

function normalizarTelefoneEventoFollowupCaptaZap(value = "") {
  const digits = normalizarTelefone(value);
  if (!digits) return "";

  const semDDI = digits.startsWith("55") ? digits.slice(2) : digits;
  if (semDDI.length === 10) return `55${semDDI.slice(0, 2)}9${semDDI.slice(2)}`;
  if (semDDI.length === 11) return `55${semDDI}`;

  return digits;
}

function followupBateEventoCaptaZap(item = {}, detail = {}) {
  if (!item || !detail) return false;

  const eventoId = String(
    detail.followup_id ||
      detail.id ||
      detail._followup_chamado_id ||
      detail._follow_up_id ||
      "",
  ).trim();

  const itemId = String(
    item._followup_chamado_id ||
      item._follow_up_id ||
      item.followup_id ||
      item.id ||
      "",
  ).trim();

  if (eventoId && itemId && eventoId === itemId) return true;

  const protocoloEvento = Number(
    detail.protocolo_id || detail.chamado_id || detail.id_chamado || 0,
  );
  const protocoloItem = Number(
    item.protocolo_id || item.chamado_id || item.id_chamado || 0,
  );

  if (protocoloEvento && protocoloItem && protocoloEvento === protocoloItem) {
    return true;
  }

  const telefoneEvento = normalizarTelefoneEventoFollowupCaptaZap(
    detail.telefone || detail.numero || detail.phone || "",
  );
  const telefoneItem = normalizarTelefoneEventoFollowupCaptaZap(
    item.telefone || item.numero || item.phone || item.whatsapp || "",
  );

  return Boolean(
    telefoneEvento &&
    telefoneItem &&
    telefoneEvento.slice(-11) === telefoneItem.slice(-11),
  );
}

function removerFollowupPorEventoCaptaZap(lista = [], detail = {}) {
  return (Array.isArray(lista) ? lista : []).filter(
    (item) => !followupBateEventoCaptaZap(item, detail),
  );
}

function upsertFollowupListaCaptaZap(lista = [], followup = null) {
  if (!followup) return Array.isArray(lista) ? lista : [];

  const idNovo = String(
    followup._followup_chamado_id ||
      followup._follow_up_id ||
      followup.followup_id ||
      followup.id ||
      "",
  ).trim();

  const base = Array.isArray(lista) ? lista : [];

  if (!idNovo) return [followup, ...base];

  const semDuplicar = base.filter((item) => {
    const idItem = String(
      item?._followup_chamado_id ||
        item?._follow_up_id ||
        item?.followup_id ||
        item?.id ||
        "",
    ).trim();

    return idItem !== idNovo;
  });

  return [followup, ...semDuplicar];
}

function conversaBateEventoFollowupCaptaZap(conversa = {}, detail = {}) {
  if (!conversa || !detail) return false;

  if (followupBateEventoCaptaZap(conversa, detail)) return true;

  const protocoloEvento = Number(
    detail.protocolo_id || detail.chamado_id || detail.id_chamado || 0,
  );
  const protocoloConversa = Number(
    conversa.protocolo_id ||
      conversa.chamado_id ||
      conversa.id_chamado ||
      conversa.chamada_id ||
      0,
  );

  if (
    protocoloEvento &&
    protocoloConversa &&
    protocoloEvento === protocoloConversa
  ) {
    return true;
  }

  const telefoneEvento = normalizarTelefoneEventoFollowupCaptaZap(
    detail.telefone || detail.numero || detail.phone || "",
  );
  const telefoneConversa = normalizarTelefoneEventoFollowupCaptaZap(
    conversa.telefone ||
      conversa.numero ||
      conversa.phone ||
      conversa.whatsapp ||
      "",
  );

  return Boolean(
    telefoneEvento &&
    telefoneConversa &&
    telefoneEvento.slice(-11) === telefoneConversa.slice(-11),
  );
}

function limparResumoFollowupPorEventoCaptaZap(conversa = {}, detail = {}) {
  if (!conversa || !conversaBateEventoFollowupCaptaZap(conversa, detail)) {
    return conversa;
  }

  const filtrar = (lista = []) =>
    (Array.isArray(lista) ? lista : []).filter(
      (item) => !followupBateEventoCaptaZap(item, detail),
    );

  const historicoFiltrado = filtrar(
    conversa._followups_historico || conversa._follow_up_historico || [],
  );
  const fluxoFiltrado = filtrar(
    conversa._followup_fluxo_completo ||
      conversa._followups_fluxo ||
      conversa._follow_up_fluxo ||
      [],
  );

  const temPendenteRestante = fluxoFiltrado.some((item) =>
    ["pendente", "em_andamento"].includes(
      String(item?.status || item?._follow_up_status || "").toLowerCase(),
    ),
  );

  return {
    ...conversa,
    _followups_historico: historicoFiltrado,
    _follow_up_historico: historicoFiltrado,
    _followup_fluxo_completo: fluxoFiltrado,
    _followups_fluxo: fluxoFiltrado,
    _follow_up_fluxo: fluxoFiltrado,
    _follow_up_pendente: temPendenteRestante,
    _follow_up_atual: temPendenteRestante ? conversa._follow_up_atual : null,
    _follow_up_proximo_pendente: temPendenteRestante
      ? conversa._follow_up_proximo_pendente
      : null,
    _follow_up_id: temPendenteRestante ? conversa._follow_up_id : null,
    _followup_chamado_id: temPendenteRestante
      ? conversa._followup_chamado_id
      : null,
    _follow_up_ordem: temPendenteRestante ? conversa._follow_up_ordem : null,
    _follow_up_etapa: temPendenteRestante ? conversa._follow_up_etapa : null,
    _follow_up_canal: temPendenteRestante ? conversa._follow_up_canal : "",
    _follow_up_titulo: temPendenteRestante ? conversa._follow_up_titulo : "",
    _follow_up_etapa_label: temPendenteRestante
      ? conversa._follow_up_etapa_label
      : "",
    _follow_up_texto: temPendenteRestante ? conversa._follow_up_texto : "",
    _follow_up_liberado_em: temPendenteRestante
      ? conversa._follow_up_liberado_em
      : null,
    status_followup: temPendenteRestante ? conversa.status_followup : "",
    status_followup_status: temPendenteRestante
      ? conversa.status_followup_status
      : "",
    status_followup_detalhe: temPendenteRestante
      ? conversa.status_followup_detalhe
      : "",
  };
}

function montarVariantesTelefoneBuscaCaptaZap(valor = "") {
  const digits = normalizarTelefone(valor);
  if (!digits) return [];

  const variantes = new Set([digits]);
  const semDDI = digits.startsWith("55") ? digits.slice(2) : digits;

  if (semDDI) {
    variantes.add(semDDI);
    variantes.add(`55${semDDI}`);
  }

  if (semDDI.length === 10) {
    const comNono = `${semDDI.slice(0, 2)}9${semDDI.slice(2)}`;
    variantes.add(comNono);
    variantes.add(`55${comNono}`);
  }

  if (semDDI.length === 11 && semDDI[2] === "9") {
    const semNono = `${semDDI.slice(0, 2)}${semDDI.slice(3)}`;
    variantes.add(semNono);
    variantes.add(`55${semNono}`);
  }

  return [...variantes].filter(Boolean);
}

function parseTimestampCaptaZap(value) {
  if (!value) return 0;

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  if (typeof value === "number") {
    return value < 10000000000 ? value * 1000 : value;
  }

  const text = String(value || "").trim();
  if (!text) return 0;

  const mysqlDate = text.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  );

  if (mysqlDate) {
    const parsedMysql = new Date(
      Number(mysqlDate[1]),
      Number(mysqlDate[2]) - 1,
      Number(mysqlDate[3]),
      Number(mysqlDate[4] || 0),
      Number(mysqlDate[5] || 0),
      Number(mysqlDate[6] || 0),
      0,
    ).getTime();

    if (!Number.isNaN(parsedMysql)) return parsedMysql;
  }

  const parsed = new Date(text).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function EmpresaNaoSelecionada() {
  return (
    <div className="flex h-full min-h-0 w-full items-center justify-center bg-slate-100 px-6">
      <div className="max-w-md rounded-[32px] border border-slate-300 bg-white p-8 text-center shadow-xl">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[26px] bg-amber-50 text-amber-600 ring-1 ring-amber-200">
          <Building2 size={30} />
        </div>

        <h2 className="mt-5 text-xl font-semibold text-slate-950">
          Nenhuma empresa ativa
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-500">
          Selecione uma empresa no CaptaCRM para carregar conversas, contatos,
          atendimentos e leads do CaptaZap.
        </p>
      </div>
    </div>
  );
}

function AmbientBackground() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 bg-[#F4F7FB]" />

      <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-blue-50 via-white/40 to-transparent" />

      <div className="absolute -left-40 top-10 h-[360px] w-[360px] rounded-full bg-blue-500/10 blur-3xl" />
      <div className="absolute right-[-120px] top-28 h-[360px] w-[360px] rounded-full bg-orange-400/10 blur-3xl" />
    </div>
  );
}

function HeaderSearchInput({ value = "", onSearchChange }) {
  const [texto, setTexto] = useState(value || "");

  useEffect(() => {
    setTexto(value || "");
  }, [value]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      onSearchChange?.(texto);
    }, 280);

    return () => window.clearTimeout(timer);
  }, [texto, onSearchChange]);

  return (
    <div className="hidden min-w-[260px] max-w-[480px] flex-1 2xl:block">
      <div className="relative">
        <Search
          size={17}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
        />

        <input
          value={texto}
          onChange={(event) => setTexto(event.target.value)}
          placeholder="Buscar conversa, lead ou telefone"
          className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50/70 pl-10 pr-3 text-[12px] font-semibold text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-500/10"
        />
      </div>
    </div>
  );
}

function HeaderUserCard({ captador }) {
  return (
    <div
      className="hidden h-9 max-w-[180px] items-center gap-2 px-1 xl:flex"
      title={[captador?.nome, captador?.email].filter(Boolean).join(" · ")}
    >
      <HeaderCaptadorAvatar captador={captador} />
      <span className="max-w-[125px] truncate text-[11px] font-bold text-slate-700">
        {captador.nome}
      </span>
    </div>
  );
}

function HeaderCaptadorAvatar({ captador }) {
  const [erroImagem, setErroImagem] = useState(false);
  const foto = getImagemUrl(captador?.foto);
  const inicial =
    String(captador?.nome || "C")
      .trim()
      .charAt(0)
      .toUpperCase() || "C";

  return (
    <div className="relative h-8 w-8 shrink-0">
      <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-100 ring-1 ring-slate-200">
        {foto && !erroImagem ? (
          <img
            src={foto}
            alt={captador?.nome || "Captador"}
            loading="eager"
            decoding="async"
            onError={() => setErroImagem(true)}
            className="h-full w-full object-cover object-center"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-slate-800 text-xs font-black text-white">
            {inicial || <UserRound size={16} />}
          </div>
        )}
      </div>
      <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-emerald-500" />
    </div>
  );
}

function HeaderAction({ children, title, onClick = () => {} }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
    >
      {children}
    </button>
  );
}

function HeaderNotificacoesChromeButton({
  ativo = false,
  permissao = "default",
  onClick,
}) {
  const bloqueado = permissao === "denied";
  const titulo = bloqueado
    ? "Alertas bloqueados no Chrome"
    : ativo
      ? "Alertas do Chrome ativos"
      : "Ativar alertas do Chrome";

  return (
    <button
      type="button"
      title={titulo}
      aria-label={titulo}
      onClick={onClick}
      className={`relative flex h-9 w-9 items-center justify-center rounded-lg border transition ${
        bloqueado
          ? "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
          : ativo
            ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800"
      }`}
    >
      {bloqueado ? <BellOff size={15} /> : <BellRing size={15} />}
      {ativo ? (
        <span className="absolute right-1 top-1 h-2 w-2 rounded-full border border-white bg-emerald-500" />
      ) : null}
    </button>
  );
}

function HeaderSupervisaoButton({ totalEquipe = 0, totalFila = 0, onClick }) {
  const totalMonitorado = Number(totalEquipe || 0) + Number(totalFila || 0);

  return (
    <button
      type="button"
      title={`${Number(totalEquipe || 0)} em atendimento · ${Number(totalFila || 0)} na fila`}
      aria-label="Abrir supervisão da equipe"
      onClick={onClick}
      className="relative hidden h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 sm:flex"
    >
      <ShieldCheck size={16} />
      {totalMonitorado > 0 ? (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-700 px-1 text-[8px] font-black text-white ring-2 ring-white">
          {totalMonitorado > 99 ? "99+" : totalMonitorado}
        </span>
      ) : null}
    </button>
  );
}

function CompactMetric({ label, value, tone }) {
  const tones = {
    blue: {
      shell:
        "border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50 text-blue-800 shadow-blue-900/10",
      icon: "bg-blue-600 text-white shadow-blue-700/25",
      glow: "bg-blue-400/20",
      dot: "bg-blue-500",
    },
    amber: {
      shell:
        "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 text-amber-800 shadow-orange-900/10",
      icon: "bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-orange-700/25",
      glow: "bg-orange-400/20",
      dot: "bg-orange-500",
    },
    emerald: {
      shell:
        "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50 text-emerald-800 shadow-emerald-900/10",
      icon: "bg-emerald-500 text-white shadow-emerald-700/25",
      glow: "bg-emerald-400/20",
      dot: "bg-emerald-500",
    },
    violet: {
      shell:
        "border-violet-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 text-violet-800 shadow-violet-900/10",
      icon: "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-violet-700/25",
      glow: "bg-violet-400/20",
      dot: "bg-violet-500",
    },
  };

  const icons = {
    Ativas: <Activity size={15} />,
    Fila: <Inbox size={15} />,
    Leads: <TrendingUp size={15} />,
    Resposta: <Clock3 size={15} />,
  };

  const config = tones[tone] || tones.blue;

  return (
    <div
      className={`group relative h-10 min-w-[88px] overflow-hidden rounded-xl border px-2 py-1 shadow-sm transition hover:-translate-y-0.5 ${config.shell}`}
    >
      <div
        className={`pointer-events-none absolute -right-6 -top-6 h-16 w-16 rounded-full blur-2xl transition group-hover:scale-125 ${config.glow}`}
      />

      <div className="relative flex h-full items-center gap-1.5">
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg shadow-sm ${config.icon}`}
        >
          {icons[label] || <Activity size={15} />}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
            <span className="text-[8px] font-black uppercase leading-none tracking-wide opacity-70">
              {label}
            </span>
          </div>

          <div className="mt-0.5 text-[13px] font-black leading-none tracking-tight">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

function LaunchMetric({ icon, label, value, text, tone }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 ring-blue-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    violet: "bg-violet-50 text-violet-700 ring-violet-200",
  };

  return (
    <div className="rounded-[20px] border border-slate-300 bg-[#F8FAFC] p-2.5 sm:rounded-[22px] sm:p-3 lg:p-3.5">
      <div
        className={`mb-2 flex h-8 w-8 items-center justify-center rounded-2xl ring-1 sm:mb-3 sm:h-9 sm:w-9 ${tones[tone]}`}
      >
        {icon}
      </div>

      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </div>

      <div className="mt-0.5 text-xl font-semibold text-slate-950 sm:mt-1 sm:text-2xl">
        {value}
      </div>

      <div className="mt-0.5 text-[10px] text-slate-500 sm:mt-1 sm:text-xs">
        {text}
      </div>
    </div>
  );
}

function ProducaoHojeMetric({
  icon,
  label,
  value,
  tone,
  compact = false,
  help = "",
  tooltipAlign = "center",
}) {
  const tones = {
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    cyan: "border-cyan-200 bg-cyan-50 text-cyan-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    green: "border-green-200 bg-green-50 text-green-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    gold: "border-yellow-300 bg-yellow-50 text-yellow-800",
  };

  const tooltip = help
    ? `${label}: ${help}`
    : `${label}: ${String(value ?? "")}`;

  const tooltipPosition =
    tooltipAlign === "left"
      ? "left-0 translate-x-0"
      : tooltipAlign === "right"
        ? "right-0 translate-x-0"
        : "left-1/2 -translate-x-1/2";

  const tooltipArrowPosition =
    tooltipAlign === "left"
      ? "left-5 translate-x-0"
      : tooltipAlign === "right"
        ? "right-5 translate-x-0"
        : "left-1/2 -translate-x-1/2";

  return (
    <div
      className={`group relative min-w-0 cursor-help rounded-xl border px-1.5 py-1.5 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md sm:rounded-2xl sm:px-2 sm:py-2 ${
        tones[tone] || tones.blue
      }`}
      aria-label={tooltip}
    >
      <div className="mx-auto flex h-6 w-6 items-center justify-center rounded-xl bg-white/75 ring-1 ring-current/15 sm:h-7 sm:w-7">
        {icon}
      </div>
      <div
        className={`mt-1.5 truncate font-black leading-none sm:mt-2 ${
          compact ? "text-[11px] sm:text-[13px]" : "text-[15px] sm:text-[18px]"
        }`}
      >
        {value}
      </div>
      <div className="mt-0.5 truncate text-[8px] font-black uppercase tracking-wide opacity-70 sm:mt-1 sm:text-[9px]">
        {label}
      </div>
      <div
        className={`pointer-events-none absolute bottom-[calc(100%+8px)] z-40 hidden w-[min(14rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-[11px] font-semibold leading-4 text-slate-700 shadow-xl group-hover:block ${tooltipPosition}`}
      >
        {help || String(value ?? "")}
        <span
          className={`absolute top-full h-2 w-2 -translate-y-1 rotate-45 border-b border-r border-slate-200 bg-white ${tooltipArrowPosition}`}
        />
      </div>
    </div>
  );
}

function DarkStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/12 px-3 py-3 ring-1 ring-white/15">
      <div className="text-[11px] text-blue-100">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white">{value}</div>
    </div>
  );
}

function SoftStat({ icon, label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    violet: "bg-violet-50 text-violet-700 ring-violet-200",
  };

  return (
    <div className={`rounded-[24px] p-4 ring-1 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">{label}</span>
        {icon}
      </div>
      <div className="mt-3 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function ActionShortcut({ icon, title, description }) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-3 rounded-[22px] border border-slate-300 bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300 hover:bg-orange-50"
    >
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
        {icon}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900">
          {title}
        </span>

        <span className="mt-0.5 block truncate text-xs text-slate-500">
          {description}
        </span>
      </span>

      <ArrowRight size={15} className="text-slate-300" />
    </button>
  );
}

function normalizarWhatsappLidCaptaZap(value = "") {
  const raw = String(value || "").trim().toLowerCase();
  const match = raw.match(/([0-9]{5,})@lid$/i);
  return match ? `${match[1]}@lid` : "";
}

function ehWhatsappLidCaptaZap(value = "") {
  return Boolean(normalizarWhatsappLidCaptaZap(value));
}

function normalizarIdentidadeWhatsappComparacaoCaptaZap(value = "") {
  const lid = normalizarWhatsappLidCaptaZap(value);
  if (lid) return `lid:${lid}`;

  const digits = normalizarTelefone(value);
  if (!digits) return "";

  return `tel:${digits.slice(-11)}`;
}

function normalizarTelefoneLeadBrasil(value = "") {
  // @lid é identidade WhatsApp, não telefone de CRM.
  if (ehWhatsappLidCaptaZap(value)) return "";

  let phone = normalizarTelefone(value);

  if (!phone) return "";

  // Remove prefixos comuns antes de validar.
  // Exemplos:
  // 5561996368800 -> 61996368800
  // 556192617045  -> 6192617045 -> 61992617045
  if (phone.startsWith("0055")) {
    phone = phone.slice(4);
  }

  if (phone.startsWith("55") && phone.length > 11) {
    phone = phone.slice(2);
  }

  // Remove zero inicial de discagem, se vier como 061996368800 ou 06192617045.
  if (phone.startsWith("0") && phone.length > 10) {
    phone = phone.slice(1);
  }

  // Se veio como DDD + número antigo de 8 dígitos, insere o nono dígito.
  // Exemplo: 6192617045 -> 61992617045
  if (phone.length === 10) {
    phone = `${phone.slice(0, 2)}9${phone.slice(2)}`;
  }

  return phone;
}

function telefoneLeadBrasilValido(value = "") {
  const phone = normalizarTelefoneLeadBrasil(value);

  // DDD com 2 dígitos + celular iniciando em 9 + 8 dígitos.
  return /^[1-9]{2}9\d{8}$/.test(phone);
}

function normalizarTelefoneWhatsappBrasil(value = "") {
  const lid = normalizarWhatsappLidCaptaZap(value);
  if (lid) return lid;

  const leadPhone = normalizarTelefoneLeadBrasil(value);

  if (telefoneLeadBrasilValido(leadPhone)) {
    return `55${leadPhone}`;
  }

  const phone = normalizarTelefone(value);

  if (!phone) return "";

  if (phone.startsWith("55") && phone.length >= 12) {
    return phone;
  }

  return phone;
}

function getDataLeadHoje() {
  const hoje = new Date();

  const ano = hoje.getFullYear();
  const mes = String(hoje.getMonth() + 1).padStart(2, "0");
  const dia = String(hoje.getDate()).padStart(2, "0");

  return `${ano}-${mes}-${dia}`;
}

function validarPayloadCriacaoLeadCaptaZap(payload = {}) {
  const obrigatorios = {
    nome_lead: payload.nome_lead,
    data_lead: payload.data_lead,
    id_procedimento: payload.id_procedimento,
    empresa_id: payload.empresa_id,
    telefone1: payload.telefone1,
  };

  const faltando = Object.entries(obrigatorios)
    .filter(
      ([, value]) => value === undefined || value === null || value === "",
    )
    .map(([key]) => key);

  if (faltando.length > 0) {
    throw new Error(
      `Campos obrigatórios faltando antes de salvar: ${faltando.join(", ")}`,
    );
  }

  if (!telefoneLeadBrasilValido(payload.telefone1)) {
    throw new Error(
      "Telefone inválido. Use DDD + 9 + número. Exemplo: 61996368800. Se vier com 55, o sistema remove automaticamente.",
    );
  }
}

function findById(lista = [], id) {
  if (!id || !Array.isArray(lista)) return null;

  return lista.find((item) => String(getItemId(item)) === String(id)) || null;
}

function getItemId(item = {}) {
  return (
    item.id ||
    item.value ||
    item.id_procedimento ||
    item.id_fonte_lead ||
    item.id_fonte ||
    item.id_regiao ||
    item.id_campanha ||
    item.codigo ||
    ""
  );
}

function getItemNome(item = {}, fields = ["nome"]) {
  if (!item) return "";

  for (const field of fields) {
    if (item[field]) return String(item[field]);
  }

  return (
    item.label ||
    item.title ||
    item.descricao ||
    item.nome ||
    item.regiao ||
    item.procedimento ||
    item.campanha ||
    item.fonte ||
    ""
  );
}

async function criarLeadPeloCaptaZap(payload) {
  const body = {
    ...payload,
    lead: payload,
  };

  try {
    return await requestJson("/whatsapp/captazap/converter-lead", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (error) {
    const message = String(error?.message || "");

    const rotaNaoExiste =
      message.includes("404") ||
      message.toLowerCase().includes("not found") ||
      message.toLowerCase().includes("cannot post");

    if (!rotaNaoExiste) {
      throw error;
    }

    // Fallback para projetos onde a criação padrão de lead já existe.
    return requestJson("/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }
}

async function vincularConversaLeadCaptaZap({
  empresaId,
  conversa,
  telefone,
  leadId,
  usuarioId = null,
  usuarioNome = "",
}) {
  if (!empresaId || !leadId) return null;

  return requestJson("/whatsapp/captazap/vincular-lead", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      empresa_id: empresaId,
      conversa_id: conversa?.conversa_id || conversa?.id || null,
      telefone,
      telefone_lead: normalizarTelefoneLeadBrasil(telefone),
      telefone_whatsapp: normalizarTelefoneWhatsappBrasil(telefone),
      lead_id: leadId,
      usuario_id: usuarioId || null,
      captador_id: usuarioId || null,
      responsavel_id: usuarioId || null,
      usuario_nome: usuarioNome || "",
      captador_nome: usuarioNome || "",
      responsavel_nome: usuarioNome || "",
      status_chamado: "aberto",
    }),
  });
}

function normalizarLeadCriadaDoCaptaZap({
  response,
  payload,
  conversa,
  empresaId,
}) {
  const raw =
    response?.lead ||
    response?.data?.lead ||
    response?.data ||
    response?.novaLead ||
    response?.leadCriada ||
    response ||
    {};

  const leadId =
    raw.id ||
    raw.lead_id ||
    raw.id_lead ||
    raw.insertId ||
    response?.id ||
    response?.lead_id ||
    response?.insertId ||
    response?.data?.id ||
    response?.data?.lead_id ||
    response?.data?.insertId ||
    null;

  return {
    ...raw,
    id: leadId || raw.id,
    lead_id: raw.lead_id || leadId || raw.id,
    empresa_id: raw.empresa_id || payload.empresa_id || empresaId,
    id_empresa:
      raw.id_empresa || raw.empresa_id || payload.empresa_id || empresaId,
    empresa_nome: raw.empresa_nome || conversa?.empresa_nome,
    data_lead: raw.data_lead || payload.data_lead || getDataLeadHoje(),
    data_cadastro:
      raw.data_cadastro || payload.data_cadastro || payload.data_lead,
    criado_em: raw.criado_em || raw.created_at || payload.criado_em,
    nome_lead:
      raw.nome_lead ||
      raw.nome ||
      payload.nome_lead ||
      conversa?.nome ||
      payload.telefone1,
    nome:
      raw.nome ||
      raw.nome_lead ||
      payload.nome_lead ||
      conversa?.nome ||
      payload.telefone1,
    telefone1: normalizarTelefoneLeadBrasil(
      raw.telefone1 ||
        raw.telefone ||
        raw.celular ||
        raw.whatsapp ||
        payload.telefone1,
    ),
    telefone: normalizarTelefoneLeadBrasil(
      raw.telefone ||
        raw.telefone1 ||
        raw.celular ||
        raw.whatsapp ||
        payload.telefone1,
    ),
    celular: normalizarTelefoneLeadBrasil(raw.celular || payload.telefone1),
    whatsapp: normalizarTelefoneLeadBrasil(raw.whatsapp || payload.telefone1),
    telefone_whatsapp:
      raw.telefone_whatsapp ||
      raw.numero_whatsapp ||
      payload.telefone_whatsapp ||
      payload.numero_whatsapp ||
      normalizarTelefoneWhatsappBrasil(payload.telefone1),
    numero_whatsapp:
      raw.numero_whatsapp ||
      raw.telefone_whatsapp ||
      payload.numero_whatsapp ||
      payload.telefone_whatsapp ||
      normalizarTelefoneWhatsappBrasil(payload.telefone1),
    fonte:
      raw.fonte ||
      raw.nome_fonte ||
      payload.fonte ||
      payload.nome_fonte ||
      payload.origem ||
      "",
    nome_fonte:
      raw.nome_fonte ||
      raw.fonte ||
      payload.nome_fonte ||
      payload.fonte ||
      payload.origem ||
      "",
    origem: raw.origem || payload.origem,
    status_negociacao:
      raw.status_negociacao || payload.status_negociacao || "Prospectando",
    status_agendamento:
      raw.status_agendamento || payload.status_agendamento || "Sem agendamento",
    potencial: raw.potencial || payload.potencial || "",

    id_procedimento: raw.id_procedimento || payload.id_procedimento || null,
    procedimento:
      raw.procedimento || raw.nome_procedimento || payload.procedimento || "",
    nome_procedimento:
      raw.nome_procedimento ||
      raw.procedimento ||
      payload.nome_procedimento ||
      "",

    id_fonte_lead:
      raw.id_fonte_lead || raw.id_fonte || payload.id_fonte_lead || null,
    id_fonte:
      raw.id_fonte || raw.id_fonte_lead || payload.id_fonte_lead || null,

    id_regiao: raw.id_regiao || payload.id_regiao || null,
    regiao: raw.regiao || raw.nome_regiao || payload.regiao || "",
    nome_regiao: raw.nome_regiao || raw.regiao || payload.nome_regiao || "",

    id_campanha: raw.id_campanha || payload.id_campanha || null,
    campanha: raw.campanha || raw.nome_campanha || payload.campanha || "",
    nome_campanha:
      raw.nome_campanha || raw.campanha || payload.nome_campanha || "",

    usuario_criador_id:
      raw.usuario_criador_id ||
      raw.captador_id ||
      payload.usuario_criador_id ||
      payload.captador_id,
    captador_id:
      raw.captador_id ||
      raw.usuario_criador_id ||
      payload.captador_id ||
      payload.usuario_criador_id,
    usuario_criador_nome:
      raw.usuario_criador_nome ||
      raw.captador_nome ||
      payload.usuario_criador_nome ||
      payload.captador_nome,
    captador_nome:
      raw.captador_nome ||
      raw.usuario_criador_nome ||
      payload.captador_nome ||
      payload.usuario_criador_nome,
  };
}

function getNomeInicialLeadConversao(conversa) {
  const telefone = normalizarTelefone(conversa?.numero || conversa?.telefone);
  const lead = conversa?.lead || null;

  if (lead?.id && telefone && !leadPertenceAoTelefoneCaptaZap(lead, telefone)) {
    return "";
  }

  const nome = String(
    conversa?.nome || lead?.nome_lead || lead?.nome || "",
  ).trim();

  if (!nome) return telefone || "";

  if (normalizarTelefone(nome) === telefone) return "";

  return nome;
}

function getUsuarioId(usuario) {
  return (
    usuario?.id ||
    usuario?.usuario_id ||
    usuario?.id_usuario ||
    usuario?.user_id ||
    usuario?.idUser ||
    null
  );
}

function getNomeUsuarioSistema(usuario) {
  return (
    usuario?.nome ||
    usuario?.name ||
    usuario?.nome_usuario ||
    usuario?.usuario ||
    usuario?.email ||
    "Captador"
  );
}

function criarMockConversas({ empresaId, empresaNome }) {
  if (!empresaId) {
    return {
      minhasConversas: [],
      filaDeEspera: [],
      contatos: [],
    };
  }

  return {
    minhasConversas: [
      {
        id: 1,
        empresa_id: empresaId,
        empresa_nome: empresaNome,
        nome: "João Silva",
        numero: "5511999999999",
        telefone_normalizado: "5511999999999",
        canal: "whatsapp",
        status: "em_atendimento",
        favorito: true,
        preview: "Oi, queria saber sobre protocolo...",
        foto: "",
        origem: "WhatsApp",
        tags: ["Novo lead", "Orçamento"],
        naoLidas: 1,
        hora: "14:20",
        conversa_ativa: true,
        mensagens: [
          {
            id: 101,
            empresa_id: empresaId,
            de: "cliente",
            texto: "Oi, queria saber sobre protocolo...",
            hora: "14:20",
            status: "recebida",
          },
          {
            id: 102,
            empresa_id: empresaId,
            de: "operador",
            texto: "Claro, João. Posso te ajudar.",
            hora: "14:21",
            status: "enviada",
          },
        ],
        timeline: [
          {
            tipo: "Mensagem",
            descricao: "Contato iniciou conversa pelo WhatsApp.",
            data: "Hoje",
          },
        ],
      },
    ],

    filaDeEspera: [
      {
        id: 2,
        empresa_id: empresaId,
        empresa_nome: empresaNome,
        nome: "Maria Oliveira",
        numero: "5521999999999",
        telefone_normalizado: "5521999999999",
        canal: "whatsapp",
        status: "aguardando_atendimento",
        favorito: false,
        preview: "Olá, me envie o orçamento!",
        foto: "",
        origem: "Campanha WhatsApp",
        tags: ["Fila"],
        naoLidas: 1,
        hora: "13:58",
        conversa_ativa: true,
        mensagens: [
          {
            id: 201,
            empresa_id: empresaId,
            de: "cliente",
            texto: "Olá, me envie o orçamento!",
            hora: "13:58",
            status: "recebida",
          },
        ],
        timeline: [
          {
            tipo: "Entrada",
            descricao: "Conversa entrou na fila de espera.",
            data: "Hoje",
          },
        ],
      },
    ],

    contatos: [],
  };
}

function criarMockLeads({ empresaId, empresaNome, usuario }) {
  if (!empresaId) return [];

  return [
    {
      id: 9001,
      empresa_id: empresaId,
      empresa_nome: empresaNome,
      nome_lead: "Carlos Souza",
      telefone1: "5531999999999",
      usuario_criador_id: usuario?.id || usuario?.usuario_id || null,
      usuario_criador_nome: usuario?.nome || usuario?.name || "Usuário atual",
      status_negociacao: "Negociando",
      status_agendamento: "Sem agendamento",
      potencial: "Alto",
      total_orcamento: 3500,
      total_contratado: 0,
      fonte: "CaptaCRM",
      data_lead: new Date().toISOString(),
      tags: ["Carteira CRM", "Sem conversa ativa"],
    },
  ];
}

function getEmpresaNome(empresa) {
  if (!empresa) return "Empresa não identificada";

  return (
    empresa.nome_fantasia ||
    empresa.nome ||
    empresa.razao_social ||
    `Empresa ${empresa.id}`
  );
}

async function requestJson(path, options = {}) {
  const rawPath = String(path || "");

  // As rotas reais do backend do CaptaCRM/CaptaZap não devem receber /api
  // automaticamente aqui. O caminho informado na chamada deve ser preservado.
  // Ex.: requestJson("/leads") => https://api.captacrm.com.br/leads
  const normalizedPath = rawPath.startsWith("http")
    ? rawPath
    : rawPath.startsWith("/")
      ? rawPath
      : `/${rawPath}`;

  const url = normalizedPath.startsWith("http")
    ? normalizedPath
    : API_BASE
      ? `${API_BASE}${normalizedPath}`
      : normalizedPath;

  const response = await fetch(url, {
    method: "GET",
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });

  const text = await response.text();
  const data = text ? safeJson(text) : null;

  if (!response.ok) {
    const message =
      data?.erro || data?.error || data?.message || `HTTP ${response.status}`;

    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    error.code = data?.code || data?.codigo || null;
    error.url = url;
    throw error;
  }

  return data;
}

function getAuthHeaders() {
  if (typeof window === "undefined") {
    return { "Content-Type": "application/json" };
  }

  const local = window.localStorage;
  const session = window.sessionStorage;

  const raw = String(
    local?.getItem("token") ||
      local?.getItem("authToken") ||
      local?.getItem("jwt") ||
      session?.getItem("token") ||
      session?.getItem("authToken") ||
      session?.getItem("jwt") ||
      "",
  ).trim();

  const token = raw
    ? raw.startsWith("Bearer ")
      ? raw
      : `Bearer ${raw}`
    : "";

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: token } : {}),
  };
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function pickArray(result, keys = []) {
  if (result?.status === "rejected") {
    console.warn("CaptaZap | falha ao carregar recurso:", result.reason);
    return [];
  }

  const value = result?.value ?? result;

  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;

  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
    if (Array.isArray(value?.data?.[key])) return value.data[key];
  }

  return [];
}

async function buscarLeadsCRMRemotasCaptaZap({ empresaId, termo }) {
  const termoOriginal = String(termo || "").trim();
  const termoNormalizado = normalizar(termoOriginal);
  if (!empresaId || termoNormalizado.length < 3) return [];

  // A carga principal já busca a carteira inteira: pesquisa local primeiro,
  // sem fazer oito consultas serializadas de 4.000+ leads a cada digitação.
  // Caso a carteira falhe ou esteja incompleta, preservamos busca remota.
  try {
    const leads = await carregarLeadsCRMCacheCaptaZap(empresaId);
    const encontrados = (Array.isArray(leads) ? leads : []).filter((lead) =>
      pertenceEmpresa(lead, empresaId) &&
      leadCombinaComBuscaContatoCaptaZap(lead, termoNormalizado),
    );
    if (encontrados.length) return normalizarLeadsVinculadasCaptazap(encontrados);
  } catch (erro) {
    console.warn("CaptaZap Speed | carteira CRM indisponível; buscando no backend:", erro?.message || erro);
  }

  // Compatibilidade com cadastros não presentes na carteira principal.
  // Endpoints específicos são tentados antes do fallback completo legado.
  const encoded = encodeURIComponent(termoOriginal);
  const rotas = [
    `/leads/buscar?empresa_id=${empresaId}&mostrarInativas=true&q=${encoded}&limit=80`,
    `/leads/busca?empresa_id=${empresaId}&mostrarInativas=true&q=${encoded}&limit=80`,
  ];
  if (termoNormalizado.length >= 5) {
    rotas.push(`/leads?empresa_id=${empresaId}&mostrarInativas=true&limit=10000`);
  }
  for (const rota of rotas) {
    try {
      const dados = await requestJson(rota);
      const encontrados = pickArray(dados, ["leads", "items", "results", "registros", "data"])
        .filter((lead) => pertenceEmpresa(lead, empresaId) && leadCombinaComBuscaContatoCaptaZap(lead, termoNormalizado));
      if (encontrados.length) return normalizarLeadsVinculadasCaptazap(encontrados);
    } catch (erro) {
      if (import.meta.env?.DEV) console.warn("CaptaZap Speed | busca alternativa:", rota, erro?.message || erro);
    }
  }
  return [];
}

function leadCombinaComBuscaContatoCaptaZap(lead = {}, termoNormalizado = "") {
  if (!termoNormalizado) return true;

  const texto = [
    lead?.id,
    lead?.lead_id,
    lead?.leadId,
    lead?.nome_lead,
    lead?.nomeLead,
    lead?.nome,
    lead?.name,
    lead?.telefone1,
    lead?.telefone2,
    lead?.telefone,
    lead?.numero,
    lead?.crc,
    lead?.cpf,
    lead?.documento,
    lead?.status_negociacao,
    lead?.status_agendamento,
    lead?.fonte_lead,
    lead?.fonte,
    lead?.potencial,
  ]
    .filter(Boolean)
    .join(" ");

  const textoNormalizado = normalizar(texto);
  const telefoneBusca = normalizarTelefone(termoNormalizado);
  const telefoneLead = normalizarTelefone(
    `${lead?.telefone1 || ""} ${lead?.telefone2 || ""} ${lead?.telefone || ""} ${lead?.numero || ""}`,
  );

  return (
    textoNormalizado.includes(termoNormalizado) ||
    (telefoneBusca && telefoneLead.includes(telefoneBusca))
  );
}

function pertenceEmpresa(item, empresaId) {
  if (!item || !empresaId) return false;

  const itemEmpresaId =
    item.empresa_id ||
    item.empresaId ||
    item.id_empresa ||
    item.empresa?.id ||
    item.lead?.empresa_id;

  if (!itemEmpresaId) return true;

  return String(itemEmpresaId) === String(empresaId);
}

function leadApareceNoCaptaZap(lead) {
  if (!lead) return false;

  const ativoRaw =
    lead.ativo ??
    lead.active ??
    lead.status_ativo ??
    lead.lead_ativo ??
    lead.is_active;

  if (
    ativoRaw === 0 ||
    ativoRaw === false ||
    String(ativoRaw).toLowerCase() === "false" ||
    String(ativoRaw).toLowerCase() === "inativo"
  ) {
    return false;
  }

  const camposStatus = [
    lead.status,
    lead.status_lead,
    lead.situacao,
    lead.situacao_lead,
    lead.status_negociacao,
    lead.etapa,
    lead.pipeline_status,
  ]
    .filter(Boolean)
    .map((value) => normalizar(value));

  const termosBloqueados = [
    "perdido",
    "perdida",
    "lead perdido",
    "lead perdida",
    "descartado",
    "descartada",
    "cancelado",
    "cancelada",
    "inativo",
    "inativa",
    "bloqueado",
    "bloqueada",
    "excluido",
    "excluida",
    "desqualificado",
    "desqualificada",
    "sem interesse",
    "nao tem interesse",
    "não tem interesse",
    "perda",
  ];

  return !camposStatus.some((status) =>
    termosBloqueados.some((termo) => status.includes(termo)),
  );
}

function contatoAvulsoToConversaCaptaZap({
  contato,
  empresaId,
  empresaNome,
}) {
  const telefone =
    contato?.telefone_normalizado || contato?.telefone || contato?.numero || "";
  const nomeManual = String(contato?.nome || "").trim();
  const nomeWhatsapp = String(contato?.nome_whatsapp || "").trim();
  const nome =
    nomeManual ||
    nomeWhatsapp ||
    formatTelefoneCurtoCaptaZap(telefone) ||
    "Contato sem nome";

  return normalizarConversaCaptazap({
    ...contato,
    id: `contato-${contato?.id || normalizarTelefone(telefone)}`,
    contato_id: contato?.id || null,
    contatoId: contato?.id || null,
    empresa_id: contato?.empresa_id || empresaId,
    empresa_nome: contato?.empresa_nome || empresaNome,
    nome,
    contato_nome: nomeManual,
    contatoNome: nomeManual,
    nome_whatsapp: nomeWhatsapp,
    nomeWhatsapp,
    dra_malu_classificacao: contato?.dra_malu_classificacao || "padrao",
    telefone,
    numero: telefone,
    canal: "whatsapp",
    status: "contato",
    preview: "Contato salvo no CaptaZap",
    origem: "CaptaZap",
    tags: [CAPTAZAP_SOMENTE_CONTATO_TAG, "CaptaZap"],
    naoLidas: 0,
    conversa_ativa: false,
    sem_conversa: true,
    _captazap_contato_avulso: true,
    _captazap_badge_laranja: true,
    badge_tipo_cadastro: CAPTAZAP_SOMENTE_CONTATO_BADGE,
    tipo_cadastro_visual: "contato",
    _captazap_origem_contatos: true,
    _captazap_contexto_fixo_contato: true,
    _forcar_historico_por_telefone: true,
    criado_em: contato?.criado_em || null,
    atualizado_em: contato?.atualizado_em || null,
    ultima_mensagem_em: contato?.atualizado_em || contato?.criado_em || null,
  });
}

function leadToContatoConversa({
  lead,
  empresaId,
  empresaNome,
  usuarios,
  fontes,
  campanhas,
  procedimentos,
  regioes,
  dentistas,
}) {
  const leadId = getLeadId(lead);
  const nome = lead.nome_lead || lead.nome || lead.name || `Lead ${leadId}`;
  const telefone =
    lead.telefone1 ||
    lead.telefone ||
    lead.celular ||
    lead.whatsapp ||
    lead.numero ||
    "";

  const captadorId =
    lead.usuario_criador_id || lead.captador_id || lead.id_usuario;
  const captadorNome =
    lead.usuario_criador_nome ||
    lead.captador_nome ||
    lead.usuario_nome ||
    getNomeUsuario(captadorId, usuarios);

  const fonte =
    lead.nome_fonte ||
    lead.fonte ||
    getNomeById(lead.id_fonte_lead, fontes, ["nome"]) ||
    "CaptaCRM";

  const campanha =
    lead.nome_campanha ||
    getNomeById(lead.id_campanha, campanhas, ["nome"]) ||
    "";

  const procedimento =
    lead.nome_procedimento ||
    lead.procedimento ||
    getNomeById(lead.id_procedimento, procedimentos, ["nome"]) ||
    "";

  const regiao =
    lead.nome_regiao ||
    lead.regiao ||
    getNomeById(lead.id_regiao, regioes, ["regiao", "nome"]) ||
    "";

  const dentista =
    lead.nome_dentista ||
    lead.dentista ||
    getNomeById(lead.id_dentista, dentistas, [
      "profissional",
      "nome",
      "nome_dentista",
    ]) ||
    "";

  const leadReativavel = lead._captaZap_reativavel === true;

  const tags = [
    leadReativavel ? "Lead reativável" : "Lead CRM",
    leadReativavel ? "Voltou pelo WhatsApp" : "",
    fonte,
    campanha,
    procedimento,
    regiao,
    dentista,
    lead.potencial ? `Potencial ${lead.potencial}` : "",
    lead.status_negociacao,
    lead.transferencia_pendente ? "Transferência pendente" : "",
  ].filter(Boolean);

  return {
    id: `lead-${leadId}`,
    empresa_id: lead.empresa_id || empresaId,
    empresa_nome: lead.empresa_nome || empresaNome,
    nome,
    numero: telefone,
    telefone_normalizado: normalizarTelefone(telefone),
    canal: "whatsapp",
    status: leadReativavel ? "lead_reativavel" : "lead_crm",
    _captazap_origem_contatos: true,
    _contato_crm_consulta: true,
    _captazap_contexto_fixo_contato: true,
    _captazap_lead_clicada_id: leadId,
    _forcar_historico_por_telefone: true,
    favorito: Boolean(lead.favorito),
    preview: leadReativavel
      ? `Lead antiga encontrada: ${montarPreviewLead(lead)}`
      : montarPreviewLead(lead),
    foto: getImagemUrl(
      lead.foto_original ||
        lead.foto_url_original ||
        lead.foto_perfil ||
        lead.fotoPerfil ||
        lead.foto ||
        lead.foto_url ||
        lead.avatar ||
        lead.avatar_url ||
        lead.imagem ||
        lead.imagem_url ||
        "",
    ),
    origem: fonte,
    tags,
    naoLidas: 0,
    hora: lead.atualizado_em || lead.updated_at ? "CRM" : "",
    conversa_ativa: false,
    sem_conversa: true,
    lead_reativavel: leadReativavel,
    lead_id: leadId,
    leadId,
    lead_contexto_id: leadId,
    leadContextoId: leadId,
    lead,
    leads_vinculadas: [lead],
    leadsVinculadas: [lead],
    total_leads_vinculadas: 1,
    totalLeadsVinculadas: 1,
    telefone_compartilhado: false,
    telefoneCompartilhado: false,
    // O captador/dono da lead NÃO é responsável operacional do chamado.
    // Na aba Contatos, o usuário pode consultar o histórico, mas precisa
    // assumir o chamado para responder.
    captador_id: null,
    captador_nome: "",
    responsavel_id: null,
    responsavel_nome: "",
    lead_captador_id: captadorId,
    lead_captador_nome: captadorNome,
    dono_lead_id: captadorId,
    dono_lead_nome: captadorNome,
    transferencia_pendente: Boolean(lead.transferencia_pendente),
    mensagens: [],
    tarefas: lead.tarefas || [],
    interacoes: lead.interacoes || [],
    agendamentos: lead.agendamentos || [],
    ultima_interacao: lead.ultima_interacao,
    proxima_tarefa: lead.proxima_tarefa,
    ultimo_agendamento: lead._ultimo_agendamento,
    timeline: getTimelineLead(lead),
  };
}

function enriquecerConversaComLead({
  conversa,
  leads,
  usuarios,
  fontes,
  campanhas,
  procedimentos,
  regioes,
  dentistas,
}) {
  if (!conversa) return conversa;

  const conversaLeadId =
    getLeadContextoIdConversa(conversa) || conversa.lead_id || conversa.leadId;
  const conversaTelefone = normalizarTelefone(
    conversa.numero || conversa.telefone,
  );
  const leadsVinculadas = getLeadsVinculadasConversa(conversa);
  const telefoneCompartilhado = leadsVinculadas.length > 1;

  const leadEncontrada =
    leads.find((lead) => String(getLeadId(lead)) === String(conversaLeadId)) ||
    getLeadVinculadaPorId(conversa, conversaLeadId) ||
    (!telefoneCompartilhado
      ? leads.find(
          (lead) =>
            normalizarTelefone(
              lead.telefone1 ||
                lead.telefone ||
                lead.celular ||
                lead.whatsapp ||
                lead.numero,
            ) === conversaTelefone,
        )
      : null);

  if (!leadEncontrada) {
    return normalizarConversaCaptazap(conversa);
  }

  const contatoLead = leadToContatoConversa({
    lead: leadEncontrada,
    empresaId: conversa.empresa_id || conversa.empresaId,
    empresaNome: conversa.empresa_nome || conversa.empresaNome,
    usuarios,
    fontes,
    campanhas,
    procedimentos,
    regioes,
    dentistas,
  });

  // IMPORTANTE:
  // O captador/dono da lead CRM não é o responsável operacional do chamado.
  // Se misturar os dois, a conversa pode sumir de quem puxou e voltar para
  // o captador original da lead.
  const statusChamado = normalizar(
    conversa.status_chamado ||
      conversa.chamado_status ||
      conversa.chamada_status ||
      "",
  );
  const estaEmFila = Boolean(
    conversa.em_fila ||
    conversa.fila_atendimento ||
    conversa.aguardando_atendimento ||
    conversa.aba_captazap === "fila",
  );
  const podeUsarCaptadorComoAliasOperacional =
    !estaEmFila && !statusChamado.includes("fech");

  const responsavelIdOperacional = getPrimeiroValor([
    conversa._captazap_assumida_por,
    conversa.responsavel_id,
    conversa.usuario_responsavel_id,
    conversa.atendente_id,
    conversa.operador_id,
    podeUsarCaptadorComoAliasOperacional ? conversa.captador_id : "",
    conversa.responsavel?.id,
    conversa.usuario_responsavel?.id,
    conversa.atendente?.id,
    conversa.operador?.id,
    podeUsarCaptadorComoAliasOperacional ? conversa.captador?.id : "",
  ]);

  const responsavelNomeOperacional = getPrimeiroValor([
    conversa._captazap_assumida_por_nome,
    conversa.responsavel_nome,
    conversa.usuario_responsavel_nome,
    conversa.nome_responsavel,
    conversa.atendente_nome,
    conversa.operador_nome,
    podeUsarCaptadorComoAliasOperacional ? conversa.captador_nome : "",
    conversa.responsavel?.nome,
    conversa.usuario_responsavel?.nome,
    conversa.atendente?.nome,
    conversa.operador?.nome,
    podeUsarCaptadorComoAliasOperacional ? conversa.captador?.nome : "",
  ]);

  return {
    ...contatoLead,
    ...conversa,
    nome: contatoLead.nome || conversa.nome,
    numero: conversa.numero || contatoLead.numero,
    lead_id: conversa.lead_id || getLeadId(leadEncontrada),
    leadId: conversa.leadId || conversa.lead_id || getLeadId(leadEncontrada),
    lead_contexto_id:
      getLeadContextoIdConversa(conversa) || getLeadId(leadEncontrada),
    leadContextoId:
      getLeadContextoIdConversa(conversa) || getLeadId(leadEncontrada),
    lead: leadEncontrada,

    // Responsável do CHAMADO/atendimento.
    captador_id: responsavelIdOperacional || null,
    captador_nome: responsavelNomeOperacional || "",
    responsavel_id: responsavelIdOperacional || null,
    responsavel_nome: responsavelNomeOperacional || "",
    usuario_responsavel_id:
      responsavelIdOperacional || conversa.usuario_responsavel_id || null,
    usuario_responsavel_nome:
      responsavelNomeOperacional || conversa.usuario_responsavel_nome || "",

    // Dono/captador da LEAD, separado apenas para exibição/histórico.
    // NUNCA usar captador_id/captador_nome aqui, porque esses campos representam
    // o responsável operacional do chamado no CaptaZap.
    lead_captador_id:
      contatoLead.lead_captador_id || contatoLead.dono_lead_id || null,
    lead_captador_nome:
      contatoLead.lead_captador_nome || contatoLead.dono_lead_nome || "",
    dono_lead_id:
      contatoLead.dono_lead_id || contatoLead.lead_captador_id || null,
    dono_lead_nome:
      contatoLead.dono_lead_nome || contatoLead.lead_captador_nome || "",

    transferencia_pendente: contatoLead.transferencia_pendente,
    conversa_ativa: true,
    sem_conversa: false,
    tarefas: leadEncontrada.tarefas || [],
    interacoes: leadEncontrada.interacoes || [],
    agendamentos: leadEncontrada.agendamentos || [],
    ultima_interacao: leadEncontrada.ultima_interacao,
    proxima_tarefa: leadEncontrada.proxima_tarefa,
    ultimo_agendamento: leadEncontrada._ultimo_agendamento,
  };
}

function criarConversaLocalAPartirDeLead({ conversa, empresaId, empresaNome }) {
  const lead = conversa.lead || conversa.lead_dados || conversa.leadDados || {};
  const leadId = conversa.lead_id || conversa.leadId || lead.id || null;
  const telefone = conversa.numero || conversa.telefone || lead.telefone1 || "";

  const leadCaptadorId =
    conversa.lead_captador_id ||
    conversa.dono_lead_id ||
    lead.usuario_criador_id ||
    lead.captador_id ||
    lead.id_usuario ||
    null;

  const leadCaptadorNome =
    conversa.lead_captador_nome ||
    conversa.dono_lead_nome ||
    lead.usuario_criador_nome ||
    lead.captador_nome ||
    lead.usuario_nome ||
    "";

  return {
    ...conversa,
    id: String(conversa.id || "").startsWith("lead-")
      ? `nova-${leadId || normalizarTelefone(telefone)}-${Date.now()}`
      : conversa.id ||
        `nova-${leadId || normalizarTelefone(telefone)}-${Date.now()}`,
    empresa_id: conversa.empresa_id || lead.empresa_id || empresaId,
    empresa_nome: conversa.empresa_nome || lead.empresa_nome || empresaNome,
    nome:
      conversa.nome ||
      lead.nome_lead ||
      lead.nome ||
      telefone ||
      "Contato sem nome",
    numero: telefone,
    telefone_normalizado: normalizarTelefone(telefone),
    canal: conversa.canal || "whatsapp",
    status: "nova_conversa",
    preview: "Nova conversa iniciada pelo CaptaZap",
    origem: conversa.origem || lead.fonte || "CaptaCRM",
    lead_id: leadId,
    lead,

    // Não usar o captador/dono da lead como responsável operacional.
    // A conversa só vira Minha quando for assumida/enviada pelo usuário.
    captador_id: conversa.responsavel_id || null,
    captador_nome: conversa.responsavel_nome || "",
    responsavel_id: conversa.responsavel_id || null,
    responsavel_nome: conversa.responsavel_nome || "",

    // Dono da lead fica separado para exibição/histórico.
    lead_captador_id: leadCaptadorId,
    lead_captador_nome: leadCaptadorNome,
    dono_lead_id: leadCaptadorId,
    dono_lead_nome: leadCaptadorNome,

    conversa_ativa: true,
    sem_conversa: false,
    mensagens: mensagensNormalizadas,
    timeline: Array.isArray(conversa.timeline)
      ? conversa.timeline
      : [
          {
            tipo: "CaptaZap",
            descricao: "Conversa iniciada a partir da carteira de leads.",
            data: "Agora",
          },
        ],
  };
}

function prepararConversaReativadaSeNecessario(conversa) {
  if (!conversa?.lead_reativavel && !conversa?.lead?._captaZap_reativavel) {
    return conversa;
  }

  const timeline = Array.isArray(conversa.timeline) ? conversa.timeline : [];

  return {
    ...conversa,
    lead_reativavel: true,
    reativacao_pendente: true,
    tags: Array.from(
      new Set([
        ...(conversa.tags || []),
        "Lead reativável",
        "Voltou pelo WhatsApp",
      ]),
    ),
    timeline: [
      {
        tipo: "Reativação",
        descricao:
          "Lead antiga/inativa encontrada pelo telefone. Ao receber ou iniciar conversa, deve ser reativada no CRM.",
        data: "Agora",
      },
      ...timeline,
    ],
  };
}

function montarPreviewLead(lead) {
  if (lead.status_negociacao) return `Negociação: ${lead.status_negociacao}`;
  if (lead.status_agendamento) return `Agendamento: ${lead.status_agendamento}`;
  if (lead.potencial) return `Potencial: ${lead.potencial}`;

  return "Lead cadastrada no CaptaCRM";
}

function montarBuscaItem(item = {}) {
  const lead =
    getLeadPrincipalConversaCardSafe(item) ||
    item.lead ||
    item.lead_dados ||
    item.leadDados ||
    item;

  const leadsVinculadas = getLeadsVinculadasBuscaCaptaZap(item);

  const camposLeadsVinculadas = leadsVinculadas.flatMap((leadItem) => [
    leadItem?.id,
    leadItem?.lead_id,
    leadItem?.leadId,
    leadItem?.nome_lead,
    leadItem?.nomeLead,
    leadItem?.nome,
    leadItem?.name,
    leadItem?.nome_cliente,
    leadItem?.paciente,
    leadItem?.telefone1,
    leadItem?.telefone2,
    leadItem?.telefone,
    leadItem?.celular,
    leadItem?.whatsapp,
    leadItem?.numero,
    leadItem?.status_negociacao,
    leadItem?.status_agendamento,
    leadItem?.potencial,
    leadItem?.fonte,
    leadItem?.nome_fonte,
    leadItem?.campanha,
    leadItem?.nome_campanha,
    leadItem?.procedimento,
    leadItem?.nome_procedimento,
    leadItem?.procedimento_nome,
    leadItem?.nome_regiao,
    leadItem?.regiao,
    leadItem?.nome_dentista,
    leadItem?.dentista_nome,
    leadItem?.profissional_nome,
    leadItem?.nome_profissional,
  ]);

  return [
    item.id,
    item.lead_id,
    item.leadId,
    item.lead_contexto_id,
    item.leadContextoId,
    item.nome,
    item.nome_lead,
    item.nomeLead,
    item.name,
    item.paciente,
    item.numero,
    item.telefone,
    item.telefone1,
    item.telefone2,
    item.celular,
    item.whatsapp,
    item.telefone_normalizado,
    item.preview,
    item.status,
    item.status_chamado,
    item.status_negociacao,
    item.status_agendamento,
    item.origem,
    item.canal,
    item.captador_nome,
    item.responsavel_nome,
    item.usuario_responsavel_nome,
    item.empresa_nome,
    item.empresaNome,
    item.tags?.join(" "),
    item.profissional_nome,
    item.nome_profissional,
    item.dentista_nome,
    item.nome_dentista,
    item.procedimento_nome,
    item.nome_procedimento,
    lead?.id,
    lead?.lead_id,
    lead?.leadId,
    lead?.nome_lead,
    lead?.nome,
    lead?.name,
    lead?.telefone1,
    lead?.telefone2,
    lead?.telefone,
    lead?.celular,
    lead?.whatsapp,
    lead?.numero,
    lead?.status_negociacao,
    lead?.status_agendamento,
    lead?.potencial,
    lead?.fonte,
    lead?.nome_fonte,
    lead?.nome_campanha,
    lead?.nome_procedimento,
    lead?.procedimento_nome,
    lead?.nome_regiao,
    lead?.nome_dentista,
    lead?.dentista_nome,
    lead?.profissional_nome,
    lead?.nome_profissional,
    ...camposLeadsVinculadas,
  ]
    .filter(Boolean)
    .join(" ");
}

function getLeadsVinculadasBuscaCaptaZap(item = {}) {
  const candidatos = [
    item.leads_vinculadas,
    item.leadsVinculadas,
    item.telefone_leads,
    item.telefoneLeads,
    item.leads_relacionadas,
    item.leadsRelacionadas,
  ];

  const lista = [];

  for (const candidato of candidatos) {
    if (Array.isArray(candidato)) lista.push(...candidato.filter(Boolean));
  }

  if (item.lead && typeof item.lead === "object") lista.push(item.lead);

  // Na aba Contatos, o próprio item pode ser uma lead crua vinda do CRM.
  if (
    item.nome_lead ||
    item.telefone1 ||
    item.id_dentista ||
    item.id_procedimento
  ) {
    lista.push(item);
  }

  return normalizarLeadsVinculadasCaptazap(lista);
}

function getLeadPrincipalConversaCardSafe(item = {}) {
  const leads = getLeadsVinculadasBuscaCaptaZap(item);
  const contextoId = String(
    item.lead_contexto_id || item.leadContextoId || item.contexto_lead_id || "",
  );

  if (contextoId) {
    const contexto = leads.find((leadItem) =>
      [leadItem?.id, leadItem?.lead_id, leadItem?.leadId, leadItem?.id_lead]
        .filter(Boolean)
        .some((id) => String(id) === contextoId),
    );

    if (contexto) return contexto;
  }

  return item.lead || leads[0] || null;
}

function encontrarConversaAbertaPorTelefone({
  telefone,
  listas,
  zapiInstanceId = "",
}) {
  const telefoneNormalizado =
    normalizarTelefoneWhatsappBrasil(telefone) || normalizarTelefone(telefone);
  const telefoneSemDDI = normalizarTelefoneLeadBrasil(telefone);

  if (!telefoneNormalizado && !telefoneSemDDI) return null;

  const instanciaFiltro = String(zapiInstanceId || "").trim();
  const abertas = [
    ...(listas.minhasConversas || []),
    ...(listas.filaDeEspera || []),
    ...(listas.followUp || []),
  ];

  return (
    abertas.find((item) => {
      if (!getTemConversaAtiva(item)) return false;
      if (
        instanciaFiltro &&
        getConexaoInstanceIdConversaCaptaZap(item) !== instanciaFiltro
      ) {
        return false;
      }

      const itemTelefone = getTelefoneHistoricoWhatsAppCaptaZap(item);
      const itemSemDDI = normalizarTelefoneLeadBrasil(itemTelefone);

      return Boolean(
        (telefoneNormalizado && itemTelefone === telefoneNormalizado) ||
        (telefoneSemDDI && itemSemDDI === telefoneSemDDI),
      );
    }) || null
  );
}

function getTelefoneHistoricoWhatsAppCaptaZap(conversa = {}) {
  const principal = getTelefoneHistoricoPrincipalCaptaZap(conversa);
  const canal = String(conversa?.canal || "").trim().toLowerCase();
  const instancia = String(
    conversa?.zapi_instance_id ||
      conversa?.zapiInstanceId ||
      conversa?.conexao_instance_id ||
      conversa?.conexaoInstanceId ||
      "",
  ).trim();

  // Instagram não usa telefone. O backend grava um alias estável "ig:<senderId>"
  // para compatibilidade com o mensageiro legado. Nunca devemos transformar esse
  // alias em número WhatsApp, senão o histórico some ao recarregar a conversa.
  if (
    canal === "instagram" ||
    instancia.toLowerCase().startsWith("instagram:") ||
    String(principal || "").trim().toLowerCase().startsWith("ig:")
  ) {
    return String(principal || "").trim();
  }

  return (
    normalizarTelefoneWhatsappBrasil(principal) || normalizarTelefone(principal)
  );
}

function encontrarAtendimentoPorTelefoneCaptaZap({
  telefone,
  listas = {},
  extras = [],
  zapiInstanceId = "",
} = {}) {
  const telefoneNormalizado = normalizarTelefoneWhatsappBrasil(telefone);
  const telefoneSemDDI = normalizarTelefoneLeadBrasil(telefone);

  if (!telefoneNormalizado && !telefoneSemDDI) return null;

  const instanciaFiltro = String(zapiInstanceId || "").trim();
  const candidatos = [
    ...(listas.minhasConversas || []),
    ...(listas.filaDeEspera || []),
    ...(listas.followUp || []),
    ...(listas.fechados || []),
    ...(extras || []),
  ]
    .filter(Boolean)
    .filter((item) => {
      if (!instanciaFiltro) return true;
      return (
        getConexaoInstanceIdConversaCaptaZap(item) === instanciaFiltro
      );
    });

  const mesmoTelefone = (item = {}) => {
    const itemTelefones = getTelefonesHistoricoCandidatosCaptaZap(item);
    const itemTelefonesNormalizados = itemTelefones.flatMap((valor) => [
      normalizarTelefoneWhatsappBrasil(valor),
      normalizarTelefoneLeadBrasil(valor),
      normalizarTelefone(valor),
    ]);

    return itemTelefonesNormalizados.some((valor) =>
      Boolean(
        valor &&
        ((telefoneNormalizado && valor === telefoneNormalizado) ||
          (telefoneSemDDI && valor === telefoneSemDDI)),
      ),
    );
  };

  const ordenados = candidatos.filter(mesmoTelefone).sort((a, b) => {
    const aAberto = getStatusChamadoVisual(a) === "aberto" ? 1 : 0;
    const bAberto = getStatusChamadoVisual(b) === "aberto" ? 1 : 0;

    if (aAberto !== bAberto) return bAberto - aAberto;

    const aTemResponsavel =
      getResponsavelConversaId(a) || getResponsavelConversaNome(a) ? 1 : 0;
    const bTemResponsavel =
      getResponsavelConversaId(b) || getResponsavelConversaNome(b) ? 1 : 0;

    if (aTemResponsavel !== bTemResponsavel) {
      return bTemResponsavel - aTemResponsavel;
    }

    const dataA = new Date(
      a.ultima_mensagem_em || a.updated_at || a.aberto_em || a.created_at || 0,
    ).getTime();
    const dataB = new Date(
      b.ultima_mensagem_em || b.updated_at || b.aberto_em || b.created_at || 0,
    ).getTime();

    return (
      (Number.isFinite(dataB) ? dataB : 0) -
      (Number.isFinite(dataA) ? dataA : 0)
    );
  });

  return ordenados[0] || null;
}

function precisaAssumirParaResponderCaptaZap(
  conversa = {},
  { usuarioId, usuarioNome } = {},
) {
  if (!conversa) return false;

  // Chamado fechado não aceita envio. O fluxo de reabertura/assunção é
  // tratado pelos botões do cabeçalho.
  if (getStatusChamadoVisual(conversa) !== "aberto") return false;

  // Fila de Proteção continua preservando o responsável anterior apenas para
  // contexto/auditoria. Operacionalmente ninguém é dono enquanto estiver na
  // Fila: é obrigatório clicar em Assumir antes de conversar.
  if (isFilaProtecaoCaptaZap(conversa)) return true;

  const responsavelId = getResponsavelConversaId(conversa);
  const responsavelNome = getResponsavelConversaNome(conversa);

  const mesmoId =
    usuarioId && responsavelId && String(usuarioId) === String(responsavelId);
  const mesmoNome =
    usuarioNome &&
    responsavelNome &&
    normalizarNomeComparacao(usuarioNome) ===
      normalizarNomeComparacao(responsavelNome);

  // Se já sou o responsável, não há por que assumir novamente.
  if (mesmoId || mesmoNome) return false;

  // Sem responsável, chamado de outro operador, contato vindo da carteira CRM
  // ou conversa ainda sem chamado operacional: todos exigem Assumir primeiro.
  return true;
}

function isContatoCRMConsultaCaptaZap(conversa = {}) {
  if (!conversa) return false;

  const origemContato = Boolean(
    conversa._captazap_origem_contatos ||
    conversa._contato_crm_consulta ||
    conversa.sem_conversa === true ||
    conversa.semConversa === true,
  );

  const id = String(conversa.id || conversa.conversa_id || "");
  const idContatoCRM = id.startsWith("lead-") || id.startsWith("contato-");
  const status = normalizar(
    conversa.status || conversa.status_atendimento || "",
  );
  const statusContatoCRM =
    status.includes("lead_crm") ||
    status.includes("lead reativavel") ||
    status.includes("lead_reativavel");

  const jaTemChamadoOperacional = Boolean(
    conversa.conversa_ativa === true ||
    conversa.conversaAtiva === true ||
    conversa.chamado_id ||
    conversa.id_chamado ||
    conversa.chamada_id,
  );

  return Boolean(
    (origemContato || idContatoCRM || statusContatoCRM) &&
    !jaTemChamadoOperacional,
  );
}

function deveCarregarHistoricoPorTelefoneCaptaZap(conversa = {}) {
  // Histórico do WhatsApp é sempre do telefone. Quando o atendimento veio
  // da aba Contatos/CRM e foi mesclado com um chamado existente, ele passa
  // a ter chamado_id, mas ainda deve carregar mensagens por telefone.
  // O lead_id/contexto serve para CRM, agenda, tarefas e interações.
  return Boolean(
    isContatoCRMConsultaCaptaZap(conversa) ||
    conversa._captazap_origem_contatos ||
    conversa._contato_crm_consulta ||
    conversa._captazap_contato_com_chamado_existente ||
    conversa._forcar_historico_por_telefone ||
    conversa.forcarHistoricoPorTelefone,
  );
}

function getTemConversaAtiva(conversa) {
  if (!conversa) return false;

  const statusChamado = getStatusChamadoVisual(conversa);

  // Chamado fechado sem nova resposta não é operacional:
  // não fica em Minhas nem na Fila. Vai para histórico/fechados.
  // Chamado fechado COM nova resposta vira operacional para reabertura.
  if (statusChamado === "fechado") {
    return isChamadoFechadoComRespostaCaptaZap(conversa);
  }

  if (conversa.sem_conversa || conversa.semConversa) return false;
  if (conversa.conversa_ativa === false || conversa.conversaAtiva === false) {
    return false;
  }

  if (conversa.conversa_ativa === true || conversa.conversaAtiva === true) {
    return true;
  }

  const id = String(conversa.id || "");
  const idVirtual =
    id.startsWith("lead-") ||
    id.startsWith("contato-") ||
    id.startsWith("nova-");

  if (idVirtual && !Array.isArray(conversa.mensagens)) return false;

  if (conversa.conversa_id || conversa.conversaId) return true;
  if (conversa.ticket_id || conversa.ticketId) return true;

  const mensagens = Array.isArray(conversa.mensagens) ? conversa.mensagens : [];

  if (mensagens.length > 0) return true;

  const status = String(conversa.status || "").toLowerCase();

  if (
    status.includes("atendimento") ||
    status.includes("aguardando") ||
    status.includes("fila") ||
    status.includes("aberta") ||
    status.includes("ativa")
  ) {
    return true;
  }

  return false;
}

function isConversaDeOutroResponsavel(
  conversa,
  { usuarioId, usuarioNome } = {},
) {
  if (!getTemConversaAtiva(conversa)) return false;

  if (isChamadoFechadoComRespostaCaptaZap(conversa)) return false;

  const responsavelId = getResponsavelConversaId(conversa);
  const responsavelNome = getResponsavelConversaNome(conversa);

  const temResponsavel = Boolean(responsavelId || responsavelNome);

  if (!temResponsavel) return false;

  const mesmoId =
    usuarioId && responsavelId && String(responsavelId) === String(usuarioId);

  const mesmoNome =
    usuarioNome &&
    responsavelNome &&
    normalizarNomeComparacao(responsavelNome) ===
      normalizarNomeComparacao(usuarioNome);

  return !(mesmoId || mesmoNome);
}

function isConversaMinhaDoUsuario(
  conversa,
  { usuarioId, usuarioNome, usuarios = [] } = {},
) {
  if (!getTemConversaAtiva(conversa)) return false;
  if (isFilaProtecaoCaptaZap(conversa)) return false;

  // Compatibilidade para payload antigo: resposta após fechamento é proteção,
  // portanto não volta para Minhas enquanto aguarda cobertura.
  if (isChamadoFechadoComRespostaCaptaZap(conversa)) {
    return false;
  }

  const responsavelId = getResponsavelConversaId(conversa);
  const responsavelNome = getResponsavelConversaNome(conversa);

  if (
    usuarioId &&
    responsavelId &&
    String(responsavelId) === String(usuarioId)
  ) {
    return true;
  }

  if (
    usuarioNome &&
    responsavelNome &&
    normalizarNomeComparacao(responsavelNome) ===
      normalizarNomeComparacao(usuarioNome)
  ) {
    return true;
  }

  return false;
}

function isConversaNaFilaDeAtendimento(conversa, contextoAtendimento = {}) {
  if (!getTemConversaAtiva(conversa)) return false;

  if (isFilaProtecaoCaptaZap(conversa)) return true;

  // Compatibilidade para payload antigo ainda fechado: nova resposta entra na
  // Fila de Proteção mesmo mantendo o responsável anterior.
  if (isChamadoFechadoComRespostaCaptaZap(conversa)) {
    return true;
  }

  // Se já é minha, não aparece na Fila.
  if (isConversaMinhaDoUsuario(conversa, contextoAtendimento)) return false;

  // Regra definitiva da Fila:
  // se outro captador já puxou/assumiu o chamado, ele NÃO fica mais na fila
  // dos outros usuários. Fila é somente para pendências sem responsável,
  // transferidas ao usuário/departamento ou fechadas com nova mensagem.
  if (
    getResponsavelConversaId(conversa) ||
    getResponsavelConversaNome(conversa)
  ) {
    return false;
  }

  return isConversaPrecisaAcaoFilaGlobal(conversa);
}

function isConversaMinhaDaEquipe(conversa, contextoAtendimento = {}) {
  if (!getTemConversaAtiva(conversa)) return false;
  if (isFilaProtecaoCaptaZap(conversa)) return false;

  if (isChamadoFechadoComRespostaCaptaZap(conversa)) {
    return Boolean(
      getResponsavelConversaId(conversa) ||
      getResponsavelConversaNome(conversa),
    );
  }

  if (getStatusChamadoVisual(conversa) !== "aberto") return false;

  return Boolean(
    getResponsavelConversaId(conversa) || getResponsavelConversaNome(conversa),
  );
}

function marcarConversaMinhaEquipeSupervisao(conversa = {}, contexto = {}) {
  const responsavelNome = getResponsavelConversaNome(conversa);

  return {
    ...conversa,
    _supervisao_minhas_equipe: true,
    _supervisao_minhas_equipe_label: responsavelNome
      ? `Responsável: ${responsavelNome}`
      : "Responsável não identificado",
  };
}

function isConversaPrecisaAcaoFilaGlobal(conversa = {}) {
  if (isFilaProtecaoCaptaZap(conversa)) return true;

  // Regra única da Fila:
  // contato chamou, chamado está aberto e ainda não existe responsável humano.
  // Mensagem enviada por sistema/automação/operador NÃO é fila.
  const semResponsavel = !(
    getResponsavelConversaId(conversa) || getResponsavelConversaNome(conversa)
  );

  if (!semResponsavel) return false;

  const ultimaMensagemOperacional =
    getUltimaMensagemOperacionalFilaCaptaZap(conversa);

  if (!ultimaMensagemOperacional) return false;

  return isMensagemDoClienteCaptaZap(ultimaMensagemOperacional);
}

function isFilaProtecaoCaptaZap(conversa = {}) {
  const valores = [
    conversa?.fila_protecao,
    conversa?.filaProtecao,
    conversa?._fila_protecao,
  ];

  return valores.some((valor) => {
    if (valor === true || valor === 1) return true;
    const texto = String(valor ?? "").trim().toLowerCase();
    return ["true", "1", "sim", "yes"].includes(texto);
  });
}

function getUltimaMensagemOperacionalFilaCaptaZap(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  if (mensagens.length > 0) {
    const ordenadas = [...mensagens].sort((a, b) => {
      const dataA = getDataMensagemCaptaZap(a)?.getTime() || 0;
      const dataB = getDataMensagemCaptaZap(b)?.getTime() || 0;
      return dataB - dataA;
    });

    // Se o robô respondeu depois do cliente, a fila continua sendo do cliente.
    // Por isso ignoramos mensagens automáticas/sistema e buscamos a última
    // mensagem operacional real.
    return (
      ordenadas.find(
        (mensagem) => !isMensagemSistemaOuAutomacaoFila(mensagem),
      ) || null
    );
  }

  const ultimaMensagem = getUltimaMensagemConversa(conversa);

  if (!ultimaMensagem) return null;
  if (isMensagemSistemaOuAutomacaoFila(ultimaMensagem)) return null;

  return ultimaMensagem;
}

function isMensagemSistemaOuAutomacaoFila(mensagem = {}) {
  const autor = normalizar(
    mensagem.autor ||
      mensagem.de ||
      mensagem.from ||
      mensagem.remetente ||
      mensagem.sender ||
      mensagem.tipo_autor ||
      mensagem.ultima_mensagem_autor ||
      mensagem.ultimaMensagemAutor ||
      "",
  );

  const origem = normalizar(
    mensagem.origem ||
      mensagem.origem_envio ||
      mensagem.tipo_envio ||
      mensagem.automacao_tipo ||
      mensagem.ultima_automacao_tipo ||
      mensagem.ultimaAutomacaoTipo ||
      "",
  );

  const direcao = normalizar(
    mensagem.direcao ||
      mensagem.direction ||
      mensagem.tipo_direcao ||
      mensagem.sentido ||
      mensagem.ultima_mensagem_direcao ||
      mensagem.ultimaMensagemDirecao ||
      "",
  );

  const automacaoFlag =
    mensagem.automacao === true ||
    mensagem.automacao === 1 ||
    String(mensagem.automacao || "").toLowerCase() === "true" ||
    Boolean(
      mensagem.automacao_tipo ||
      mensagem.ultima_automacao_tipo ||
      mensagem.ultimaAutomacaoTipo,
    );

  return Boolean(
    automacaoFlag ||
    autor.includes("sistema") ||
    autor.includes("bot") ||
    autor.includes("automatico") ||
    autor.includes("automático") ||
    autor.includes("automacao") ||
    autor.includes("automação") ||
    origem.includes("sistema") ||
    origem.includes("bot") ||
    origem.includes("automatico") ||
    origem.includes("automático") ||
    origem.includes("automacao") ||
    origem.includes("automação") ||
    direcao.includes("interno"),
  );
}
function marcarConversaFilaEquipe(conversa, contexto = {}) {
  if (
    !isConversaPrecisaAcaoFilaGlobal(conversa) &&
    !isChamadoFechadoComRespostaCaptaZap(conversa)
  ) {
    return conversa;
  }

  const responsavelNome = getResponsavelConversaNome(conversa);
  const responsavelId = getResponsavelConversaId(conversa);
  const tempoMs = getTempoSemRespostaMs(conversa);
  const tempoTexto = formatTempoSemRespostaEquipe(tempoMs);
  const semResponsavel = !(responsavelId || responsavelNome);
  const filaProtecao = Boolean(
    isFilaProtecaoCaptaZap(conversa) ||
      isChamadoFechadoComRespostaCaptaZap(conversa),
  );
  const motivoProtecao =
    conversa.fila_protecao_motivo ||
    conversa.filaProtecaoMotivo ||
    (isChamadoFechadoComRespostaCaptaZap(conversa)
      ? "retorno_apos_fechamento"
      : null);
  const labelProtecao =
    conversa.fila_protecao_label ||
    conversa.filaProtecaoLabel ||
    (motivoProtecao === "retorno_apos_fechamento"
      ? "Retorno após fechamento"
      : motivoProtecao === "sem_resposta_3h"
        ? "Sem resposta há mais de 3h"
        : motivoProtecao === "recuperado_dra_malu"
          ? "Recuperado pela Dra. Malu"
          : "");

  return {
    ...conversa,
    _fila_global: true,
    _fila_equipe: true,
    _fila_resgate: Boolean(filaProtecao && (responsavelId || responsavelNome)),
    _fila_resgate_responsavel_nome: responsavelNome,
    _fila_resgate_responsavel_id: responsavelId,
    _fila_resgate_tempo_ms: Number.isFinite(tempoMs) ? tempoMs : null,
    fila_protecao: filaProtecao,
    filaProtecao,
    fila_protecao_motivo: motivoProtecao,
    filaProtecaoMotivo: motivoProtecao,
    fila_protecao_label: labelProtecao,
    filaProtecaoLabel: labelProtecao,
    status_fila_operacional: filaProtecao
      ? labelProtecao || "Fila de proteção"
      : semResponsavel
        ? "Aguardando atendimento"
        : "Aguardando resposta",
    tags: Array.from(
      new Set(
        [
          ...(conversa.tags || []),
          filaProtecao
            ? "Fila de proteção"
            : semResponsavel
              ? "Sem dono"
              : "Fila equipe",
          responsavelNome ? `Dono: ${responsavelNome}` : "",
          tempoTexto ? `Sem resposta: ${tempoTexto}` : "",
        ].filter(Boolean),
      ),
    ),
  };
}

function isConversaPendenteRespostaCliente(conversa = {}) {
  if (Number(conversa.naoLidas || 0) > 0) return true;

  const ultimaMensagem = getUltimaMensagemConversa(conversa);

  if (ultimaMensagem) {
    return isMensagemDoClienteCaptaZap(ultimaMensagem);
  }

  const status = normalizar(conversa.status || "");

  if (
    status.includes("aguardando") ||
    status.includes("pendente") ||
    status.includes("fila") ||
    status.includes("recebida") ||
    status.includes("nova") ||
    status.includes("novo")
  ) {
    return true;
  }

  return false;
}

function getTempoSemRespostaMs(conversa = {}) {
  const dataReferencia =
    getDataUltimaMensagemCliente(conversa) ||
    getDataConversaOperacional(conversa);

  if (!dataReferencia) return Number.NaN;

  return Date.now() - dataReferencia.getTime();
}

function getDataUltimaMensagemCliente(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens) ? conversa.mensagens : [];

  const mensagensCliente = mensagens
    .filter(isMensagemDoClienteCaptaZap)
    .map((mensagem) => ({
      mensagem,
      data: getDataMensagemCaptaZap(mensagem),
    }))
    .filter((item) => item.data);

  if (mensagensCliente.length > 0) {
    return mensagensCliente.sort(
      (a, b) => b.data.getTime() - a.data.getTime(),
    )[0].data;
  }

  const ultimaMensagem = getUltimaMensagemConversa(conversa);

  if (ultimaMensagem && isMensagemDoClienteCaptaZap(ultimaMensagem)) {
    return getDataMensagemCaptaZap(ultimaMensagem);
  }

  return null;
}

function getUltimaMensagemConversa(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens) ? conversa.mensagens : [];

  if (mensagens.length > 0) {
    return [...mensagens].sort((a, b) => {
      const dataA = getDataMensagemCaptaZap(a)?.getTime() || 0;
      const dataB = getDataMensagemCaptaZap(b)?.getTime() || 0;

      return dataB - dataA;
    })[0];
  }

  const direcao =
    conversa.ultimaMensagemDirecao ||
    conversa.ultima_mensagem_direcao ||
    conversa.ultimaDirecao ||
    conversa.ultima_direcao ||
    conversa.last_direction ||
    conversa.direction ||
    conversa.direcao_ultima_mensagem ||
    conversa.direcao ||
    "";

  const autor =
    conversa.ultimaMensagemAutor ||
    conversa.ultima_mensagem_autor ||
    conversa.autor_ultima_mensagem ||
    conversa.autor ||
    conversa.de ||
    "";

  const texto =
    conversa.ultimaMensagem ||
    conversa.ultima_mensagem ||
    conversa.preview ||
    conversa.texto ||
    conversa.mensagem ||
    "";

  if (!direcao && !autor && !texto) return null;

  return {
    direcao,
    autor,
    de: autor,
    texto,
    datahora:
      conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.datahora ||
      conversa.updated_at ||
      conversa.created_at ||
      conversa.timestamp,
    created_at:
      conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.updated_at ||
      conversa.created_at,
    timestamp: conversa.timestamp,
  };
}

function isMensagemDoClienteCaptaZap(mensagem = {}) {
  const direcao = normalizar(mensagem.direcao || mensagem.direction || "");
  const autor = normalizar(
    mensagem.autor || mensagem.from || mensagem.de || mensagem.remetente || "",
  );

  const fromMe =
    mensagem.fromMe === true ||
    mensagem.from_me === true ||
    String(mensagem.fromMe || mensagem.from_me || "").toLowerCase() === "true";

  if (fromMe) return false;

  if (
    direcao.includes("enviada") ||
    direcao.includes("saida") ||
    direcao.includes("outbound") ||
    direcao.includes("outgoing") ||
    direcao.includes("sent") ||
    autor.includes("operador") ||
    autor.includes("atendente") ||
    autor.includes("captador") ||
    autor.includes("usuario") ||
    autor.includes("sistema")
  ) {
    return false;
  }

  if (
    direcao.includes("recebida") ||
    autor.includes("cliente") ||
    autor.includes("contato") ||
    autor.includes("lead")
  ) {
    return true;
  }

  // Quando vem mensagem real sem direção clara, mas não é fromMe,
  // consideramos como recebida para não esconder pendência da fila.
  if (mensagem.texto || mensagem.mensagem || mensagem.message || mensagem.id) {
    return true;
  }

  return false;
}

function getDataMensagemCaptaZap(mensagem = {}) {
  return parseDataCaptaZap(
    mensagem.datahora ||
      mensagem.created_at ||
      mensagem.enviada_em ||
      mensagem.timestamp ||
      mensagem.data ||
      mensagem.hora,
  );
}

function getDataEntradaCaptaZap(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens) ? conversa.mensagens : [];

  const primeiraMensagem = mensagens
    .map(getDataMensagemCaptaZap)
    .filter((data) => data && !Number.isNaN(data.getTime()))
    .sort((a, b) => a.getTime() - b.getTime())[0];

  if (primeiraMensagem) return primeiraMensagem;

  return parseDataCaptaZap(
    conversa.data_entrada_captazap ||
      conversa.entrada_captazap_em ||
      conversa.captazap_entrada_em ||
      conversa.primeira_mensagem_em ||
      conversa.data_primeira_mensagem ||
      conversa.first_message_at ||
      conversa.iniciado_em ||
      conversa.aberto_em ||
      conversa.created_at ||
      conversa.lead?.data_entrada_captazap ||
      conversa.lead?.primeira_mensagem_em ||
      conversa.lead?.created_at ||
      "",
  );
}

function formatDataEntradaCaptaZap(conversa = {}) {
  const data = getDataEntradaCaptaZap(conversa);

  if (!data || Number.isNaN(data.getTime())) return "";

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDataConversaOperacional(conversa = {}) {
  return parseDataCaptaZap(
    conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.updated_at ||
      conversa.created_at ||
      conversa.datahora ||
      conversa.timestamp ||
      conversa.data_ultima_mensagem ||
      conversa.hora,
  );
}

function parseDataCaptaZap(value) {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  const raw = String(value || "").trim();

  if (!raw) return null;

  // "21:47" sozinho é hora do dia, não dá para saber há quanto tempo.
  if (/^\d{1,2}:\d{2}$/.test(raw)) return null;

  // Datas do MySQL normalmente chegam como "YYYY-MM-DD HH:mm:ss" sem timezone.
  // Para o CaptaZap, valor sem timezone é tratado como horário local da operação
  // (America/Sao_Paulo), não como UTC. Isso evita fechamento parecer 3h depois
  // e reabrir chamado indevidamente.
  const mysqlLocal = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
  );

  if (mysqlLocal) {
    const [, ano, mes, dia, hora, minuto, segundo = "00"] = mysqlLocal;
    const dataLocal = new Date(
      Number(ano),
      Number(mes) - 1,
      Number(dia),
      Number(hora),
      Number(minuto),
      Number(segundo),
    );

    return Number.isNaN(dataLocal.getTime()) ? null : dataLocal;
  }

  const brLocal = raw.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:,?\s+)(\d{2}):(\d{2})(?::(\d{2}))?$/,
  );

  if (brLocal) {
    const [, dia, mes, ano, hora, minuto, segundo = "00"] = brLocal;
    const dataLocal = new Date(
      Number(ano),
      Number(mes) - 1,
      Number(dia),
      Number(hora),
      Number(minuto),
      Number(segundo),
    );

    return Number.isNaN(dataLocal.getTime()) ? null : dataLocal;
  }

  const data = new Date(raw);

  if (Number.isNaN(data.getTime())) return null;

  return data;
}

function formatTempoSemRespostaEquipe(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "";

  const totalMin = Math.floor(ms / 60000);
  const dias = Math.floor(totalMin / 1440);
  const horas = Math.floor((totalMin % 1440) / 60);
  const minutos = totalMin % 60;

  if (dias > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${minutos}m`;

  return `${minutos}m`;
}

function isConversaFechadaHistorico(conversa = {}) {
  return (
    getStatusChamadoVisual(conversa) === "fechado" &&
    !isChamadoFechadoComRespostaCaptaZap(conversa)
  );
}

function deveAssumirConversaAoAbrir(conversa, { usuarioId, usuarioNome } = {}) {
  if (!usuarioId && !usuarioNome) return false;

  if (isConversaMinhaDoUsuario(conversa, { usuarioId, usuarioNome })) {
    return false;
  }

  const responsavelId = getResponsavelConversaId(conversa);
  const responsavelNome = getResponsavelConversaNome(conversa);

  // Se já existe responsável, não puxa automaticamente.
  // Chamado assumido por Anny/Lara/etc. sai da fila dos demais.
  if (responsavelId || responsavelNome) return false;

  return getTemConversaAtiva(conversa);
}

function aplicarResponsavelConversa(conversa, { usuarioId, usuarioNome } = {}) {
  const lead = conversa?.lead || null;

  return {
    ...conversa,
    status: "em_atendimento",
    status_chamado:
      conversa?.status_chamado === "fechado" ? "fechado" : "aberto",
    chamado_status:
      conversa?.chamado_status === "fechado" ? "fechado" : "aberto",
    chamada_status:
      conversa?.chamada_status === "fechado" ? "fechado" : "aberto",

    fila_atendimento: false,
    aguardando_atendimento: false,
    em_fila: false,
    _fila_global: false,
    _fila_equipe: false,
    _fila_resgate: false,

    // v66: assumir/responder precisa retirar o card da Fila imediatamente,
    // sem esperar SSE, polling ou F5. A proteção é estado da Fila; quando um
    // humano toma posse do atendimento, todos os aliases locais são zerados.
    fila_protecao: false,
    filaProtecao: false,
    _fila_protecao: false,
    fila_protecao_motivo: null,
    filaProtecaoMotivo: null,
    fila_protecao_label: "",
    filaProtecaoLabel: "",
    fila_protecao_em: null,
    filaProtecaoEm: null,
    responsavel_anterior_id: null,
    responsavel_anterior_nome: "",
    _fila_resgate_responsavel_id: null,
    _fila_resgate_responsavel_nome: "",
    _fila_resgate_tempo_ms: null,

    aba_captazap: "minhas",
    naoLidas: 0,

    // Responsável operacional do chamado.
    captador_id: usuarioId || conversa.captador_id || null,
    captador_nome: usuarioNome || conversa.captador_nome || "",
    responsavel_id: usuarioId || conversa.responsavel_id || null,
    responsavel_nome: usuarioNome || conversa.responsavel_nome || "",
    usuario_responsavel_id:
      usuarioId || conversa.usuario_responsavel_id || null,
    usuario_responsavel_nome:
      usuarioNome || conversa.usuario_responsavel_nome || "",
    atendente_id: usuarioId || conversa.atendente_id || null,
    atendente_nome: usuarioNome || conversa.atendente_nome || "",
    operador_id: usuarioId || conversa.operador_id || null,
    operador_nome: usuarioNome || conversa.operador_nome || "",

    // Dono da lead preservado em campos separados.
    lead_captador_id:
      conversa.lead_captador_id ||
      lead?.usuario_criador_id ||
      lead?.captador_id ||
      null,
    lead_captador_nome:
      conversa.lead_captador_nome ||
      lead?.usuario_criador_nome ||
      lead?.captador_nome ||
      "",
    dono_lead_id:
      conversa.dono_lead_id ||
      lead?.usuario_criador_id ||
      lead?.captador_id ||
      null,
    dono_lead_nome:
      conversa.dono_lead_nome ||
      lead?.usuario_criador_nome ||
      lead?.captador_nome ||
      "",

    _captazap_assumida_por: usuarioId || usuarioNome || null,
    _captazap_assumida_por_nome: usuarioNome || "",

    tags: Array.from(new Set([...(conversa.tags || []), "Em atendimento"])),

    // Não gravar responsavel/captador dentro da lead.
    // A lead mantém seu dono original; o chamado guarda quem está atendendo.
    lead,
  };
}

function getChaveContextoPacienteCaptaZap(
  conversa = {},
  empresaIdFallback = null,
) {
  if (typeof window === "undefined") return "";

  const lead = conversa?.lead || {};
  const empresaId =
    empresaIdFallback ||
    conversa?.empresa_id ||
    conversa?.empresaId ||
    lead?.empresa_id ||
    "";
  const telefone = normalizarTelefone(
    conversa?.telefone ||
      conversa?.numero ||
      conversa?.phone ||
      conversa?.whatsapp ||
      lead?.telefone1 ||
      lead?.telefone ||
      lead?.celular ||
      lead?.whatsapp ||
      "",
  );

  if (!empresaId || !telefone) return "";

  // Usa os últimos 11 dígitos para manter o mesmo contexto entre 6199... e 556199...
  const telefoneLocal = telefone.slice(-11) || telefone;
  return `captazap:lead-contexto:${empresaId}:${telefoneLocal}`;
}

function salvarContextoPacienteCaptaZap(
  conversa = {},
  leadId,
  empresaIdFallback = null,
) {
  if (typeof window === "undefined") return;

  const leadIdSeguro = Number(leadId || 0);
  if (!leadIdSeguro) return;

  // Segurança: nunca persiste contexto se a lead não pertence ao telefone da conversa.
  // Isso impede que uma conversa seja jogada para paciente/lead de outro WhatsApp.
  const leadSeguro = getLeadVinculadaSeguraPorIdCaptaZap(
    conversa,
    leadIdSeguro,
  );
  if (!leadSeguro && getLeadsVinculadasConversa(conversa).length > 0) return;

  const key = getChaveContextoPacienteCaptaZap(conversa, empresaIdFallback);
  if (!key) return;

  try {
    window.localStorage?.setItem(key, String(leadIdSeguro));
  } catch {
    // localStorage pode estar bloqueado em navegação privada; mantém somente o estado em memória.
  }
}

function obterContextoPacienteCaptaZap(
  conversa = {},
  empresaIdFallback = null,
) {
  if (typeof window === "undefined") return null;

  const key = getChaveContextoPacienteCaptaZap(conversa, empresaIdFallback);
  if (!key) return null;

  try {
    const raw = window.localStorage?.getItem(key);
    const value = Number(raw || 0);
    if (!Number.isFinite(value) || value <= 0) return null;

    // Se a conversa trouxe leads vinculadas, valida o telefone antes de reutilizar localStorage.
    if (getLeadsVinculadasConversa(conversa).length > 0) {
      return getLeadVinculadaSeguraPorIdCaptaZap(conversa, value)
        ? value
        : null;
    }

    return value;
  } catch {
    return null;
  }
}

function getUltimos8TelefoneCaptaZap(value = "") {
  return String(value || "")
    .replace(/\D/g, "")
    .slice(-8);
}

function getTelefonePrincipalConversaCaptaZap(conversa = {}) {
  return (
    conversa?.telefone ||
    conversa?.numero ||
    conversa?.phone ||
    conversa?.whatsapp ||
    ""
  );
}

function leadPertenceAoTelefoneCaptaZap(lead = {}, telefone = "") {
  const telConversa = getUltimos8TelefoneCaptaZap(telefone);
  if (!telConversa) return false;

  const telefonesLead = [
    lead.telefone1,
    lead.telefone2,
    lead.telefone,
    lead.celular,
    lead.whatsapp,
    lead.numero,
  ]
    .map(getUltimos8TelefoneCaptaZap)
    .filter(Boolean);

  return telefonesLead.includes(telConversa);
}

function getLeadVinculadaSeguraPorIdCaptaZap(conversa = {}, leadId) {
  const leadIdSeguro = Number(leadId || 0);
  if (!leadIdSeguro) return null;

  const telefone = getTelefonePrincipalConversaCaptaZap(conversa);
  const lead = getLeadsVinculadasConversa(conversa).find(
    (item) => Number(getLeadId(item) || 0) === leadIdSeguro,
  );

  if (!lead) return null;
  return leadPertenceAoTelefoneCaptaZap(lead, telefone) ? lead : null;
}

function getLeadContextoSeguroConversaCaptaZap(conversa = {}) {
  const telefone = getTelefonePrincipalConversaCaptaZap(conversa);
  const leads = getLeadsVinculadasConversa(conversa);

  const direto =
    conversa.lead_contexto_id ||
    conversa.leadContextoId ||
    conversa.contexto_lead_id ||
    conversa.contextoLeadId ||
    null;

  // Fonte da verdade: o contexto definido no chamado pelo backend.
  // localStorage não pode decidir a identidade visual da conversa, porque pode
  // reaproveitar um paciente antigo e misturar nome/telefone de contatos diferentes.
  if (direto) {
    const leadDireta = getLeadVinculadaSeguraPorIdCaptaZap(conversa, direto);
    if (leadDireta) return Number(direto);

    // Se a API trouxe leads vinculadas e o contexto não pertence ao telefone,
    // ignora o contexto em vez de mostrar lead errada.
    if (leads.length > 0) return null;
  }

  // Quando existe somente uma lead válida no telefone, podemos assumir contexto.
  if (leads.length === 1) {
    const leadUnica = leads[0];
    return leadPertenceAoTelefoneCaptaZap(leadUnica, telefone)
      ? getLeadId(leadUnica)
      : null;
  }

  // Telefone compartilhado: nunca escolher automaticamente.
  if (leads.length > 1) return null;

  // Última defesa: só usa conversa.lead se o telefone também bater.
  if (
    conversa.lead &&
    leadPertenceAoTelefoneCaptaZap(conversa.lead, telefone)
  ) {
    return conversa.lead_id || conversa.leadId || conversa.lead?.id || null;
  }

  return null;
}

function aplicarContextoPacientePersistidoCaptaZap(
  conversa = {},
  empresaIdFallback = null,
) {
  if (!conversa || typeof conversa !== "object") return conversa;

  // Identidade do paciente em contexto deve vir do backend/chamado.
  // Mantemos esta função por compatibilidade, mas ela não injeta mais dados
  // vindos do localStorage para evitar contaminação visual entre contatos.
  return conversa;
}

function aplicarEstadoChamadoAutoritativoCaptaZap(
  conversa = {},
  chamadoAtual = {},
) {
  if (!conversa || !chamadoAtual) return conversa;

  const statusAtual = getStatusChamadoVisual(chamadoAtual);
  if (!["aberto", "fechado", "transferido"].includes(statusAtual)) {
    return conversa;
  }

  const responsavelId =
    chamadoAtual.responsavel_id ??
    chamadoAtual.captador_id ??
    conversa.responsavel_id ??
    null;
  const responsavelNome =
    chamadoAtual.responsavel_nome ??
    chamadoAtual.captador_nome ??
    conversa.responsavel_nome ??
    "";

  const filaProtecaoAtual = [
    chamadoAtual.fila_protecao,
    chamadoAtual.filaProtecao,
    chamadoAtual._fila_protecao,
  ].some(
    (valor) =>
      valor === true ||
      valor === 1 ||
      String(valor || "").toLowerCase() === "true",
  );
  const filaProtecaoMotivoAtual =
    chamadoAtual.fila_protecao_motivo ||
    chamadoAtual.filaProtecaoMotivo ||
    null;
  const filaProtecaoLabelAtual =
    chamadoAtual.fila_protecao_label ||
    chamadoAtual.filaProtecaoLabel ||
    (filaProtecaoMotivoAtual === "retorno_apos_fechamento"
      ? "Retorno após fechamento"
      : filaProtecaoMotivoAtual === "sem_resposta_3h"
        ? "Sem resposta há mais de 3h"
        : filaProtecaoMotivoAtual === "recuperado_dra_malu"
          ? "Recuperado pela Dra. Malu"
          : "");

  const base = {
    ...conversa,
    chamado_id:
      chamadoAtual.chamado_id ||
      chamadoAtual.id_chamado ||
      chamadoAtual.id ||
      conversa.chamado_id ||
      null,
    id_chamado:
      chamadoAtual.id_chamado ||
      chamadoAtual.chamado_id ||
      chamadoAtual.id ||
      conversa.id_chamado ||
      conversa.chamado_id ||
      null,
    status_chamado: statusAtual,
    chamado_status: statusAtual,
    chamada_status: statusAtual,
    responsavel_id: responsavelId,
    responsavel_nome: responsavelNome,
    captador_id: responsavelId,
    captador_nome: responsavelNome,
    fila_protecao: filaProtecaoAtual,
    filaProtecao: filaProtecaoAtual,
    _fila_protecao: filaProtecaoAtual,
    fila_protecao_motivo: filaProtecaoMotivoAtual,
    filaProtecaoMotivo: filaProtecaoMotivoAtual,
    fila_protecao_label: filaProtecaoLabelAtual,
    filaProtecaoLabel: filaProtecaoLabelAtual,
    aberto_em:
      chamadoAtual.aberto_em ?? chamadoAtual.abertoEm ?? conversa.aberto_em ?? null,
    fechado_em:
      chamadoAtual.fechado_em ?? chamadoAtual.fechadoEm ??
      (statusAtual === "fechado" ? conversa.fechado_em || null : null),
    fechadoEm:
      chamadoAtual.fechadoEm ?? chamadoAtual.fechado_em ??
      (statusAtual === "fechado" ? conversa.fechadoEm || conversa.fechado_em || null : null),
    fechado_por_id:
      chamadoAtual.fechado_por_id ??
      (statusAtual === "fechado" ? conversa.fechado_por_id || null : null),
    fechado_por_nome:
      chamadoAtual.fechado_por_nome ??
      (statusAtual === "fechado" ? conversa.fechado_por_nome || "" : ""),
    motivo_fechamento:
      chamadoAtual.motivo_fechamento ??
      chamadoAtual.motivoFechamento ??
      (statusAtual === "fechado" ? conversa.motivo_fechamento || "" : ""),
    motivoFechamento:
      chamadoAtual.motivoFechamento ??
      chamadoAtual.motivo_fechamento ??
      (statusAtual === "fechado" ? conversa.motivoFechamento || conversa.motivo_fechamento || "" : ""),
    _captazap_status_chamado_reconciliado: true,
    _captazap_status_chamado_reconciliado_em: new Date().toISOString(),
  };

  if (statusAtual === "fechado") {
    return normalizarConversaCaptazap({
      ...base,
      status: "fechado",
      conversa_ativa: false,
      conversaAtiva: false,
      sem_conversa: true,
      semConversa: true,
      aba_captazap: "fechados",
      em_fila: false,
      fila_atendimento: false,
      aguardando_atendimento: false,
      _fila_global: false,
      _fila_equipe: false,
      naoLidas: 0,
      reabertura_pendente: getClienteRespondeuDepoisDeFechado(base),
      reaberturaPendente: getClienteRespondeuDepoisDeFechado(base),
    });
  }

  if (statusAtual === "aberto") {
    const semResponsavelAtual = !responsavelId;
    const emFilaAtual = Boolean(
      filaProtecaoAtual || semResponsavelAtual,
    );

    return normalizarConversaCaptazap({
      ...base,
      status: emFilaAtual
        ? "aguardando_atendimento"
        : String(base.status || "").toLowerCase() === "fechado"
          ? "em_atendimento"
          : base.status || "em_atendimento",
      conversa_ativa: true,
      conversaAtiva: true,
      sem_conversa: false,
      semConversa: false,
      aba_captazap: emFilaAtual ? "fila" : "minhas",
      em_fila: emFilaAtual,
      fila_atendimento: emFilaAtual,
      aguardando_atendimento: emFilaAtual,
      _fila_global: emFilaAtual,
      _fila_equipe: emFilaAtual,
      fechado_em: null,
      fechadoEm: null,
      fechado_por_id: null,
      fechado_por_nome: "",
      motivo_fechamento: "",
      motivoFechamento: "",
    });
  }

  return normalizarConversaCaptazap(base);
}

async function buscarConversasOperacaoCaptaZap({
  empresaId,
  termo,
  aba = "operacao",
  usuarioId = null,
  zapiInstanceId = "",
}) {
  if (!empresaId || !String(termo || "").trim()) return [];

  const termoOriginal = String(termo || "").trim();
  const abaNormalizada = String(aba || "operacao")
    .trim()
    .toLowerCase();
  const abaRemota = ["minhas", "fila", "fechados"].includes(abaNormalizada)
    ? abaNormalizada
    : "operacao";

  const params = new URLSearchParams({
    empresa_id: String(empresaId),
    aba: abaRemota,
    busca: termoOriginal,
    limit: "60",
  });

  if (usuarioId) params.set("usuario_id", String(usuarioId));
  if (String(zapiInstanceId || "").trim()) {
    params.set("zapi_instance_id", String(zapiInstanceId).trim());
  }

  try {
    const res = await requestJson(
      `/whatsapp/captazap/conversas-v2?${params.toString()}`,
    );
    return pickArray(res, [
      "conversas",
      "chamados",
      "data",
      "items",
      "results",
    ]).map(normalizarConversaCaptazap);
  } catch (error) {
    console.warn("CaptaZap | busca operacional V2 falhou:", error);
    return [];
  }
}

function marcarResultadoBuscaOperacaoCaptaZap(
  conversa = {},
  usuarioAtual = {},
) {
  const responsavelId = getResponsavelConversaId(conversa);
  const responsavelNome = getResponsavelConversaNome(conversa);
  const usuarioId = usuarioAtual?.usuarioId;
  const usuarioNome = usuarioAtual?.usuarioNome;
  const mesmoId =
    usuarioId && responsavelId && String(responsavelId) === String(usuarioId);
  const mesmoNome =
    usuarioNome &&
    responsavelNome &&
    normalizarNomeComparacao(responsavelNome) ===
      normalizarNomeComparacao(usuarioNome);
  const outroResponsavel = Boolean(
    (responsavelId || responsavelNome) && !(mesmoId || mesmoNome),
  );

  return {
    ...conversa,
    _captazap_busca_operacional: true,
    _captazap_bloqueado_por_responsavel: Boolean(outroResponsavel),
    _captazap_pode_assumir: Boolean(outroResponsavel),
    _captazap_mensagem_bloqueio: outroResponsavel
      ? `Chamado com ${responsavelNome || "outro responsável"}. Clique para visualizar bloqueado e assumir, se necessário.`
      : "",
  };
}

async function assumirConversaCaptaZap({
  empresaId,
  conversa,
  usuarioId,
  usuarioNome,
  acao = "assumir",
  chamadoAnteriorId = null,
}) {
  if (!empresaId || !conversa || (!usuarioId && !usuarioNome)) return null;

  const telefone = conversa.numero || conversa.telefone || "";
  const leadContextoId = getLeadContextoIdConversa(conversa);
  const leadId =
    leadContextoId || conversa.lead_id || conversa.leadId || conversa.lead?.id;

  return requestJson("/whatsapp/captazap/assumir", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      empresa_id: empresaId,
      conversa_id: conversa.conversa_id || conversa.id || null,
      zapi_instance_id: getConexaoInstanceIdConversaCaptaZap(conversa) || null,
      telefone,
      lead_id: leadId || null,
      lead_contexto_id: leadContextoId || leadId || null,
      usuario_id: usuarioId || null,
      captador_id: usuarioId || null,
      responsavel_id: usuarioId || null,
      usuario_nome: usuarioNome || "",
      captador_nome: usuarioNome || "",
      responsavel_nome: usuarioNome || "",

      acao,
      criar_novo_chamado:
        acao === "abrir_novo_chamado" || conversa?.criar_novo_chamado === true,
      novo_chamado:
        acao === "abrir_novo_chamado" || conversa?.novo_chamado === true,
      chamado_anterior_id:
        chamadoAnteriorId ||
        conversa?.chamado_anterior_id ||
        conversa?.chamado_origem_id ||
        null,
      chamado_id: conversa?.chamado_id || conversa?.id_chamado || null,
      status_chamado:
        conversa?.status_chamado || conversa?.chamado_status || "aberto",
      responsavel_anterior_id: getResponsavelConversaId(conversa) || null,
      responsavel_anterior_nome: getResponsavelConversaNome(conversa) || "",
      origem_fila: Boolean(
        isFilaProtecaoCaptaZap(conversa) ||
          String(conversa?.aba_captazap || "").toLowerCase() === "fila" ||
          conversa?.em_fila === true ||
          conversa?._fila_global === true ||
          conversa?._fila_equipe === true,
      ),
    }),
  });
}

function getResponsavelConversaId(conversa = {}) {
  return getPrimeiroValor([
    conversa._captazap_assumida_por,
    conversa.responsavel_id,
    conversa.usuario_responsavel_id,
    conversa.atendente_id,
    conversa.operador_id,

    // captador_id aqui é apenas alias operacional vindo do chamado/backend.
    // Nunca usar lead.captador_id para decidir Minhas/Fila.
    conversa.captador_id,

    conversa.responsavel?.id,
    conversa.usuario_responsavel?.id,
    conversa.atendente?.id,
    conversa.operador?.id,
    conversa.captador?.id,
  ]);
}

function getResponsavelConversaNome(conversa = {}) {
  return getPrimeiroValor([
    conversa._captazap_assumida_por_nome,
    conversa.responsavel_nome,
    conversa.usuario_responsavel_nome,
    conversa.nome_responsavel,
    conversa.atendente_nome,
    conversa.operador_nome,

    // captador_nome aqui é apenas alias operacional vindo do chamado/backend.
    // Nunca usar lead.captador_nome para decidir Minhas/Fila.
    conversa.captador_nome,

    conversa.responsavel?.nome,
    conversa.usuario_responsavel?.nome,
    conversa.atendente?.nome,
    conversa.operador?.nome,
    conversa.captador?.nome,
  ]);
}

function getPrimeiroValor(values = []) {
  for (const value of values) {
    const text = String(value ?? "").trim();

    if (!text) continue;
    if (text === "0") continue;
    if (text.toLowerCase() === "null") continue;
    if (text.toLowerCase() === "undefined") continue;

    return text;
  }

  return "";
}

function normalizarNomeComparacao(value = "") {
  return normalizar(value).replace(/\s+/g, " ").trim();
}

function getMensagemChaveCaptaZap(mensagem = {}) {
  const chaveForte =
    mensagem.id ||
    mensagem.whatsapp_id ||
    mensagem.whatsappId ||
    mensagem.msgid ||
    mensagem.messageId ||
    mensagem.messageID ||
    mensagem.message_id ||
    mensagem.wa_media_id;

  if (chaveForte) return String(chaveForte);

  return [
    mensagem.telefone || "",
    mensagem.lead_id || "",
    mensagem.datahora ||
      mensagem.created_at ||
      mensagem.enviada_em ||
      mensagem.recebida_em ||
      mensagem.timestamp ||
      "",
    mensagem.direcao || mensagem.direction || "",
    mensagem.autor || mensagem.de || "",
    mensagem.texto || mensagem.mensagem || mensagem.message || "",
  ]
    .filter(Boolean)
    .join("|");
}

function getMensagemTimestampOrdenacaoCaptaZap(mensagem = {}) {
  return parseTimestampCaptaZap(
    mensagem.datahora ||
      mensagem.created_at ||
      mensagem.updated_at ||
      mensagem.enviada_em ||
      mensagem.recebida_em ||
      mensagem.timestamp ||
      mensagem.time ||
      mensagem.date ||
      "",
  );
}

function getMensagemIdOrdenacaoCaptaZap(mensagem = {}) {
  const id = Number(
    mensagem.id ||
      mensagem.whatsapp_id ||
      mensagem.whatsappId ||
      mensagem.message_id ||
      mensagem.msgid ||
      0,
  );

  return Number.isFinite(id) ? id : 0;
}

function ordenarMensagensConversaCaptaZap(mensagens = [], limite = 200) {
  const map = new Map();

  for (const mensagem of Array.isArray(mensagens) ? mensagens : []) {
    if (!mensagem) continue;

    const chave =
      getMensagemChaveCaptaZap(mensagem) ||
      `sem-chave-${map.size}-${getMensagemTimestampOrdenacaoCaptaZap(mensagem)}`;

    const anterior = map.get(chave);

    map.set(chave, {
      ...(anterior || {}),
      ...mensagem,
    });
  }

  return [...map.values()]
    .sort((a, b) => {
      const diff =
        getMensagemTimestampOrdenacaoCaptaZap(a) -
        getMensagemTimestampOrdenacaoCaptaZap(b);

      if (diff !== 0) return diff;

      return (
        getMensagemIdOrdenacaoCaptaZap(a) - getMensagemIdOrdenacaoCaptaZap(b)
      );
    })
    .slice(-limite);
}

function mesclarMensagensConversa(mensagens = [], novaMensagem = {}) {
  const novas = Array.isArray(novaMensagem) ? novaMensagem : [novaMensagem];

  return ordenarMensagensConversaCaptaZap([
    ...(Array.isArray(mensagens) ? mensagens : []),
    ...novas.filter(Boolean),
  ]);
}

function getUltimaAtividadeConversaCaptaZap(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  const ultimaMensagemTs = mensagens.reduce((maior, mensagem) => {
    const ts = getMensagemTimestampOrdenacaoCaptaZap(mensagem);
    return ts > maior ? ts : maior;
  }, 0);

  const ultimaConversaTs = parseTimestampCaptaZap(
    conversa.ultimaMensagemEm ||
      conversa.ultima_mensagem_em ||
      conversa.last_message_at ||
      conversa.data_ultima_mensagem ||
      conversa.ultima_mensagem_datahora ||
      conversa.datahora ||
      conversa.timestamp ||
      conversa.updated_at ||
      conversa.created_at ||
      conversa.hora ||
      "",
  );

  return Math.max(ultimaMensagemTs, ultimaConversaTs, 0);
}

function getUltimoIdConversaCaptaZap(conversa = {}) {
  const mensagens = Array.isArray(conversa.mensagens)
    ? conversa.mensagens.filter(Boolean)
    : [];

  const maiorMensagemId = mensagens.reduce((maior, mensagem) => {
    const id = getMensagemIdOrdenacaoCaptaZap(mensagem);
    return id > maior ? id : maior;
  }, 0);

  const idConversa = Number(
    conversa.ultima_mensagem_id ||
      conversa.ultimaMensagemId ||
      conversa.whatsapp_id ||
      conversa.whatsappId ||
      conversa.mensagem_id ||
      conversa.message_id ||
      conversa.id ||
      conversa.conversa_id ||
      0,
  );

  return Math.max(
    maiorMensagemId,
    Number.isFinite(idConversa) ? idConversa : 0,
  );
}

function ordenarConversasPorUltimaAtividadeCaptaZap(lista = []) {
  return dedupeConversas(Array.isArray(lista) ? lista : []).sort((a, b) => {
    const diff =
      getUltimaAtividadeConversaCaptaZap(b) -
      getUltimaAtividadeConversaCaptaZap(a);

    if (diff !== 0) return diff;

    return getUltimoIdConversaCaptaZap(b) - getUltimoIdConversaCaptaZap(a);
  });
}

function upsertConversa(lista, conversa) {
  const base = Array.isArray(lista) ? lista : [];
  const chave = getConversationKey(conversa);
  const existe = base.some((item) => getConversationKey(item) === chave);

  const atualizada = existe
    ? base.map((item) =>
        getConversationKey(item) === chave
          ? mesclarConversaDedupeCaptaZap(item, conversa)
          : item,
      )
    : [normalizarConversaCaptazap(conversa), ...base];

  return ordenarConversasPorUltimaAtividadeCaptaZap(atualizada);
}

function getLeadIdBuscaCaptaZap(lead = {}) {
  return lead?.id || lead?.lead_id || lead?.leadId || lead?.id_lead || null;
}

function getNomeLeadBuscaCaptaZap(lead = {}) {
  return (
    lead?.nome_lead ||
    lead?.nomeLead ||
    lead?.nome ||
    lead?.name ||
    lead?.nome_cliente ||
    lead?.paciente ||
    ""
  );
}

function getSnapshotUltimaMensagemCaptaZap(conversa = {}) {
  const ultimaMensagem = getUltimaMensagemConversa(conversa) || {};

  const texto =
    ultimaMensagem.texto ||
    ultimaMensagem.mensagem ||
    ultimaMensagem.message ||
    conversa.ultimaMensagem ||
    conversa.ultima_mensagem ||
    conversa.preview ||
    conversa.texto ||
    conversa.mensagem ||
    "";

  const direcao =
    ultimaMensagem.direcao ||
    ultimaMensagem.direction ||
    conversa.ultimaMensagemDirecao ||
    conversa.ultima_mensagem_direcao ||
    conversa.ultimaDirecao ||
    conversa.ultima_direcao ||
    conversa.direcao ||
    "";

  const autor =
    ultimaMensagem.autor ||
    ultimaMensagem.de ||
    ultimaMensagem.from ||
    conversa.ultimaMensagemAutor ||
    conversa.ultima_mensagem_autor ||
    conversa.autor_ultima_mensagem ||
    conversa.autor ||
    "";

  const datahora =
    ultimaMensagem.datahora ||
    ultimaMensagem.created_at ||
    ultimaMensagem.timestamp ||
    conversa.ultimaMensagemEm ||
    conversa.ultima_mensagem_em ||
    conversa.last_message_at ||
    conversa.data_ultima_mensagem ||
    conversa.datahora ||
    conversa.updated_at ||
    conversa.created_at ||
    "";

  return {
    texto,
    direcao,
    autor,
    datahora,
    timestamp: getUltimaAtividadeConversaCaptaZap(conversa),
    mensagem: {
      ...ultimaMensagem,
      texto,
      mensagem: texto,
      direcao,
      autor,
      de: autor,
      datahora,
    },
  };
}

function isMensagemDoOperadorCaptaZap(mensagem = {}) {
  const direcao = normalizar(mensagem.direcao || mensagem.direction || "");
  const autor = normalizar(
    mensagem.autor || mensagem.from || mensagem.de || mensagem.remetente || "",
  );

  const fromMe =
    mensagem.fromMe === true ||
    mensagem.from_me === true ||
    String(mensagem.fromMe || mensagem.from_me || "").toLowerCase() === "true";

  return Boolean(
    fromMe ||
    direcao.includes("enviada") ||
    direcao.includes("saida") ||
    direcao.includes("outbound") ||
    direcao.includes("outgoing") ||
    direcao.includes("sent") ||
    autor.includes("operador") ||
    autor.includes("atendente") ||
    autor.includes("captador") ||
    autor.includes("usuario") ||
    autor.includes("voce") ||
    autor.includes("você"),
  );
}

function preservarUltimaMensagemMaisRecenteCaptaZap(
  mesclada = {},
  atual = {},
  proximo = {},
) {
  const mensagens = mesclarMensagensConversa(
    atual.mensagens || [],
    proximo.mensagens || [],
  );

  const baseComMensagens = { ...mesclada, mensagens };
  const snapshotMesclado = getSnapshotUltimaMensagemCaptaZap(baseComMensagens);
  const snapshotAtual = getSnapshotUltimaMensagemCaptaZap(atual);
  const snapshotProximo = getSnapshotUltimaMensagemCaptaZap(proximo);

  const candidatos = [snapshotMesclado, snapshotAtual, snapshotProximo]
    .filter(Boolean)
    .sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0));

  const snapshot = candidatos[0] || snapshotMesclado;
  const ultima = snapshot.mensagem || {};
  const texto = snapshot.texto || mesclada.preview || "";
  const datahora = snapshot.datahora || mesclada.ultima_mensagem_em || "";
  const direcao = snapshot.direcao || mesclada.ultima_mensagem_direcao || "";
  const autor = snapshot.autor || mesclada.ultima_mensagem_autor || "";
  const clienteFalouPorUltimo = isMensagemDoClienteCaptaZap(ultima);
  const operadorFalouPorUltimo = isMensagemDoOperadorCaptaZap(ultima);

  return {
    ...mesclada,
    mensagens,
    preview: texto || mesclada.preview,
    ultima_mensagem: texto || mesclada.ultima_mensagem,
    ultimaMensagem: texto || mesclada.ultimaMensagem,
    ultima_mensagem_em: datahora || mesclada.ultima_mensagem_em,
    ultimaMensagemEm: datahora || mesclada.ultimaMensagemEm,
    datahora: datahora || mesclada.datahora,
    ultima_mensagem_direcao: direcao || mesclada.ultima_mensagem_direcao,
    ultimaMensagemDirecao: direcao || mesclada.ultimaMensagemDirecao,
    ultima_direcao: direcao || mesclada.ultima_direcao,
    ultimaDirecao: direcao || mesclada.ultimaDirecao,
    ultima_mensagem_autor: autor || mesclada.ultima_mensagem_autor,
    ultimaMensagemAutor: autor || mesclada.ultimaMensagemAutor,
    precisa_responder: clienteFalouPorUltimo
      ? true
      : operadorFalouPorUltimo
        ? false
        : mesclada.precisa_responder,
    precisaResponder: clienteFalouPorUltimo
      ? true
      : operadorFalouPorUltimo
        ? false
        : mesclada.precisaResponder,
    status_operacional: clienteFalouPorUltimo
      ? "responder"
      : operadorFalouPorUltimo && mesclada.status_operacional === "responder"
        ? ""
        : mesclada.status_operacional,
  };
}

function mesclarConversaDedupeCaptaZap(atual = {}, proximo = {}) {
  const telefone =
    proximo.telefone ||
    proximo.numero ||
    atual.telefone ||
    atual.numero ||
    proximo.telefone1 ||
    atual.telefone1 ||
    "";
  const telefoneNormalizado = normalizarTelefone(telefone);

  const leadsVinculadas = normalizarLeadsVinculadasCaptazap([
    ...getLeadsVinculadasBuscaCaptaZap(atual),
    ...getLeadsVinculadasBuscaCaptaZap(proximo),
  ]).filter((lead) =>
    telefoneNormalizado
      ? leadPertenceAoTelefoneCaptaZap(lead, telefoneNormalizado)
      : true,
  );

  const contextoIdExplicito =
    proximo.lead_contexto_id ||
    proximo.leadContextoId ||
    atual.lead_contexto_id ||
    atual.leadContextoId ||
    null;

  const contextoLead = contextoIdExplicito
    ? leadsVinculadas.find(
        (lead) =>
          String(getLeadIdBuscaCaptaZap(lead) || "") ===
          String(contextoIdExplicito),
      )
    : null;

  const leadUnica = leadsVinculadas.length === 1 ? leadsVinculadas[0] : null;
  const leadDiretaProximo = getLeadPrincipalConversaCardSafe(proximo);
  const leadDiretaAtual = getLeadPrincipalConversaCardSafe(atual);
  const leadDiretaSegura = [leadDiretaProximo, leadDiretaAtual].find((lead) =>
    lead && telefoneNormalizado
      ? leadPertenceAoTelefoneCaptaZap(lead, telefoneNormalizado)
      : Boolean(lead),
  );

  const leadFinal = contextoLead || leadUnica || leadDiretaSegura || null;
  const leadFinalId = getLeadIdBuscaCaptaZap(leadFinal);
  const contextoFinal = contextoLead
    ? contextoIdExplicito
    : leadsVinculadas.length === 1
      ? leadFinalId
      : null;

  const nomeLead =
    contextoFinal || leadsVinculadas.length <= 1
      ? getNomeLeadBuscaCaptaZap(leadFinal)
      : "";

  // v67: nome manual do CaptaZap é autoritativo e não pode ser apagado por
  // atualização parcial, telefone ou metadata do WhatsApp.
  const nomeContatoManual = String(
    proximo?.contato_nome ||
      proximo?.contatoNome ||
      atual?.contato_nome ||
      atual?.contatoNome ||
      "",
  ).trim();
  const nomeWhatsapp = String(
    proximo?.nome_whatsapp ||
      proximo?.nomeWhatsapp ||
      atual?.nome_whatsapp ||
      atual?.nomeWhatsapp ||
      "",
  ).trim();
  const nomeVisualSeguro = [proximo?.nome, atual?.nome]
    .map((valor) => String(valor || "").trim())
    .find((valor) => {
      if (!valor) return false;
      if (["contato sem nome", "novo contato", "whatsapp"].includes(normalizar(valor))) {
        return false;
      }
      const digitos = normalizarTelefone(valor);
      return !digitos || /[a-zA-ZÀ-ÿ]/.test(valor);
    }) || "";

  const mesclada = {
    ...atual,
    ...proximo,
    telefone,
    numero:
      proximo.numero ||
      proximo.telefone ||
      atual.numero ||
      atual.telefone ||
      telefone,
    lead: leadFinal,
    lead_id:
      contextoFinal ||
      proximo.lead_id ||
      proximo.leadId ||
      atual.lead_id ||
      atual.leadId ||
      null,
    leadId:
      contextoFinal ||
      proximo.leadId ||
      proximo.lead_id ||
      atual.leadId ||
      atual.lead_id ||
      null,
    lead_contexto_id: contextoFinal,
    leadContextoId: contextoFinal,
    leads_vinculadas: leadsVinculadas,
    leadsVinculadas: leadsVinculadas,
    total_leads_vinculadas: leadsVinculadas.length,
    totalLeadsVinculadas: leadsVinculadas.length,
    telefone_compartilhado: leadsVinculadas.length > 1,
    telefoneCompartilhado: leadsVinculadas.length > 1,
    contexto_lead_obrigatorio: leadsVinculadas.length > 1 && !contextoFinal,
    contextoLeadObrigatorio: leadsVinculadas.length > 1 && !contextoFinal,
    contato_nome: nomeContatoManual,
    contatoNome: nomeContatoManual,
    nome_whatsapp: nomeWhatsapp,
    nomeWhatsapp,
    dra_malu_classificacao:
      proximo?.dra_malu_classificacao ||
      atual?.dra_malu_classificacao ||
      "padrao",
    nome:
      nomeLead ||
      nomeContatoManual ||
      nomeWhatsapp ||
      nomeVisualSeguro ||
      proximo.nome_lead ||
      atual.nome_lead ||
      telefone ||
      "Contato sem nome",
  };

  return normalizarConversaCaptazap(
    preservarUltimaMensagemMaisRecenteCaptaZap(mesclada, atual, proximo),
  );
}

function dedupeConversas(lista) {
  const map = new Map();

  for (const item of Array.isArray(lista) ? lista : []) {
    if (!item) continue;
    const key = getConversationKey(item);

    if (!map.has(key)) {
      map.set(key, item);
      continue;
    }

    map.set(key, mesclarConversaDedupeCaptaZap(map.get(key), item));
  }

  return [...map.values()];
}

function getConexaoInstanceIdConversaCaptaZap(conversa = {}) {
  return String(
    conversa?.zapi_instance_id ||
      conversa?.zapiInstanceId ||
      conversa?.conexao_instance_id ||
      conversa?.conexaoInstanceId ||
      conversa?.instance_id ||
      conversa?.instanceId ||
      conversa?.chamada?.zapi_instance_id ||
      conversa?.chamado?.zapi_instance_id ||
      conversa?.mensagem?.zapi_instance_id ||
      "",
  ).trim();
}

function getConversationKey(conversa) {
  if (!conversa) return "sem-chave";

  const empresa = conversa.empresa_id || conversa.empresaId || "sem-empresa";
  const instancia = getConexaoInstanceIdConversaCaptaZap(conversa);
  const telefoneKey = normalizarIdentidadeWhatsappComparacaoCaptaZap(
    conversa.numero || conversa.telefone || "",
  );
  const chamado =
    conversa.chamado_id ||
    conversa.id_chamado ||
    conversa.ticket_id ||
    conversa.conversa_id ||
    conversa.conversaId ||
    "";
  const lead =
    conversa.lead_contexto_id ||
    conversa.leadContextoId ||
    conversa.contexto_lead_id ||
    conversa.contextoLeadId ||
    conversa.lead_id ||
    conversa.leadId ||
    conversa.lead?.id;
  const id = conversa.id || "";

  const contatoCRM = Boolean(
    conversa._captazap_origem_contatos ||
    conversa._contato_crm_consulta ||
    conversa._captazap_contexto_fixo_contato ||
    String(id || "").startsWith("lead-"),
  );

  // Regra de integridade:
  // - conversa operacional do WhatsApp é por telefone;
  // - contato/lead do CRM é por paciente.
  // Em WhatsApp compartilhado, deduplicar contatos por telefone faz Vanilda,
  // Glaucia etc. virarem um único card e o contexto operacional fica instável.
  if (contatoCRM && lead) {
    return instancia
      ? `${empresa}-inst-${instancia}-crm-lead-${lead}`
      : `${empresa}-crm-lead-${lead}`;
  }

  if (instancia && telefoneKey) {
    return `${empresa}-inst-${instancia}-tel-${telefoneKey}`;
  }
  if (telefoneKey) return `${empresa}-tel-${telefoneKey}`;
  if (chamado) return `${empresa}-chamado-${chamado}`;
  if (lead) return `${empresa}-lead-${lead}`;
  if (id) return `${empresa}-id-${id}`;

  return `${empresa}-sem-chave`;
}

function agruparPorLeadId(lista = []) {
  const map = new Map();

  for (const item of lista || []) {
    const leadId = getLeadIdFromRelatedItem(item);

    if (!leadId) continue;

    const key = String(leadId);
    const arr = map.get(key) || [];

    arr.push(item);
    map.set(key, arr);
  }

  return map;
}

function agruparTransferenciasPorLeadId(lista = []) {
  const map = new Map();

  for (const item of lista || []) {
    const leadId = getLeadIdFromRelatedItem(item);

    if (!leadId) continue;

    map.set(String(leadId), item);
  }

  return map;
}

function getLeadId(lead) {
  return (
    lead?.id ||
    lead?.lead_id ||
    lead?.leadId ||
    lead?.id_lead ||
    lead?.idLead ||
    null
  );
}

function getLeadIdFromRelatedItem(item) {
  return (
    item?.lead_id ||
    item?.leadId ||
    item?.id_lead ||
    item?.idLead ||
    item?.id_leads ||
    item?.lead?.id ||
    item?.lead?.lead_id ||
    null
  );
}

function getTimelineLead(lead) {
  const timeline = [];

  if (lead?.criado_em || lead?.data_lead) {
    timeline.push({
      tipo: "Lead criada",
      data: lead.criado_em || lead.data_lead,
      descricao: "Lead encontrada no CaptaCRM e disponível no CaptaZap.",
    });
  }

  if (lead?.status_negociacao) {
    timeline.push({
      tipo: "Negociação",
      data: lead?.atualizado_em || lead?.updated_at || "",
      descricao: `Status atual: ${lead.status_negociacao}.`,
    });
  }

  if (lead?.transferencia_pendente) {
    timeline.push({
      tipo: "Transferência",
      data: "",
      descricao: "Existe solicitação de transferência pendente para esta lead.",
    });
  }

  return timeline;
}

function getUltimoItem(lista, dateFields = []) {
  if (!Array.isArray(lista) || lista.length === 0) return null;

  return [...lista].sort((a, b) => {
    const dateA = new Date(getFirstValue(a, dateFields) || 0).getTime();
    const dateB = new Date(getFirstValue(b, dateFields) || 0).getTime();

    return (
      (Number.isNaN(dateB) ? 0 : dateB) - (Number.isNaN(dateA) ? 0 : dateA)
    );
  })[0];
}

function getProximaTarefa(lista) {
  if (!Array.isArray(lista) || lista.length === 0) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const tarefasFuturas = lista
    .filter((tarefa) => {
      const status = String(tarefa?.status || "").toLowerCase();
      const concluida = [
        "concluida",
        "concluido",
        "finalizada",
        "finalizado",
        "feito",
        "feita",
      ].includes(status);

      if (concluida) return false;

      const data = new Date(tarefa?.data_vencimento || tarefa?.data || 0);

      if (Number.isNaN(data.getTime())) return true;

      return data >= hoje;
    })
    .sort((a, b) => {
      const dateA = new Date(a?.data_vencimento || a?.data || 0).getTime();
      const dateB = new Date(b?.data_vencimento || b?.data || 0).getTime();

      return (
        (Number.isNaN(dateA) ? 0 : dateA) - (Number.isNaN(dateB) ? 0 : dateB)
      );
    });

  return tarefasFuturas[0] || null;
}

function getFirstValue(obj, fields = []) {
  if (!obj) return "";

  for (const field of fields) {
    if (obj[field]) return obj[field];
  }

  return "";
}

function getNomeById(id, lista, fields = ["nome"]) {
  if (!id || !Array.isArray(lista)) return "";

  const item = lista.find((x) => String(x.id) === String(id));

  if (!item) return "";

  for (const field of fields) {
    if (item[field]) return item[field];
  }

  return "";
}

function getNomeUsuario(id, usuarios) {
  if (!id || !Array.isArray(usuarios)) return "";

  const usuario = usuarios.find((u) => String(u.id) === String(id));

  return usuario?.nome || usuario?.name || "";
}

function usuarioTemPermissaoVisaoEquipeCaptaZap(usuario = {}) {
  const usuarioLocal = getUsuarioLocalStorage();
  const user = {
    ...usuarioLocal,
    ...(usuario || {}),
  };

  // Regra da empresa:
  // Administrador tem acesso total ao sistema e vê a aba Equipe sem precisar
  // marcar permissão manualmente. Demais perfis precisam da permissão funcional.
  if (isUsuarioAdministradorSistema(user)) {
    return true;
  }

  return temPermissaoUsuario(user, [
    "captazap.supervisionar_equipe",
    "captazap.ver_equipe",
    "captazap.ver_minhas_equipe",
    "captazap.minhas_equipe",
    "captazap.minhas_equipe.visualizar",
    "captazap.equipe",
    "captazap.equipe.visualizar",
    "captazap.atendimentos_equipe",
    "captazap.supervisao",
    "captazap.supervisor",
    "captazap.supervisionar",
    "atendimentos.ver_equipe",
    "ver_atendimentos_equipe",
    "visao_equipe_captazap",
    "whatsapp.ver_equipe",
    "whatsapp.supervisionar_equipe",
  ]);
}

function usuarioTemPermissaoFixarAcompanhamentoCaptaZap(usuario = {}) {
  const usuarioLocal = getUsuarioLocalStorage();
  const user = {
    ...usuarioLocal,
    ...(usuario || {}),
  };

  // Administrador continua com acesso total por padrão. Demais perfis só
  // enxergam/acionam o botão quando a permissão funcional estiver marcada.
  if (isUsuarioAdministradorSistema(user)) {
    return true;
  }

  return temPermissaoUsuario(user, [
    "captazap.fixar_acompanhamento",
  ]);
}

function isUsuarioAdministradorSistema(usuario = {}) {
  if (!usuario || typeof usuario !== "object") return false;

  const flagsAdmin = [
    usuario.admin,
    usuario.is_admin,
    usuario.isAdmin,
    usuario.administrador,
    usuario.isAdministrador,
    usuario.super_admin,
    usuario.superAdmin,
    usuario.acesso_total,
    usuario.acessoTotal,
    usuario.root,
  ];

  if (
    flagsAdmin.some(
      (value) =>
        value === true ||
        value === 1 ||
        String(value || "").toLowerCase() === "true" ||
        String(value || "").toLowerCase() === "1",
    )
  ) {
    return true;
  }

  const camposPerfil = [
    usuario.perfil,
    usuario.role,
    usuario.tipo,
    usuario.tipo_usuario,
    usuario.tipoUsuario,
    usuario.cargo,
    usuario.funcao,
    usuario.nivel_acesso,
    usuario.nivelAcesso,
    usuario.grupo,
    usuario.permissao,
  ];

  return camposPerfil
    .filter(Boolean)
    .map((value) => normalizar(value))
    .some(
      (value) =>
        value === "admin" ||
        value === "administrador" ||
        value === "super admin" ||
        value === "superadministrador" ||
        value.includes("administrador") ||
        value.includes("admin geral"),
    );
}

function temPermissaoUsuario(usuario = {}, permissoesAceitas = []) {
  if (
    !usuario ||
    !Array.isArray(permissoesAceitas) ||
    !permissoesAceitas.length
  ) {
    return false;
  }

  const permissoesNormalizadas = new Set(
    permissoesAceitas.map((permissao) => normalizarPermissao(permissao)),
  );

  const candidatos = [
    usuario.permissoes,
    usuario.permissions,
    usuario.acessos,
    usuario.roles,
    usuario.claims,
    usuario.modulos_permitidos,
    usuario.permissoes_usuario,
  ];

  for (const candidato of candidatos) {
    if (permissaoContemAlguma(candidato, permissoesNormalizadas)) {
      return true;
    }
  }

  return false;
}

function permissaoContemAlguma(value, permissoesNormalizadas) {
  if (!value) return false;

  if (typeof value === "string") {
    return value
      .split(/[;,|\s]+/)
      .map((item) => normalizarPermissao(item))
      .some((item) => permissoesNormalizadas.has(item));
  }

  if (Array.isArray(value)) {
    return value.some((item) =>
      permissaoContemAlguma(item, permissoesNormalizadas),
    );
  }

  if (typeof value === "object") {
    return Object.entries(value).some(([key, itemValue]) => {
      const chaveNormalizada = normalizarPermissao(key);

      if (
        permissoesNormalizadas.has(chaveNormalizada) &&
        itemValue !== false &&
        itemValue !== 0
      ) {
        return true;
      }

      if (itemValue && typeof itemValue === "object") {
        return permissaoContemAlguma(itemValue, permissoesNormalizadas);
      }

      if (typeof itemValue === "string") {
        return permissoesNormalizadas.has(normalizarPermissao(itemValue));
      }

      return false;
    });
  }

  return false;
}

function normalizarPermissao(value = "") {
  return normalizar(value).replace(/[\s-]+/g, "_");
}

function getUsuarioHeader(usuario) {
  const usuarioLocal = getUsuarioLocalStorage();

  const user = {
    ...usuarioLocal,
    ...(usuario || {}),
    foto:
      usuario?.foto ||
      usuario?.foto_original ||
      usuario?.foto_url_original ||
      usuarioLocal?.foto ||
      usuarioLocal?.foto_original ||
      usuarioLocal?.foto_url_original ||
      "",
  };

  const nome =
    user?.nome ||
    user?.name ||
    user?.nome_usuario ||
    user?.usuario ||
    user?.email ||
    "Captador";

  const email = user?.email || user?.login || "";

  const foto = getImagemUrl(
    user?.foto_original ||
      user?.foto_url_original ||
      user?.foto_perfil ||
      user?.fotoPerfil ||
      user?.foto ||
      user?.foto_url ||
      user?.avatar ||
      user?.avatar_url ||
      user?.imagem ||
      user?.imagem_url ||
      user?.perfil_foto ||
      "",
  );

  return {
    nome,
    email,
    foto,
  };
}

const avatarContatoListCache = new Map();

async function buscarAvatarContatoCaptaZap({ empresaId, telefone }) {
  const phone = normalizarTelefone(telefone);

  if (!empresaId || !phone) return "";

  const cacheKey = `${empresaId}:${phone}`;

  if (avatarContatoListCache.has(cacheKey)) {
    return avatarContatoListCache.get(cacheKey);
  }

  try {
    const data = await requestJson(
      `/whatsapp/captazap/avatar?empresa_id=${encodeURIComponent(
        empresaId,
      )}&phone=${encodeURIComponent(phone)}`,
    );

    const profileUrl = getImagemUrl(data?.profileUrl || "");

    avatarContatoListCache.set(cacheKey, profileUrl || "");

    return profileUrl || "";
  } catch {
    avatarContatoListCache.set(cacheKey, "");
    return "";
  }
}

function getAvatarConversa(conversa = {}) {
  const lead = conversa.lead || {};

  return getImagemUrl(
    conversa.profileUrl ||
      conversa.profile_url ||
      conversa.avatar_url ||
      conversa.avatar ||
      conversa.foto ||
      conversa.foto_url ||
      conversa.foto_perfil ||
      lead.profileUrl ||
      lead.profile_url ||
      lead.avatar_url ||
      lead.avatar ||
      lead.foto ||
      lead.foto_url ||
      lead.foto_perfil ||
      "",
  );
}

function getChavePreferenciaNotificacoesChromeCaptaZap({
  empresaId = null,
  usuarioId = null,
} = {}) {
  return [
    CAPTAZAP_CHROME_NOTIFICACOES_STORAGE_PREFIX,
    empresaId || "sem-empresa",
    usuarioId || "sem-usuario",
  ].join(":");
}

function suportaNotificacoesChromeCaptaZap() {
  return Boolean(
    typeof window !== "undefined" &&
    (typeof window.isSecureContext === "undefined" || window.isSecureContext) &&
    "Notification" in window &&
    typeof window.Notification === "function",
  );
}

function getPermissaoNotificacoesChromeCaptaZap() {
  if (!suportaNotificacoesChromeCaptaZap()) return "unsupported";

  return window.Notification.permission || "default";
}

function lerPreferenciaNotificacoesChromeCaptaZap({
  empresaId = null,
  usuarioId = null,
} = {}) {
  if (typeof window === "undefined") return false;

  try {
    return (
      window.localStorage.getItem(
        getChavePreferenciaNotificacoesChromeCaptaZap({
          empresaId,
          usuarioId,
        }),
      ) === "1"
    );
  } catch {
    return false;
  }
}

function salvarPreferenciaNotificacoesChromeCaptaZap({
  empresaId = null,
  usuarioId = null,
  ativo = false,
} = {}) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      getChavePreferenciaNotificacoesChromeCaptaZap({
        empresaId,
        usuarioId,
      }),
      ativo ? "1" : "0",
    );
  } catch {
    // Sem acesso ao localStorage, a preferência dura apenas até fechar a aba.
  }
}

function janelaCaptaZapEstaEmFoco() {
  if (typeof document === "undefined") return false;

  if (document.hidden) return false;

  try {
    return typeof document.hasFocus !== "function" || document.hasFocus();
  } catch {
    return !document.hidden;
  }
}

let captaZapAudioContext = null;
let captaZapSomLiberado = false;
let captaZapUltimoSomAt = 0;

const captaZapMensagensComSom = new Map();
const CAPTAZAP_AUDIO_DEDUPE_MS = 15000;

function liberarSomCaptaZap() {
  captaZapSomLiberado = true;

  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) return;

    if (!captaZapAudioContext) {
      captaZapAudioContext = new AudioContextClass();
    }

    if (captaZapAudioContext.state === "suspended") {
      captaZapAudioContext.resume().catch(() => null);
    }
  } catch {
    // navegador sem suporte ou bloqueado
  }
}

function deveTocarSomMensagem(payload = {}) {
  const direcao = String(
    payload.direcao || payload.direction || "",
  ).toLowerCase();

  const autor = String(
    payload.autor || payload.from || payload.de || "",
  ).toLowerCase();

  const fromMe =
    payload.fromMe === true ||
    payload.from_me === true ||
    String(payload.fromMe || payload.from_me || "").toLowerCase() === "true";

  if (fromMe) return false;

  if (
    direcao === "enviada" ||
    direcao === "interno" ||
    autor === "operador" ||
    autor === "atendente" ||
    autor === "sistema"
  ) {
    return false;
  }

  const recebidaExplicitamente =
    direcao === "recebida" ||
    autor === "cliente" ||
    autor === "contato" ||
    autor === "lead";

  if (!recebidaExplicitamente) return false;

  return Boolean(
    payload.texto ||
    payload.mensagem ||
    payload.message ||
    payload.legenda ||
    payload.tipo_midia ||
    payload.tipoMidia ||
    payload.msgid ||
    payload.messageId ||
    payload.message_id ||
    payload.id,
  );
}

function getResponsavelIdSomCaptaZap(obj = {}) {
  return (
    obj?.responsavel_id ||
    obj?.captador_id ||
    obj?.usuario_responsavel_id ||
    obj?.atendente_id ||
    obj?.operador_id ||
    obj?._captazap_assumida_por ||
    obj?._captazap_responsavel_original_id ||
    null
  );
}

function getResponsavelNomeSomCaptaZap(obj = {}) {
  return (
    obj?.responsavel_nome ||
    obj?.captador_nome ||
    obj?.usuario_responsavel_nome ||
    obj?.atendente_nome ||
    obj?.operador_nome ||
    obj?._captazap_assumida_por_nome ||
    obj?._captazap_responsavel_original_nome ||
    ""
  );
}

function normalizarNomeSomCaptaZap(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

function encontrarConversaSomCaptaZap(payload = {}, conversas = []) {
  const telefonePayload = normalizarIdentidadeWhatsappComparacaoCaptaZap(
    payload.telefone || payload.phone || payload.numero || "",
  );
  const instanciaPayload = getConexaoInstanceIdConversaCaptaZap(payload);

  const leadPayload = String(payload.lead_id || payload.leadId || "");
  const chamadoPayload = String(
    payload.chamado_id || payload.id_chamado || payload.protocolo_id || "",
  );

  return (
    (conversas || []).find((conversa) => {
      const instanciaConversa = getConexaoInstanceIdConversaCaptaZap(conversa);
      if (
        instanciaPayload &&
        instanciaConversa &&
        instanciaPayload !== instanciaConversa
      ) {
        return false;
      }

      const telefoneConversa = normalizarIdentidadeWhatsappComparacaoCaptaZap(
        conversa?.telefone || conversa?.numero || "",
      );

      const leadConversa = String(
        conversa?.lead_id || conversa?.leadId || conversa?.lead?.id || "",
      );

      const chamadoConversa = String(
        conversa?.chamado_id ||
          conversa?.id_chamado ||
          conversa?.protocolo_id ||
          "",
      );

      if (
        telefonePayload &&
        telefoneConversa &&
        telefonePayload === telefoneConversa
      ) {
        return true;
      }

      if (leadPayload && leadConversa && leadPayload === leadConversa) {
        return true;
      }

      if (
        chamadoPayload &&
        chamadoConversa &&
        chamadoPayload === chamadoConversa
      ) {
        return true;
      }

      return false;
    }) || null
  );
}

function conversaEstaNaFilaSomCaptaZap(conversa = {}) {
  const aba = String(conversa?.aba_captazap || "")
    .trim()
    .toLowerCase();

  return Boolean(
    aba === "fila" ||
    conversa?.em_fila === true ||
    conversa?.fila_atendimento === true ||
    conversa?.aguardando_atendimento === true ||
    conversa?._fila_global === true ||
    conversa?._fila_equipe === true,
  );
}

function deveTocarSomParaUsuarioCaptaZap({
  payload = {},
  conversa = null,
  usuarioId = null,
  usuarioNome = "",
} = {}) {
  const usuarioIdStr = usuarioId ? String(usuarioId) : "";
  const usuarioNomeNorm = normalizarNomeSomCaptaZap(usuarioNome);

  const responsavelPayloadId = getResponsavelIdSomCaptaZap(payload);
  const responsavelPayloadNome = getResponsavelNomeSomCaptaZap(payload);

  const responsavelConversaId = getResponsavelIdSomCaptaZap(conversa || {});
  const responsavelConversaNome = getResponsavelNomeSomCaptaZap(conversa || {});

  const responsavelId = responsavelPayloadId || responsavelConversaId;
  const responsavelNome = responsavelPayloadNome || responsavelConversaNome;

  const responsavelIdStr = responsavelId ? String(responsavelId) : "";
  const responsavelNomeNorm = normalizarNomeSomCaptaZap(responsavelNome);

  // Se existe responsável, toca somente para o responsável do chamado.
  if (responsavelIdStr) {
    return Boolean(usuarioIdStr && usuarioIdStr === responsavelIdStr);
  }

  if (responsavelNomeNorm) {
    return Boolean(usuarioNomeNorm && usuarioNomeNorm === responsavelNomeNorm);
  }

  // Sem responsável identificado: toca apenas se a conversa já está conhecida
  // como Fila neste usuário. Isso evita tocar para todos em mensagens de outro captador.
  if (conversa) {
    return conversaEstaNaFilaSomCaptaZap(conversa);
  }

  return false;
}

function somMensagemJaProcessado(payload = {}) {
  const agora = Date.now();

  for (const [key, value] of captaZapMensagensComSom.entries()) {
    if (agora - value > CAPTAZAP_AUDIO_DEDUPE_MS) {
      captaZapMensagensComSom.delete(key);
    }
  }

  const id =
    payload.msgid ||
    payload.messageId ||
    payload.message_id ||
    payload.id ||
    "";

  const telefone = normalizarIdentidadeWhatsappComparacaoCaptaZap(
    payload.telefone || payload.phone || payload.numero || "",
  );

  const texto =
    payload.texto ||
    payload.mensagem ||
    payload.message ||
    payload.legenda ||
    "";

  const data =
    payload.datahora || payload.created_at || payload.timestamp || "";

  const chave = id
    ? `id:${id}`
    : `fallback:${telefone}:${String(texto).slice(0, 80)}:${data}`;

  if (!chave || chave === "fallback:::") return false;

  if (captaZapMensagensComSom.has(chave)) {
    return true;
  }

  captaZapMensagensComSom.set(chave, agora);
  return false;
}

function tocarSomMensagemRecebida() {
  if (typeof window === "undefined") return;

  const agora = Date.now();

  // Evita metralhar som quando chegam vários eventos seguidos.
  if (agora - captaZapUltimoSomAt < 850) return;

  captaZapUltimoSomAt = agora;

  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextClass) return;

    if (!captaZapAudioContext) {
      captaZapAudioContext = new AudioContextClass();
    }

    if (captaZapAudioContext.state === "suspended") {
      if (!captaZapSomLiberado) return;
      captaZapAudioContext.resume().catch(() => null);
    }

    const ctx = captaZapAudioContext;
    const now = ctx.currentTime;

    // Volume mais forte e perceptível, sem virar alarme agressivo.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.46, now + 0.018);
    master.gain.exponentialRampToValueAtTime(0.18, now + 0.16);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.52);
    master.connect(ctx.destination);

    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(620, now);
    osc1.frequency.exponentialRampToValueAtTime(760, now + 0.12);
    osc1.connect(master);
    osc1.start(now);
    osc1.stop(now + 0.24);

    const osc2 = ctx.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(980, now + 0.11);
    osc2.frequency.exponentialRampToValueAtTime(1220, now + 0.25);
    osc2.connect(master);
    osc2.start(now + 0.11);
    osc2.stop(now + 0.36);

    const osc3 = ctx.createOscillator();
    osc3.type = "square";
    osc3.frequency.setValueAtTime(440, now + 0.02);

    const bodyGain = ctx.createGain();
    bodyGain.gain.setValueAtTime(0.0001, now);
    bodyGain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);

    osc3.connect(bodyGain);
    bodyGain.connect(master);
    osc3.start(now + 0.02);
    osc3.stop(now + 0.22);
  } catch {
    // Se o navegador bloquear áudio, apenas ignora.
  }
}

function tocarSomToastNovaMensagemCaptaZap() {
  if (typeof window === "undefined") return;

  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    if (!captaZapAudioContext) {
      captaZapAudioContext = new AudioContextClass();
    }

    if (captaZapAudioContext.state === "suspended") {
      if (!captaZapSomLiberado) return;
      captaZapAudioContext.resume().catch(() => null);
    }

    const ctx = captaZapAudioContext;
    const now = ctx.currentTime;

    // Sinal curto e leve do pop-up. O som principal da mensagem continua separado.
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.16, now + 0.012);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 0.26);
    master.connect(ctx.destination);

    const osc1 = ctx.createOscillator();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(880, now);
    osc1.frequency.exponentialRampToValueAtTime(1120, now + 0.08);
    osc1.connect(master);
    osc1.start(now);
    osc1.stop(now + 0.14);

    const osc2 = ctx.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(1320, now + 0.09);
    osc2.connect(master);
    osc2.start(now + 0.09);
    osc2.stop(now + 0.24);
  } catch {
    // Se o navegador bloquear áudio, apenas ignora.
  }
}

function getPreviewMensagemRealtime(payload = {}) {
  if (payload.texto) return payload.texto;

  const tipo = String(
    payload.tipo_midia || payload.tipoMidia || "",
  ).toLowerCase();

  if (tipo === "audio") return "Mensagem de áudio";
  if (tipo === "imagem" || tipo === "image") return "Imagem";
  if (tipo === "video") return "Vídeo";

  return "Nova mensagem";
}

function getChaveNotificacaoMensagemCaptaZap(payload = {}) {
  const instancia = getConexaoInstanceIdConversaCaptaZap(payload);
  const id =
    payload.msgid ||
    payload.messageId ||
    payload.message_id ||
    payload.whatsapp_id ||
    payload.id ||
    "";

  if (id) return `id:${instancia || "sem-instancia"}:${id}`;

  const telefone = normalizarTelefone(
    payload.telefone || payload.phone || payload.numero || "",
  );
  const texto = String(
    payload.texto ||
      payload.mensagem ||
      payload.message ||
      payload.legenda ||
      "",
  ).slice(0, 80);
  const data =
    payload.datahora || payload.created_at || payload.timestamp || "";

  if (!telefone && !texto && !data) return "";

  return `fallback:${instancia || "sem-instancia"}:${telefone}:${texto}:${data}`;
}

function limitarTextoNotificacaoCaptaZap(value = "") {
  const texto = String(value || "Nova mensagem")
    .replace(/\s+/g, " ")
    .trim();

  if (texto.length <= 110) return texto;

  return `${texto.slice(0, 107)}...`;
}



function formatDuracaoLigacaoCaptaZap(segundos = 0) {
  const total = Math.max(0, Number(segundos || 0));
  const minutos = Math.floor(total / 60);
  const seg = Math.floor(total % 60);
  return `${String(minutos).padStart(2, "0")}:${String(seg).padStart(2, "0")}`;
}


function normalizarDataHoraLigacaoCaptaZap(value) {
  if (!value) return "";

  // Se já veio com timezone explícito (Z ou +/-HH:mm), preserva.
  const original = String(value).trim();
  if (!original) return "";

  if (/[zZ]$/.test(original) || /[+-]\d{2}:\d{2}$/.test(original)) {
    return original;
  }

  // As datas de captazap_ligacoes são gravadas em UTC no backend/MySQL.
  // MySQL DATETIME volta sem timezone, então precisamos marcar como UTC
  // antes de o navegador converter para o horário local do operador.
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(original)) {
    return `${original.replace(" ", "T")}Z`;
  }

  // Fallback defensivo: se o Date conseguir interpretar, mantém o valor.
  const data = new Date(original);
  if (Number.isNaN(data.getTime())) return original;

  return original;
}

function ligacaoCaptaZapParaMensagem(ligacao = {}) {
  const direcao = String(ligacao?.direcao || "recebida").toLowerCase();
  const status = String(ligacao?.status || "").toLowerCase();
  const duracao = Math.max(0, Number(ligacao?.duracao_segundos || 0));

  let resumo = direcao === "realizada" || direcao === "saida"
    ? "📞 Ligação realizada"
    : "📞 Ligação recebida";

  if (status === "missed") {
    resumo = "🔴 Ligação não atendida";
  } else if (status === "rejected") {
    resumo = "📵 Ligação recusada";
  } else if (status === "failed") {
    resumo = "⚠️ Falha na ligação";
  } else if (duracao > 0) {
    resumo += ` • ${formatDuracaoLigacaoCaptaZap(duracao)}`;
  }

  const operador =
    ligacao?.atendido_por_nome ||
    ligacao?.usuario_nome ||
    ligacao?.responsavel_nome ||
    "";

  if (operador && duracao > 0) {
    resumo += ` • ${operador}`;
  }

  const datahora = normalizarDataHoraLigacaoCaptaZap(
    ligacao?.finalizado_em ||
      ligacao?.atendido_em ||
      ligacao?.iniciado_em ||
      ligacao?.criado_em ||
      new Date().toISOString(),
  );

  return normalizarMensagemCaptazap({
    id: `call-${ligacao?.id || ligacao?.call_id || Date.now()}`,
    msgid: `call-${ligacao?.call_id || ligacao?.id || Date.now()}`,
    texto: resumo,
    mensagem: resumo,
    direcao:
      direcao === "realizada" || direcao === "saida"
        ? "enviada"
        : "recebida",
    autor:
      direcao === "realizada" || direcao === "saida"
        ? "operador"
        : "cliente",
    de:
      direcao === "realizada" || direcao === "saida"
        ? "operador"
        : "cliente",
    fromMe: direcao === "realizada" || direcao === "saida",
    datahora,
    created_at: datahora,
    tipo: "ligacao",
    tipo_mensagem: "ligacao",
    _captazap_ligacao: true,
    status_ligacao: status,
    duracao_segundos: duracao,
  });
}

function nomeValidoParaChamadaCaptaZap(valor = "", telefone = "") {
  const texto = String(valor || "").trim();
  if (!texto) return false;

  if (
    /\.(pdf|docx?|xlsx?|pptx?|jpe?g|png|gif|webp|mp3|ogg|wav|mp4|mov|avi|zip|rar|txt|csv)$/i.test(
      texto,
    )
  ) {
    return false;
  }

  const semExtensao = texto.replace(/\.[a-z0-9]{2,5}$/i, "");
  if (/^[a-f0-9_-]{12,}$/i.test(semExtensao)) return false;

  const normalizado = normalizar(texto);
  if (
    [
      "contato sem nome",
      "novo contato",
      "whatsapp",
      "arquivo",
      "documento",
      "imagem",
      "audio",
      "video",
    ].includes(normalizado)
  ) {
    return false;
  }

  const digitosNome = normalizarTelefone(texto);
  const digitosTelefone = normalizarTelefone(telefone);

  if (
    digitosNome &&
    !/[a-zA-ZÀ-ÿ]/.test(texto) &&
    (!digitosTelefone ||
      digitosNome.slice(-11) === digitosTelefone.slice(-11))
  ) {
    return false;
  }

  // Precisa ter pelo menos uma letra para ser tratado como nome humano/empresa.
  return /[a-zA-ZÀ-ÿ]/.test(texto);
}

function getNomeSeguroChamadaCaptaZap(conversa = {}, telefone = "") {
  if (!conversa) {
    return formatTelefoneChamadaCaptaZap(telefone) || telefone || "Contato";
  }

  const leads = normalizarLeadsVinculadasCaptazap([
    ...(Array.isArray(conversa?.leads_vinculadas)
      ? conversa.leads_vinculadas
      : []),
    ...(Array.isArray(conversa?.leadsVinculadas)
      ? conversa.leadsVinculadas
      : []),
    conversa?.lead,
  ]);

  const candidatos = [
    ...leads.flatMap((lead) => [
      lead?.nome_lead,
      lead?.nome,
      lead?.name,
    ]),
    conversa?.contato_nome,
    conversa?.contatoNome,
    conversa?.nome_whatsapp,
    conversa?.nomeWhatsapp,
    conversa?.nome,
  ];

  return (
    candidatos
      .map((valor) => String(valor || "").trim())
      .find((valor) => nomeValidoParaChamadaCaptaZap(valor, telefone)) ||
    formatTelefoneChamadaCaptaZap(telefone) ||
    telefone ||
    "Contato"
  );
}


function normalizarTelefoneChamadaCaptaZap(value = "") {
  const whatsappNormalizado = normalizarTelefoneWhatsappBrasil(value);

  if (ehWhatsappLidCaptaZap(whatsappNormalizado)) {
    return whatsappNormalizado;
  }

  const digits = normalizarTelefone(whatsappNormalizado || value);
  if (!digits) return "";

  // Z-API pode entregar 55 + DDD + 8 dígitos no formato legado.
  // normalizarTelefoneWhatsappBrasil corrige para o padrão com 9º dígito.
  if (digits.startsWith("55") && digits.length >= 13) {
    return digits.slice(2, 13);
  }

  return normalizarTelefoneLeadBrasil(digits) || digits.slice(-11);
}

function formatTelefoneChamadaCaptaZap(value = "") {
  const lid = normalizarWhatsappLidCaptaZap(value);
  if (lid) return formatTelefoneCurtoCaptaZap(lid);

  const tel = normalizarTelefoneLeadBrasil(value);

  if (tel.length === 11) {
    return `(${tel.slice(0, 2)}) ${tel.slice(2, 7)}-${tel.slice(7)}`;
  }

  if (tel.length === 10) {
    return `(${tel.slice(0, 2)}) ${tel.slice(2, 6)}-${tel.slice(6)}`;
  }

  return formatTelefoneCurtoCaptaZap(value);
}

function tocarSomLigacaoRecebidaCaptaZap() {
  if (typeof window === "undefined") return;

  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    if (!captaZapAudioContext) {
      captaZapAudioContext = new AudioContextClass();
    }

    if (captaZapAudioContext.state === "suspended") {
      if (!captaZapSomLiberado) return;
      captaZapAudioContext.resume().catch(() => null);
    }

    const ctx = captaZapAudioContext;
    const inicio = ctx.currentTime;

    // Toque discreto de chamada: dois "dings" suaves.
    // Somente onda seno, volume baixo e ataque/saída macios para não cansar.
    const ding = (offset, frequencia, volume = 0.075) => {
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, inicio + offset);
      gain.gain.exponentialRampToValueAtTime(
        volume,
        inicio + offset + 0.035,
      );
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        inicio + offset + 0.34,
      );
      gain.connect(ctx.destination);

      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(frequencia, inicio + offset);
      osc.connect(gain);
      osc.start(inicio + offset);
      osc.stop(inicio + offset + 0.35);
    };

    ding(0, 523.25, 0.070);
    ding(0.46, 659.25, 0.080);
  } catch {
    // Mantém o aviso visual caso o navegador não permita áudio.
  }
}

function formatTelefoneCurtoCaptaZap(value = "") {
  const lid = normalizarWhatsappLidCaptaZap(value);
  if (lid) {
    const digitsLid = lid.replace(/\D/g, "");
    const final = digitsLid.slice(-6);
    return final ? `Contato WhatsApp · LID …${final}` : "Contato WhatsApp · LID";
  }

  const digits = normalizarTelefone(value);
  const tel = digits.slice(-11);

  if (tel.length === 11) {
    return `(${tel.slice(0, 2)}) ${tel.slice(2, 7)}-${tel.slice(7)}`;
  }

  if (tel.length === 10) {
    return `(${tel.slice(0, 2)}) ${tel.slice(2, 6)}-${tel.slice(6)}`;
  }

  return value || "";
}

function encontrarConversaPorNotificacaoCaptaZap(alvo = {}, listas = []) {
  const telefoneAlvo = normalizarIdentidadeWhatsappComparacaoCaptaZap(
    alvo.telefone || alvo.numero || "",
  );
  const leadAlvo = String(alvo.lead_id || alvo.leadId || "");
  const chamadoAlvo = String(
    alvo.chamado_id || alvo.id_chamado || alvo.protocolo_id || "",
  );
  const instanciaAlvo = getConexaoInstanceIdConversaCaptaZap(alvo);
  const candidatos = (listas || []).filter(Boolean);

  // O ID do chamado é a identidade mais forte. Em multiconexão, nunca deixe
  // uma coincidência de telefone na Conexão 1 roubar a notificação da Conexão 2.
  if (chamadoAlvo) {
    const porChamado = candidatos.find((conversa) => {
      const chamadoConversa = String(
        conversa.chamado_id ||
          conversa.id_chamado ||
          conversa.protocolo_id ||
          "",
      );
      return chamadoConversa && chamadoConversa === chamadoAlvo;
    });
    if (porChamado) return porChamado;
  }

  const candidatosDaInstancia = instanciaAlvo
    ? candidatos.filter(
        (conversa) =>
          getConexaoInstanceIdConversaCaptaZap(conversa) === instanciaAlvo,
      )
    : candidatos;

  return (
    candidatosDaInstancia.find((conversa) => {
      const telefoneConversa = normalizarIdentidadeWhatsappComparacaoCaptaZap(
        conversa.telefone || conversa.numero || "",
      );
      const leadConversa = String(
        conversa.lead_id || conversa.leadId || conversa.lead?.id || "",
      );

      if (
        telefoneAlvo &&
        telefoneConversa &&
        telefoneAlvo === telefoneConversa
      ) {
        return true;
      }

      if (leadAlvo && leadConversa && leadAlvo === leadConversa) {
        return true;
      }

      return false;
    }) || null
  );
}

function normalizarTelefoneComparacao(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function isMesmaConversaCaptazap(conversa = {}, payload = {}) {
  const leadPayload = payload.lead_id || payload.leadId;
  const telefonePayload = payload.telefone || payload.phone || payload.numero;
  const instanciaPayload = getConexaoInstanceIdConversaCaptaZap(payload);
  const instanciaAtual = getConexaoInstanceIdConversaCaptaZap(conversa);

  if (
    instanciaPayload &&
    instanciaAtual &&
    instanciaPayload !== instanciaAtual
  ) {
    return false;
  }

  const leadAtual = conversa.lead_id || conversa.leadId || conversa.lead?.id;
  const telefoneAtual = conversa.numero || conversa.telefone || "";

  const mesmaLead =
    leadPayload && leadAtual && String(leadPayload) === String(leadAtual);

  const identidadePayload =
    normalizarIdentidadeWhatsappComparacaoCaptaZap(telefonePayload);
  const identidadeAtual =
    normalizarIdentidadeWhatsappComparacaoCaptaZap(telefoneAtual);

  const mesmaIdentidade = Boolean(
    identidadePayload &&
      identidadeAtual &&
      identidadePayload === identidadeAtual,
  );

  return (
    (mesmaLead && (!instanciaPayload || !instanciaAtual)) ||
    (mesmaIdentidade &&
      (!instanciaPayload || !instanciaAtual || instanciaPayload === instanciaAtual))
  );
}

function getClienteRespondeuDepoisDeFechado(conversa = {}) {
  if (getStatusChamadoVisual(conversa) !== "fechado") return false;

  // Só deve voltar para a Fila quando houver evidência de mensagem NOVA
  // depois do fechamento. Se o captador fechou o chamado depois de ler a
  // mensagem do cliente, o chamado deve permanecer em Fechados.
  const flagRespostaExplicita = Boolean(
    conversa.cliente_respondeu_apos_fechamento ||
    conversa.respondeu_apos_fechamento ||
    conversa.nova_mensagem_apos_fechamento ||
    conversa.novaRespostaAposFechamento ||
    conversa._fechado_com_resposta,
  );

  // Não use flags de fila como prova suficiente em chamado fechado.
  // Ao fechar manualmente, o backend/estado pode ainda trazer em_fila/_fila_global
  // por causa da última mensagem recebida antes do fechamento.
  // A única prova confiável é:
  // 1. flag explícita de nova mensagem após fechamento; ou
  // 2. data da última mensagem do cliente maior que a data de fechamento.
  const ultimaMensagem = getUltimaMensagemConversa(conversa);

  if (!ultimaMensagem) return false;
  if (!isMensagemDoClienteCaptaZap(ultimaMensagem)) return false;

  const dataUltimaMensagem =
    getDataMensagemCaptaZap(ultimaMensagem) ||
    getDataConversaOperacional(conversa);

  const dataFechamento = getDataFechamentoChamado(conversa);

  // Se existe flag explícita mas também existe data confiável, a data manda:
  // só volta para a Fila se a mensagem do cliente for posterior ao fechamento.
  if (dataUltimaMensagem && dataFechamento) {
    return dataUltimaMensagem.getTime() > dataFechamento.getTime() + 1000;
  }

  // Sem data confiável, aceita somente flag explícita real vinda do backend/SSE.
  // Não inferimos "nova resposta" apenas porque a última mensagem é do cliente.
  return flagRespostaExplicita;
}

function formatDataHoraCaptaZap(value) {
  if (!value) return "";

  const data = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(data.getTime())) return String(value);

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getDataFechamentoChamado(conversa = {}) {
  const raw =
    conversa.fechado_em ||
    conversa.fechadoEm ||
    conversa.data_fechamento ||
    conversa.dataFechamento ||
    conversa.closed_at ||
    conversa.closedAt ||
    "";

  if (!raw) return null;

  const data = parseDataCaptaZap(raw);

  if (!data || Number.isNaN(data.getTime())) return null;

  return data;
}

function normalizarLeadsVinculadasCaptazap(leads = []) {
  if (!Array.isArray(leads)) return [];

  const map = new Map();

  for (const rawLead of leads) {
    if (!rawLead || typeof rawLead !== "object") continue;

    const id = getLeadId(rawLead);
    const empresaId = rawLead.empresa_id || rawLead.empresaId || "";
    const telefone = normalizarTelefone(
      rawLead.telefone1 ||
        rawLead.telefone2 ||
        rawLead.telefone ||
        rawLead.celular ||
        rawLead.whatsapp ||
        rawLead.numero ||
        "",
    ).slice(-11);
    const nomeNormalizado = normalizar(
      rawLead.nome_lead || rawLead.nome || rawLead.nome_cliente || "",
    );

    const key = id
      ? `id:${empresaId}:${id}`
      : telefone || nomeNormalizado
        ? `fallback:${empresaId}:${telefone}:${nomeNormalizado}`
        : "";

    if (!key) continue;

    const atual = map.get(key) || {};
    const lead = {
      ...atual,
      ...rawLead,
      id: id || rawLead.id || atual.id || null,
      lead_id: rawLead.lead_id || atual.lead_id || id || null,
      leadId: rawLead.leadId || atual.leadId || id || null,
      nome_lead:
        atual.nome_lead ||
        rawLead.nome_lead ||
        rawLead.nome ||
        rawLead.name ||
        rawLead.nomePaciente ||
        (id ? `Lead ${id}` : "Paciente"),
      telefone1:
        atual.telefone1 ||
        rawLead.telefone1 ||
        rawLead.telefone ||
        rawLead.celular ||
        rawLead.whatsapp ||
        rawLead.numero ||
        "",
    };

    map.set(key, lead);
  }

  return Array.from(map.values());
}

function textoIndicaRecuperacaoDraMaluCaptaZap(value = "") {
  const texto = normalizar(value);

  if (!texto) return false;

  return Boolean(
    texto.includes("recuperado pela dra malu") ||
    texto.includes("recuperado pela dra. malu") ||
    texto.includes("chamado recuperado pela dra") ||
    texto.includes("dra malu ia") ||
    texto.includes("dra. malu ia") ||
    texto.includes("dra_malu_ia") ||
    texto.includes("dra_malu"),
  );
}

function getBooleanDraMaluCaptaZapFrontend(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  return ["1", "true", "sim", "yes"].includes(
    String(value ?? "")
      .trim()
      .toLowerCase(),
  );
}

function isMensagemRecuperadoDraMaluCaptaZap(msg = {}) {
  const texto =
    msg.texto ||
    msg.mensagem ||
    msg.message ||
    msg.legenda ||
    msg.caption ||
    msg.preview ||
    msg.ultima_mensagem ||
    "";
  const canal = normalizar(msg.canal || msg.channel || "");
  const autor = normalizar(msg.autor || msg.de || msg.from || "");
  const direcao = normalizar(msg.direcao || msg.direction || "");
  const origemEnvio = normalizar(
    msg.origem_envio || msg.origemEnvio || msg.automacao_tipo || "",
  );
  const tipoResposta = normalizar(
    msg.dra_malu_tipo_resposta || msg.tipo_resposta || msg.tipoResposta || "",
  );

  const textoRecuperacao = textoIndicaRecuperacaoDraMaluCaptaZap(texto);
  const canalInterno =
    canal.includes("interno") || canal.includes("captazap_interno");
  const origemDraMalu =
    origemEnvio.includes("dra_malu") ||
    tipoResposta.includes("dra_malu") ||
    autor.includes("dra malu") ||
    autor.includes("dra. malu") ||
    autor.includes("sistema");

  return Boolean(
    textoRecuperacao &&
    (canalInterno || origemDraMalu || direcao.includes("intern")),
  );
}

function getRecuperacaoDraMaluInfoCaptaZap(conversa = {}, mensagens = []) {
  const mensagensLista = Array.isArray(mensagens) ? mensagens : [];
  const ultimaMensagem = mensagensLista[mensagensLista.length - 1] || null;
  const temMensagemRecuperacao = mensagensLista.some((msg) =>
    isMensagemRecuperadoDraMaluCaptaZap(msg),
  );
  const ultimaEhRecuperacao = ultimaMensagem
    ? isMensagemRecuperadoDraMaluCaptaZap(ultimaMensagem)
    : false;

  const flagsDiretas = Boolean(
    conversa.recuperado_dra_malu ||
    conversa.recuperadoDraMalu ||
    conversa._recuperado_dra_malu ||
    conversa.origem_recuperacao === "dra_malu_ia" ||
    conversa.origemRecuperacao === "dra_malu_ia" ||
    conversa.recuperado_por_ia ||
    conversa.recuperadoPorIa,
  );

  const textoResumo = [
    conversa.preview,
    conversa.ultima_mensagem,
    conversa.ultimaMensagem,
    conversa.mensagem,
    conversa.observacao,
    conversa.observacao_fechamento,
    conversa.ultimo_evento,
  ]
    .filter(Boolean)
    .join(" \n ");

  const textoRecuperacao = textoIndicaRecuperacaoDraMaluCaptaZap(textoResumo);
  const recuperado = Boolean(
    flagsDiretas || temMensagemRecuperacao || textoRecuperacao,
  );
  const valorOperacional =
    conversa.recuperado_operacional_ativo ??
    conversa.recuperadoOperacionalAtivo;
  const temEstadoOperacionalExplicito =
    valorOperacional !== undefined && valorOperacional !== null;
  const operacionalAtivo = temEstadoOperacionalExplicito
    ? getBooleanDraMaluCaptaZapFrontend(valorOperacional)
    : recuperado;

  return {
    recuperado: operacionalAtivo,
    recuperadoHistorico: recuperado,
    operacionalAtivo,
    temEstadoOperacionalExplicito,
    ultimaEhRecuperacao: Boolean(ultimaEhRecuperacao || textoRecuperacao),
    temMensagemRecuperacao,
    mensagem: ultimaMensagem,
  };
}

function aplicarSinalizacaoRecuperadoDraMaluCaptaZap(
  conversa = {},
  mensagensNormalizadas = [],
) {
  const info = getRecuperacaoDraMaluInfoCaptaZap(
    conversa,
    mensagensNormalizadas,
  );

  const tagsAtuais = Array.isArray(conversa.tags) ? conversa.tags : [];
  const tagsSemAlertaDraMalu = tagsAtuais.filter(
    (tag) =>
      tag !== CAPTAZAP_DRA_MALU_RECUPERADO_BADGE && tag !== "IA recuperou",
  );

  if (!info.operacionalAtivo) {
    return {
      ...conversa,
      recuperado_operacional_ativo: false,
      recuperadoOperacionalAtivo: false,
      _captazap_destaque_amarelo: false,
      _captazap_badge_amarelo: false,
      _captazap_classe_visual: "",
      badge_recuperacao: undefined,
      badgeRecuperacao: undefined,
      badge_recuperacao_dra_malu: undefined,
      badgeRecuperacaoDraMalu: undefined,
      tags: tagsSemAlertaDraMalu,
    };
  }

  const previewOriginal =
    conversa.preview ||
    conversa.ultima_mensagem ||
    conversa.ultimaMensagem ||
    conversa.mensagem ||
    "";
  const previewRecuperacao = info.ultimaEhRecuperacao
    ? CAPTAZAP_DRA_MALU_RECUPERADO_BADGE
    : previewOriginal;

  return {
    ...conversa,
    recuperado_dra_malu: true,
    recuperadoDraMalu: true,
    _recuperado_dra_malu: true,
    recuperado_por_ia: true,
    recuperadoPorIa: true,
    origem_recuperacao: "dra_malu_ia",
    origemRecuperacao: "dra_malu_ia",
    recuperado_operacional_ativo: true,
    recuperadoOperacionalAtivo: true,
    badge_recuperacao: CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
    badgeRecuperacao: CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
    badge_recuperacao_dra_malu: CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
    badgeRecuperacaoDraMalu: CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
    _captazap_destaque_amarelo: true,
    _captazap_badge_amarelo: true,
    _captazap_classe_visual: "recuperado-dra-malu",
    preview: previewRecuperacao || CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
    ultima_mensagem: previewRecuperacao || CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
    ultimaMensagem: previewRecuperacao || CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
    tags: Array.from(
      new Set([
        ...tagsAtuais,
        CAPTAZAP_DRA_MALU_RECUPERADO_BADGE,
        "IA recuperou",
      ]),
    ),
  };
}

function extrairMensagensRespostaCaptaZap(data = {}) {
  const candidatos = [
    data?.mensagens,
    data?.messages,
    data?.data,
    data?.items,
    data?.results,
    data?.historico,
    data?.historico_mensagens,
    data?.historicoMensagens,
    data?.conversa?.mensagens,
    data?.conversa?.messages,
    data?.chat?.mensagens,
    data?.chat?.messages,
  ];

  const lista = candidatos.find((item) => Array.isArray(item)) || [];

  return lista.map(normalizarMensagemCaptazap);
}

function getTelefonesHistoricoCandidatosCaptaZap(conversa = {}) {
  const leadsVinculadas = [
    ...(Array.isArray(conversa?.leads_vinculadas)
      ? conversa.leads_vinculadas
      : []),
    ...(Array.isArray(conversa?.leadsVinculadas)
      ? conversa.leadsVinculadas
      : []),
  ];

  const candidatos = [
    conversa?._captazap_telefone_historico,
    conversa?.telefone_historico,
    conversa?.telefoneHistorico,
    conversa?.numero,
    conversa?.telefone,
    conversa?.telefone1,
    conversa?.telefone2,
    conversa?.celular,
    conversa?.whatsapp,
    conversa?.numero_whatsapp,
    conversa?.telefone_whatsapp,
    conversa?.phone,
    conversa?.telefone_normalizado,
    conversa?.lead?.telefone_whatsapp,
    conversa?.lead?.numero_whatsapp,
    conversa?.lead?.telefone1,
    conversa?.lead?.telefone2,
    conversa?.lead?.telefone,
    conversa?.lead?.celular,
    conversa?.lead?.whatsapp,
    conversa?.lead?.numero,
    conversa?.lead?.phone,
    ...leadsVinculadas.flatMap((lead = {}) => [
      lead.telefone_whatsapp,
      lead.numero_whatsapp,
      lead.telefone1,
      lead.telefone2,
      lead.telefone,
      lead.celular,
      lead.whatsapp,
      lead.numero,
      lead.phone,
    ]),
  ];

  const unicos = new Set();

  candidatos.forEach((valor) => {
    const raw = String(valor || "").trim();
    const digits = normalizarTelefone(raw);
    const leadBrasil = normalizarTelefoneLeadBrasil(raw);
    const whatsappBrasil = normalizarTelefoneWhatsappBrasil(raw);

    [raw, digits, leadBrasil, whatsappBrasil]
      .filter(Boolean)
      .forEach((item) => unicos.add(String(item).trim()));
  });

  return Array.from(unicos).filter(Boolean);
}

function getTelefoneHistoricoPrincipalCaptaZap(conversa = {}) {
  return getTelefonesHistoricoCandidatosCaptaZap(conversa)[0] || "";
}

function montarContextoLeadsConversa(data = {}) {
  const leads = normalizarLeadsVinculadasCaptazap(
    data.leads_vinculadas || data.leadsVinculadas || [],
  );

  return {
    leads_vinculadas: leads,
    leadsVinculadas: leads,
    // Conta somente leads distintas reais. Não confiar em totais antigos vindos
    // do backend/estado, pois eles podem manter "2 pacientes" após deduplicação.
    total_leads_vinculadas: leads.length,
    totalLeadsVinculadas: leads.length,
    telefone_compartilhado: leads.length > 1,
    telefoneCompartilhado: leads.length > 1,
    lead_contexto_id:
      data.lead_contexto_id ||
      data.leadContextoId ||
      (leads.length === 1 ? getLeadId(leads[0]) : null),
    leadContextoId:
      data.leadContextoId ||
      data.lead_contexto_id ||
      (leads.length === 1 ? getLeadId(leads[0]) : null),
  };
}

function getLeadsVinculadasConversa(conversa = {}) {
  return normalizarLeadsVinculadasCaptazap(
    conversa.leads_vinculadas || conversa.leadsVinculadas || [],
  );
}

function getLeadContextoIdConversa(conversa = {}) {
  return getLeadContextoSeguroConversaCaptaZap(conversa);
}

function getLeadVinculadaPorId(conversa = {}, leadId) {
  if (!leadId) return null;

  return (
    getLeadsVinculadasConversa(conversa).find(
      (lead) => String(getLeadId(lead)) === String(leadId),
    ) || null
  );
}

function conversaExigeSelecaoPaciente(conversa = {}) {
  const leads = getLeadsVinculadasConversa(conversa);

  return leads.length > 1 && !getLeadContextoIdConversa(conversa);
}

function validarContextoPacienteConversa(conversa = {}, acao = "continuar") {
  if (!conversaExigeSelecaoPaciente(conversa)) return true;

  window.alert(
    `Este telefone está vinculado a ${getLeadsVinculadasConversa(conversa).length} pacientes. Selecione o paciente em contexto antes de ${acao}.`,
  );

  return false;
}

function normalizarConversaCaptazap(conversa = {}) {
  const leadOriginal = conversa.lead || {};
  const telefone =
    conversa._captazap_telefone_historico ||
    conversa.numero ||
    conversa.telefone ||
    conversa.telefone1 ||
    conversa.celular ||
    conversa.whatsapp ||
    conversa.numero_whatsapp ||
    conversa.telefone_whatsapp ||
    conversa.phone ||
    leadOriginal.telefone_whatsapp ||
    leadOriginal.numero_whatsapp ||
    leadOriginal.telefone1 ||
    leadOriginal.telefone ||
    leadOriginal.celular ||
    leadOriginal.whatsapp ||
    leadOriginal.numero ||
    leadOriginal.phone ||
    "";

  const telefoneEhLid = ehWhatsappLidCaptaZap(telefone);
  const telefoneNormalizado = telefoneEhLid ? "" : normalizarTelefone(telefone);
  const identidadeWhatsapp =
    normalizarIdentidadeWhatsappComparacaoCaptaZap(telefone);
  const leadOriginalPertenceAoTelefone = leadOriginal?.id
    ? !telefoneEhLid &&
      leadPertenceAoTelefoneCaptaZap(leadOriginal, telefoneNormalizado)
    : true;
  const lead = leadOriginalPertenceAoTelefone ? leadOriginal : {};

  const leadsVinculadas = normalizarLeadsVinculadasCaptazap(
    conversa.leads_vinculadas || conversa.leadsVinculadas || [],
  ).filter((leadItem) =>
    telefoneNormalizado
      ? leadPertenceAoTelefoneCaptaZap(leadItem, telefoneNormalizado)
      : true,
  );
  const leadContextoIdBruto =
    conversa.lead_contexto_id ||
    conversa.leadContextoId ||
    conversa.contexto_lead_id ||
    conversa.contextoLeadId ||
    (leadsVinculadas.length === 1 ? getLeadId(leadsVinculadas[0]) : null);
  const leadContexto = getLeadVinculadaPorId(
    { leads_vinculadas: leadsVinculadas },
    leadContextoIdBruto,
  );
  const leadContextoIdSeguro = leadContexto?.id || null;
  const telefoneCompartilhadoSemContexto =
    leadsVinculadas.length > 1 && !leadContextoIdSeguro;
  const leadFinal =
    leadContexto ||
    (leadsVinculadas.length === 1 ? leadsVinculadas[0] : null) ||
    (telefoneCompartilhadoSemContexto ? {} : lead);
  const leadFinalId = getLeadId(leadFinal) || null;
  const leadDivergenteRemovida = Boolean(
    leadOriginal?.id && !leadOriginalPertenceAoTelefone,
  );

  const statusChamado = getStatusChamadoVisual(conversa);

  const fechado = statusChamado === "fechado";

  const clienteRespondeuDepoisDeFechado = getClienteRespondeuDepoisDeFechado({
    ...conversa,
    status_chamado: statusChamado,
    chamado_status: statusChamado,
  });

  const operacional = !fechado || clienteRespondeuDepoisDeFechado;
  const mensagensNormalizadas = ordenarMensagensConversaCaptaZap(
    Array.isArray(conversa.mensagens) ? conversa.mensagens : [],
  );
  const recuperacaoDraMaluInfo = getRecuperacaoDraMaluInfoCaptaZap(
    conversa,
    mensagensNormalizadas,
  );
  const previewRecuperadoDraMalu = recuperacaoDraMaluInfo.ultimaEhRecuperacao
    ? CAPTAZAP_DRA_MALU_RECUPERADO_BADGE
    : "";
  const temHistoricoOuIdentidadeWhatsApp = Boolean(
    conversa.conversa_id ||
    conversa.conversaId ||
    conversa.chamado_id ||
    conversa.chamadoId ||
    conversa.id_chamado ||
    conversa.chamada_id ||
    conversa.ticket_id ||
    conversa.ticketId ||
    mensagensNormalizadas.length > 0 ||
    conversa._historico_whatsapp_carregado ||
    conversa._historico_whatsapp_tentado ||
    conversa._captazap_conversa_operacional_id ||
    conversa._captazap_chamado_operacional_id ||
    conversa._captazap_telefone_historico ||
    (deveCarregarHistoricoPorTelefoneCaptaZap(conversa) && telefone),
  );

  return aplicarSinalizacaoRecuperadoDraMaluCaptaZap(
    {
      ...conversa,
      id:
        conversa.id ||
        conversa.conversa_id ||
        `whatsapp-${identidadeWhatsapp || leadContextoIdSeguro || "sem-lead"}`,
      conversa_id:
        conversa.conversa_id ||
        conversa.id ||
        `whatsapp-${identidadeWhatsapp || leadContextoIdSeguro || "sem-lead"}`,
      empresa_id:
        conversa.empresa_id || leadFinal.empresa_id || lead.empresa_id,
      nome:
        leadFinal?.nome_lead ||
        leadFinal?.nome ||
        (telefoneCompartilhadoSemContexto
          ? telefone || "WhatsApp compartilhado"
          : conversa.nome || lead.nome_lead || lead.nome || telefone) ||
        "Contato sem nome",
      numero: telefone,
      telefone,
      telefone_normalizado:
        normalizarWhatsappLidCaptaZap(telefone) || normalizarTelefone(telefone),
      foto: getAvatarConversa(conversa),
      avatar: getAvatarConversa(conversa),
      avatar_url: getAvatarConversa(conversa),
      profileUrl: getAvatarConversa(conversa),
      canal: conversa.canal || "whatsapp",
      status: fechado
        ? "fechado"
        : conversa.status || conversa.status_atendimento || "em_atendimento",
      status_chamado: statusChamado || conversa.status_chamado || "aberto",
      chamado_status: statusChamado || conversa.chamado_status || "aberto",
      status_negociacao:
        conversa.status_negociacao ||
        leadFinal?.status_negociacao ||
        lead.status_negociacao ||
        "",
      // Campos de preview definidos uma unica vez adiante, com prioridade
      // para a sinalizacao de recuperacao da Dra. Malu.
      direcao:
        conversa.direcao ||
        conversa.ultima_direcao ||
        conversa.ultimaDirecao ||
        conversa.ultima_mensagem_direcao ||
        conversa.ultimaMensagemDirecao ||
        conversa.last_direction ||
        conversa.direction ||
        "",
      ultima_direcao:
        conversa.ultima_direcao ||
        conversa.ultimaDirecao ||
        conversa.ultima_mensagem_direcao ||
        conversa.ultimaMensagemDirecao ||
        conversa.direcao ||
        conversa.last_direction ||
        conversa.direction ||
        "",
      ultimaDirecao:
        conversa.ultimaDirecao ||
        conversa.ultima_direcao ||
        conversa.ultima_mensagem_direcao ||
        conversa.ultimaMensagemDirecao ||
        conversa.direcao ||
        conversa.last_direction ||
        conversa.direction ||
        "",
      ultimaMensagemEm:
        conversa.ultimaMensagemEm ||
        conversa.ultima_mensagem_em ||
        conversa.last_message_at ||
        conversa.data_ultima_mensagem ||
        conversa.updated_at ||
        conversa.created_at ||
        conversa.datahora ||
        "",
      ultima_mensagem_em:
        conversa.ultima_mensagem_em ||
        conversa.ultimaMensagemEm ||
        conversa.last_message_at ||
        conversa.data_ultima_mensagem ||
        conversa.updated_at ||
        conversa.created_at ||
        conversa.datahora ||
        "",
      hora:
        conversa.hora ||
        formatHoraMensagemLocal(
          conversa.ultimaMensagemEm ||
            conversa.ultima_mensagem_em ||
            conversa.last_message_at ||
            conversa.data_ultima_mensagem ||
            conversa.updated_at ||
            conversa.created_at ||
            conversa.datahora,
        ),
      lead_contexto_id: leadContextoIdSeguro,
      leadContextoId: leadContextoIdSeguro,
      contexto_lead_id: leadContextoIdSeguro,
      contextoLeadId: leadContextoIdSeguro,
      lead_id: leadFinalId,
      leadId: leadFinalId,
      lead: leadFinalId ? leadFinal : null,
      contexto_lead_obrigatorio:
        Boolean(
          conversa.contexto_lead_obrigatorio ||
          conversa.contextoLeadObrigatorio,
        ) || telefoneCompartilhadoSemContexto,
      contextoLeadObrigatorio:
        Boolean(
          conversa.contextoLeadObrigatorio ||
          conversa.contexto_lead_obrigatorio,
        ) || telefoneCompartilhadoSemContexto,
      contexto_lead_definido: Boolean(leadContextoIdSeguro),
      contextoLeadDefinido: Boolean(leadContextoIdSeguro),
      _lead_divergente_removida: leadDivergenteRemovida,
      _lead_divergente_original_id: leadDivergenteRemovida
        ? leadOriginal.id
        : null,
      _lead_divergente_original_nome: leadDivergenteRemovida
        ? leadOriginal.nome_lead || leadOriginal.nome || ""
        : "",
      leads_vinculadas: leadsVinculadas,
      leadsVinculadas,
      // Estado visual deve refletir a lista deduplicada atual.
      // Se mantiver total_leads_vinculadas antigo, o QueueList continua exibindo
      // "2 pacientes" mesmo com uma única lead real vinculada.
      total_leads_vinculadas: leadsVinculadas.length,
      totalLeadsVinculadas: leadsVinculadas.length,
      telefone_compartilhado: leadsVinculadas.length > 1,
      telefoneCompartilhado: leadsVinculadas.length > 1,
      conversa_ativa: operacional,
      conversaAtiva: operacional,
      sem_conversa: temHistoricoOuIdentidadeWhatsApp ? false : !operacional,
      semConversa: temHistoricoOuIdentidadeWhatsApp ? false : !operacional,
      aba_captazap: fechado
        ? operacional
          ? getDestinoReaberturaChamadoFechado(conversa).minhas
            ? "minhas"
            : "fila"
          : "fechados"
        : conversa.aba_captazap,
      fila_atendimento: fechado
        ? operacional
          ? getDestinoReaberturaChamadoFechado(conversa).fila
          : false
        : conversa.fila_atendimento,
      aguardando_atendimento: fechado
        ? operacional
          ? getDestinoReaberturaChamadoFechado(conversa).fila
          : false
        : conversa.aguardando_atendimento,
      em_fila: fechado
        ? operacional
          ? getDestinoReaberturaChamadoFechado(conversa).fila
          : false
        : conversa.em_fila,
      _fila_global: fechado
        ? operacional
          ? getDestinoReaberturaChamadoFechado(conversa).fila
          : false
        : conversa._fila_global,
      _fila_equipe: fechado
        ? operacional
          ? getDestinoReaberturaChamadoFechado(conversa).fila
          : false
        : conversa._fila_equipe,
      _fechado_com_resposta: fechado && operacional,
      cliente_respondeu_apos_fechamento: fechado && operacional ? true : false,
      preview:
        previewRecuperadoDraMalu ||
        conversa.preview ||
        conversa.ultima_mensagem ||
        conversa.ultimaMensagem ||
        conversa.last_message ||
        conversa.mensagem ||
        "Conversa WhatsApp",
      ultima_mensagem:
        previewRecuperadoDraMalu ||
        conversa.ultima_mensagem ||
        conversa.ultimaMensagem ||
        conversa.last_message ||
        conversa.preview ||
        conversa.mensagem ||
        "",
      ultimaMensagem:
        previewRecuperadoDraMalu ||
        conversa.ultimaMensagem ||
        conversa.ultima_mensagem ||
        conversa.last_message ||
        conversa.preview ||
        conversa.mensagem ||
        "",
      mensagens: mensagensNormalizadas,
      _captazap_real: true,
    },
    mensagensNormalizadas,
  );
}

function normalizarMensagemCaptazap(msg = {}) {
  const direcao = normalizar(
    msg.direcao ||
      msg.direction ||
      msg.tipo_direcao ||
      msg.sentido ||
      (msg.fromMe === true || msg.from_me === true ? "enviada" : ""),
  );
  const tipoMidia =
    msg.tipo_midia || msg.tipoMidia || msg.tipoMidiaMensagem || null;
  const midiaUrl = normalizarUrlMidiaCaptaZap(
    msg.midia_url ||
      msg.mediaUrl ||
      msg.media_url ||
      msg.url_midia ||
      msg.arquivo_url ||
      msg.fileUrl ||
      msg.file_url ||
      msg.downloadUrl ||
      msg.download_url ||
      msg.imageUrl ||
      msg.image_url ||
      msg.videoUrl ||
      msg.video_url ||
      msg.audioUrl ||
      msg.audio_url ||
      msg.url ||
      "",
  );

  const texto =
    msg.texto ||
    msg.mensagem ||
    msg.legenda ||
    msg.caption ||
    (tipoMidia === "audio"
      ? "Mensagem de áudio"
      : tipoMidia === "imagem"
        ? "Imagem"
        : tipoMidia === "video"
          ? "Vídeo"
          : tipoMidia === "documento"
            ? "Documento"
            : "");
  const recuperadoDraMalu = isMensagemRecuperadoDraMaluCaptaZap({
    ...msg,
    texto,
  });
  const textoFinal = recuperadoDraMalu
    ? CAPTAZAP_DRA_MALU_RECUPERADO_TEXTO_CHAT
    : texto;

  return {
    ...msg,
    id:
      msg.id ||
      msg.msgid ||
      msg.messageId ||
      msg.message_id ||
      `msg-${Date.now()}`,
    de: recuperadoDraMalu
      ? "sistema"
      : direcao.includes("recebid") ||
          direcao.includes("entrada") ||
          direcao.includes("inbound") ||
          msg.autor === "cliente"
        ? "cliente"
        : direcao.includes("intern")
          ? "sistema"
          : "operador",
    texto: textoFinal,
    mensagem: recuperadoDraMalu ? textoFinal : msg.mensagem || textoFinal,
    direcao: recuperadoDraMalu
      ? "interno"
      : msg.direcao ||
        msg.direction ||
        (direcao.includes("recebid") ||
        direcao.includes("entrada") ||
        direcao.includes("inbound")
          ? "recebida"
          : direcao.includes("intern")
            ? "interno"
            : "enviada"),
    direction: recuperadoDraMalu
      ? "interno"
      : msg.direction ||
        msg.direcao ||
        (direcao.includes("recebid") ||
        direcao.includes("entrada") ||
        direcao.includes("inbound")
          ? "recebida"
          : direcao.includes("intern")
            ? "interno"
            : "enviada"),
    fromMe: recuperadoDraMalu
      ? false
      : msg.fromMe === true ||
        msg.from_me === true ||
        direcao.includes("enviad") ||
        direcao.includes("saida") ||
        direcao.includes("outbound") ||
        direcao.includes("outgoing") ||
        direcao.includes("sent"),
    hora:
      msg.hora ||
      formatHoraMensagemLocal(msg.datahora || msg.created_at || msg.enviada_em),
    status: msg.status || "",
    tipo_midia: tipoMidia,
    tipoMidia,

    // Normaliza mídia para a URL pública da API.
    // Quando o backend salva como /uploads/... ou storage/..., o browser não pode
    // usar a URL relativa ao domínio do front; precisa apontar para api.captacrm.com.br.
    midia_url: midiaUrl,
    mediaUrl: midiaUrl,
    media_url: midiaUrl,
    url_midia: midiaUrl,
    arquivo_url: midiaUrl,
    fileUrl: midiaUrl,
    downloadUrl: midiaUrl,
    imageUrl: tipoMidia === "imagem" ? midiaUrl : msg.imageUrl,
    image_url: tipoMidia === "imagem" ? midiaUrl : msg.image_url,
    videoUrl: tipoMidia === "video" ? midiaUrl : msg.videoUrl,
    video_url: tipoMidia === "video" ? midiaUrl : msg.video_url,
    audioUrl: tipoMidia === "audio" ? midiaUrl : msg.audioUrl,
    audio_url: tipoMidia === "audio" ? midiaUrl : msg.audio_url,
    recuperado_dra_malu: recuperadoDraMalu,
    recuperadoDraMalu,
    _recuperado_dra_malu: recuperadoDraMalu,
    _captazap_mensagem_interna:
      recuperadoDraMalu || Boolean(msg._captazap_mensagem_interna),
    _captazap_destaque_amarelo:
      recuperadoDraMalu || Boolean(msg._captazap_destaque_amarelo),
    _captazap_classe_visual: recuperadoDraMalu
      ? "recuperado-dra-malu"
      : msg._captazap_classe_visual || "",
    tipo_sistema: recuperadoDraMalu
      ? "recuperado_dra_malu"
      : msg.tipo_sistema || msg.tipoSistema || null,
    tipoSistema: recuperadoDraMalu
      ? "recuperado_dra_malu"
      : msg.tipoSistema || msg.tipo_sistema || null,
    canal: recuperadoDraMalu
      ? msg.canal || "captazap_interno"
      : msg.canal || "whatsapp",
  };
}

function normalizarUrlMidiaCaptaZap(value = "") {
  const url = String(value || "").trim();

  if (!url) return "";

  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("blob:") ||
    url.startsWith("data:image/") ||
    url.startsWith("data:video/") ||
    url.startsWith("data:audio/") ||
    url.startsWith("data:application/")
  ) {
    return url;
  }

  if (url.startsWith("//")) {
    return `https:${url}`;
  }

  if (url.startsWith("/storage/") || url.startsWith("/uploads/")) {
    return `${API_PUBLIC_BASE}${url}`;
  }

  if (url.startsWith("storage/") || url.startsWith("uploads/")) {
    return `${API_PUBLIC_BASE}/${url}`;
  }

  if (url.startsWith("/")) {
    return `${API_PUBLIC_BASE}${url}`;
  }

  return `${API_PUBLIC_BASE}/${url}`;
}

function formatHoraMensagemLocal(value) {
  if (!value) return "";
  const data = new Date(value);
  if (Number.isNaN(data.getTime())) return "";

  return data.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getApiPublicBase(baseRaw = "") {
  const fallback = "https://api.captacrm.com.br";
  const base = String(baseRaw || "").trim();

  if (!base) return fallback;

  if (
    base === "/api" ||
    base === "api" ||
    base.startsWith("/api/") ||
    base.startsWith("./") ||
    base.startsWith("../")
  ) {
    return fallback;
  }

  if (base.startsWith("http://") || base.startsWith("https://")) {
    return base.replace(/\/api\/?$/, "").replace(/\/$/, "");
  }

  if (base.startsWith("/")) return fallback;

  return fallback;
}

function getEmpresaAtivaLocalStorage() {
  if (typeof window === "undefined") return {};

  try {
    return JSON.parse(window.localStorage?.getItem("empresaAtiva") || "{}");
  } catch {
    return {};
  }
}

function getUsuarioLocalStorage() {
  try {
    const usuario = JSON.parse(window.localStorage?.getItem("usuario") || "{}");
    const usuarioLogado = JSON.parse(
      window.localStorage?.getItem("usuarioLogado") || "{}",
    );

    return {
      ...usuario,
      ...usuarioLogado,
      foto:
        usuarioLogado?.foto ||
        usuario?.foto ||
        usuarioLogado?.foto_original ||
        usuario?.foto_original ||
        "",
    };
  } catch {
    return {};
  }
}

const perfilInstagramCaptaZapCache = new Map();

function isInstagramConversaCaptaZap(conversa = {}) {
  const canal = String(conversa?.canal || "").trim().toLowerCase();
  const instancia = String(
    conversa?.zapi_instance_id ||
      conversa?.zapiInstanceId ||
      conversa?.conexao_instance_id ||
      conversa?.conexaoInstanceId ||
      "",
  ).trim().toLowerCase();
  const telefone = String(
    conversa?.telefone || conversa?.numero || "",
  ).trim().toLowerCase();

  return Boolean(
    canal === "instagram" ||
      instancia.startsWith("instagram:") ||
      telefone.startsWith("ig:"),
  );
}

function getInstagramExternalUserIdConversaCaptaZap(conversa = {}) {
  const direto = String(
    conversa?.external_user_id ||
      conversa?.externalUserId ||
      conversa?.instagram_user_id ||
      conversa?.instagramUserId ||
      "",
  ).trim();
  if (direto) return direto;

  const telefone = String(
    conversa?.telefone || conversa?.numero || "",
  ).trim();
  if (telefone.toLowerCase().startsWith("ig:")) {
    return telefone.slice(3).trim();
  }

  const chave = String(
    conversa?.external_conversation_id ||
      conversa?.externalConversationId ||
      conversa?.chave_conversa ||
      "",
  ).trim();
  const match = chave.match(/:user:([^:]+)$/i);
  return match?.[1] ? String(match[1]).trim() : "";
}

async function buscarPerfilInstagramCaptaZap({
  empresaId,
  externalUserId,
}) {
  const userId = String(externalUserId || "").trim();
  if (!empresaId || !userId) return null;

  const cacheKey = `${empresaId}:${userId}`;
  if (perfilInstagramCaptaZapCache.has(cacheKey)) {
    return perfilInstagramCaptaZapCache.get(cacheKey);
  }

  try {
    const data = await requestJson(
      `/whatsapp/instagram/perfil?empresa_id=${encodeURIComponent(
        empresaId,
      )}&external_user_id=${encodeURIComponent(userId)}`,
    );

    const perfil = data?.perfil || data?.data || data || {};
    const normalizado = {
      id: String(perfil?.id || userId),
      name: String(perfil?.name || perfil?.nome || "").trim(),
      username: String(perfil?.username || perfil?.usuario || "").trim(),
      profileUrl: getImagemUrl(
        perfil?.profileUrl ||
          perfil?.profile_pic ||
          perfil?.profile_picture_url ||
          "",
      ),
    };

    perfilInstagramCaptaZapCache.set(cacheKey, normalizado);
    return normalizado;
  } catch (error) {
    console.warn(
      "CaptaZap | não foi possível carregar perfil do Instagram:",
      error?.message || error,
    );
    perfilInstagramCaptaZapCache.set(cacheKey, null);
    return null;
  }
}


function getImagemUrl(value) {
  const url = String(value || "").trim();

  if (!url) return "";

  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:image/") ||
    url.startsWith("blob:")
  ) {
    return url;
  }

  if (url.startsWith("//")) {
    return `https:${url}`;
  }

  if (url.startsWith("/storage/") || url.startsWith("/uploads/")) {
    return `${API_PUBLIC_BASE}${url}`;
  }

  if (url.startsWith("storage/") || url.startsWith("uploads/")) {
    return `${API_PUBLIC_BASE}/${url}`;
  }

  if (url.startsWith("/")) {
    return url;
  }

  return `${API_PUBLIC_BASE}/${url}`;
}

function calcularProducaoCaptadorHojeCaptaZap({
  empresaId,
  usuarioId,
  leads = [],
  agendamentos = [],
  recebimentosFinanceiros = [],
}) {
  const hojeISO = getHojeIsoLocalCaptaZap();
  const userId = String(usuarioId || "");

  const leadsValidas = (Array.isArray(leads) ? leads : []).filter((lead) => {
    if (!empresaId || !lead) return false;
    const leadEmpresaId = lead.empresa_id || lead.empresaId;
    return String(leadEmpresaId) === String(empresaId);
  });

  const leadById = new Map();
  for (const lead of leadsValidas) {
    const id = lead?.id || lead?.lead_id || lead?.leadId;
    if (id) leadById.set(String(id), lead);
  }

  const isDoCaptadorLogado = (lead) => {
    if (!lead || !userId) return false;
    const captadorId =
      lead.usuario_criador_id ||
      lead.captador_id ||
      lead.id_usuario ||
      lead.usuario_id ||
      lead.responsavel_id;

    return String(captadorId || "") === userId;
  };

  const resumo = {
    cadastrouHoje: 0,
    agendouHoje: 0,
    reagendouHoje: 0,
    producaoHoje: 0,
    agendaHoje: 0,
    compareceuHoje: 0,
    faltouHoje: 0,
    taxaPresencaHoje: 0,
    fechamentosHoje: 0,
    receitaHoje: 0,
  };

  for (const lead of leadsValidas) {
    if (!isDoCaptadorLogado(lead)) continue;

    const dataCriacao = dayStrCaptaZap(
      lead.criado_em || lead.created_at || lead.data_lead || lead.createdAt,
    );

    if (dataCriacao === hojeISO) resumo.cadastrouHoje += 1;
  }

  const agendaHojeFinalPorLead = new Map();

  for (const agendamento of Array.isArray(agendamentos) ? agendamentos : []) {
    const leadId = String(
      agendamento?.lead_id || agendamento?.id_lead || agendamento?.leadId || "",
    );
    const lead = leadById.get(leadId);

    if (!lead || !isDoCaptadorLogado(lead)) continue;

    const status = String(
      agendamento?.status_agendamento || agendamento?.status || "",
    ).trim();
    const dataEvento = dayStrCaptaZap(
      agendamento?.criado_em ||
        agendamento?.created_at ||
        agendamento?.data_criacao ||
        agendamento?.atualizado_em ||
        agendamento?.updated_at ||
        agendamento?.data_agendamento,
    );
    const dataAgenda = dayStrCaptaZap(
      agendamento?.data_agendamento ||
        agendamento?.data ||
        agendamento?.data_marcada ||
        agendamento?.agendado_para,
    );

    if (dataEvento === hojeISO && status === "Agendado") {
      resumo.agendouHoje += 1;
    }

    if (dataEvento === hojeISO && status === "Reagendado") {
      resumo.reagendouHoje += 1;
    }

    if (
      dataAgenda === hojeISO &&
      ["Agendado", "Reagendado", "Compareceu", "Faltou"].includes(status)
    ) {
      const keyLead = leadId || String(agendamento?.id || Math.random());
      const candidate = {
        status,
        sortId: Number(agendamento?.id || 0),
        sortTime: Math.max(
          toSortableDateTimeCaptaZap(
            agendamento?.data_agendamento || agendamento?.data,
          ),
          toSortableDateTimeCaptaZap(
            agendamento?.criado_em ||
              agendamento?.created_at ||
              agendamento?.updated_at,
          ),
        ),
      };

      const current = agendaHojeFinalPorLead.get(keyLead);
      if (
        !current ||
        candidate.sortId > current.sortId ||
        candidate.sortTime > current.sortTime
      ) {
        agendaHojeFinalPorLead.set(keyLead, candidate);
      }
    }
  }

  for (const item of agendaHojeFinalPorLead.values()) {
    resumo.agendaHoje += 1;
    if (item.status === "Compareceu") resumo.compareceuHoje += 1;
    if (item.status === "Faltou") resumo.faltouHoje += 1;
  }

  for (const recebimento of Array.isArray(recebimentosFinanceiros)
    ? recebimentosFinanceiros
    : []) {
    const dataRecebimento = getRecebimentoDateIsoCaptaZap(recebimento);
    const valor = toMoneyNumCaptaZap(
      recebimento?.valor ||
        recebimento?.valor_recebido ||
        recebimento?.total ||
        recebimento?.valor_total,
    );

    if (dataRecebimento !== hojeISO || valor <= 0) continue;

    const leadId = String(
      recebimento?.lead_id || recebimento?.id_lead || recebimento?.leadId || "",
    );
    const lead = leadById.get(leadId);
    const captadorId =
      recebimento?.usuario_criador_id ||
      recebimento?.captador_id ||
      lead?.usuario_criador_id ||
      lead?.captador_id ||
      lead?.id_usuario;

    if (!userId || String(captadorId || "") !== userId) continue;

    resumo.fechamentosHoje += 1;
    resumo.receitaHoje += valor;
  }

  resumo.producaoHoje = resumo.agendouHoje + resumo.reagendouHoje;

  const basePresenca = resumo.compareceuHoje + resumo.faltouHoje;
  resumo.taxaPresencaHoje = basePresenca
    ? +((100 * resumo.compareceuHoje) / basePresenca).toFixed(1)
    : 0;

  return resumo;
}

function getHojeIsoLocalCaptaZap(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

function dayStrCaptaZap(value) {
  if (!value) return null;

  const s = String(value).trim();
  if (!s) return null;

  const head10 = s.length >= 10 ? s.slice(0, 10) : s;
  if (/^\d{4}-\d{2}-\d{2}$/.test(head10)) return head10;

  const br = head10.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;

  const ms = new Date(s.replace(" ", "T")).getTime();
  if (!Number.isFinite(ms)) return null;

  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function toSortableDateTimeCaptaZap(value) {
  if (!value) return 0;

  const s = String(value).trim();
  if (!s) return 0;

  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}(:\d{2})?$/.test(s)) {
    const ms = new Date(s.replace(" ", "T")).getTime();
    return Number.isFinite(ms) ? ms : 0;
  }

  const ms = new Date(s).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function getRecebimentoDateIsoCaptaZap(item = {}) {
  return dayStrCaptaZap(
    item?.data_recebimento ||
      item?.data_pagamento ||
      item?.created_at ||
      item?.criado_em,
  );
}

function toMoneyNumCaptaZap(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;

  const s = String(value)
    .replace(/\s|R\$\s?/g, "")
    .trim();

  if (/^[\d.]+,\d{2}$/.test(s)) {
    return Number(s.replace(/\./g, "").replace(",", "."));
  }

  const n = Number(s.replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function moedaCaptaZap(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

function calcularPendenciasAgendaCaptaZap({
  agendamentos = [],
  leads = [],
  empresaId,
  empresaNome,
  usuarios = [],
  fontes = [],
  campanhas = [],
  procedimentos = [],
  regioes = [],
  dentistas = [],
  usuarioId = null,
  usuarioNome = "",
} = {}) {
  const listaAgendamentos = Array.isArray(agendamentos) ? agendamentos : [];
  const leadsValidas = Array.isArray(leads) ? leads : [];
  const leadById = new Map(
    leadsValidas
      .map((lead) => [String(getLeadId(lead) || ""), lead])
      .filter(([leadId]) => Boolean(leadId)),
  );

  const hoje = inicioDiaLocalCaptaZap(new Date());
  const amanhaInicio = addDiasLocalCaptaZap(hoje, 1);
  const depoisAmanhaInicio = addDiasLocalCaptaZap(hoje, 2);
  const limiteReagendar = addDiasLocalCaptaZap(hoje, -45);

  const agendasConsolidadas = consolidarUltimoStatusAgendasCaptaZap(
    listaAgendamentos,
    empresaId,
  );

  const agendaFuturaValidaPorLead = new Set();

  for (const item of agendasConsolidadas) {
    const leadId = String(item.leadId || "");
    if (!leadId) continue;

    const dataAgenda = item.dataAgenda;
    if (!dataAgenda || dataAgenda < hoje) continue;

    if (isStatusAgendaValidoCaptaZap(item.statusNorm)) {
      agendaFuturaValidaPorLead.add(leadId);
    }
  }

  const pendenciasPorLead = new Map();

  for (const item of agendasConsolidadas) {
    const agenda = item.agendamento || {};
    const leadId = String(item.leadId || "");
    const lead = leadById.get(leadId) || agenda.lead || agenda.lead_dados || {};

    // Lead marcada como perdida/inativa/descartada não pode continuar em
    // Pendência Agenda. Esse fluxo é para recuperação operacional de agenda,
    // não para pacientes já encerrados comercialmente.
    if (isLeadForaPendenciaAgendaCaptaZap(lead)) continue;

    const telefone =
      agenda.telefone ||
      agenda.telefone1 ||
      lead.telefone1 ||
      lead.telefone ||
      lead.celular ||
      lead.whatsapp ||
      "";

    if (
      !pendenciaAgendaPertenceAoUsuarioCaptaZap({
        agendamento: agenda,
        lead,
        usuarioId,
        usuarioNome,
      })
    ) {
      continue;
    }

    const chaveLead = leadId || normalizarTelefone(telefone);

    if (!chaveLead) continue;

    const dataAgenda = item.dataAgenda;
    if (!dataAgenda) continue;

    // A aba Pendência Agenda deve mostrar somente recuperação de agenda
    // (faltou/cancelou sem nova agenda futura). Confirmação de amanhã fica
    // na régua/agenda, não nesta pendência operacional.
    const confirmarAmanha = false;

    const precisaReagendar =
      isStatusAgendaCanceladoOuFaltaCaptaZap(item.statusNorm) &&
      dataAgenda >= limiteReagendar &&
      !agendaFuturaValidaPorLead.has(leadId);

    if (!confirmarAmanha && !precisaReagendar) continue;

    const tipoPendencia = confirmarAmanha ? "confirmar_amanha" : "reagendar";
    const prioridade = confirmarAmanha ? 2 : 1;
    const statusLabel = formatarStatusAgendaPendenciaCaptaZap(
      item.statusOriginal,
      tipoPendencia,
    );
    const dataLabel = formatarDataAgendaPendenciaCaptaZap(dataAgenda);
    const contatoBase = lead?.id
      ? leadToContatoConversa({
          lead,
          empresaId,
          empresaNome,
          usuarios,
          fontes,
          campanhas,
          procedimentos,
          regioes,
          dentistas,
        })
      : {
          id: `agenda-pendencia-${normalizarTelefone(telefone) || item.agendamentoId || item.sortTime}`,
          empresa_id: empresaId,
          empresa_nome: empresaNome,
          nome:
            agenda.nome_lead ||
            agenda.nome ||
            agenda.paciente ||
            telefone ||
            "Contato sem nome",
          numero: telefone,
          telefone: telefone,
          telefone_normalizado: normalizarTelefone(telefone),
          canal: "whatsapp",
          lead: {},
          leads_vinculadas: [],
          leadsVinculadas: [],
        };

    const pendencia = normalizarConversaCaptazap({
      ...contatoBase,
      id: `agenda-pendencia-${tipoPendencia}-${chaveLead}-${item.agendamentoId || item.sortTime}`,
      conversa_id: `agenda-pendencia-${tipoPendencia}-${chaveLead}-${item.agendamentoId || item.sortTime}`,
      empresa_id: contatoBase.empresa_id || empresaId,
      empresa_nome: contatoBase.empresa_nome || empresaNome,
      nome:
        contatoBase.nome ||
        lead?.nome_lead ||
        agenda.nome_lead ||
        agenda.nome ||
        telefone ||
        "Contato sem nome",
      numero: contatoBase.numero || telefone,
      telefone: contatoBase.telefone || telefone,
      canal: "whatsapp",
      status:
        tipoPendencia === "confirmar_amanha" ? "Confirmar agenda" : "Reagendar",
      status_agendamento: item.statusOriginal || statusLabel,
      data_agendamento: agenda.data_agendamento || agenda.data || "",
      preview:
        tipoPendencia === "confirmar_amanha"
          ? `Agenda amanhã ainda não confirmada · ${dataLabel}`
          : `${statusLabel} · precisa reagendar`,
      hora: dataLabel,
      updated_at:
        agenda.atualizado_em ||
        agenda.updated_at ||
        agenda.criado_em ||
        agenda.created_at ||
        agenda.data_agendamento ||
        new Date().toISOString(),
      ultimaMensagemEm:
        agenda.atualizado_em ||
        agenda.updated_at ||
        agenda.data_agendamento ||
        "",
      conversa_ativa: false,
      conversaAtiva: false,
      sem_conversa: true,
      semConversa: true,
      lead_id: leadId || contatoBase.lead_id || null,
      leadId: leadId || contatoBase.leadId || null,
      lead_contexto_id: leadId || contatoBase.lead_contexto_id || null,
      leadContextoId: leadId || contatoBase.leadContextoId || null,
      lead: lead?.id ? lead : contatoBase.lead || {},
      leads_vinculadas: lead?.id ? [lead] : contatoBase.leads_vinculadas || [],
      leadsVinculadas: lead?.id ? [lead] : contatoBase.leadsVinculadas || [],
      ultimo_agendamento: agenda,
      _ultimo_agendamento: agenda,
      agendamentos: [agenda],
      _captazap_origem_contatos: true,
      _contato_crm_consulta: true,
      _captazap_contexto_fixo_contato: true,
      _forcar_historico_por_telefone: true,
      _captazap_pendencia_agenda: true,
      _captazap_pendencia_agenda_tipo: tipoPendencia,
      _captazap_pendencia_agenda_label:
        tipoPendencia === "confirmar_amanha" ? "Confirmar amanhã" : "Reagendar",
      _captazap_pendencia_agenda_prioridade: prioridade,
      _captazap_pendencia_agenda_data: agenda.data_agendamento || agenda.data,
      tags: [
        "Pendência Agenda",
        tipoPendencia === "confirmar_amanha" ? "Confirmar amanhã" : "Reagendar",
        statusLabel,
        ...(Array.isArray(contatoBase.tags) ? contatoBase.tags : []),
      ].filter(Boolean),
    });

    const atual = pendenciasPorLead.get(chaveLead);
    if (
      !atual ||
      prioridade > (atual._captazap_pendencia_agenda_prioridade || 0) ||
      item.sortTime > (atual._captazap_pendencia_agenda_sort || 0)
    ) {
      pendenciasPorLead.set(chaveLead, {
        ...pendencia,
        _captazap_pendencia_agenda_sort: item.sortTime,
      });
    }
  }

  return [...pendenciasPorLead.values()].sort((a, b) => {
    const prioridadeDiff =
      (b._captazap_pendencia_agenda_prioridade || 0) -
      (a._captazap_pendencia_agenda_prioridade || 0);
    if (prioridadeDiff !== 0) return prioridadeDiff;

    const dataA = toTimeAgendaPendenciaCaptaZap(
      a._captazap_pendencia_agenda_data || a.data_agendamento,
    );
    const dataB = toTimeAgendaPendenciaCaptaZap(
      b._captazap_pendencia_agenda_data || b.data_agendamento,
    );
    return dataA - dataB;
  });
}

function isLeadForaPendenciaAgendaCaptaZap(lead = {}) {
  const statusNegociacao = normalizar(
    lead.status_negociacao ||
      lead.statusNegociacao ||
      lead.situacao ||
      lead.status ||
      "",
  );

  const statusAgenda = normalizar(
    lead.status_agendamento || lead.statusAgendamento || "",
  );

  const texto = `${statusNegociacao} ${statusAgenda}`.trim();

  return Boolean(
    texto.includes("perdid") ||
    texto.includes("inativ") ||
    texto.includes("descart") ||
    texto.includes("sem interesse") ||
    texto.includes("sem-interesse") ||
    texto.includes("migr"),
  );
}

function pendenciaAgendaPertenceAoUsuarioCaptaZap({
  agendamento = {},
  lead = {},
  usuarioId,
  usuarioNome = "",
} = {}) {
  const usuarioIdStr = String(usuarioId || "").trim();
  const usuarioNomeNorm = normalizar(usuarioNome);

  // A fila Pendência Agenda é operacional, igual "Minhas".
  // Portanto, não deve misturar agenda de outros captadores. A visão geral
  // deve ficar na Supervisão, não no CaptaZap do operador.
  if (!usuarioIdStr && !usuarioNomeNorm) return false;

  const idsResponsavel = [
    agendamento.responsavel_id,
    agendamento.usuario_responsavel_id,
    agendamento.atendente_id,
    agendamento.operador_id,
    agendamento.captador_id,
    agendamento.usuario_id,
    agendamento.id_usuario,
    agendamento.criado_por_id,
    agendamento.usuario_criador_id,
    agendamento.created_by,
    agendamento.user_id,
    agendamento.lead?.responsavel_id,
    agendamento.lead?.usuario_responsavel_id,
    agendamento.lead?.captador_id,
    agendamento.lead?.usuario_criador_id,
    agendamento.lead?.id_usuario,
    agendamento.lead?.user_id,
    lead.responsavel_id,
    lead.usuario_responsavel_id,
    lead.captador_id,
    lead.usuario_criador_id,
    lead.id_usuario,
    lead.user_id,
    lead.owner_id,
    lead.dono_id,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  if (usuarioIdStr && idsResponsavel.some((id) => id === usuarioIdStr)) {
    return true;
  }

  const nomesResponsavel = [
    agendamento.responsavel_nome,
    agendamento.usuario_responsavel_nome,
    agendamento.atendente_nome,
    agendamento.operador_nome,
    agendamento.captador_nome,
    agendamento.usuario_nome,
    agendamento.nome_usuario,
    agendamento.criado_por_nome,
    agendamento.usuario_criador_nome,
    agendamento.lead?.responsavel_nome,
    agendamento.lead?.usuario_responsavel_nome,
    agendamento.lead?.captador_nome,
    agendamento.lead?.usuario_criador_nome,
    agendamento.lead?.usuario_nome,
    lead.responsavel_nome,
    lead.usuario_responsavel_nome,
    lead.captador_nome,
    lead.usuario_criador_nome,
    lead.usuario_nome,
    lead.nome_usuario,
    lead.dono_nome,
    lead.owner_name,
    lead.ownerName,
  ]
    .map((value) => normalizar(value))
    .filter(Boolean);

  return Boolean(
    usuarioNomeNorm &&
    nomesResponsavel.some((nome) => nome === usuarioNomeNorm),
  );
}

function consolidarUltimoStatusAgendasCaptaZap(agendamentos = [], empresaId) {
  const map = new Map();

  for (const agendamento of Array.isArray(agendamentos) ? agendamentos : []) {
    if (!agendamento || typeof agendamento !== "object") continue;

    const itemEmpresaId = agendamento.empresa_id || agendamento.empresaId;
    if (
      empresaId &&
      itemEmpresaId &&
      String(itemEmpresaId) !== String(empresaId)
    ) {
      continue;
    }

    const leadId = String(
      agendamento.lead_id ||
        agendamento.leadId ||
        agendamento.id_lead ||
        agendamento.lead?.id ||
        "",
    );
    const telefone = normalizarTelefone(
      agendamento.telefone ||
        agendamento.telefone1 ||
        agendamento.numero ||
        agendamento.lead?.telefone1 ||
        agendamento.lead?.telefone ||
        "",
    );
    const dataAgenda = parseDataAgendaPendenciaCaptaZap(
      agendamento.data_agendamento ||
        agendamento.data ||
        agendamento.data_marcada ||
        agendamento.agendado_para ||
        agendamento.agendadoPara,
    );

    if (!dataAgenda) continue;

    const chavePessoa = leadId || telefone;
    if (!chavePessoa) continue;

    const chaveAgenda = `${chavePessoa}|${dataAgenda.getTime()}`;
    const statusOriginal = String(
      agendamento.status_agendamento || agendamento.status || "Agendado",
    ).trim();
    const statusNorm = normalizar(statusOriginal);
    const sortTime = Math.max(
      Number(agendamento.id || 0),
      toTimeAgendaPendenciaCaptaZap(agendamento.atualizado_em),
      toTimeAgendaPendenciaCaptaZap(agendamento.updated_at),
      toTimeAgendaPendenciaCaptaZap(agendamento.criado_em),
      toTimeAgendaPendenciaCaptaZap(agendamento.created_at),
      dataAgenda.getTime(),
    );

    const atual = map.get(chaveAgenda);
    if (!atual || sortTime >= atual.sortTime) {
      map.set(chaveAgenda, {
        agendamento,
        agendamentoId: agendamento.id || agendamento.agendamento_id || "",
        leadId,
        dataAgenda,
        statusOriginal,
        statusNorm,
        sortTime,
      });
    }
  }

  return [...map.values()];
}

function isStatusAgendaConfirmadoCaptaZap(statusNorm = "") {
  return normalizar(statusNorm).includes("confirm");
}

function isStatusAgendaCanceladoOuFaltaCaptaZap(statusNorm = "") {
  const status = normalizar(statusNorm);
  return status.includes("cancel") || status.includes("falt");
}

function isStatusAgendaFinalAtendimentoCaptaZap(statusNorm = "") {
  const status = normalizar(statusNorm);
  return (
    status.includes("comparec") ||
    status.includes("realiz") ||
    status.includes("atendid") ||
    status.includes("finaliz")
  );
}

function isStatusAgendaValidoCaptaZap(statusNorm = "") {
  const status = normalizar(statusNorm);

  if (isStatusAgendaCanceladoOuFaltaCaptaZap(status)) return false;
  if (status.includes("perdid") || status.includes("descart")) return false;

  return (
    !status ||
    status.includes("agend") ||
    status.includes("reagend") ||
    status.includes("confirm") ||
    isStatusAgendaFinalAtendimentoCaptaZap(status)
  );
}

function formatarStatusAgendaPendenciaCaptaZap(statusOriginal = "", tipo = "") {
  const status = String(statusOriginal || "").trim();
  if (status)
    return status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  return tipo === "confirmar_amanha" ? "Agendado" : "Reagendar";
}

function formatarDataAgendaPendenciaCaptaZap(value) {
  const data =
    value instanceof Date ? value : parseDataAgendaPendenciaCaptaZap(value);
  if (!data) return "";

  return data.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function parseDataAgendaPendenciaCaptaZap(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const data = new Date(value < 10000000000 ? value * 1000 : value);
    return Number.isNaN(data.getTime()) ? null : data;
  }

  const texto = String(value || "").trim();
  if (!texto) return null;

  const matchMysql = texto.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (matchMysql) {
    const data = new Date(
      Number(matchMysql[1]),
      Number(matchMysql[2]) - 1,
      Number(matchMysql[3]),
      Number(matchMysql[4] || 0),
      Number(matchMysql[5] || 0),
      Number(matchMysql[6] || 0),
      0,
    );
    return Number.isNaN(data.getTime()) ? null : data;
  }

  const parsed = new Date(texto);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toTimeAgendaPendenciaCaptaZap(value) {
  const data = parseDataAgendaPendenciaCaptaZap(value);
  return data ? data.getTime() : 0;
}

function inicioDiaLocalCaptaZap(value) {
  const data = value instanceof Date ? new Date(value) : new Date();
  data.setHours(0, 0, 0, 0);
  return data;
}

function addDiasLocalCaptaZap(value, dias) {
  const data = new Date(value);
  data.setDate(data.getDate() + Number(dias || 0));
  data.setHours(0, 0, 0, 0);
  return data;
}

function normalizarTelefone(valor) {
  return String(valor || "").replace(/\D/g, "");
}

function normalizar(valor) {
  return String(valor || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}
